
/* assets/services.js */
// ===================== DATA LAYER =====================
const DB_KEY = 'foot_ankle_cases_v2';
const AI_COUNT_KEY = 'foot_ankle_ai_count';
const ADMISSION_TASK_KEY = 'foot_ankle_admission_tasks_v1';

function loadDB() {
  try { return JSON.parse(localStorage.getItem(DB_KEY) || '[]'); } catch { return []; }
}
function saveDB(data) { localStorage.setItem(DB_KEY, JSON.stringify(data)); }
function getAICount() { return parseInt(localStorage.getItem(AI_COUNT_KEY) || '0'); }
function incAICount() { localStorage.setItem(AI_COUNT_KEY, getAICount() + 1); }
function loadAdmissionTasks() {
  try { return JSON.parse(localStorage.getItem(ADMISSION_TASK_KEY) || '[]'); } catch { return []; }
}
function saveAdmissionTasks(tasks) { localStorage.setItem(ADMISSION_TASK_KEY, JSON.stringify(tasks)); }

let currentCaseId = null;
let currentAiOutput = '';
let currentCasePhase = 'preop';
let currentFolderFilter = 'all';
let currentModuleFilter = 'all';
let currentDashboardSearch = '';
let currentWorkScope = 'all';   // 个人工作台范围：all/mine_todo/created/to_review/rejected/followup
let currentTodoScope = 'mine_todo'; // 独立待办台当前标签
let currentCenterFilter = 'all'; // 研究中心筛选：all / none / center:<id>
let currentSortMode = 'updated_desc';
let currentWorkbenchQueueFilter = 'all'; // 工作台摘要队列：all/pending/review/rejected/followup
let currentFacetFilters = {
  owner: 'all',
  review: 'all',
  admission: 'all',
  followup: 'all',
};
const AUTH_KEY = 'foot_ankle_auth_v1';
let _centerCache = [];

function loadAuth() {
  try { return JSON.parse(localStorage.getItem(AUTH_KEY) || '{}'); } catch { return {}; }
}
function saveAuth(data) { localStorage.setItem(AUTH_KEY, JSON.stringify(data)); }
function clearAuth() { localStorage.removeItem(AUTH_KEY); }
function getAuthToken() { return loadAuth().accessToken || ''; }
function getAuthUser() { return loadAuth().user || null; }
function isDemoAuth() { return /^demo-local-/.test(getAuthToken()); }
function getCurrentUserKey() {
  const user = getAuthUser();
  return user?.id || user?.username || '';
}
function getCachedCenters() { return Array.isArray(_centerCache) ? _centerCache : []; }
function getDefaultCenterId() {
  const user = getAuthUser();
  return user?.default_center_id || loadSettings().defaultCenterId || null;
}
function getCenterName(centerId) {
  if (!centerId) return '未归属中心';
  const c = getCachedCenters().find(item => String(item.id) === String(centerId));
  return c?.name || `中心 ${centerId}`;
}
async function refreshCenters() {
  if (!isCloudMode() || !isLoggedIn()) {
    _centerCache = [];
    return _centerCache;
  }
  try {
    _centerCache = await backendRequest('/api/centers/');
    if (!Array.isArray(_centerCache)) _centerCache = [];
    return _centerCache;
  } catch (err) {
    _centerCache = [];
    showToast('⚠️', `研究中心读取失败：${err.message}`);
    return _centerCache;
  }
}
async function setDefaultCenter(centerId) {
  const id = centerId ? Number(centerId) : null;
  const user = getAuthUser();
  if (!user) throw new Error('请先登录');
  if (isCloudMode()) {
    const updated = await backendRequest(`/api/users/${user.id}`, {
      method: 'PUT',
      body: JSON.stringify({ default_center_id: id }),
    });
    const auth = loadAuth();
    saveAuth({ ...auth, user: { ...(auth.user || {}), ...updated } });
  }
  const settings = loadSettings();
  saveSettingsData({ ...settings, defaultCenterId: id });
  return id;
}
function caseMatchesCenter(c) {
  if (!currentCenterFilter || currentCenterFilter === 'all') return true;
  if (currentCenterFilter === 'none') return !c.center_id && !c.centerId;
  const m = String(currentCenterFilter).match(/^center:(\d+)$/);
  if (!m) return true;
  return String(c.center_id || c.centerId || '') === m[1];
}
function isSystemAdminUser(user = getAuthUser()) {
  return user?.role === 'admin';
}
function isChiefClinical(user = getAuthUser()) {
  return user?.clinical_title === 'chief';
}
function canViewAllCases(user = getAuthUser()) {
  // 系统管理员可见全部；临床"主任"只在审核流转里有特权，不再绕过可见性
  return isSystemAdminUser(user);
}
function canCurrentUserViewCase(c) {
  if (!c) return false;
  // 云端模式下后端权威：只要后端把它返给了前端，就说明用户有权
  if (isCloudMode()) {
    if (c.backendId) return true;  // 由 syncCloudCasesToLocal 同步而来
    // 还没同步过的纯本地 case，让 admin 看见
    return canViewAllCases();
  }
  // 本地模式继续按字段比对
  if (canViewAllCases()) return true;
  const user = getAuthUser();
  if (!user) return true;  // 本地未登录可看全部
  const userKey = String(getCurrentUserKey());
  const ownerKeys = [
    c.createdByUserId,
    c.createdByUsername,
    c.created_by,
    c.created_by_username,
    c.ownerUserId,
    c.ownerUsername,
  ].filter(value => value !== undefined && value !== null).map(String);
  if (ownerKeys.length === 0) return true;  // 老数据
  return ownerKeys.includes(userKey) || ownerKeys.includes(String(user.username || ''));
}
function getVisibleCases() {
  return loadDB().filter(canCurrentUserViewCase);
}
function isLoggedIn() {
  if (isCloudMode() && isDemoAuth()) return false;
  return !!getAuthToken();
}

// ===================== SETTINGS =====================
const SETTINGS_KEY = 'foot_ankle_settings_v1';
const GENERATION_MODEL_PRESETS = [
  { provider: 'deepseek', model: 'deepseek-v4-pro', label: 'DeepSeek V4 Pro（报告/PPT/主任审核）' },
  { provider: 'deepseek', model: 'deepseek-v4-flash', label: 'DeepSeek V4 Flash（摘要/低成本）' },
  { provider: 'deepseek', model: 'deepseek-chat', label: 'DeepSeek Chat（兼容旧版）' },
  { provider: 'deepseek', model: 'deepseek-reasoner', label: 'DeepSeek Reasoner（复杂推理）' },
  { provider: 'anthropic', model: 'claude-sonnet-4-20250514', label: 'jeniya 均衡（质量/速度均衡）' },
  { provider: 'anthropic', model: 'claude-opus-4-20250514', label: 'jeniya 高级（复杂病例）' },
  { provider: 'anthropic', model: 'claude-haiku-4-5-20251001', label: 'jeniya 轻量（简单摘要）' },
  { provider: 'openai', model: 'gpt-4.1', label: 'GPT-4.1（通用文书）' },
  { provider: 'openai', model: 'gpt-4.1-mini', label: 'GPT-4.1 Mini（低成本）' },
  { provider: 'openai', model: 'o3', label: 'OpenAI o3（复杂推理）' },
  { provider: 'glm', model: 'glm-4-plus', label: 'GLM-4 Plus（中文通用）' },
  { provider: 'glm', model: 'glm-4-flash', label: 'GLM-4 Flash（低成本）' },
  { provider: 'gemini', model: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' },
  { provider: 'gemini', model: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash' },
  { provider: 'banana', model: 'banana-pro', label: 'Banana Pro（兼容网关）' },
  { provider: 'compatible', model: 'custom-model', label: '第三方兼容网关（自定义）' }
];
function loadSettings() {
  try { return JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}'); } catch { return {}; }
}
function saveSettingsData(data) { localStorage.setItem(SETTINGS_KEY, JSON.stringify(data)); }
function getAPIKey() { return loadSettings().apiKey || ''; }
function getAIModel() { return loadSettings().model || 'deepseek-v4-pro'; }
function getAIProvider() { return loadSettings().provider || 'deepseek'; }
function getProviderLabel(provider) {
  return ({
    anthropic: 'jeniya',
    openai: 'GPT/OpenAI',
    deepseek: 'DeepSeek',
    gemini: 'Gemini',
    glm: 'GLM',
    banana: 'Banana Pro',
    compatible: '兼容网关'
  })[provider] || provider || '模型';
}
function getCustomGenerationModels() {
  return Array.isArray(loadSettings().customModels) ? loadSettings().customModels : [];
}
function getGenerationModelOptions(provider = '') {
  const custom = getCustomGenerationModels();
  const merged = [...GENERATION_MODEL_PRESETS, ...custom];
  const seen = new Set();
  return merged.filter(item => {
    if (provider && item.provider !== provider) return false;
    const key = `${item.provider}:${item.model}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
function getAppConfig() { return window.FOOT_ANKLE_CONFIG || {}; }
function getConfiguredBackendBase() { return (getAppConfig().backendUrl || '').trim().replace(/\/$/, ''); }
function isCloudMode() { return !!getAppConfig().cloudMode || !!getConfiguredBackendBase(); }
function getBackendBase() { return (getConfiguredBackendBase() || loadSettings().backendUrl || '').trim().replace(/\/$/, ''); }
function getBananaEndpoint() { return (loadSettings().bananaEndpoint || '').trim(); }
function getCompatibleEndpoint() { return (loadSettings().compatibleEndpoint || '').trim(); }

// Token 自动刷新：同一时刻只发一次 refresh 请求，多个并发请求排队等待结果
let _refreshingPromise = null;
async function _tryRefreshToken() {
  if (_refreshingPromise) return _refreshingPromise;
  _refreshingPromise = (async () => {
    try {
      const { refreshToken } = loadAuth();
      if (!refreshToken) return false;
      const base = getBackendBase();
      const resp = await fetch(`${base}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!resp.ok) return false;
      const data = await resp.json();
      const auth = loadAuth();
      saveAuth({ ...auth, accessToken: data.access_token, refreshToken: data.refresh_token || refreshToken });
      return true;
    } catch { return false; }
    finally { _refreshingPromise = null; }
  })();
  return _refreshingPromise;
}

async function backendRequest(path, options = {}, requireAuth = true) {
  const base = getBackendBase();
  // 空 base（nginx 同源代理）也允许通过；仅非云端且无 base 时才报错
  if (!base && !isCloudMode()) throw new Error('请先在设置中配置后端地址');

  const _exec = async () => {
    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
    const headers = { ...(isFormData ? {} : { 'Content-Type': 'application/json' }), ...(options.headers || {}) };
    if (requireAuth && getAuthToken()) headers.Authorization = `Bearer ${getAuthToken()}`;
    return fetch(`${base}${path}`, { ...options, headers });
  };

  let resp;
  try {
    resp = await _exec();
  } catch (e) {
    // fetch 在断网/跨域/地址错误时抛 TypeError —— 翻译成人话，避免暴露 "Failed to fetch"
    const ne = new Error('网络连接失败，请检查网络或后端地址后重试。');
    ne.isNetwork = true; ne.friendly = ne.message; ne.cause = e;
    throw ne;
  }

  // 401 → 尝试静默刷新 token，成功则透明重试原请求
  if (requireAuth && resp.status === 401) {
    const ok = await _tryRefreshToken();
    if (ok) resp = await _exec();
  }

  const contentType = resp.headers.get('content-type') || '';
  const hasBody = resp.status !== 204 && resp.status !== 205;
  const data = hasBody
    ? (contentType.includes('application/json') ? await resp.json() : await resp.text())
    : null;

  if (!resp.ok) {
    // detail 可能是字符串（普通错误）或数组（FastAPI 422 校验错误列表）
    let msg;
    if (typeof data === 'string') {
      msg = data;
    } else if (Array.isArray(data?.detail)) {
      msg = data.detail.map(e => e?.msg || '参数校验失败').join('；');
    } else {
      // 不直接 JSON.stringify 整个响应体，避免把后端内部结构/异常细节暴露给用户
      msg = data?.detail || `请求失败：${resp.status}`;
    }
    const error = new Error(msg || `请求失败：${resp.status}`);
    error.status = resp.status;
    error.detail = msg;                              // 原始后端措辞，保留供管理员排查
    error.friendly = humanizeApiError(resp.status, msg);
    error.message = error.friendly;                  // 默认展示友好措辞；现有 err.message toast 自动受益
    if (requireAuth && resp.status === 401) {
      clearAuth();
      if (typeof renderAuthState === 'function') renderAuthState();
      error.friendly = '登录已过期或尚未登录，请重新登录后再试。';
      error.message = error.friendly;
    }
    throw error;
  }
  return data;
}

// 把后端 HTTP 状态翻译成面向医生用户的“人话 + 下一步动作”。
// 安全措辞（无权/不存在）保持不泄露资源是否存在，但给出可操作的指引。
function humanizeApiError(status, rawDetail) {
  switch (status) {
    case 400: return '提交的内容有误，请检查后重试。';
    case 401: return '登录已过期或尚未登录，请重新登录后再试。';
    case 403: return '你没有权限执行此操作。如需访问，请让病例创建者把病例共享给你，或联系科室管理员开通权限。';
    case 404: return '没有找到目标，可能已被删除，或你当前没有访问权限。请刷新列表后重试。';
    case 409: return '操作与当前状态冲突，请刷新后重试。';
    case 412: return '该记录刚被他人修改过，请刷新到最新版本后再编辑。';
    case 413: return '文件过大，超出上传限制，请压缩后再上传。';
    case 422: return rawDetail || '提交的内容未通过校验，请检查必填项。';
    case 429: return '操作太频繁了，请稍候片刻再试。';
  }
  if (status >= 500) return '服务器暂时开小差了，请稍后重试；若反复出现请联系管理员。';
  return rawDetail || '操作失败，请重试。';
}

// 功能入口的按角色友好门禁文案：登录态 / 模式不同，给出不同的下一步。
function featureGateMessage(feature = '该功能') {
  if (isCloudMode() && !isLoggedIn()) return `请先登录后再使用${feature}。`;
  if (!isCloudMode()) return `${feature}需要云端后端支持。当前为本地演示模式，请联系管理员开通云端后端。`;
  return `请先在系统设置中完成配置后再使用${feature}。`;
}

// ── 患者隐私默认脱敏（仅前端显示层；本地数据本身不变）────────────────
// 列表 / 详情 / 导出 / AI 标题默认只显示脱敏姓名；点击“显示患者信息”后本次会话明文。
let _piiRevealed = false;
function isPiiRevealed() { return _piiRevealed; }
function togglePiiReveal() {
  _piiRevealed = !_piiRevealed;
  if (typeof renderSidebar === 'function') renderSidebar();
  if (typeof renderDashboard === 'function') renderDashboard(currentModuleFilter || 'all');
  if (typeof renderCaseForm === 'function' && currentCaseId) renderCaseForm(currentCaseId);
  if (typeof renderPiiToggle === 'function') renderPiiToggle();
  if (typeof showToast === 'function') {
    showToast(_piiRevealed ? '👁️' : '🙈',
      _piiRevealed ? '已显示患者信息（仅本设备本次会话）' : '已隐藏患者信息（默认脱敏）');
  }
}
function maskName(name) {
  const s = String(name || '').trim();
  if (!s) return '';
  if (s.length <= 2) return s[0] + '*';
  return s[0] + '*'.repeat(s.length - 2) + s[s.length - 1];
}
function maskPhone(p) {
  const s = String(p || '').replace(/\s/g, '');
  if (!s) return '';
  if (s.length < 7) return '***';
  return s.slice(0, 3) + '****' + s.slice(-4);
}
function maskIdCard(v) {
  const s = String(v || '').trim();
  if (!s) return '';
  if (s.length <= 4) return s[0] + '***';
  return s.slice(0, 3) + '*'.repeat(Math.max(1, s.length - 6)) + s.slice(-3);
}
// 按字段 key 推断并脱敏 PII 值（用于导出/列表等显示层）。非 PII 原样返回。
function maskPiiFieldValue(key, value) {
  if (value === undefined || value === null) return value;
  const k = String(key || '').toLowerCase();
  if (k === 'basic_name' || k === 'name' || /姓名/.test(k)) return maskName(value);
  if (/phone|tel|mobile|电话|手机/.test(k)) return maskPhone(value);
  if (/id_card|idcard|sfz|身份/.test(k)) return maskIdCard(value);
  return value;
}
// 病例展示用姓名：脱敏开关关闭→脱敏；打开→明文。缺省“未命名患者”。
function displayPatientName(c) {
  const raw = (c && c.data && (c.data.name || c.data.basic_name)) || '';
  if (!raw) return '未命名患者';
  return _piiRevealed ? raw : maskName(raw);
}

function resolveBackendAssetUrl(url) {
  if (!url) return '';
  if (/^https?:\/\//i.test(url) || url.startsWith('data:') || url.startsWith('blob:')) return url;
  return `${getBackendBase()}${url.startsWith('/') ? url : `/${url}`}`;
}

const AUTH_MEDIA_PLACEHOLDER = 'data:image/gif;base64,R0lGODlhAQABAAAAACw=';
const _authMediaObjectUrls = new Map();
const _authMediaPromises = new Map();

function _escapeAttr(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function _removeMediaPreviewState(el) {
  const frame = el?.closest?.('.image-thumb');
  if (!frame) return;
  frame.classList.remove('image-thumb--media-loading', 'image-thumb--media-error', 'image-thumb--media-ready');
  frame.querySelector('.thumb-preview-status')?.remove();
}

function _setMediaPreviewState(el, state, source) {
  const frame = el?.closest?.('.image-thumb');
  if (!frame) return;
  frame.classList.remove('image-thumb--media-loading', 'image-thumb--media-error', 'image-thumb--media-ready');
  if (state) frame.classList.add(`image-thumb--media-${state}`);
  if (state !== 'error') frame.querySelector('.thumb-preview-status')?.remove();
  if (state === 'error' && !frame.querySelector('.thumb-preview-status')) {
    const overlay = document.createElement('div');
    overlay.className = 'thumb-preview-status thumb-preview-error';
    overlay.innerHTML = `
      <span>影像预览失败</span>
      <button type="button">重新加载</button>`;
    overlay.querySelector('button')?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      retryMediaPreview(frame, source);
    });
    frame.appendChild(overlay);
  }
}

function isAuthenticatedMediaApiUrl(url) {
  const resolved = resolveBackendAssetUrl(url);
  return /\/api\/media\/\d+(?:[?#].*)?$/i.test(resolved);
}

function mediaPreviewSrc(url) {
  const resolved = resolveBackendAssetUrl(url);
  if (!isAuthenticatedMediaApiUrl(resolved)) return resolved;
  return _authMediaObjectUrls.get(resolved) || AUTH_MEDIA_PLACEHOLDER;
}

function mediaAuthAttrs(url, label = '', isVideo = false) {
  const resolved = resolveBackendAssetUrl(url);
  if (!isAuthenticatedMediaApiUrl(resolved)) return '';
  return ` data-auth-media-src="${_escapeAttr(resolved)}" data-auth-media-label="${_escapeAttr(label)}" data-auth-media-video="${isVideo ? '1' : '0'}"`;
}

async function fetchAuthenticatedMediaObjectUrl(url) {
  const resolved = resolveBackendAssetUrl(url);
  if (!isAuthenticatedMediaApiUrl(resolved)) return resolved;
  if (_authMediaObjectUrls.has(resolved)) return _authMediaObjectUrls.get(resolved);
  if (_authMediaPromises.has(resolved)) return _authMediaPromises.get(resolved);

  const load = (async () => {
    const doFetch = () => fetch(resolved, {
      headers: getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {},
    });
    let resp = await doFetch();
    if (resp.status === 401 && await _tryRefreshToken()) resp = await doFetch();
    if (!resp.ok) throw new Error('影像读取失败');
    const objectUrl = URL.createObjectURL(await resp.blob());
    _authMediaObjectUrls.set(resolved, objectUrl);
    return objectUrl;
  })().finally(() => _authMediaPromises.delete(resolved));

  _authMediaPromises.set(resolved, load);
  return load;
}

function invalidateAuthenticatedMediaObjectUrl(url) {
  const resolved = resolveBackendAssetUrl(url);
  const objectUrl = _authMediaObjectUrls.get(resolved);
  if (objectUrl) URL.revokeObjectURL(objectUrl);
  _authMediaObjectUrls.delete(resolved);
  _authMediaPromises.delete(resolved);
}

function _hydrateOneAuthenticatedMedia(el, force = false) {
  const source = el.getAttribute('data-auth-media-src');
  if (!source) return;
  if (!force && el.getAttribute('data-auth-media-ready') === '1') return;
  _setMediaPreviewState(el, 'loading', source);
  fetchAuthenticatedMediaObjectUrl(source)
    .then(objectUrl => {
      const label = el.getAttribute('data-auth-media-label') || '';
      const isVideo = el.getAttribute('data-auth-media-video') === '1';
      if ('src' in el) el.src = objectUrl;
      el.setAttribute('data-auth-media-ready', '1');
      _setMediaPreviewState(el, 'ready', source);
      el.onclick = () => {
        if (isVideo) openLightbox(objectUrl, label, true);
        else if (el.tagName === 'IMG') openLightbox(objectUrl, label, false);
        else window.open(objectUrl, '_blank');
      };
    })
    .catch(() => {
      el.removeAttribute('data-auth-media-ready');
      _setMediaPreviewState(el, 'error', source);
    });
}

function hydrateAuthenticatedMedia(root = document) {
  const nodes = [];
  if (root?.matches?.('[data-auth-media-src]')) nodes.push(root);
  nodes.push(...Array.from(root.querySelectorAll?.('[data-auth-media-src]') || []));
  nodes.forEach(el => {
    _hydrateOneAuthenticatedMedia(el);
  });
}

function retryMediaPreview(target, explicitSource = '') {
  const frame = target?.closest?.('.image-thumb') || target;
  const nodes = Array.from(frame?.querySelectorAll?.('[data-auth-media-src]') || []);
  if (nodes.length) {
    nodes.forEach(el => {
      const source = explicitSource || el.getAttribute('data-auth-media-src');
      invalidateAuthenticatedMediaObjectUrl(source);
      el.removeAttribute('data-auth-media-ready');
      _hydrateOneAuthenticatedMedia(el, true);
    });
    return;
  }
  const media = frame?.querySelector?.('img, video');
  if (media?.src) {
    _removeMediaPreviewState(media);
    try {
      const u = new URL(media.src, window.location.href);
      u.searchParams.set('preview_retry', String(Date.now()));
      media.src = u.toString();
    } catch {
      media.src = media.src;
    }
  }
}

function markMediaPreviewError(el) {
  if (!el?.closest?.('.image-thumb')) return;
  _setMediaPreviewState(el, 'error', '');
}

// ===================== MEDIA SLOT HELPERS =====================
/**
 * 读取某个槽位的图片数组（统一新旧两种数据格式）。
 *
 * 旧格式：c.media[key] = { name, data, ... }   ← 单张图片对象
 * 新格式：c.media[key] = [{ name, data, ... }]  ← 图片对象数组
 *
 * 返回始终是数组，调用方不需要区分格式。
 */
function getSlotImages(c, key, legacyKey) {
  const val = c?.media?.[key] ?? (legacyKey ? c?.media?.[legacyKey] : undefined);
  if (!val) return [];
  if (Array.isArray(val)) return val.filter(Boolean);
  if (typeof val === 'object' && (val.data || val.cloud)) return [val];
  return [];
}

/**
 * 将一张新图追加到槽位（自动处理旧格式迁移）。
 * 返回更新后的槽位值（调用方负责写回 c.media[key]）。
 */
function appendSlotImage(c, key, mediaObj) {
  const existing = c?.media?.[key];
  if (!existing) return [mediaObj];
  if (Array.isArray(existing)) return [...existing, mediaObj];
  // 旧格式单对象 → 迁移成数组
  if (existing.data || existing.cloud) return [existing, mediaObj];
  return [mediaObj];
}



/* assets/observability.js */
// ===================== FRONTEND ERROR REPORTING =====================
// DSN is never exposed to the browser. The backend decides whether Sentry is enabled.
(function initFrontendErrorReporting() {
  const endpoint = '/api/observability/client-errors';
  const PII_PATTERNS = [
    [/\b\d{6}(?:19|20)?\d{2}\d{2}\d{2}\d{3}[\dXx]\b/g, '<redacted-id-card>'],
    [/\b1[3-9]\d{9}\b/g, '<redacted-phone>'],
    [/Bearer\s+[A-Za-z0-9._~+\-/=]+/gi, 'Bearer <redacted>'],
  ];

  function cleanText(value, maxLen = 1000) {
    let text = String(value || '').slice(0, maxLen);
    PII_PATTERNS.forEach(([pattern, replacement]) => { text = text.replace(pattern, replacement); });
    return text;
  }

  function cleanHref() {
    try {
      const url = new URL(window.location.href);
      return `${url.origin}${url.pathname}`;
    } catch (_) {
      return '';
    }
  }

  function reportClientError(payload) {
    const body = JSON.stringify({
      message: cleanText(payload.message, 500),
      source: cleanText(payload.source, 200),
      lineno: payload.lineno || null,
      colno: payload.colno || null,
      stack: cleanText(payload.stack, 4000),
      href: cleanHref(),
      user_agent: cleanText(navigator.userAgent || '', 300),
    });
    try {
      const base = (typeof getBackendBase === 'function') ? getBackendBase() : '';
      const url = `${base}${endpoint}`;
      if (navigator.sendBeacon) {
        const ok = navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
        if (ok) return;
      }
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
    } catch (_) {}
  }

  window.addEventListener('error', (event) => {
    reportClientError({
      message: event.message || event.error?.message || 'frontend error',
      source: event.filename || '',
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack || '',
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason || {};
    reportClientError({
      message: reason.message || String(reason || 'unhandled rejection'),
      source: 'unhandledrejection',
      stack: reason.stack || '',
    });
  });
})();



/* assets/modules.js */
// ===================== MODULE DEFINITIONS =====================
// 分类：足踝矫形 / 足踝创伤 / 运动医学
const MODULE_CATEGORIES = ['足踝矫形', '足踝创伤', '运动医学', '足踝其他/神经'];

const MODULES = {
  // ─── 足踝矫形 ───────────────────────────────
  A: {
    name: '拇外翻 (Hallux Valgus)',
    icon: '🦶', color: '#2563eb', tag: 'A',
    category: '足踝矫形',
    sections: withWorkflowSections(getSectionsA())
  },
  B: {
    name: '踝关节置换 (TAA)',
    icon: '🦿', color: '#0f766e', tag: 'B',
    category: '足踝矫形',
    sections: withWorkflowSections(getSectionsB())
  },
  C: {
    name: '踝关节融合 (Arthrodesis)',
    icon: '🔩', color: '#7c3aed', tag: 'C',
    category: '足踝矫形',
    sections: withWorkflowSections(getSectionsC())
  },
  F: {
    name: '扁平足 (Flatfoot)',
    icon: '🦵', color: '#0e7490', tag: 'F',
    category: '足踝矫形',
    sections: withWorkflowSections(getSectionsF())
  },
  G: {
    name: '高弓足 (Cavus Foot)',
    icon: '🏔️', color: '#0891b2', tag: 'G',
    category: '足踝矫形',
    sections: withWorkflowSections(getSectionsG())
  },

  // ─── 足踝创伤 ───────────────────────────────
  D: {
    name: '踝关节骨折 (Ankle Fracture)',
    icon: '🦴', color: '#b45309', tag: 'D',
    category: '足踝创伤',
    sections: withWorkflowSections(getSectionsD())
  },
  J: {
    name: 'Lisfranc 损伤',
    icon: '🩼', color: '#a16207', tag: 'J',
    category: '足踝创伤',
    sections: withWorkflowSections(getSectionsJ())
  },
  K: {
    name: '跖骨基底撕脱骨折（第一、第五）',
    icon: '🦴', color: '#92400e', tag: 'K',
    category: '足踝创伤',
    sections: withWorkflowSections(getSectionsK())
  },
  L: {
    name: '跟骨骨折 (Calcaneal Fracture)',
    icon: '🦶', color: '#9a3412', tag: 'L',
    category: '足踝创伤',
    sections: withWorkflowSections(getSectionsL())
  },

  // ─── 运动医学 ───────────────────────────────
  E: {
    name: '跟腱病 (Achilles Tendinopathy)',
    icon: '⚡', color: '#be123c', tag: 'E',
    category: '运动医学',
    sections: withWorkflowSections(getSectionsE())
  },
  H: {
    name: '踝关节韧带损伤',
    icon: '🤸', color: '#db2777', tag: 'H',
    category: '运动医学',
    sections: withWorkflowSections(getSectionsH())
  },
  I: {
    name: '距骨软骨损伤 (OCD)',
    icon: '🧊', color: '#c026d3', tag: 'I',
    category: '运动医学',
    sections: withWorkflowSections(getSectionsI())
  },
  O: {
    name: '腓骨肌腱滑脱 (Peroneal Tendon Subluxation)',
    icon: '🩻', color: '#e11d48', tag: 'O',
    category: '运动医学',
    sections: withWorkflowSections(getSectionsO())
  },

  // ─── 足踝其他 / 神经 ─────────────────────────
  M: {
    name: '跗骨联合 (Tarsal Coalition)',
    icon: '🧩', color: '#4f46e5', tag: 'M',
    category: '足踝其他/神经',
    sections: withWorkflowSections(getSectionsM())
  },
  N: {
    name: '莫顿神经瘤 (Morton Neuroma)',
    icon: '🧠', color: '#9333ea', tag: 'N',
    category: '足踝其他/神经',
    sections: withWorkflowSections(getSectionsN())
  }
};

function withWorkflowSections(sections) {
  // 共享注入：在每个模块的「基本信息」段末尾追加一个「诊断补充说明」自由文本框
  // （全可空，落 cases.data.basic_diagnosis_note）。用途：主要诊断之外补充合并/伴随情况。
  // 仅在 wrapper 层追加新字段，不修改任何 getSectionsX 的既有字段定义。
  const withDiagNote = sections.map(sec => {
    if (sec.id !== 'basic' || sec.type === 'media') return sec;
    if ((sec.fields || []).some(f => f.id === 'diagnosis_note')) return sec;
    return {
      ...sec,
      fields: [
        ...sec.fields,
        { id: 'diagnosis_note', label: '诊断补充说明', type: 'textarea', full: true,
          placeholder: '主要诊断之外的合并/伴随情况、特殊说明等（选填）' }
      ]
    };
  });
  return [getWorkflowSection(), ...withDiagNote];
}

function getWorkflowSection() {
  return {
    id: 'workflow',
    icon: '🧭',
    title: '流程阶段与审核',
    fields: [
      { id: 'case_stage', label: '病例阶段', type: 'select', req: true, opts: ['术前', '术后', '随访'] },
      { id: 'entry_doctor', label: '录入医生', type: 'text', placeholder: '住院医/管床医生姓名' },
      { id: 'deadline', label: '本阶段截止日期', type: 'date' },
      { id: 'workflow_notes', label: '阶段备注', type: 'textarea', full: true }
    ]
  };
}

function getSectionsA() {
  return [
    {
      id: 'basic', icon: '👤', title: '基本信息',
      fields: [
        {id:'name',label:'姓名',type:'text',req:true},
        {id:'age',label:'年龄',type:'number',unit:'岁',req:true},
        {id:'gender',label:'性别',type:'select',opts:['女','男']},
        {id:'side',label:'患侧',type:'select',opts:['左足','右足','双足'],req:true},
        {id:'caseid',label:'住院号',type:'text'},
        {id:'date',label:'就诊日期',type:'date',req:true},
        {id:'bmi',label:'BMI',type:'number',unit:'kg/m²'},
        {id:'occupation',label:'职业',type:'text'},
        {id:'chief_complaint',label:'主诉',type:'textarea',full:true,req:true},
        {id:'duration',label:'病程',type:'text',full:true,placeholder:'如：右足拇趾外翻畸形3年，疼痛加重6个月'}
      ]
    },
    {
      id: 'history', icon: '📋', title: '病史采集',
      fields: [
        {id:'pain_vas',label:'疼痛VAS评分（静息）',type:'number',unit:'0-10'},
        {id:'pain_vas_active',label:'疼痛VAS评分（活动）',type:'number',unit:'0-10'},
        {id:'footwear_limitation',label:'穿鞋限制',type:'select',opts:['无','轻度','中度','严重']},
        {id:'walking_distance',label:'步行距离',type:'select',opts:['无限制','500m以上','500m以内','100m以内','室内']},
        {id:'comorbidity',label:'合并症',type:'checkboxes',opts:['糖尿病','类风湿关节炎','痛风','骨质疏松','高血压','周围神经病','扁平足','其他']},
        {id:'family_hx',label:'家族史',type:'select',opts:['无','有（母亲）','有（父亲）','有（兄弟姐妹）']},
        {id:'shoe_type',label:'常穿鞋型',type:'select',opts:['平底宽头','高跟鞋','尖头鞋','运动鞋','凉鞋']},
        {id:'previous_treatment',label:'既往治疗',type:'textarea',full:true}
      ]
    },
    {
      id: 'physical', icon: '🔍', title: '体格检查',
      fields: [
        {id:'hva_clinical',label:'HVA临床估测',type:'number',unit:'°'},
        {id:'ima_clinical',label:'IMA临床估测',type:'number',unit:'°'},
        {id:'first_mtp_rom_dors',label:'第一MTP背伸ROM',type:'number',unit:'°'},
        {id:'first_mtp_rom_plant',label:'第一MTP跖屈ROM',type:'number',unit:'°'},
        {id:'first_tmt_stability',label:'第一TMT稳定性',type:'select',opts:['稳定','轻度不稳','明显不稳']},
        {id:'pronation_sign',label:'旋前畸形',type:'select',opts:['无','轻度','中度','重度']},
        {id:'lesser_toe',label:'小趾畸形',type:'select',opts:['无','锤状趾','爪形趾','漂浮趾','交叉趾']},
        {id:'bunionette',label:'小拇囊炎',type:'select',opts:['无','有']},
        {id:'callus',label:'胼胝分布',type:'text',full:true},
        {id:'neurovascular',label:'神经血管',type:'select',opts:['正常','足背动脉减弱','胫后动脉减弱','感觉减退']},
        {id:'physical_notes',label:'查体备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'imaging', icon: '📷', title: '影像数据',
      fields: [
        {id:'hva_xray',label:'HVA（X线）',type:'number',unit:'°',req:true},
        {id:'ima_xray',label:'IMA（X线）',type:'number',unit:'°',req:true},
        {id:'dmaa',label:'DMAA',type:'number',unit:'°'},
        {id:'sesamoid_position',label:'籽骨位置',type:'select',opts:['I位','II位','III位','IV位','V位','VI位','VII位']},
        {id:'articular_surface',label:'关节面形态',type:'select',opts:['正常','轻度侵蚀','中度侵蚀','关节炎']},
        {id:'first_mtp_oa',label:'第一MTP骨关节炎',type:'select',opts:['无（0级）','轻度（I级）','中度（II级）','重度（III级）']},
        {id:'arch_type',label:'足弓类型',type:'select',opts:['正常','高弓','低弓/扁平']},
        {id:'lateral_round',label:'外侧皮质圆弧度',type:'number',unit:'mm'},
        {id:'imaging_notes',label:'影像学备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'scores', icon: '📊', title: '功能评分量表',
      fields: [
        {id:'aofas_preop',label:'AOFAS评分（术前）',type:'number',unit:'分/100',req:true},
        {id:'aofas_pain',label:'AOFAS疼痛子项',type:'number',unit:'分/40'},
        {id:'aofas_function',label:'AOFAS功能子项',type:'number',unit:'分/45'},
        {id:'aofas_alignment',label:'AOFAS力线子项',type:'number',unit:'分/15'},
        {id:'faam_adl',label:'FAAM ADL评分',type:'number',unit:'%'},
        {id:'faam_sport',label:'FAAM运动评分',type:'number',unit:'%'},
        {id:'vas_satisfaction',label:'患者满意度预期',type:'select',opts:['完全康复','明显改善','轻度改善','维持现状']}
      ]
    },
    {
      id: 'plan', icon: '⚕️', title: '手术方案',
      fields: [
        {id:'procedure_type',label:'手术术式',type:'select',opts:['Scarf截骨','Chevron截骨','Ludloff截骨','Lapidus融合','近端截骨','Mitchell截骨','软组织松解'],req:true},
        {id:'additional_proc',label:'联合手术',type:'checkboxes',opts:['McBride软组织松解','Akin截骨','小趾矫形','第一MTP融合','Weil截骨','腓骨短肌腱转位']},
        {id:'fixation',label:'内固定方式',type:'select',opts:['螺钉固定','钢板螺钉','克氏针','无固定']},
        {id:'anesthesia',label:'麻醉方式',type:'select',opts:['腰硬联合','全麻','腰麻','局部阻滞']},
        {id:'surgeon',label:'主刀医师',type:'text'},
        {id:'op_plan_notes',label:'手术计划备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'media', icon: '🖼️', title: '影像资料上传',
      type: 'media',
      slots: getStructuredMediaSlots()
    }
  ];
}

function getSectionsB() {
  return [
    {
      id: 'basic', icon: '👤', title: '基本信息',
      fields: [
        {id:'name',label:'姓名',type:'text',req:true},
        {id:'age',label:'年龄',type:'number',unit:'岁',req:true},
        {id:'gender',label:'性别',type:'select',opts:['男','女']},
        {id:'side',label:'患侧',type:'select',opts:['左踝','右踝'],req:true},
        {id:'caseid',label:'住院号',type:'text'},
        {id:'date',label:'就诊日期',type:'date',req:true},
        {id:'bmi',label:'BMI',type:'number',unit:'kg/m²'},
        {id:'weight',label:'体重',type:'number',unit:'kg'},
        {id:'chief_complaint',label:'主诉',type:'textarea',full:true,req:true},
        {id:'duration',label:'病程',type:'text',full:true}
      ]
    },
    {
      id: 'history', icon: '📋', title: '病史采集',
      fields: [
        {id:'etiology',label:'病因',type:'select',opts:['原发性骨关节炎','创伤后关节炎','类风湿关节炎','血清阴性脊柱关节病','缺血性坏死','其他'],req:true},
        {id:'trauma_history',label:'创伤史',type:'textarea',full:true},
        {id:'pain_vas_rest',label:'静息痛VAS',type:'number',unit:'分'},
        {id:'pain_vas_walk',label:'行走痛VAS',type:'number',unit:'分'},
        {id:'walking_aids',label:'步行辅助',type:'select',opts:['无','手杖','双拐','轮椅']},
        {id:'walking_distance',label:'步行距离',type:'select',opts:['无限制','1km以上','500m以上','100m以内','室内']},
        {id:'stair_climbing',label:'上下楼梯',type:'select',opts:['正常','需扶手','困难','不能']},
        {id:'previous_surgeries',label:'既往踝部手术',type:'textarea',full:true},
        {id:'comorbidity',label:'合并症',type:'checkboxes',opts:['糖尿病','类风湿关节炎','骨质疏松','外周动脉病','免疫抑制','皮质类固醇使用','神经系统疾病','慢性皮肤病变']}
      ]
    },
    {
      id: 'physical', icon: '🔍', title: '体格检查',
      fields: [
        {id:'ankle_rom_dors',label:'踝关节背伸ROM',type:'number',unit:'°'},
        {id:'ankle_rom_plant',label:'踝关节跖屈ROM',type:'number',unit:'°'},
        {id:'subtalar_motion',label:'距下关节活动',type:'select',opts:['正常','轻度受限','中度受限','僵硬']},
        {id:'hindfoot_alignment',label:'后足力线',type:'select',opts:['中立','轻度外翻','中度外翻','重度外翻','内翻'],req:true},
        {id:'ankle_stability',label:'踝关节稳定性',type:'select',opts:['稳定','前抽屉阳性','内翻应力阳性','不稳']},
        {id:'ligament_laxity',label:'韧带松弛',type:'select',opts:['无','轻度','中度','重度']},
        {id:'skin_condition',label:'皮肤情况',type:'select',opts:['正常','瘢痕形成','皮肤薄弱','血管病变表现']},
        {id:'muscle_strength',label:'肌力（腓肠肌）',type:'select',opts:['5级','4级','3级','2级以下']},
        {id:'neurology',label:'神经功能',type:'select',opts:['正常','感觉减退','本体感觉减退']},
        {id:'physical_notes',label:'查体备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'imaging', icon: '📷', title: '影像数据',
      fields: [
        {id:'ankle_oa_grade',label:'踝关节OA分级',type:'select',opts:['Kellgren-Lawrence II','Kellgren-Lawrence III','Kellgren-Lawrence IV'],req:true},
        {id:'hindfoot_coronal',label:'后足冠状面力线',type:'number',unit:'°（外翻+/内翻-）'},
        {id:'sagittal_alignment',label:'矢状面力线',type:'number',unit:'°（前倾/后倾）'},
        {id:'tibial_plafond',label:'胫骨穹顶形态',type:'select',opts:['正常','轻度侵蚀','严重侵蚀','骨坏死']},
        {id:'bone_stock',label:'骨量评估',type:'select',opts:['充足','轻度不足','明显不足（需植骨）']},
        {id:'fibula_status',label:'腓骨状态',type:'select',opts:['正常','骨折愈合','缺失','外固定残留']},
        {id:'ct_bone_loss',label:'CT骨缺损（mm）',type:'number',unit:'mm'},
        {id:'previous_implant',label:'既往内固定',type:'text',full:false},
        {id:'imaging_notes',label:'影像学备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'scores', icon: '📊', title: '功能评分量表',
      fields: [
        {id:'aofas_preop',label:'AOFAS踝后足评分（术前）',type:'number',unit:'分/100',req:true},
        {id:'aofas_pain',label:'AOFAS疼痛',type:'number',unit:'分/40'},
        {id:'aofas_function',label:'AOFAS功能',type:'number',unit:'分/50'},
        {id:'aofas_alignment',label:'AOFAS力线',type:'number',unit:'分/10'},
        {id:'sf36_physical',label:'SF-36体格评分',type:'number'},
        {id:'sf36_mental',label:'SF-36精神评分',type:'number'},
        {id:'fas_score',label:'足踝功能量表（FAS）',type:'number'},
        {id:'patient_expectation',label:'患者手术期望',type:'select',opts:['消除疼痛','恢复正常行走','恢复运动','改善生活质量']}
      ]
    },
    {
      id: 'implant', icon: '🦿', title: '假体选择与手术方案',
      fields: [
        {id:'implant_brand',label:'假体品牌',type:'select',opts:['宽悦']},
        {id:'tibial_implant_size',label:'胫骨假体型号',type:'text'},
        {id:'talar_implant_size',label:'距骨假体型号',type:'text'},
        {id:'insert_implant_size',label:'内衬（聚乙烯）假体型号',type:'text'},
        {id:'adjunct_proc',label:'联合手术',type:'checkboxes',opts:['腓骨截骨延长','跟骨截骨','距下关节融合','韧带重建','腓骨肌腱修复','跟腱延长','Grice手术']},
        {id:'bone_graft',label:'植骨计划',type:'select',opts:['不需要','自体植骨','异体植骨','人工骨']},
        {id:'anesthesia',label:'麻醉方式',type:'select',opts:['腰硬联合','全麻','腰麻']},
        {id:'tourniquet',label:'止血带计划',type:'select',opts:['大腿止血带','无止血带']},
        {id:'surgeon',label:'主刀医师',type:'text'},
        {id:'plan_notes',label:'手术计划备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'media', icon: '🖼️', title: '影像资料上传',
      type: 'media',
      slots: getStructuredMediaSlots()
    }
  ];
}

function getSectionsC() {
  return [
    {
      id: 'basic', icon: '👤', title: '基本信息',
      fields: [
        {id:'name',label:'姓名',type:'text',req:true},
        {id:'age',label:'年龄',type:'number',unit:'岁',req:true},
        {id:'gender',label:'性别',type:'select',opts:['男','女']},
        {id:'side',label:'患侧',type:'select',opts:['左踝','右踝'],req:true},
        {id:'caseid',label:'住院号',type:'text'},
        {id:'date',label:'就诊日期',type:'date',req:true},
        {id:'chief_complaint',label:'主诉',type:'textarea',full:true,req:true},
        {id:'duration',label:'病程',type:'text',full:true}
      ]
    },
    {
      id: 'history', icon: '📋', title: '病史采集',
      fields: [
        {id:'etiology',label:'病因',type:'select',opts:['创伤后关节炎','原发性骨关节炎','类风湿关节炎','感染后关节炎','AVN','神经关节病','置换翻修'],req:true},
        {id:'fusion_indication',label:'融合指征',type:'checkboxes',opts:['TAA失败翻修','严重骨缺损','感染','神经肌肉疾病','TAA禁忌','患者意愿']},
        {id:'pain_vas',label:'疼痛VAS',type:'number',unit:'分'},
        {id:'previous_surgeries',label:'既往手术',type:'textarea',full:true},
        {id:'comorbidity',label:'合并症',type:'checkboxes',opts:['糖尿病','吸烟','骨质疏松','免疫抑制','血管病变','神经病变']}
      ]
    },
    {
      id: 'physical', icon: '🔍', title: '体格检查',
      fields: [
        {id:'ankle_stiffness',label:'踝关节僵硬程度',type:'select',opts:['轻度','中度','完全僵硬']},
        {id:'hindfoot_alignment',label:'后足力线',type:'select',opts:['中立','外翻','内翻'],req:true},
        {id:'subtalar_status',label:'距下关节状态',type:'select',opts:['正常','关节炎','僵硬','已融合']},
        {id:'skin_quality',label:'皮肤质量（前方）',type:'select',opts:['良好','瘢痕','薄弱','坏死风险']},
        {id:'vascular_status',label:'血管状态',type:'select',opts:['正常','ABI>0.9','ABI 0.5-0.9','严重缺血']},
        {id:'physical_notes',label:'查体备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'imaging', icon: '📷', title: '影像数据',
      fields: [
        {id:'ankle_oa_grade',label:'OA分级',type:'select',opts:['III级','IV级'],req:true},
        {id:'bone_loss_estimate',label:'骨缺损估计',type:'number',unit:'mm'},
        {id:'fibula_status',label:'腓骨状态',type:'select',opts:['完整','骨折','缺损']},
        {id:'subtalar_oa',label:'距下关节OA',type:'select',opts:['无','轻度','中度','重度']},
        {id:'deformity_coronal',label:'冠状面畸形',type:'number',unit:'°'},
        {id:'deformity_sagittal',label:'矢状面畸形',type:'number',unit:'°'},
        {id:'imaging_notes',label:'影像学备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'scores', icon: '📊', title: '功能评分量表',
      fields: [
        {id:'aofas_preop',label:'AOFAS踝后足评分（术前）',type:'number',unit:'分/100',req:true},
        {id:'aofas_pain',label:'疼痛子项',type:'number',unit:'分'},
        {id:'aofas_function',label:'功能子项',type:'number',unit:'分'},
        {id:'faam_adl',label:'FAAM ADL',type:'number',unit:'%'},
        {id:'patient_goal',label:'患者主要目标',type:'select',opts:['消除疼痛','恢复行走','穿正常鞋','恢复劳动']}
      ]
    },
    {
      id: 'plan', icon: '⚕️', title: '手术方案',
      fields: [
        {id:'fusion_approach',label:'手术入路',type:'select',opts:['前方入路','后方入路（微创）','关节镜辅助','外侧延伸入路'],req:true},
        {id:'fixation_method',label:'固定方式',type:'select',opts:['2枚螺钉','3枚螺钉','髓内钉','钢板+螺钉','外固定架'],req:true},
        {id:'bone_graft',label:'植骨方案',type:'select',opts:['不需要','自体髂骨','自体腓骨','异体骨','人工骨替代物']},
        {id:'fibula_plan',label:'腓骨处理',type:'select',opts:['保留','截骨用作植骨','切除']},
        {id:'subtalar_fusion',label:'是否同期距下融合',type:'select',opts:['否','是']},
        {id:'target_position',label:'融合目标位置',type:'text',placeholder:'如：中立位0°，外翻5°，外旋5°',full:true},
        {id:'surgeon',label:'主刀医师',type:'text'},
        {id:'plan_notes',label:'手术计划备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'media', icon: '🖼️', title: '影像资料上传',
      type: 'media',
      slots: getStructuredMediaSlots()
    }
  ];
}

function getSectionsD() {
  return [
    {
      id: 'basic', icon: '👤', title: '基本信息',
      fields: [
        {id:'name',label:'姓名',type:'text',req:true},
        {id:'age',label:'年龄',type:'number',unit:'岁',req:true},
        {id:'gender',label:'性别',type:'select',opts:['男','女']},
        {id:'side',label:'患侧',type:'select',opts:['左踝','右踝'],req:true},
        {id:'caseid',label:'住院号',type:'text'},
        {id:'date',label:'就诊/受伤日期',type:'date',req:true},
        {id:'injury_mechanism',label:'受伤机制',type:'select',opts:['扭伤/低能量','坠落','交通伤','运动伤','直接撞击','其他'],req:true},
        {id:'open_fracture',label:'开放性骨折',type:'select',opts:['闭合','Gustilo I','Gustilo II','Gustilo IIIa','Gustilo IIIb','Gustilo IIIc'],req:true},
        {id:'chief_complaint',label:'主诉',type:'textarea',full:true,req:true}
      ]
    },
    {
      id: 'classification', icon: '🔖', title: '骨折分型',
      fields: [
        {id:'fx_pattern',label:'骨折类型',type:'select',opts:['单踝骨折（内）','单踝骨折（外）','单踝骨折（后）','双踝骨折','三踝骨折','四踝骨折（含 Chaput / Tillaux）','Pilon 样累及'],req:true},
        {id:'lauge_hansen',label:'Lauge-Hansen 分型',type:'select',opts:['SAD I','SAD II','SER I','SER II','SER III','SER IV','PER I','PER II','PER III','PER IV','PAB I','PAB II','无法判断']},
        {id:'weber',label:'Danis-Weber 分型',type:'select',opts:['Weber A（联合下）','Weber B（联合水平）','Weber C（联合上）','不适用']},
        {id:'ao_ota',label:'AO/OTA 分型',type:'text',placeholder:'如 44B2 / 44C3'},
        {id:'medial_malleolus',label:'内踝骨折',type:'select',opts:['无','有，未移位','有，移位 < 2mm','有，移位 ≥ 2mm'],req:true},
        {id:'lateral_malleolus',label:'外踝/腓骨骨折',type:'select',opts:['无','Weber A','Weber B','Weber C（联合上）','高位腓骨骨折（Maisonneuve）'],req:true},
        {id:'posterior_malleolus',label:'后踝骨折',type:'select',opts:['无','有，Bartonicek I（关节外）','有，Bartonicek II（后内侧）','有，Bartonicek III（大块关节面）','有，Bartonicek IV（累及腓骨切迹）'],req:true},
        {id:'fragment_size',label:'后踝骨折块大小',type:'number',unit:'%（关节面）',hideIf:{classification_posterior_malleolus:'无'}},
        {id:'fragment_displacement',label:'后踝骨折块移位',type:'number',unit:'mm',hideIf:{classification_posterior_malleolus:'无'}},
        {id:'anterior_tibia',label:'前踝（Chaput/Tillaux）',type:'select',opts:['无','Chaput 撕脱','Tillaux 撕脱','均累及']},
        {id:'syndesmosis',label:'下胫腓联合损伤',type:'select',opts:['无','稳定','不稳定'],req:true},
        {id:'deltoid_injury',label:'三角韧带损伤',type:'select',opts:['无','部分','完全']},
        {id:'talar_shift',label:'距骨移位',type:'select',opts:['无','轻度（≤ 2mm）','明显（> 2mm）'],req:true},
        {id:'fracture_notes',label:'分型备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'imaging', icon: '📷', title: '影像数据',
      fields: [
        {id:'xray_views',label:'X线摄片',type:'checkboxes',opts:['正位','侧位','踝穴位（Mortise）','应力位']},
        {id:'ct_done',label:'CT 检查',type:'select',opts:['已完成','未做','已预约']},
        {id:'mri_done',label:'MRI 检查',type:'select',opts:['未做','已完成（韧带评估）','已预约']},
        {id:'medial_clear_space',label:'内侧间隙（MCS）',type:'number',unit:'mm'},
        {id:'superior_clear_space',label:'上方间隙（SCS）',type:'number',unit:'mm'},
        {id:'talocrural_angle',label:'距骨-胫骨角',type:'number',unit:'°'},
        {id:'talar_tilt',label:'距骨倾斜',type:'number',unit:'°'},
        {id:'talar_shift_mm',label:'距骨外移距离',type:'number',unit:'mm'},
        {id:'fibula_length',label:'腓骨长度',type:'select',opts:['正常','短缩','延长']},
        {id:'articular_comminution',label:'关节面粉碎程度',type:'select',opts:['无','轻度','中度','重度']},
        {id:'posterior_shift',label:'后方骨折块移位距离',type:'number',unit:'mm',hideIf:{classification_posterior_malleolus:'无'}},
        {id:'fragment_volume',label:'后踝骨折块体积估计（CT）',type:'text',hideIf:{classification_posterior_malleolus:'无'}},
        {id:'imaging_notes',label:'影像学备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'plan', icon: '⚕️', title: '手术方案',
      fields: [
        {id:'position',label:'手术体位',type:'select',opts:['仰卧位','侧卧位','俯卧位','漂浮体位'],req:true},
        {id:'approach_lateral',label:'外侧入路',type:'select',opts:['无','直接外侧','后外侧','改良前外侧']},
        {id:'approach_medial',label:'内侧入路',type:'select',opts:['无','直接内侧','后内侧']},
        {id:'approach_posterior',label:'后踝入路',type:'select',opts:['无','后外侧入路','后内侧入路','后正中入路','俯卧位双后侧入路']},
        {id:'lateral_fixation',label:'外踝/腓骨固定',type:'select',opts:['无','腓骨钢板（外侧）','腓骨钢板（后外侧抗滑）','髓内钉（FNS/类似）','螺钉/克氏针','张力带']},
        {id:'medial_fixation',label:'内踝固定',type:'select',opts:['无','空心螺钉','张力带','迷你钢板']},
        {id:'posterior_fixation',label:'后踝固定',type:'select',opts:['无','空心螺钉（前→后）','空心螺钉（后→前）','支撑钢板','拮抗螺钉','直接螺钉固定']},
        {id:'syndesmosis_fixation',label:'下胫腓联合处理',type:'select',opts:['无需处理','螺钉固定（3.5/4.5）','弹性固定（TightRope/Endobutton）','钢板加压'],req:true},
        {id:'deltoid_repair',label:'三角韧带处理',type:'select',opts:['未处理','一期修复','锚钉修复','延期重建']},
        {id:'staged_surgery',label:'分期手术',type:'select',opts:['一期切开复位内固定','跨踝外固定 + 二期切开','单纯外固定']},
        {id:'antibiotic_prophylaxis',label:'抗生素预防',type:'select',opts:['头孢一代','头孢二代','克林霉素','其他','无']},
        {id:'surgeon',label:'主刀医师',type:'text'},
        {id:'plan_notes',label:'手术计划备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'media', icon: '🖼️', title: '影像资料上传',
      type: 'media',
      slots: getStructuredMediaSlots()
    }
  ];
}

function getSectionsE() {
  return [
    {
      id: 'basic', icon: '👤', title: '基本信息',
      fields: [
        {id:'name',label:'姓名',type:'text',req:true},
        {id:'age',label:'年龄',type:'number',unit:'岁',req:true},
        {id:'gender',label:'性别',type:'select',opts:['男','女']},
        {id:'side',label:'患侧',type:'select',opts:['左侧','右侧','双侧'],req:true},
        {id:'caseid',label:'住院号',type:'text'},
        {id:'date',label:'就诊日期',type:'date',req:true},
        {id:'sports_level',label:'运动水平',type:'select',opts:['久坐','休闲娱乐','业余竞技','职业运动员']},
        {id:'chief_complaint',label:'主诉',type:'textarea',full:true,req:true},
        {id:'duration',label:'病程',type:'text',full:true}
      ]
    },
    {
      id: 'history', icon: '📋', title: '病史采集',
      fields: [
        {id:'lesion_location',label:'病变部位',type:'select',opts:['止点型（<2cm内）','非止点型（2-6cm）','止点型+非止点型','完全断裂'],req:true},
        {id:'acute_rupture',label:'是否急性断裂',type:'select',opts:['是（72小时内）','是（>72小时）','否（慢性病变）']},
        {id:'pain_vas',label:'疼痛VAS',type:'number',unit:'分'},
        {id:'morning_stiffness',label:'晨起僵硬',type:'select',opts:['无','<30分钟','30-60分钟','>60分钟']},
        {id:'conservative_treatment',label:'保守治疗史',type:'checkboxes',opts:['休息','体外冲击波','PRP注射','物理治疗','跟腱固定']},
        {id:'conservative_duration',label:'保守治疗时间',type:'number',unit:'月'},
        {id:'comorbidity',label:'合并症',type:'checkboxes',opts:['糖尿病','肥胖','痛风','高血压','氟喹诺酮使用','类固醇使用']}
      ]
    },
    {
      id: 'physical', icon: '🔍', title: '体格检查',
      fields: [
        {id:'thompson_test',label:'Thompson试验',type:'select',opts:['阴性','阳性（断裂）']},
        {id:'matles_test',label:'Matles试验',type:'select',opts:['正常','异常']},
        {id:'calf_atrophy',label:'小腿萎缩',type:'select',opts:['无','轻度','明显']},
        {id:'tendon_gap',label:'触及间隙（断裂）',type:'select',opts:['无','可触及','明显间隙']},
        {id:'insertional_spur',label:'止点骨赘（Haglund）',type:'select',opts:['无','轻度','明显']},
        {id:'swelling',label:'局部肿胀',type:'select',opts:['无','轻度','中度','重度']},
        {id:'single_leg_raise',label:'单腿提踵',type:'select',opts:['正常','减弱','不能']},
        {id:'physical_notes',label:'查体备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'imaging', icon: '📷', title: '影像数据',
      fields: [
        {id:'mri_done',label:'MRI检查',type:'select',opts:['已完成','未做','已预约'],req:true},
        {id:'mri_finding',label:'MRI分级（Schweitzer）',type:'select',opts:['I级（局灶性信号）','II级（弥漫性信号）','III级（部分撕裂）','IV级（完全断裂）']},
        {id:'lesion_length',label:'病变长度',type:'number',unit:'cm'},
        {id:'haglund_deformity',label:'Haglund畸形',type:'select',opts:['无','轻度','中度','重度']},
        {id:'retrocalcaneal_bursitis',label:'跟骨后滑囊炎',type:'select',opts:['无','有']},
        {id:'xray_spurs',label:'X线骨赘',type:'select',opts:['无','前方骨赘','后方骨赘','止点钙化']},
        {id:'imaging_notes',label:'影像学备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'scores', icon: '📊', title: '功能评分量表',
      fields: [
        {id:'visa_a',label:'VISA-A评分',type:'number',unit:'分/100',req:true},
        {id:'aofas_preop',label:'AOFAS踝后足评分',type:'number',unit:'分/100'},
        {id:'patient_expect',label:'患者期望',type:'select',opts:['恢复运动','日常行走','消除疼痛']}
      ]
    },
    {
      id: 'plan', icon: '⚕️', title: '手术方案',
      fields: [
        {id:'procedure',label:'手术术式',type:'select',opts:['跟腱清创+重建','止点重建+Haglund切除','FHL腱转位','同种异体腱重建','急性修复（端端缝合）','微创技术'],req:true},
        {id:'augmentation',label:'增强材料',type:'select',opts:['无','人工韧带','肌腱转位','生物补片']},
        {id:'haglund_resection',label:'Haglund切除',type:'select',opts:['是','否']},
        {id:'calcaneal_osteotomy',label:'跟骨截骨',type:'select',opts:['是','否']},
        {id:'surgeon',label:'主刀医师',type:'text'},
        {id:'plan_notes',label:'手术计划备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'media', icon: '🖼️', title: '影像资料上传',
      type: 'media',
      slots: getStructuredMediaSlots({ mri: true })
    }
  ];
}

function getSectionsF() {
  // 扁平足 (Flatfoot) 专用：以 PTTD/获得性平足为主线
  return [
    {
      id: 'basic', icon: '👤', title: '基本信息',
      fields: [
        {id:'name',label:'姓名',type:'text',req:true},
        {id:'age',label:'年龄',type:'number',unit:'岁',req:true},
        {id:'gender',label:'性别',type:'select',opts:['男','女']},
        {id:'side',label:'患侧',type:'select',opts:['左足','右足','双足'],req:true},
        {id:'flat_type',label:'平足类型',type:'select',opts:['柔性平足（PTTD）','僵硬性平足','创伤后平足','类风湿性平足','先天性平足'],req:true},
        {id:'caseid',label:'住院号',type:'text'},
        {id:'date',label:'就诊日期',type:'date',req:true},
        {id:'bmi',label:'BMI',type:'number',unit:'kg/m²'},
        {id:'chief_complaint',label:'主诉',type:'textarea',full:true,req:true},
        {id:'duration',label:'病程',type:'text',full:true}
      ]
    },
    {
      id: 'history', icon: '📋', title: '病史采集',
      fields: [
        {id:'pttd_stage',label:'PTTD 分期 (Johnson-Strom)',type:'select',opts:['I 期（无畸形，肌腱炎）','II A 期（柔性外翻）','II B 期（前足外展）','III 期（僵硬畸形）','IV 期（继发踝外翻）','不适用']},
        {id:'pain_location',label:'疼痛部位',type:'checkboxes',opts:['内踝下方','足底内侧','跖骨头跖侧','外踝下方（撞击）','踝关节内侧','Lisfranc 区域']},
        {id:'pain_vas',label:'疼痛 VAS',type:'number',unit:'分'},
        {id:'walking_distance',label:'步行距离',type:'select',opts:['无限制','500m 以上','500m 以内','100m 以内','室内']},
        {id:'conservative_tx',label:'保守治疗史',type:'checkboxes',opts:['矫形垫','足踝支具','物理治疗','口服止痛药','PRP 注射','无']},
        {id:'family_hx',label:'家族史',type:'select',opts:['无','有（直系）']},
        {id:'comorbidity',label:'合并症',type:'checkboxes',opts:['糖尿病','类风湿关节炎','骨质疏松','肥胖','痛风','其他']}
      ]
    },
    {
      id: 'physical', icon: '🔍', title: '体格检查',
      fields: [
        {id:'hindfoot_alignment',label:'后足力线',type:'select',opts:['外翻（轻度）','外翻（中度）','外翻（重度）','中立'],req:true},
        {id:'hindfoot_angle',label:'后足力线角',type:'number',unit:'°'},
        {id:'flexibility',label:'足弓柔性',type:'select',opts:['柔性（负重消失）','半柔性','僵硬']},
        {id:'arch_height',label:'足弓高度（负重）',type:'number',unit:'mm'},
        {id:'forefoot_abduction',label:'前足外展',type:'select',opts:['无','轻度','中度','重度']},
        {id:'too_many_toes',label:'过多趾征',type:'select',opts:['阴性','阳性（+）','阳性（++）']},
        {id:'single_heel_rise',label:'单腿提踵试验',type:'select',opts:['正常','减弱','不能']},
        {id:'ankle_equinus',label:'踝关节马蹄（Silfverskiöld）',type:'select',opts:['无','轻度（<5°）','中度（5-10°）','重度（>10°）']},
        {id:'pttn_tenderness',label:'胫后肌腱压痛',type:'select',opts:['无','轻度','明显']},
        {id:'physical_notes',label:'查体备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'imaging', icon: '📷', title: '影像数据',
      fields: [
        {id:'calcaneal_pitch',label:'跟骨俯仰角',type:'number',unit:'°'},
        {id:'talo_1st_mt',label:'距骨-第一跖骨角（正位 Meary）',type:'number',unit:'°'},
        {id:'talo_1st_mt_lat',label:'距骨-第一跖骨角（侧位 Meary）',type:'number',unit:'°'},
        {id:'tn_coverage',label:'距舟覆盖角',type:'number',unit:'°'},
        {id:'arch_index',label:'足弓指数',type:'number'},
        {id:'hindfoot_moment',label:'后足力矩臂',type:'number',unit:'mm'},
        {id:'ankle_oa',label:'踝关节 OA',type:'select',opts:['无','轻度','中度','重度']},
        {id:'subtalar_oa',label:'距下关节 OA',type:'select',opts:['无','轻度','中度','重度']},
        {id:'pttn_mri',label:'胫后肌腱 MRI',type:'select',opts:['正常','腱鞘炎','部分撕裂','完全撕裂','不适用']},
        {id:'spring_ligament',label:'弹簧韧带损伤',type:'select',opts:['完整','部分撕裂','完全撕裂','不适用']},
        {id:'imaging_notes',label:'影像学备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'scores', icon: '📊', title: '功能评分量表',
      fields: [
        {id:'aofas_preop',label:'AOFAS 踝后足评分',type:'number',unit:'分/100',req:true},
        {id:'faam_adl',label:'FAAM ADL',type:'number',unit:'%'},
        {id:'foot_function_index',label:'足功能指数 (FFI)',type:'number'},
        {id:'patient_goal',label:'患者主要目标',type:'select',opts:['消除疼痛','矫正外观','穿正常鞋','恢复运动']}
      ]
    },
    {
      id: 'plan', icon: '⚕️', title: '手术方案',
      fields: [
        {id:'procedure_flat',label:'矫形术式',type:'checkboxes',opts:['跟骨内移截骨 (MDCO)','外侧柱延长（Evans）','Cotton 楔形截骨','FDL 腱转位','弹簧韧带重建','胫后肌腱清创/修复','距下关节融合','三关节融合']},
        {id:'achilles_lengthening',label:'跟腱延长',type:'select',opts:['否','Strayer','Vulpius','经皮三切口']},
        {id:'fixation',label:'内固定方式',type:'select',opts:['螺钉固定','钢板螺钉','锚钉','克氏针']},
        {id:'anesthesia',label:'麻醉方式',type:'select',opts:['腰硬联合','全麻','腰麻','局部阻滞']},
        {id:'surgeon',label:'主刀医师',type:'text'},
        {id:'plan_notes',label:'手术计划备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'media', icon: '🖼️', title: '影像资料上传',
      type: 'media',
      slots: getStructuredMediaSlots()
    }
  ];
}

// ============================================================
// G: 高弓足 (Cavus Foot)
// ============================================================
function getSectionsG() {
  return [
    {
      id: 'basic', icon: '👤', title: '基本信息',
      fields: [
        {id:'name',label:'姓名',type:'text',req:true},
        {id:'age',label:'年龄',type:'number',unit:'岁',req:true},
        {id:'gender',label:'性别',type:'select',opts:['男','女']},
        {id:'side',label:'患侧',type:'select',opts:['左足','右足','双足'],req:true},
        {id:'cavus_type',label:'高弓足类型',type:'select',opts:['先天性高弓足','神经源性高弓足（CMT 等）','创伤后高弓足','特发性高弓足','医源性高弓足'],req:true},
        {id:'caseid',label:'住院号',type:'text'},
        {id:'date',label:'就诊日期',type:'date',req:true},
        {id:'chief_complaint',label:'主诉',type:'textarea',full:true,req:true},
        {id:'duration',label:'病程',type:'text',full:true}
      ]
    },
    {
      id: 'history', icon: '📋', title: '病史采集',
      fields: [
        {id:'neurology_disease',label:'神经系统疾病',type:'checkboxes',opts:['Charcot-Marie-Tooth (CMT)','脊髓栓系','脑瘫','脊髓灰质炎后遗症','脊髓损伤','无']},
        {id:'family_hx',label:'家族史',type:'select',opts:['无','有（CMT 等遗传性疾病）','有（其他）'],req:true},
        {id:'electrophysiology',label:'肌电图 / 神经传导',type:'select',opts:['未检查','正常','异常（脱髓鞘）','异常（轴索）','异常（神经源性）']},
        {id:'pain_vas',label:'疼痛 VAS',type:'number',unit:'分'},
        {id:'pain_location',label:'疼痛部位',type:'checkboxes',opts:['足跟外侧','跖骨头跖侧','第5跖骨基底','外踝下方','足背','胫前']},
        {id:'instability_episodes',label:'反复扭伤',type:'select',opts:['无','偶发（每年<2次）','频繁（每年≥3次）']},
        {id:'shoe_wear',label:'鞋底磨损部位',type:'select',opts:['正常','外侧（前足）','外侧（足跟）','双侧外侧','其他']},
        {id:'previous_treatment',label:'既往治疗',type:'textarea',full:true}
      ]
    },
    {
      id: 'physical', icon: '🔍', title: '体格检查',
      fields: [
        {id:'hindfoot_alignment',label:'后足力线',type:'select',opts:['内翻（轻度）','内翻（中度）','内翻（重度）','中立'],req:true},
        {id:'hindfoot_angle',label:'后足力线角',type:'number',unit:'°'},
        {id:'forefoot_alignment',label:'前足力线',type:'select',opts:['正常','旋前（PF 第一跖列下降）','旋后']},
        {id:'first_ray_plantarflex',label:'第一跖列跖屈',type:'select',opts:['无','轻度','明显']},
        {id:'coleman_block',label:'Coleman 积木试验',type:'select',opts:['阳性（前足驱动，柔性）','阴性（后足僵硬）','部分可纠正'],req:true},
        {id:'clawing_toes',label:'爪形趾',type:'select',opts:['无','轻度','中度','重度']},
        {id:'muscle_strength',label:'肌力评估',type:'textarea',full:true,placeholder:'胫前/胫后/腓骨长短肌/腓肠肌/趾屈伸肌 各级别（MRC 0-5）'},
        {id:'sensation',label:'感觉',type:'select',opts:['正常','足部远端减退','足底感觉缺失','袜套样减退']},
        {id:'reflexes',label:'踝反射',type:'select',opts:['正常','减弱','消失','亢进']},
        {id:'physical_notes',label:'查体备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'imaging', icon: '📷', title: '影像数据',
      fields: [
        {id:'calcaneal_pitch',label:'跟骨俯仰角',type:'number',unit:'°'},
        {id:'meary_angle',label:'Meary 角（侧位）',type:'number',unit:'°（背伸 +/跖屈 -）'},
        {id:'hibbs_angle',label:'Hibbs 角',type:'number',unit:'°'},
        {id:'talo_calcaneal_lat',label:'距跟角（侧位）',type:'number',unit:'°'},
        {id:'djian_annonier',label:'Djian-Annonier 角',type:'number',unit:'°'},
        {id:'first_mt_dorsiflex',label:'第一跖骨背屈',type:'number',unit:'°'},
        {id:'ankle_oa',label:'踝关节 OA',type:'select',opts:['无','轻度','中度','重度']},
        {id:'subtalar_oa',label:'距下关节 OA',type:'select',opts:['无','轻度','中度','重度']},
        {id:'midfoot_oa',label:'中足 OA',type:'select',opts:['无','轻度','中度','重度']},
        {id:'imaging_notes',label:'影像学备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'scores', icon: '📊', title: '功能评分量表',
      fields: [
        {id:'aofas_preop',label:'AOFAS 踝后足评分',type:'number',unit:'分/100',req:true},
        {id:'faam_adl',label:'FAAM ADL',type:'number',unit:'%'},
        {id:'foot_function_index',label:'足功能指数 (FFI)',type:'number'},
        {id:'sf36_physical',label:'SF-36 体格评分',type:'number'},
        {id:'patient_goal',label:'患者主要目标',type:'select',opts:['消除疼痛','减少扭伤','矫正外观','恢复运动','改善步态']}
      ]
    },
    {
      id: 'plan', icon: '⚕️', title: '手术方案',
      fields: [
        {id:'procedure_cavus',label:'矫形术式（多选）',type:'checkboxes',opts:['跖腱膜松解','第一跖骨背屈截骨 (Dorsiflexion OT)','Dwyer 跟骨外翻截骨','Steindler 松解','腓骨长肌→腓骨短肌转位','胫后肌腱转位（PTT→足背）','Jones 手术（EHL 转位）','爪形趾矫形','三关节融合（僵硬畸形）']},
        {id:'achilles_lengthening',label:'跟腱延长',type:'select',opts:['否','Strayer','Vulpius','经皮三切口']},
        {id:'fixation',label:'内固定方式',type:'select',opts:['螺钉','锁定钢板','锚钉','克氏针','外固定']},
        {id:'anesthesia',label:'麻醉方式',type:'select',opts:['腰硬联合','全麻','腰麻']},
        {id:'tourniquet',label:'止血带计划',type:'select',opts:['大腿止血带','踝部止血带','无止血带']},
        {id:'surgeon',label:'主刀医师',type:'text'},
        {id:'plan_notes',label:'手术计划备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'media', icon: '🖼️', title: '影像资料上传',
      type: 'media',
      slots: getStructuredMediaSlots()
    }
  ];
}

// ============================================================
// H: 踝关节韧带损伤 (Ankle Ligament Injury)
// ============================================================
function getSectionsH() {
  return [
    {
      id: 'basic', icon: '👤', title: '基本信息',
      fields: [
        {id:'name',label:'姓名',type:'text',req:true},
        {id:'age',label:'年龄',type:'number',unit:'岁',req:true},
        {id:'gender',label:'性别',type:'select',opts:['男','女']},
        {id:'side',label:'患侧',type:'select',opts:['左踝','右踝','双踝'],req:true},
        {id:'caseid',label:'住院号',type:'text'},
        {id:'date',label:'就诊日期',type:'date',req:true},
        {id:'sports_level',label:'运动水平',type:'select',opts:['久坐','休闲','业余竞技','职业运动员']},
        {id:'injury_mechanism',label:'受伤机制',type:'select',opts:['内翻扭伤（最常见）','外翻扭伤','旋转扭伤','直接撞击','不详'],req:true},
        {id:'injury_phase',label:'损伤阶段',type:'select',opts:['急性（≤4 周）','亚急性（4-12 周）','慢性（>3 个月）'],req:true},
        {id:'chief_complaint',label:'主诉',type:'textarea',full:true,req:true},
        {id:'duration',label:'病程',type:'text',full:true}
      ]
    },
    {
      id: 'history', icon: '📋', title: '病史采集',
      fields: [
        {id:'pain_vas',label:'疼痛 VAS',type:'number',unit:'分'},
        {id:'instability_episodes',label:'反复扭伤次数',type:'select',opts:['首次','偶发（每年<2次）','频繁（每年≥3次）','慢性不稳定']},
        {id:'giving_way',label:'打软腿（giving way）',type:'select',opts:['无','偶尔','频繁','日常活动即出现']},
        {id:'self_confidence',label:'对踝关节信心',type:'select',opts:['正常','轻度下降','明显下降','需护具才敢活动']},
        {id:'conservative_tx',label:'保守治疗史',type:'checkboxes',opts:['制动/支具','物理治疗','本体感觉训练','PRP 注射','口服止痛药','无']},
        {id:'conservative_duration',label:'保守治疗时长',type:'number',unit:'月'},
        {id:'previous_sprains',label:'既往扭伤史',type:'textarea',full:true},
        {id:'comorbidity',label:'合并症',type:'checkboxes',opts:['全身关节松弛','骨质疏松','糖尿病','腓骨肌腱病','痛风','其他']}
      ]
    },
    {
      id: 'physical', icon: '🔍', title: '体格检查',
      fields: [
        {id:'anterior_drawer',label:'前抽屉试验',type:'select',opts:['阴性','可疑阳性','明显阳性'],req:true},
        {id:'talar_tilt',label:'距骨倾斜试验',type:'select',opts:['阴性','可疑阳性','明显阳性'],req:true},
        {id:'squeeze_test',label:'挤压试验（下胫腓）',type:'select',opts:['阴性','阳性']},
        {id:'external_rotation_test',label:'外旋应力试验',type:'select',opts:['阴性','阳性']},
        {id:'peroneal_tenderness',label:'腓骨肌腱压痛',type:'select',opts:['无','轻度','明显']},
        {id:'eversion_strength',label:'外翻肌力（腓骨肌）',type:'select',opts:['5级','4级','3级','2级以下']},
        {id:'single_leg_balance',label:'单腿平衡（睁眼/闭眼）',type:'select',opts:['正常','轻度受损','明显受损','无法完成']},
        {id:'beighton_score',label:'Beighton 评分（全身松弛）',type:'number',unit:'分/9'},
        {id:'tendinopathy',label:'腓骨肌腱病迹象',type:'select',opts:['无','腱鞘炎','半脱位','部分撕裂']},
        {id:'physical_notes',label:'查体备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'imaging', icon: '📷', title: '影像数据',
      fields: [
        {id:'xray_views',label:'X 线摄片',type:'checkboxes',opts:['负重正位','负重侧位','踝穴位','应力位（前抽屉）','应力位（内翻）']},
        {id:'stress_anterior',label:'前抽屉应力位',type:'number',unit:'mm（>10 异常）'},
        {id:'stress_talar_tilt',label:'距骨倾斜应力位',type:'number',unit:'°（>9° 异常）'},
        {id:'mri_done',label:'MRI 检查',type:'select',opts:['已完成','未做','已预约'],req:true},
        {id:'atfl',label:'距腓前韧带 (ATFL)',type:'select',opts:['完整','部分撕裂','完全撕裂','陈旧瘢痕']},
        {id:'cfl',label:'跟腓韧带 (CFL)',type:'select',opts:['完整','部分撕裂','完全撕裂','陈旧瘢痕']},
        {id:'ptfl',label:'距腓后韧带 (PTFL)',type:'select',opts:['完整','部分撕裂','完全撕裂']},
        {id:'syndesmosis_injury',label:'下胫腓联合',type:'select',opts:['完整','不稳定（轻）','不稳定（明显）']},
        {id:'osteochondral_lesion',label:'软骨损伤（伴随）',type:'select',opts:['无','内侧距骨穹窿','外侧距骨穹窿','胫骨穹窿','其他']},
        {id:'bone_bruise',label:'骨挫伤',type:'select',opts:['无','距骨','腓骨','胫骨远端','跟骨']},
        {id:'imaging_notes',label:'影像学备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'scores', icon: '📊', title: '功能评分量表',
      fields: [
        {id:'aofas_preop',label:'AOFAS 踝后足评分',type:'number',unit:'分/100',req:true},
        {id:'faam_adl',label:'FAAM ADL',type:'number',unit:'%'},
        {id:'faam_sport',label:'FAAM Sport',type:'number',unit:'%'},
        {id:'cait',label:'Cumberland 踝不稳定量表 (CAIT)',type:'number',unit:'分/30'},
        {id:'kt_score',label:'Karlsson-Peterson 评分',type:'number',unit:'分/100'},
        {id:'patient_expect',label:'患者期望',type:'select',opts:['恢复运动','日常行走稳定','消除疼痛','重返竞技']}
      ]
    },
    {
      id: 'plan', icon: '⚕️', title: '手术方案',
      fields: [
        {id:'procedure',label:'手术术式',type:'select',opts:['Broström 修复','改良 Broström-Gould','Watson-Jones 重建','Chrisman-Snook 重建','解剖重建（自体腘绳肌腱）','解剖重建（同种异体腱）','InternalBrace 增强','关节镜下修复'],req:true},
        {id:'graft_source',label:'移植物来源',type:'select',opts:['不适用','自体腘绳肌腱','自体腓骨长肌','同种异体腱','人工韧带']},
        {id:'augmentation',label:'增强材料',type:'select',opts:['无','锚钉','InternalBrace 缝合带','人工韧带']},
        {id:'arthroscopy',label:'同期关节镜处理',type:'checkboxes',opts:['滑膜清理','软骨清创','OCD 微骨折','游离体取出','撞击切除','无']},
        {id:'syndesmosis_fixation',label:'下胫腓固定（如需要）',type:'select',opts:['无需处理','螺钉固定','弹性固定（TightRope）']},
        {id:'peroneal_repair',label:'腓骨肌腱处理',type:'select',opts:['无','清创','修复','上支持带重建']},
        {id:'anesthesia',label:'麻醉方式',type:'select',opts:['腰硬联合','全麻','腰麻','局部阻滞']},
        {id:'surgeon',label:'主刀医师',type:'text'},
        {id:'plan_notes',label:'手术计划备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'media', icon: '🖼️', title: '影像资料上传',
      type: 'media',
      slots: getStructuredMediaSlots({ mri: true })
    }
  ];
}

// ============================================================
// I: 距骨软骨损伤 (Osteochondral Lesion of the Talus, OCD)
// ============================================================
function getSectionsI() {
  return [
    {
      id: 'basic', icon: '👤', title: '基本信息',
      fields: [
        {id:'name',label:'姓名',type:'text',req:true},
        {id:'age',label:'年龄',type:'number',unit:'岁',req:true},
        {id:'gender',label:'性别',type:'select',opts:['男','女']},
        {id:'side',label:'患侧',type:'select',opts:['左踝','右踝','双踝'],req:true},
        {id:'caseid',label:'住院号',type:'text'},
        {id:'date',label:'就诊日期',type:'date',req:true},
        {id:'sports_level',label:'运动水平',type:'select',opts:['久坐','休闲','业余竞技','职业运动员']},
        {id:'injury_related',label:'是否创伤相关',type:'select',opts:['明确外伤','反复扭伤','无明确外伤'],req:true},
        {id:'chief_complaint',label:'主诉',type:'textarea',full:true,req:true},
        {id:'duration',label:'病程',type:'text',full:true}
      ]
    },
    {
      id: 'history', icon: '📋', title: '病史采集',
      fields: [
        {id:'pain_vas',label:'疼痛 VAS',type:'number',unit:'分'},
        {id:'pain_character',label:'疼痛特点',type:'select',opts:['深部钝痛','负重后加重','晨僵','间歇性锐痛','静息痛']},
        {id:'locking',label:'交锁',type:'select',opts:['无','偶尔','频繁']},
        {id:'catching',label:'卡顿感',type:'select',opts:['无','偶尔','频繁']},
        {id:'swelling',label:'肿胀',type:'select',opts:['无','活动后出现','持续存在']},
        {id:'previous_sprains',label:'既往扭伤史',type:'select',opts:['无','1-2 次','频繁','慢性不稳定']},
        {id:'conservative_tx',label:'保守治疗史',type:'checkboxes',opts:['制动','物理治疗','口服止痛药','关节内注射（皮质类固醇）','PRP/透明质酸','无']},
        {id:'conservative_duration',label:'保守治疗时长',type:'number',unit:'月'},
        {id:'comorbidity',label:'合并症',type:'checkboxes',opts:['踝关节不稳定','骨质疏松','糖尿病','痛风','其他']}
      ]
    },
    {
      id: 'physical', icon: '🔍', title: '体格检查',
      fields: [
        {id:'ankle_rom_dors',label:'踝关节背伸 ROM',type:'number',unit:'°'},
        {id:'ankle_rom_plant',label:'踝关节跖屈 ROM',type:'number',unit:'°'},
        {id:'joint_line_tenderness',label:'关节线压痛',type:'select',opts:['无','内侧','外侧','双侧']},
        {id:'effusion',label:'关节积液',type:'select',opts:['无','轻度','明显']},
        {id:'crepitus',label:'摩擦音/捻发音',type:'select',opts:['无','有']},
        {id:'instability_test',label:'前抽屉/距骨倾斜',type:'select',opts:['阴性','可疑','阳性']},
        {id:'single_hop_test',label:'单腿跳试验',type:'select',opts:['正常','减弱','不能']},
        {id:'physical_notes',label:'查体备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'imaging', icon: '📷', title: '影像数据',
      fields: [
        {id:'lesion_location',label:'病变部位',type:'select',opts:['距骨穹窿（内侧）','距骨穹窿（外侧）','距骨穹窿（中央）','胫骨远端关节面','腓骨远端关节面'],req:true},
        {id:'lesion_size_ap',label:'病变前后径',type:'number',unit:'mm'},
        {id:'lesion_size_ml',label:'病变内外径',type:'number',unit:'mm'},
        {id:'lesion_depth',label:'病变深度',type:'number',unit:'mm'},
        {id:'hepple_grade',label:'Hepple MRI 分型',type:'select',opts:['I 期（软骨完整）','II 期（软骨损伤+骨折）','IIa 期（软骨损伤+骨折+骨水肿）','III 期（移位骨折）','IV 期（移位+翻转）','V 期（软骨下囊肿）'],req:true},
        {id:'berndt_harty',label:'Berndt-Harty 分级（X线）',type:'select',opts:['I 期（压缩）','II 期（部分分离）','III 期（完全分离未移位）','IV 期（移位）']},
        {id:'bone_marrow_edema',label:'骨髓水肿',type:'select',opts:['无','轻度','明显']},
        {id:'subchondral_cyst',label:'软骨下囊肿',type:'select',opts:['无','单个 <5mm','单个 ≥5mm','多发']},
        {id:'loose_body',label:'游离体',type:'select',opts:['无','有（数量已记录）']},
        {id:'cartilage_status',label:'软骨连续性',type:'select',opts:['完整','部分缺损','完全缺损']},
        {id:'imaging_notes',label:'影像学备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'scores', icon: '📊', title: '功能评分量表',
      fields: [
        {id:'aofas_preop',label:'AOFAS 踝后足评分',type:'number',unit:'分/100',req:true},
        {id:'faam_adl',label:'FAAM ADL',type:'number',unit:'%'},
        {id:'faam_sport',label:'FAAM Sport',type:'number',unit:'%'},
        {id:'vas_satisfaction',label:'患者期望',type:'select',opts:['完全康复','明显改善','轻度改善','维持现状']}
      ]
    },
    {
      id: 'plan', icon: '⚕️', title: '手术方案',
      fields: [
        {id:'arthroscopy',label:'关节镜入路',type:'select',opts:['前内/前外','后内/后外（俯卧）','前+后联合','开放（截骨入路）'],req:true},
        {id:'procedure',label:'主要术式',type:'select',opts:['微骨折 (Microfracture)','钻孔 (Drilling)','AMIC（基质诱导自体软骨形成）','自体骨软骨移植 (OAT/OATS/Mosaicplasty)','同种异体骨软骨移植','MACI（基质培养软骨细胞）','逆行钻孔','骨软骨清创+植骨'],req:true},
        {id:'graft_source',label:'移植物来源',type:'select',opts:['不适用','自体（同侧膝）','自体（髂骨松质骨）','同种异体','自体软骨细胞培养']},
        {id:'malleolar_osteotomy',label:'内/外踝截骨入路',type:'select',opts:['无','内踝截骨','外踝截骨','胫骨前方截骨']},
        {id:'bone_grafting',label:'植骨（深部/囊肿）',type:'select',opts:['无','自体','同种异体','人工骨']},
        {id:'ligament_repair',label:'伴随韧带处理',type:'select',opts:['无','Broström 修复','解剖重建','下胫腓固定']},
        {id:'anesthesia',label:'麻醉方式',type:'select',opts:['腰硬联合','全麻','腰麻','局部阻滞']},
        {id:'surgeon',label:'主刀医师',type:'text'},
        {id:'plan_notes',label:'手术计划备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'media', icon: '🖼️', title: '影像资料上传',
      type: 'media',
      slots: getStructuredMediaSlots()
    }
  ];
}

// ============================================================
// J: Lisfranc 损伤
// ============================================================
function getSectionsJ() {
  return [
    {
      id: 'basic', icon: '👤', title: '基本信息',
      fields: [
        {id:'name',label:'姓名',type:'text',req:true},
        {id:'age',label:'年龄',type:'number',unit:'岁',req:true},
        {id:'gender',label:'性别',type:'select',opts:['男','女']},
        {id:'side',label:'患侧',type:'select',opts:['左足','右足','双足'],req:true},
        {id:'caseid',label:'住院号',type:'text'},
        {id:'date',label:'就诊/受伤日期',type:'date',req:true},
        {id:'injury_mechanism',label:'受伤机制',type:'select',opts:['高能量（坠落/交通伤）','低能量（运动扭伤）','直接挤压','马蹄踩踏（旋转）'],req:true},
        {id:'energy_level',label:'能量级别',type:'select',opts:['低能量','中能量','高能量'],req:true},
        {id:'open_injury',label:'开放性损伤',type:'select',opts:['闭合','Gustilo I','Gustilo II','Gustilo III'],req:true},
        {id:'chief_complaint',label:'主诉',type:'textarea',full:true,req:true}
      ]
    },
    {
      id: 'classification', icon: '🔖', title: '损伤分型',
      fields: [
        {id:'myerson_type',label:'Myerson 分型',type:'select',opts:['A 型（同向完全移位）','B1 型（同向部分内侧）','B2 型（同向部分外侧）','C1 型（分散部分）','C2 型（分散完全）'],req:true},
        {id:'nunley_vertes',label:'Nunley-Vertes 分级（隐匿伤）',type:'select',opts:['I 级（韧带挫伤，无移位）','II 级（C1-MT2 间隙 1-5mm）','III 级（间隙 >5mm + 弓部塌陷）','不适用']},
        {id:'columns_involved',label:'累及柱',type:'checkboxes',opts:['内侧柱（C1-MT1）','中柱（C2/C3-MT2/MT3）','外侧柱（骰骨-MT4/MT5）'],req:true},
        {id:'displacement_pattern',label:'移位方向',type:'select',opts:['同向（外侧）','同向（内侧）','分散','背侧脱位','复合']},
        {id:'cuneiform_fracture',label:'楔骨骨折',type:'select',opts:['无','内侧楔骨','中间楔骨','外侧楔骨','多发']},
        {id:'navicular_cuboid',label:'舟骨/骰骨骨折',type:'select',opts:['无','舟骨','骰骨','均累及']},
        {id:'compartment_syndrome',label:'骨筋膜室综合征',type:'select',opts:['无','可疑','明确（已切开减压）']},
        {id:'fracture_notes',label:'分型备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'imaging', icon: '📷', title: '影像数据',
      fields: [
        {id:'xray_views',label:'X 线摄片',type:'checkboxes',opts:['正位','侧位','斜位（30°内旋）','负重位（核心）','应力位']},
        {id:'fleck_sign',label:'Fleck 征（C1-MT2 间撕脱）',type:'select',opts:['无','有'],req:true},
        {id:'diastasis_c1_mt2',label:'C1-MT2 跖间距',type:'number',unit:'mm（>2 异常）'},
        {id:'diastasis_c1_mt1',label:'C1-MT1 跖间距',type:'number',unit:'mm'},
        {id:'arch_loss',label:'纵弓塌陷（侧位）',type:'select',opts:['无','轻度','明显']},
        {id:'ct_done',label:'CT 检查',type:'select',opts:['已完成','未做','已预约']},
        {id:'mri_done',label:'MRI 检查',type:'select',opts:['未做','已完成（韧带评估）','已预约']},
        {id:'lisfranc_ligament',label:'Lisfranc 韧带（MRI）',type:'select',opts:['完整','部分撕裂','完全撕裂','不适用']},
        {id:'tmt_dislocation',label:'TMT 关节脱位',type:'select',opts:['无','部分脱位','完全脱位']},
        {id:'articular_comminution',label:'关节面粉碎程度',type:'select',opts:['无','轻度','中度','重度']},
        {id:'imaging_notes',label:'影像学备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'physical', icon: '🔍', title: '体格检查',
      fields: [
        {id:'tenderness_location',label:'压痛部位',type:'checkboxes',opts:['Lisfranc 韧带区域','跖跗关节背侧','内侧柱','中柱','外侧柱']},
        {id:'plantar_ecchymosis',label:'足底瘀斑（特征性）',type:'select',opts:['无','轻度','明显']},
        {id:'midfoot_stress',label:'足中段应力痛',type:'select',opts:['无','可疑','明显']},
        {id:'piano_key_test',label:'琴键试验',type:'select',opts:['阴性','阳性']},
        {id:'pulses',label:'足背动脉/胫后动脉',type:'select',opts:['正常','减弱','消失']},
        {id:'sensation',label:'感觉',type:'select',opts:['正常','减退','缺失']},
        {id:'skin_condition',label:'皮肤情况',type:'select',opts:['正常','水疱','张力性水疱','皮肤坏死','开放伤口']},
        {id:'physical_notes',label:'查体备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'plan', icon: '⚕️', title: '手术方案',
      fields: [
        {id:'treatment_strategy',label:'治疗策略',type:'select',opts:['切开复位内固定 (ORIF)','一期 TMT 关节融合','分期手术（外固定+ORIF）','保守治疗（非移位）'],req:true},
        {id:'approach',label:'手术入路',type:'select',opts:['背侧双切口','背侧三切口','内侧+背侧联合','其他']},
        {id:'columns_fixed',label:'固定柱',type:'checkboxes',opts:['内侧柱','中柱','外侧柱']},
        {id:'fixation_method',label:'固定方式',type:'select',opts:['空心螺钉跨关节','背侧锁定钢板','缝合带（TightRope/InternalBrace）','克氏针','螺钉+钢板组合'],req:true},
        {id:'lateral_column',label:'外侧柱处理',type:'select',opts:['不固定','克氏针临时固定','螺钉（短期取出）']},
        {id:'arthrodesis_joints',label:'融合关节',type:'checkboxes',opts:['C1-MT1','C2-MT2','C3-MT3','不融合']},
        {id:'dorsalis_pedis_protection',label:'足背动脉保护',type:'select',opts:['标准入路保护','解剖学异常需调整','无需特殊处理']},
        {id:'antibiotic_prophylaxis',label:'抗生素预防',type:'select',opts:['头孢一代','头孢二代','克林霉素','其他','无']},
        {id:'anesthesia',label:'麻醉方式',type:'select',opts:['腰硬联合','全麻','腰麻']},
        {id:'surgeon',label:'主刀医师',type:'text'},
        {id:'plan_notes',label:'手术计划备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'media', icon: '🖼️', title: '影像资料上传',
      type: 'media',
      slots: getStructuredMediaSlots()
    }
  ];
}

// ============================================================
// K: 跖骨基底撕脱骨折（第一、第五）
// ============================================================
function getSectionsK() {
  return [
    {
      id: 'basic', icon: '👤', title: '基本信息',
      fields: [
        {id:'name',label:'姓名',type:'text',req:true},
        {id:'age',label:'年龄',type:'number',unit:'岁',req:true},
        {id:'gender',label:'性别',type:'select',opts:['男','女']},
        {id:'side',label:'患侧',type:'select',opts:['左足','右足','双足'],req:true},
        {id:'caseid',label:'住院号',type:'text'},
        {id:'date',label:'就诊/受伤日期',type:'date',req:true},
        {id:'location',label:'骨折位置',type:'select',opts:['第一跖骨基底','第五跖骨基底','第一+第五跖骨基底'],req:true},
        {id:'injury_mechanism',label:'受伤机制',type:'select',opts:['内翻扭伤','外翻扭伤','直接撞击','应力性骨折','其他'],req:true},
        {id:'sports_level',label:'运动水平',type:'select',opts:['久坐','休闲','业余竞技','职业运动员']},
        {id:'chief_complaint',label:'主诉',type:'textarea',full:true,req:true}
      ]
    },
    {
      id: 'classification', icon: '🔖', title: '骨折分型',
      fields: [
        {id:'lawrence_botte',label:'Lawrence-Botte 分区（第5跖骨）',type:'select',opts:['I 区（基底撕脱/Pseudo-Jones）','II 区（干骺端 / Jones 骨折）','III 区（干部应力性）','不适用（第一跖骨）']},
        {id:'displacement',label:'骨折移位',type:'number',unit:'mm'},
        {id:'intra_articular',label:'累及关节',type:'select',opts:['关节外','累及 TMT 关节','累及跖间关节']},
        {id:'fragment_size_pct',label:'骨折块占关节面比例',type:'number',unit:'%'},
        {id:'comminution',label:'粉碎程度',type:'select',opts:['无（单一骨块）','轻度','重度']},
        {id:'associated_lesions',label:'合并损伤',type:'checkboxes',opts:['Lisfranc 韧带损伤','其它跖骨骨折','跟骰关节损伤','腓骨短肌腱止点损伤','骰骨骨折','无']},
        {id:'stress_fx',label:'应力性骨折特征',type:'select',opts:['无','早期（无骨痂）','延迟愈合','不愈合']},
        {id:'fracture_notes',label:'分型备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'imaging', icon: '📷', title: '影像数据',
      fields: [
        {id:'xray_views',label:'X 线摄片',type:'checkboxes',opts:['正位','侧位','斜位','负重位']},
        {id:'gap_between',label:'骨折间隙',type:'number',unit:'mm'},
        {id:'angulation',label:'成角',type:'number',unit:'°'},
        {id:'tmt_alignment',label:'TMT 关节对位',type:'select',opts:['正常','轻度移位','明显移位']},
        {id:'ct_done',label:'CT 检查',type:'select',opts:['已完成','未做','已预约']},
        {id:'mri_done',label:'MRI 检查',type:'select',opts:['未做','已完成（韧带评估）','已预约']},
        {id:'lisfranc_status',label:'Lisfranc 韧带（MRI）',type:'select',opts:['完整','部分撕裂','完全撕裂','未评估']},
        {id:'imaging_notes',label:'影像学备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'physical', icon: '🔍', title: '体格检查',
      fields: [
        {id:'tenderness',label:'压痛部位',type:'select',opts:['第5跖骨基底','第一跖骨基底','跖间','跖骨头']},
        {id:'swelling',label:'肿胀',type:'select',opts:['无','轻度','中度','重度']},
        {id:'ecchymosis',label:'瘀斑',type:'select',opts:['无','局部','足底广泛']},
        {id:'eversion_pain',label:'外翻痛',type:'select',opts:['无','有']},
        {id:'inversion_pain',label:'内翻痛',type:'select',opts:['无','有']},
        {id:'weight_bearing',label:'负重能力',type:'select',opts:['正常','部分负重','不能负重']},
        {id:'pulses',label:'足背动脉/胫后动脉',type:'select',opts:['正常','减弱']},
        {id:'physical_notes',label:'查体备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'scores', icon: '📊', title: '功能评分量表',
      fields: [
        {id:'aofas_preop',label:'AOFAS 中足评分',type:'number',unit:'分/100'},
        {id:'faam_adl',label:'FAAM ADL',type:'number',unit:'%'},
        {id:'pain_vas',label:'疼痛 VAS',type:'number',unit:'分'}
      ]
    },
    {
      id: 'plan', icon: '⚕️', title: '手术方案',
      fields: [
        {id:'treatment_strategy',label:'治疗策略',type:'select',opts:['保守治疗（石膏/支具）','切开复位内固定','髓内螺钉','张力带钢丝'],req:true},
        {id:'procedure',label:'固定方式（手术时）',type:'select',opts:['张力带钢丝','拉力螺钉','髓内螺钉','迷你钢板','锚钉重附着','克氏针','不适用']},
        {id:'fixation_size',label:'内置物规格',type:'text',placeholder:'如 4.5mm 髓内螺钉 / 2.7mm 拉力钉'},
        {id:'immobilization',label:'固定方式（术后/保守）',type:'select',opts:['短腿石膏','行走靴','硬底鞋','支具','不固定']},
        {id:'weight_bearing_plan',label:'负重计划',type:'select',opts:['立即负重','部分负重 2-4 周','非负重 2-4 周','非负重 ≥6 周']},
        {id:'anesthesia',label:'麻醉方式',type:'select',opts:['腰麻','全麻','局部阻滞']},
        {id:'surgeon',label:'主刀医师',type:'text'},
        {id:'plan_notes',label:'手术计划备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'media', icon: '🖼️', title: '影像资料上传',
      type: 'media',
      slots: getStructuredMediaSlots()
    }
  ];
}

// ============================================================
// L: 跟骨骨折 (Calcaneal Fracture)
// ============================================================
function getSectionsL() {
  return [
    {
      id: 'basic', icon: '👤', title: '基本信息',
      fields: [
        {id:'name',label:'姓名',type:'text',req:true},
        {id:'age',label:'年龄',type:'number',unit:'岁',req:true},
        {id:'gender',label:'性别',type:'select',opts:['男','女']},
        {id:'side',label:'患侧',type:'select',opts:['左跟','右跟','双跟'],req:true},
        {id:'caseid',label:'住院号',type:'text'},
        {id:'date',label:'就诊/受伤日期',type:'date',req:true},
        {id:'injury_mechanism',label:'受伤机制',type:'select',opts:['高处坠落','交通伤','直接撞击','应力性骨折','其他'],req:true},
        {id:'fall_height',label:'坠落高度',type:'number',unit:'m'},
        {id:'bilateral',label:'是否双侧',type:'select',opts:['单侧','双侧'],req:true},
        {id:'open_fracture',label:'开放性骨折',type:'select',opts:['闭合','Gustilo I','Gustilo II','Gustilo IIIa','Gustilo IIIb'],req:true},
        {id:'chief_complaint',label:'主诉',type:'textarea',full:true,req:true}
      ]
    },
    {
      id: 'classification', icon: '🔖', title: '骨折分型',
      fields: [
        {id:'intra_articular',label:'关节内 vs 关节外',type:'select',opts:['关节内（约 75%）','关节外'],req:true},
        {id:'essex_lopresti',label:'Essex-Lopresti 分型（X 线）',type:'select',opts:['关节外','关节内（舌型）','关节内（关节面塌陷型）','不适用']},
        {id:'sanders_ct',label:'Sanders CT 分型',type:'select',opts:['Sanders I（无移位）','Sanders II A','Sanders II B','Sanders II C','Sanders III AB','Sanders III AC','Sanders III BC','Sanders IV（粉碎 4 块以上）'],req:true},
        {id:'bohler_angle',label:'Böhler 角',type:'number',unit:'°（正常 20-40°）'},
        {id:'gissane_angle',label:'Gissane 角',type:'number',unit:'°（正常 95-105°）'},
        {id:'height_loss',label:'跟骨高度丢失',type:'number',unit:'mm'},
        {id:'heel_widening',label:'跟骨变宽',type:'number',unit:'mm'},
        {id:'posterior_facet',label:'后关节面累及',type:'select',opts:['未累及','部分塌陷','完全塌陷','旋转移位']},
        {id:'associated_injuries',label:'合并损伤',type:'checkboxes',opts:['对侧跟骨骨折','胸腰椎骨折','下肢其他骨折','距骨骨折','颅脑损伤','内脏损伤','无']},
        {id:'fracture_notes',label:'分型备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'imaging', icon: '📷', title: '影像数据',
      fields: [
        {id:'xray_views',label:'X 线摄片',type:'checkboxes',opts:['侧位','轴位（Harris）','正位','Broden 位']},
        {id:'ct_done',label:'CT 检查',type:'select',opts:['已完成','未做','已预约'],req:true},
        {id:'ct_3d',label:'CT 三维重建',type:'select',opts:['已完成','未做']},
        {id:'soft_tissue_tscherne',label:'软组织损伤 (Tscherne)',type:'select',opts:['0 级','I 级','II 级','III 级'],req:true},
        {id:'wrinkle_sign',label:'皮肤皱褶征',type:'select',opts:['未出现','已出现（可手术）']},
        {id:'fracture_blister',label:'骨折水疱',type:'select',opts:['无','清亮水疱','血性水疱']},
        {id:'subtalar_articular',label:'距下关节面累及评估',type:'select',opts:['完整','部分台阶（<2mm）','明显台阶（≥2mm）','严重粉碎']},
        {id:'imaging_notes',label:'影像学备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'physical', icon: '🔍', title: '体格检查',
      fields: [
        {id:'heel_pad',label:'跟垫状态',type:'select',opts:['正常','增厚','瘀斑','感觉减退']},
        {id:'skin_condition',label:'皮肤情况',type:'select',opts:['正常','水疱','张力性水疱','坏死','开放伤口']},
        {id:'compartment_syndrome',label:'骨筋膜室综合征',type:'select',opts:['无','可疑','明确（已切开减压）']},
        {id:'peroneal_tendons',label:'腓骨长短肌（脱位/夹击）',type:'select',opts:['正常','脱位','夹击','无法评估']},
        {id:'pulses',label:'足背动脉/胫后动脉',type:'select',opts:['正常','减弱','消失']},
        {id:'sensation',label:'感觉',type:'select',opts:['正常','减退（足底）','减退（背侧）','缺失']},
        {id:'sural_nerve',label:'腓肠神经功能',type:'select',opts:['正常','损伤可疑','损伤明确']},
        {id:'physical_notes',label:'查体备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'scores', icon: '📊', title: '功能评分量表',
      fields: [
        {id:'aofas_preop',label:'AOFAS 踝后足评分（伤前估测）',type:'number',unit:'分/100'},
        {id:'faam_adl',label:'FAAM ADL（伤前）',type:'number',unit:'%'},
        {id:'sf12_physical',label:'SF-12 PCS',type:'number'},
        {id:'pain_vas',label:'疼痛 VAS',type:'number',unit:'分'}
      ]
    },
    {
      id: 'plan', icon: '⚕️', title: '手术方案',
      fields: [
        {id:'timing',label:'手术时机',type:'select',opts:['急诊（开放/筋膜室综合征）','早期（≤14 天）','延期（>14 天，等待皮肤皱褶）','保守治疗'],req:true},
        {id:'approach',label:'手术入路',type:'select',opts:['扩大外侧入路 (ELA)','微创/经皮 (MICA)','跗骨窦切口 (Sinus Tarsi)','后内侧入路','距下关节镜辅助','其他'],req:true},
        {id:'reduction',label:'复位方法',type:'select',opts:['撑开复位','顶棒撬拨','间接复位（韧带牵开）','关节镜辅助直视','开放直视']},
        {id:'fixation',label:'内固定方式',type:'select',opts:['跟骨锁定钢板','跟骨非锁定钢板','空心螺钉','髓内钉（CalcaNail 等）','克氏针','外固定支架'],req:true},
        {id:'bone_grafting',label:'植骨',type:'select',opts:['不需要','自体（髂骨）','同种异体','人工骨/磷酸钙']},
        {id:'subtalar_arthrodesis',label:'一期距下关节融合',type:'select',opts:['不融合','一期融合（Sanders IV / 关节面严重粉碎）']},
        {id:'antibiotic_prophylaxis',label:'抗生素预防',type:'select',opts:['头孢一代','头孢二代','克林霉素','其他']},
        {id:'anesthesia',label:'麻醉方式',type:'select',opts:['腰硬联合','全麻','腰麻','局部阻滞']},
        {id:'tourniquet',label:'止血带',type:'select',opts:['大腿止血带','无止血带']},
        {id:'surgeon',label:'主刀医师',type:'text'},
        {id:'plan_notes',label:'手术计划备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'media', icon: '🖼️', title: '影像资料上传',
      type: 'media',
      slots: getStructuredMediaSlots()
    }
  ];
}

// ============================================================
// M: 跗骨联合 (Tarsal Coalition)
// ============================================================
function getSectionsM() {
  return [
    {
      id: 'basic', icon: '👤', title: '基本信息',
      fields: [
        {id:'name',label:'姓名',type:'text',req:true},
        {id:'age',label:'年龄',type:'number',unit:'岁',req:true},
        {id:'gender',label:'性别',type:'select',opts:['男','女']},
        {id:'side',label:'患侧',type:'select',opts:['左足','右足','双足'],req:true},
        {id:'caseid',label:'住院号',type:'text'},
        {id:'date',label:'就诊日期',type:'date',req:true},
        {id:'chief_complaint',label:'主诉',type:'textarea',full:true,req:true},
        {id:'onset_age',label:'起病年龄',type:'number',unit:'岁'},
        {id:'duration',label:'病程',type:'text',full:true,placeholder:'如：反复扭伤2年，后足疼痛6个月'}
      ]
    },
    {
      id: 'history', icon: '📋', title: '病史与症状',
      fields: [
        {id:'pain_vas',label:'疼痛 VAS',type:'number',unit:'分'},
        {id:'recurrent_sprain',label:'反复扭伤史',type:'select',opts:['无','偶发','反复']},
        {id:'rigid_flatfoot',label:'僵硬性平足表现',type:'select',opts:['无','可疑','明确'],req:true},
        {id:'activity_limitation',label:'活动受限',type:'select',opts:['无','运动受限','长距离行走受限','日常活动受限']},
        {id:'family_history',label:'家族史',type:'select',opts:['无','有','不详']},
        {id:'prior_treatment',label:'既往治疗',type:'textarea',full:true}
      ]
    },
    {
      id: 'classification', icon: '🔖', title: '联合类型与分型',
      fields: [
        {id:'coalition_site',label:'联合部位',type:'select',opts:['跟舟联合','距跟联合','跟骰联合','距舟联合','多发联合'],req:true},
        {id:'coalition_type',label:'联合性质',type:'select',opts:['骨性','软骨性','纤维性','混合/待定'],req:true},
        {id:'coalition_extent',label:'联合范围',type:'select',opts:['小范围','中等范围','大范围（>50% 关节面）','待测量']},
        {id:'subtalar_degeneration',label:'距下关节退变',type:'select',opts:['无','轻度','中度','重度'],req:true},
        {id:'hindfoot_valgus',label:'后足外翻',type:'select',opts:['无','轻度','中度','重度']},
        {id:'classification_notes',label:'分型备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'imaging', icon: '📷', title: '影像数据',
      fields: [
        {id:'xray_views',label:'X 线摄片',type:'checkboxes',opts:['负重正位','负重侧位','斜位','Harris 轴位']},
        {id:'anteater_sign',label:'Anteater 征（跟舟联合）',type:'select',opts:['无','有','不适用']},
        {id:'c_sign',label:'C-sign（距跟联合）',type:'select',opts:['无','有','可疑']},
        {id:'ct_done',label:'CT 检查',type:'select',opts:['已完成','未做','已预约'],req:true},
        {id:'mri_done',label:'MRI 检查',type:'select',opts:['未做','已完成','已预约']},
        {id:'coalition_size_pct',label:'联合占关节面比例',type:'number',unit:'%'},
        {id:'adjacent_edema',label:'邻近骨髓水肿（MRI）',type:'select',opts:['无','轻度','明显','未评估']},
        {id:'imaging_notes',label:'影像学备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'physical', icon: '🔍', title: '体格检查',
      fields: [
        {id:'subtalar_motion',label:'距下关节活动',type:'select',opts:['正常','轻度受限','明显受限','僵硬'],req:true},
        {id:'peroneal_spasm',label:'腓骨肌痉挛',type:'select',opts:['无','可疑','明确']},
        {id:'hindfoot_alignment',label:'后足力线',type:'select',opts:['中立','外翻','内翻']},
        {id:'single_heel_rise',label:'单足提踵',type:'select',opts:['可完成','困难','不能完成']},
        {id:'tenderness',label:'压痛部位',type:'checkboxes',opts:['跗骨窦','跟舟区','距下关节内侧','跟骰区','无明显压痛']},
        {id:'physical_notes',label:'查体备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'scores', icon: '📊', title: '功能评分量表',
      fields: [
        {id:'aofas_preop',label:'AOFAS 踝后足评分',type:'number',unit:'分/100'},
        {id:'faam_adl',label:'FAAM ADL',type:'number',unit:'%'},
        {id:'pain_vas',label:'疼痛 VAS',type:'number',unit:'分'}
      ]
    },
    {
      id: 'plan', icon: '⚕️', title: '治疗/手术方案',
      fields: [
        {id:'treatment_strategy',label:'治疗策略',type:'select',opts:['保守治疗','联合切除+脂肪/肌肉间置','联合切除+矫形截骨','距下关节融合','三关节融合'],req:true},
        {id:'resection_target',label:'切除目标',type:'select',opts:['跟舟联合','距跟联合','多发联合','不适用']},
        {id:'interposition',label:'间置材料',type:'select',opts:['脂肪','趾短伸肌','骨蜡/其他','无']},
        {id:'osteotomy',label:'合并矫形截骨',type:'select',opts:['无','跟骨内移截骨','外侧柱延长','内侧柱稳定','其他']},
        {id:'fusion_joint',label:'融合关节',type:'select',opts:['不融合','距下关节','三关节','其他']},
        {id:'anesthesia',label:'麻醉方式',type:'select',opts:['腰麻','全麻','神经阻滞']},
        {id:'surgeon',label:'主刀医师',type:'text'},
        {id:'plan_notes',label:'治疗计划备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'media', icon: '🖼️', title: '影像资料上传',
      type: 'media',
      slots: getStructuredMediaSlots({mri:true})
    }
  ];
}

// ============================================================
// N: 莫顿神经瘤 (Morton Neuroma)
// ============================================================
function getSectionsN() {
  return [
    {
      id: 'basic', icon: '👤', title: '基本信息',
      fields: [
        {id:'name',label:'姓名',type:'text',req:true},
        {id:'age',label:'年龄',type:'number',unit:'岁',req:true},
        {id:'gender',label:'性别',type:'select',opts:['女','男']},
        {id:'side',label:'患侧',type:'select',opts:['左足','右足','双足'],req:true},
        {id:'caseid',label:'住院号',type:'text'},
        {id:'date',label:'就诊日期',type:'date',req:true},
        {id:'chief_complaint',label:'主诉',type:'textarea',full:true,req:true},
        {id:'duration',label:'病程',type:'text',full:true,placeholder:'如：第3跖间隙烧灼痛1年，加重3个月'}
      ]
    },
    {
      id: 'history', icon: '📋', title: '病史与症状',
      fields: [
        {id:'pain_vas',label:'疼痛 VAS',type:'number',unit:'分'},
        {id:'web_space',label:'主要跖间隙',type:'select',opts:['第2跖间隙','第3跖间隙','第4跖间隙','多发'],req:true},
        {id:'pain_character',label:'疼痛性质',type:'checkboxes',opts:['烧灼痛','麻木','针刺感','异物感/踩石感','夜间痛']},
        {id:'shoe_related',label:'与鞋型相关',type:'select',opts:['宽鞋缓解','高跟/窄鞋加重','无明显相关']},
        {id:'activity_limitation',label:'活动受限',type:'select',opts:['无','长距离行走受限','运动受限','日常活动受限']},
        {id:'prior_treatment',label:'既往治疗',type:'checkboxes',opts:['换鞋/鞋垫','NSAIDs','局部封闭','物理治疗','无']},
        {id:'injection_response',label:'封闭治疗反应',type:'select',opts:['未做','明显缓解','短期缓解','无效']}
      ]
    },
    {
      id: 'physical', icon: '🔍', title: '体格检查',
      fields: [
        {id:'mulder_click',label:'Mulder 征',type:'select',opts:['阴性','阳性','可疑'],req:true},
        {id:'webspace_tenderness',label:'跖间隙压痛',type:'select',opts:['无','轻度','明显'],req:true},
        {id:'splay_test',label:'跖骨挤压诱发痛',type:'select',opts:['阴性','阳性']},
        {id:'sensory_change',label:'趾侧感觉改变',type:'select',opts:['无','麻木','感觉减退','感觉过敏']},
        {id:'metatarsalgia',label:'合并跖痛症',type:'select',opts:['无','轻度','明显']},
        {id:'callus',label:'跖底胼胝',type:'select',opts:['无','有']},
        {id:'physical_notes',label:'查体备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'imaging', icon: '📷', title: '影像/超声数据',
      fields: [
        {id:'ultrasound_done',label:'超声检查',type:'select',opts:['已完成','未做','已预约'],req:true},
        {id:'neuroma_size_mm',label:'神经瘤最大径',type:'number',unit:'mm'},
        {id:'mri_done',label:'MRI 检查',type:'select',opts:['未做','已完成','已预约']},
        {id:'bursitis',label:'跖间滑囊炎',type:'select',opts:['无','有','可疑']},
        {id:'mtp_instability',label:'MTP 不稳/跖板损伤',type:'select',opts:['无','可疑','明确']},
        {id:'stress_fracture_excluded',label:'应力骨折排除',type:'select',opts:['已排除','未排除','不适用']},
        {id:'imaging_notes',label:'影像/超声备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'scores', icon: '📊', title: '功能评分量表',
      fields: [
        {id:'aofas_lesser_mtp',label:'AOFAS Lesser MTP-IP',type:'number',unit:'分/100'},
        {id:'faam_adl',label:'FAAM ADL',type:'number',unit:'%'},
        {id:'pain_vas',label:'疼痛 VAS',type:'number',unit:'分'}
      ]
    },
    {
      id: 'plan', icon: '⚕️', title: '治疗/手术方案',
      fields: [
        {id:'treatment_strategy',label:'治疗策略',type:'select',opts:['保守治疗','超声引导封闭','射频/消融','神经松解','神经瘤切除'],req:true},
        {id:'approach',label:'手术入路',type:'select',opts:['背侧入路','跖侧入路','微创入路','不适用']},
        {id:'target_space',label:'处理跖间隙',type:'select',opts:['第2跖间隙','第3跖间隙','第4跖间隙','多发']},
        {id:'nerve_handling',label:'神经处理',type:'select',opts:['保留/松解','近端切除埋入肌间','单纯切除','不适用']},
        {id:'metatarsal_offloading',label:'跖骨减压/鞋垫',type:'select',opts:['无需','跖骨垫','定制鞋垫','合并跖骨截骨']},
        {id:'anesthesia',label:'麻醉方式',type:'select',opts:['局部阻滞','神经阻滞','腰麻','全麻']},
        {id:'surgeon',label:'主刀医师',type:'text'},
        {id:'plan_notes',label:'治疗计划备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'media', icon: '🖼️', title: '影像资料上传',
      type: 'media',
      slots: getMortonNeuromaMediaSlots()
    }
  ];
}

function getMortonNeuromaMediaSlots() {
  return [
    // 术前资料
    { id: 'preop_xray_wb_ap', phase: '术前资料', group: '术前X线', label: '术前X线（负重正位）', accept: 'image/*' },
    { id: 'preop_xray_wb_lat', phase: '术前资料', group: '术前X线', label: '术前X线（负重侧位）', accept: 'image/*' },
    { id: 'preop_xray_oblique', phase: '术前资料', group: '术前X线', label: '术前X线（斜位/前足对线）', accept: 'image/*' },
    { id: 'preop_ultrasound_static', phase: '术前资料', group: '术前超声', label: '术前超声（跖间隙静态长短轴）', accept: 'image/*,video/*' },
    { id: 'preop_ultrasound_dynamic', phase: '术前资料', group: '术前超声', label: '术前动态超声（Mulder/跖骨挤压）', accept: 'image/*,video/*' },
    { id: 'preop_ultrasound_guided', phase: '术前资料', group: '术前超声', label: '超声引导封闭/定位记录', accept: 'image/*,video/*' },
    { id: 'preop_mri_coronal', phase: '术前资料', group: '术前MRI', label: '术前MRI（冠状位/跖间隙）', accept: 'image/*' },
    { id: 'preop_mri_axial', phase: '术前资料', group: '术前MRI', label: '术前MRI（轴位/鉴别诊断）', accept: 'image/*' },
    { id: 'preop_gross', phase: '术前资料', group: '术前大体相', label: '术前大体相（局部/足趾受力）', accept: 'image/*' },
    { id: 'preop_dynamic_video', phase: '术前资料', group: '动态视频', label: '术前诱发动作视频（挤压/步行疼痛）', accept: 'video/*,image/*' },
    { id: 'preop_gait_video', phase: '术前资料', group: '步态视频', label: '术前步态视频（正侧后）', accept: 'video/*,image/*' },

    // 术中/治疗资料
    { id: 'intraop_ultrasound', phase: '术中资料', group: '术中/治疗影像', label: '术中或治疗中超声定位', accept: 'image/*,video/*' },
    { id: 'intraop_gross', phase: '术中资料', group: '术中即时大体相', label: '术中即时大体相（术野关键节点）', accept: 'image/*' },
    { id: 'intraop_video', phase: '术中资料', group: '动态视频', label: '术中/治疗过程视频（关键步骤）', accept: 'video/*,image/*' },

    // 术后/随访资料
    { id: 'postop_ultrasound_followup', phase: '术后资料', group: '术后资料', label: '术后/治疗后复查超声', accept: 'image/*,video/*' },
    { id: 'postop_gross', phase: '术后资料', group: '术后资料', label: '术后大体相（切口/局部外观）', accept: 'image/*' },
    { id: 'postop_function_video', phase: '术后资料', group: '术后资料', label: '术后功能视频（步行/足趾活动）', accept: 'video/*,image/*' },
    { id: 'postop_followup', phase: '术后资料', group: '术后资料', label: '术后随访资料（6周/3月/6月/12月）', accept: 'image/*,video/*' }
  ];
}

// ============================================================
// O: 腓骨肌腱滑脱 (Peroneal Tendon Subluxation / Dislocation) — 运动医学
// 字段草案（参照运动医学类风格，全部可空，待临床调整）
// ============================================================
function getSectionsO() {
  return [
    {
      id: 'basic', icon: '👤', title: '基本信息',
      fields: [
        {id:'name',label:'姓名',type:'text',req:true},
        {id:'age',label:'年龄',type:'number',unit:'岁',req:true},
        {id:'gender',label:'性别',type:'select',opts:['男','女']},
        {id:'side',label:'患侧',type:'select',opts:['左踝','右踝','双踝'],req:true},
        {id:'caseid',label:'住院号',type:'text'},
        {id:'date',label:'就诊日期',type:'date',req:true},
        {id:'sports_level',label:'运动水平',type:'select',opts:['久坐','休闲','业余竞技','职业运动员']},
        {id:'injury_mechanism',label:'诱发机制',type:'select',opts:['踝背伸+外翻扭伤','内翻扭伤','直接撞击','滑雪/球类急停变向','反复劳损','不详'],req:true},
        {id:'injury_phase',label:'病程阶段',type:'select',opts:['急性（≤4 周）','亚急性（4-12 周）','慢性/复发（>3 个月）'],req:true},
        {id:'chief_complaint',label:'主诉',type:'textarea',full:true,req:true},
        {id:'duration',label:'病程',type:'text',full:true}
      ]
    },
    {
      id: 'history', icon: '📋', title: '病史采集',
      fields: [
        {id:'pain_vas',label:'疼痛 VAS',type:'number',unit:'分'},
        {id:'snapping_sensation',label:'弹响/滑脱感',type:'select',opts:['无','偶发','活动时频繁','日常即出现']},
        {id:'subluxation_frequency',label:'滑脱发作频率',type:'select',opts:['首次','偶发','频繁','可主动诱发']},
        {id:'retromalleolar_swelling',label:'外踝后肿胀史',type:'select',opts:['无','间歇','持续']},
        {id:'prior_ankle_sprain',label:'既往踝扭伤史',type:'select',opts:['无','1 次','多次']},
        {id:'conservative_tx',label:'保守治疗史',type:'checkboxes',opts:['制动/支具','石膏固定','物理治疗','活动调整','口服止痛药','无']},
        {id:'conservative_duration',label:'保守治疗时长',type:'number',unit:'月'},
        {id:'history_notes',label:'病史备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'physical', icon: '🔍', title: '体格检查',
      fields: [
        {id:'subluxation_provocation',label:'腓骨肌腱滑脱诱发试验（抗阻背伸外翻）',type:'select',opts:['阴性','可疑（痛无滑脱）','阳性（可触及滑脱）','阳性（可见脱位）'],req:true},
        {id:'tendon_resting_position',label:'静息腓骨肌腱位置',type:'select',opts:['沟内（正常）','半脱位','完全脱位（外踝前方）']},
        {id:'retromalleolar_tenderness',label:'外踝后压痛',type:'select',opts:['无','轻度','明显'],req:true},
        {id:'swelling',label:'外踝后肿胀',type:'select',opts:['无','轻度','明显']},
        {id:'eversion_strength',label:'外翻肌力（腓骨肌）',type:'select',opts:['5级','4级','3级','2级以下']},
        {id:'assoc_ankle_instability',label:'伴踝关节不稳（前抽屉/距骨倾斜）',type:'select',opts:['无','可疑','明显']},
        {id:'hindfoot_alignment',label:'后足力线',type:'select',opts:['中立','内翻','外翻']},
        {id:'physical_notes',label:'查体备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'imaging', icon: '📷', title: '影像数据',
      fields: [
        {id:'xray_views',label:'X 线摄片',type:'checkboxes',opts:['踝正位','踝侧位','踝穴位','跟骨轴位']},
        {id:'fleck_sign',label:'撕脱骨片征（fleck sign，外踝后缘）',type:'select',opts:['无','可疑','明确']},
        {id:'us_done',label:'超声（动态）',type:'select',opts:['已完成','未做','已预约']},
        {id:'us_dynamic_subluxation',label:'超声动态滑脱',type:'select',opts:['未见','活动诱发半脱位','活动诱发完全脱位','未做']},
        {id:'mri_done',label:'MRI 检查',type:'select',opts:['已完成','未做','已预约'],req:true},
        {id:'spr_injury',label:'腓骨肌上支持带 (SPR) 损伤',type:'select',opts:['完整','撕脱/松弛','SPR 下假囊形成','完全撕裂']},
        {id:'peroneal_groove',label:'腓骨沟形态',type:'select',opts:['正常凹陷','浅凹','平坦','凸起（发育不良）']},
        {id:'tendon_tear',label:'腓骨肌腱损伤（纵裂/撕裂）',type:'select',opts:['无','腓骨长肌部分撕裂','腓骨短肌部分撕裂（含纵裂）','两者均累及']},
        {id:'low_lying_muscle',label:'腓骨短肌低位肌腹/副肌',type:'select',opts:['无','低位肌腹','第四腓骨肌']},
        {id:'imaging_notes',label:'影像学备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'diagnosis', icon: '🏷️', title: '分型与诊断',
      fields: [
        // 主分型:正常显示、不折叠
        {id:'eckert_davis_grade',label:'【主分型】SPR 损伤 Eckert-Davis 分型',type:'select',opts:['I 度（SPR 自骨膜剥离）','II 度（纤维软骨嵴掀起）','III 度（带骨皮质撕脱片）','IV 度（SPR 自后方撕脱）']},
        // 辅助分型:可选、非必填,不喧宾夺主
        {id:'raikin_intrasheath',label:'Raikin 鞘内（intrasheath）分型（辅助/可选）',type:'select',opts:['不适用','A 型（无腓骨肌腱撕裂）','B 型（伴腓骨肌腱撕裂）']},
        {id:'subluxation_course',label:'病程分型（辅助/可选）',type:'select',opts:['急性脱位','复发性半脱位','复发性脱位']},
        {id:'associated_injury',label:'合并伤/合并诊断（可多选）',type:'checkboxes',opts:['外侧副韧带损伤（ATFL/CFL）','距骨骨软骨损伤（OLT）','跟腓后方/腓骨肌腱沟撞击','腓骨肌腱纵裂（longitudinal split）','低位腓骨肌腹/腓骨第四肌（peroneus quartus）','慢性踝关节不稳']},
        {id:'associated_injury_other',label:'合并伤-其他（自由填写）',type:'text',full:true},
        {id:'diagnosis_summary',label:'诊断小结',type:'textarea',full:true}
      ]
    },
    {
      id: 'plan', icon: '⚕️', title: '处理方案',
      fields: [
        {id:'treatment_strategy',label:'治疗策略',type:'select',opts:['保守治疗','手术'],req:true},
        {id:'conservative_plan',label:'保守方案',type:'checkboxes',opts:['石膏/支具固定 6 周','活动限制','物理治疗','无']},
        // 术式两层:常用术式突出 + 其他术式可选
        {id:'procedure_primary',label:'【常用术式】',type:'select',opts:['骨膜瓣带线锚钉闭合术（改良 Das-De 技术）','InternalBrace 支持带重建（SPR 重建）','其他（见下）']},
        {id:'procedure_other',label:'其他术式（可选）',type:'select',opts:['SPR 直接修复/紧缩','腓骨沟骨性加深术','腓骨远端滑移截骨','腓骨肌腱纵裂修复','腱沟重建（骨阻挡/腱固定）','骨膜瓣加强']},
        {id:'groove_deepening',label:'腓骨沟加深方式',type:'select',opts:['不适用','磨削加深','骨瓣翻转加深（trapdoor）','髓内减压加深']},
        {id:'tendon_repair',label:'腓骨肌腱处理',type:'select',opts:['无','清创','纵裂修复','腱固定/腱转位']},
        {id:'anesthesia',label:'麻醉方式',type:'select',opts:['腰硬联合','全麻','腰麻','局部阻滞']},
        {id:'surgeon',label:'主刀医师',type:'text'},
        {id:'plan_notes',label:'手术计划备注',type:'textarea',full:true}
      ]
    },
    {
      id: 'media', icon: '🖼️', title: '影像资料上传',
      type: 'media',
      slots: getStructuredMediaSlots({mri:true})
    }
  ];
}

function getStructuredMediaSlots(opts) {
  // opts.mri=true：将"术前CT"板块改标为"术前CT/MRI"，供以核磁评估为主的病种
  // （跟腱病、踝关节韧带损伤）使用。仅改 group/label 文案，slot id 不变，
  // 故既往已上传影像不会失联，可安全回滚。
  const ctGroup = (opts && opts.mri) ? '术前CT/MRI' : '术前CT';
  const ctL = (opts && opts.mri) ? '术前CT/MRI' : '术前CT';
  return [
    // 术前资料
    { id: 'preop_xray_wb_ap', phase: '术前资料', group: '术前X线', label: '术前X线（负重正位）', accept: 'image/*' },
    { id: 'preop_xray_wb_lat', phase: '术前资料', group: '术前X线', label: '术前X线（负重侧位）', accept: 'image/*' },
    { id: 'preop_xray_oblique', phase: '术前资料', group: '术前X线', label: '术前X线（斜位/踝穴位）', accept: 'image/*' },
    { id: 'preop_ct_coronal', phase: '术前资料', group: ctGroup, label: `${ctL}（冠状位）`, accept: 'image/*' },
    { id: 'preop_ct_sagittal', phase: '术前资料', group: ctGroup, label: `${ctL}（矢状位）`, accept: 'image/*' },
    { id: 'preop_ct_axial', phase: '术前资料', group: ctGroup, label: `${ctL}（轴位/三维）`, accept: 'image/*' },
    { id: 'preop_gross', phase: '术前资料', group: '术前大体相', label: '术前大体相（站立位/局部）', accept: 'image/*' },
    { id: 'preop_dynamic_video', phase: '术前资料', group: '动态视频', label: '术前动态视频（关节活动）', accept: 'video/*,image/*' },
    { id: 'preop_gait_video', phase: '术前资料', group: '步态视频', label: '术前步态视频（正侧后）', accept: 'video/*,image/*' },

    // 术中资料
    { id: 'intraop_imaging', phase: '术中资料', group: '术中影像', label: '术中影像（C臂/透视关键帧）', accept: 'image/*' },
    { id: 'intraop_gross', phase: '术中资料', group: '术中即时大体相', label: '术中即时大体相（术野关键节点）', accept: 'image/*' },
    { id: 'intraop_video', phase: '术中资料', group: '动态视频', label: '术中动态视频（关键步骤）', accept: 'video/*,image/*' },

    // 术后资料
    { id: 'postop_xray_ct', phase: '术后资料', group: '术后资料', label: '术后即刻影像（X线/CT）', accept: 'image/*' },
    { id: 'postop_gross', phase: '术后资料', group: '术后资料', label: '术后大体相（切口/外观）', accept: 'image/*' },
    { id: 'postop_function_video', phase: '术后资料', group: '术后资料', label: '术后功能视频（动态/步态）', accept: 'video/*,image/*' },
    { id: 'postop_followup', phase: '术后资料', group: '术后资料', label: '术后随访资料（6周/3月/6月/12月）', accept: 'image/*,video/*' }
  ];
}

function getSlotMeta(slot, idx) {
  if (typeof slot === 'string') {
    return {
      key: `media_media_${idx}`,
      legacyKey: `media_media_${idx}`,
      label: slot,
      phase: '影像资料',
      group: '未分类',
      accept: 'image/*'
    };
  }
  return {
    key: `media_media_${slot.id || idx}`,
    legacyKey: `media_media_${idx}`,
    label: slot.label || `资料${idx + 1}`,
    phase: slot.phase || '影像资料',
    group: slot.group || '未分类',
    accept: slot.accept || 'image/*'
  };
}



/* assets/ui-utils.js */
function escapeHtml(str) {
  return String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
}

// ===================== Header more-menu =====================
function toggleMoreMenu(e) {
  if (e) e.stopPropagation();
  const popup = document.getElementById('moreMenuPopup');
  if (!popup) return;
  if (popup.hidden) openMoreMenu();
  else closeMoreMenu();
}
function openMoreMenu() {
  const popup = document.getElementById('moreMenuPopup');
  const trigger = document.querySelector('.more-menu-trigger');
  if (!popup) return;
  popup.hidden = false;
  if (trigger) trigger.setAttribute('aria-expanded', 'true');
  document.addEventListener('click', handleMoreMenuOutside);
  document.addEventListener('keydown', handleMoreMenuEsc);
}
function closeMoreMenu() {
  const popup = document.getElementById('moreMenuPopup');
  const trigger = document.querySelector('.more-menu-trigger');
  if (!popup) return;
  popup.hidden = true;
  if (trigger) trigger.setAttribute('aria-expanded', 'false');
  document.removeEventListener('click', handleMoreMenuOutside);
  document.removeEventListener('keydown', handleMoreMenuEsc);
}
function handleMoreMenuOutside(e) {
  const root = document.getElementById('moreMenuRoot');
  if (root && !root.contains(e.target)) closeMoreMenu();
}
function handleMoreMenuEsc(e) {
  if (e.key === 'Escape') closeMoreMenu();
}

// ===================== Mobile sidebar drawer =====================
function toggleMobileSidebar() {
  const sidebar = document.getElementById('sidebar');
  const backdrop = document.getElementById('sidebarBackdrop');
  if (!sidebar) return;
  const willOpen = !sidebar.classList.contains('open');
  sidebar.classList.toggle('open', willOpen);
  if (backdrop) backdrop.classList.toggle('open', willOpen);
}
function closeMobileSidebar() {
  document.getElementById('sidebar')?.classList.remove('open');
  document.getElementById('sidebarBackdrop')?.classList.remove('open');
}

// Auto-close mobile sidebar after picking a case
document.addEventListener('click', (e) => {
  if (window.innerWidth > 900) return;
  const item = e.target.closest('#sidebar .case-item');
  if (item) closeMobileSidebar();
});

// ===================== Settings tabs =====================
function switchSettingsTab(tab) {
  document.querySelectorAll('.settings-tab-btn').forEach(b => {
    b.classList.toggle('active', b.dataset.tab === tab);
  });
  document.querySelectorAll('.settings-tab-panel').forEach(p => {
    p.classList.toggle('active', p.dataset.tab === tab);
  });
}

// ===================== 应用内确认/输入弹层（替代原生 confirm/prompt） =====================
// Promise 风格：await showAppConfirm({...}) → boolean；await showAppPrompt({...}) → string|null。
// 动态创建、addEventListener 绑定（CSP 兼容，无内联 on*）；Escape=取消，Enter=确认。
// 原生 confirm/prompt 在移动端样式突兀且阻塞主线程，统一替换为本组件。

function _appDialog({ title, message, danger = false, confirmText = '确定', cancelText = '取消', input = null }) {
  return new Promise(resolve => {
    document.getElementById('appDialogOverlay')?.remove();
    const ov = document.createElement('div');
    ov.id = 'appDialogOverlay';
    ov.className = 'app-dialog-overlay';
    const inputHtml = input ? `
      <input class="field-input app-dialog-input" id="appDialogInput" type="text"
        placeholder="${escapeHtml(input.placeholder || '')}" value="${escapeHtml(input.defaultValue || '')}"
        ${input.maxLength ? `maxlength="${input.maxLength}"` : ''} autocomplete="off">` : '';
    ov.innerHTML = `
      <div class="app-dialog" role="${input ? 'dialog' : 'alertdialog'}" aria-modal="true">
        ${title ? `<div class="app-dialog-title">${escapeHtml(title)}</div>` : ''}
        ${message ? `<div class="app-dialog-message">${escapeHtml(message).replace(/\n/g, '<br>')}</div>` : ''}
        ${inputHtml}
        <div class="app-dialog-actions">
          <button class="btn btn-ghost" id="appDialogCancel">${escapeHtml(cancelText)}</button>
          <button class="btn ${danger ? 'btn-danger' : 'btn-primary'}" id="appDialogOk">${escapeHtml(confirmText)}</button>
        </div>
      </div>`;
    const done = (result) => {
      document.removeEventListener('keydown', onKey, true);
      ov.remove();
      resolve(result);
    };
    const ok = () => done(input ? (document.getElementById('appDialogInput')?.value ?? '') : true);
    const cancel = () => done(input ? null : false);
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); cancel(); }
      else if (e.key === 'Enter' && (!input || e.target.id === 'appDialogInput')) { e.stopPropagation(); ok(); }
    };
    ov.querySelector('#appDialogOk').addEventListener('click', ok);
    ov.querySelector('#appDialogCancel').addEventListener('click', cancel);
    ov.addEventListener('click', (e) => { if (e.target === ov) cancel(); });
    document.addEventListener('keydown', onKey, true);
    document.body.appendChild(ov);
    const inp = document.getElementById('appDialogInput');
    if (inp) { inp.focus(); inp.select(); }
    else ov.querySelector('#appDialogOk').focus();
  });
}

/** 确认弹层 → Promise<boolean>。danger=true 时确认键红色（删除类操作）。 */
function showAppConfirm(opts) {
  if (typeof opts === 'string') opts = { message: opts };
  return _appDialog(opts);
}

/** 输入弹层 → Promise<string|null>（取消返回 null）。 */
function showAppPrompt(opts) {
  if (typeof opts === 'string') opts = { message: opts };
  return _appDialog({ ...opts, input: { placeholder: opts.placeholder, defaultValue: opts.defaultValue, maxLength: opts.maxLength } });
}



/* assets/toast.js */
// ===================== TOAST =====================
// duration: 毫秒；传 0（或负数）则不自动消失，由调用方手动 .remove()。
// 返回 toast 元素，方便持久提示场景手动关闭。
function showToast(icon, msg, duration = 3000) {
  let stack = document.querySelector('.toast-stack');
  if (!stack) {
    stack = document.createElement('div');
    stack.className = 'toast-stack';
    document.body.appendChild(stack);
  }
  const toast = document.createElement('div');
  toast.className = 'toast';
  const iconEl = document.createElement('span');
  iconEl.className = 'toast-icon';
  iconEl.textContent = icon || '';
  const msgEl = document.createElement('span');
  msgEl.textContent = msg || '';
  toast.appendChild(iconEl);
  toast.appendChild(msgEl);
  stack.appendChild(toast);
  const trim = () => {
    while (stack.children.length > 4) stack.firstElementChild?.remove();
  };
  trim();
  if (duration > 0) {
    setTimeout(() => {
      toast.remove();
      if (!stack.children.length) stack.remove();
    }, duration);
  }
  return toast;
}



/* assets/render.js */
// ===================== UI RENDERING =====================
// 影像证据链「充足」阈值（百分比）。所有判定共用此常量，避免 35/40 各处漂移导致信号矛盾。
const MEDIA_SUFFICIENT_PCT = 40;

const CASE_PHASES = [
  { id: 'preop', label: '术前资料', hint: '病史、查体、测量、评分、手术方案' },
  { id: 'intraop', label: '术中资料', hint: '术中影像、关键步骤、即时大体相' },
  { id: 'postop', label: '术后资料', hint: '术后影像、功能视频、切口外观' },
  { id: 'followup', label: '随访追踪', hint: '复查时间点、疗效评分、并发症记录' },
];

// 顶部引导横幅：根据“模式 + 登录态”给普通用户一句明确的下一步，
// 消除“到底该登录 / 填后端 / 填 Key / 本地用”的困惑。
const _ONBOARD_DISMISS_KEY = 'foot_ankle_onboard_dismissed_v1';
function dismissOnboarding() {
  try { sessionStorage.setItem(_ONBOARD_DISMISS_KEY, '1'); } catch (_) {}
  renderOnboardingBanner();
}
function renderOnboardingBanner() {
  const el = document.getElementById('onboardingBanner');
  if (!el) return;
  const cloud = isCloudMode();
  const logged = isLoggedIn();
  // 云端 + 未登录：唯一要做的事就是登录
  if (cloud && !logged) {
    el.hidden = false;
    el.className = 'onboarding-banner onboarding-banner--login';
    el.innerHTML = `
      <span class="onboarding-icon" aria-hidden="true">👋</span>
      <span class="onboarding-text">欢迎使用足踝外科病例系统。请先<strong>登录</strong>以查看和管理病例（无需填写后端地址或 API Key）。</span>
      <button class="btn btn-primary onboarding-cta" type="button" data-action="show-auth-modal">🔐 登录</button>`;
    return;
  }
  // 本地演示模式：提示数据仅存本机，可一次性关闭
  let dismissed = false;
  try { dismissed = sessionStorage.getItem(_ONBOARD_DISMISS_KEY) === '1'; } catch (_) {}
  if (!cloud && !dismissed) {
    el.hidden = false;
    el.className = 'onboarding-banner onboarding-banner--local';
    el.innerHTML = `
      <span class="onboarding-icon" aria-hidden="true">🖥️</span>
      <span class="onboarding-text">本地演示模式：病例仅保存在本机浏览器，换设备/清缓存不会保留。正式使用请由管理员配置云端后端。</span>
      <button class="onboarding-dismiss" type="button" aria-label="知道了" data-action="dismiss-onboarding">✕</button>`;
    return;
  }
  el.hidden = true;
  el.innerHTML = '';
}

// 顶部“显示/隐藏患者信息”按钮的文案随状态切换。
function renderPiiToggle() {
  const btn = document.getElementById('piiToggleBtn');
  if (!btn) return;
  const revealed = (typeof isPiiRevealed === 'function') && isPiiRevealed();
  btn.textContent = revealed ? '🙈 隐藏患者信息' : '👁 显示患者信息';
  btn.setAttribute('aria-pressed', revealed ? 'true' : 'false');
  btn.title = revealed ? '当前显示明文姓名（仅本设备本会话），点击恢复脱敏' : '当前默认脱敏，点击显示患者姓名';
}

function renderSidebar() {
  const cases = getVisibleCases();
  const list = document.getElementById('caseList');
  list.innerHTML = '';
  const visibleCases = cases.filter(c => caseMatchesDashboardFilters(c, currentModuleFilter || 'all'));

  if (visibleCases.length === 0) {
    list.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">🗂</div>
        <div class="empty-state-title">暂无病例</div>
        <div class="empty-state-hint">还没有匹配当前筛选的病例。新建一份病例，开始结构化录入。</div>
        <div class="empty-state-action">
          <button class="btn btn-primary" data-action="show-new-case">＋ 新建病例</button>
        </div>
      </div>`;
    return;
  }

  visibleCases.forEach(c => {
    const mod = MODULES[c.module];
    const pct = calcOverallProgress(c);
    const item = document.createElement('div');
    item.className = 'case-item' + (c.id === currentCaseId ? ' active' : '');
    item.onclick = () => openCase(c.id);
    item.innerHTML = `
      <div class="case-item-name">${c.favorite ? '★ ' : ''}${escapeHtml(displayPatientName(c))}</div>
      <div class="case-item-meta">
        <span class="case-tag tag-${c.module}">${mod?.name?.split(' ')[0] || c.module}</span>
        ${renderCaseSignalTags(c, { compact: true })}
        ${getCaseTags(c).slice(0, 2).map(tag => `<span class="case-tag case-tag-muted">${escapeHtml(tag)}</span>`).join('')}
        <span class="case-progress-chip${pct === 100 ? ' complete' : ''}">${pct}%</span>
      </div>
    `;
    list.appendChild(item);
  });
}

// #13 — inject skeleton placeholder rows into a container during async loads.
// The subsequent real render (renderSidebar / hydrate) replaces them.
function renderSkeletonList(el, rows = 4) {
  if (!el) return;
  let html = '';
  for (let i = 0; i < rows; i++) {
    html += '<div class="skeleton skeleton-card"></div>';
  }
  el.innerHTML = html;
}

// #13 — inline required-field validation. Marks empty core fields on a submit
// attempt; cleared per-field on input (autoSaveField → clearFieldError).
function clearAllFieldErrors() {
  document.querySelectorAll('.field-input.is-invalid').forEach(el => el.classList.remove('is-invalid'));
  document.querySelectorAll('.field-error[data-for]').forEach(el => el.remove());
}

function clearFieldError(key) {
  const input = document.getElementById('f_' + key);
  if (input) input.classList.remove('is-invalid');
  input?.closest('.field-group')?.classList.remove('is-current-missing');
  const err = document.querySelector(`.field-error[data-for="${key}"]`);
  if (err) err.remove();
}

// Highlights the given missing core fields inline; returns how many were shown.
function markRequiredFieldErrors(missing = []) {
  clearAllFieldErrors();
  let firstEl = null;
  missing.forEach(item => {
    if (!item.key) return;
    const input = document.getElementById('f_' + item.key);
    if (!input) return;
    input.classList.add('is-invalid');
    if (!input.parentElement.querySelector(`.field-error[data-for="${item.key}"]`)) {
      const err = document.createElement('div');
      err.className = 'field-error';
      err.setAttribute('data-for', item.key);
      err.textContent = '此项为核心必填，请补充后再提交审核';
      input.insertAdjacentElement('afterend', err);
    }
    if (!firstEl) firstEl = input;
  });
  if (firstEl) firstEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
  return missing.filter(m => m.key && document.getElementById('f_' + m.key)).length;
}

function markActiveRequiredField(key) {
  document.querySelectorAll('.field-group.is-current-missing').forEach(el => el.classList.remove('is-current-missing'));
  const input = document.getElementById('f_' + key);
  input?.closest('.field-group')?.classList.add('is-current-missing');
}

function getCaseTags(c) {
  return Array.isArray(c.tags) ? c.tags : [];
}

function escapeJsArg(value) {
  return String(value).replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

function hydrateProgressWidths(root = document) {
  root.querySelectorAll('[data-progress-width]').forEach(el => {
    const raw = Number(el.dataset.progressWidth || 0);
    const pct = Math.max(0, Math.min(100, Number.isFinite(raw) ? raw : 0));
    el.style.width = `${pct}%`;
  });
}

function caseMatchesFolder(c, folder) {
  if (folder === 'all') return true;
  if (folder === 'starred') return !!c.favorite;
  const mod = MODULES[c.module];
  const pct = calcProgress(c);
  const mediaPct = calcMediaProgress(c, mod);
  const stage = getCaseStageSummary(c);
  if (folder === 'pending') return pct < 100 || mediaPct.pct < MEDIA_SUFFICIENT_PCT || stage.status === '资料待补';
  if (folder === 'review') return ['待审核', '审核中', '驳回修改'].includes(stage.review);
  if (folder === 'followup') return stage.stage === '随访' || getCaseTags(c).includes('随访病例');
  if (folder === 'archived') return stage.status === '已完成' || stage.review === '审核通过';
  return true;
}

const CASE_FACET_GROUPS = [
  // 注:病种筛选由下方 #filterBar 的具体病种块负责（含每病种计数），此处不再重复「病种大类」facet。
  {
    key: 'owner',
    label: '负责人',
    options: [
      { id: 'all', label: '全部' },
      { id: 'mine', label: '我负责' },
      { id: 'assigned', label: '已指派' },
      { id: 'unassigned', label: '未指派' },
    ],
  },
  {
    key: 'review',
    label: '审核',
    options: [
      { id: 'all', label: '全部' },
      { id: 'draft', label: '未提交' },
      { id: 'submitted', label: '待审核' },
      { id: 'reviewing', label: '审核中' },
      { id: 'approved', label: '已通过' },
      { id: 'rejected', label: '被驳回' },
    ],
  },
  {
    key: 'admission',
    label: '来源',
    options: [
      { id: 'all', label: '全部' },
      { id: 'admission', label: '住院条' },
      { id: 'manual', label: '手工建档' },
    ],
  },
  {
    key: 'followup',
    label: '随访',
    options: [
      { id: 'all', label: '全部' },
      { id: 'needed', label: '需随访' },
      { id: 'complete', label: '已完成' },
      { id: 'none', label: '未进入' },
    ],
  },
];

function normalizeReviewState(c) {
  const stage = getCaseStageSummary(c);
  const wf = c.workflow_state || '';
  if (wf === 'approved' || stage.review === '审核通过') return 'approved';
  if (wf === 'rejected' || stage.review === '驳回修改') return 'rejected';
  if (wf === 'reviewing' || stage.review === '审核中') return 'reviewing';
  if (wf === 'submitted' || stage.review === '待审核') return 'submitted';
  return 'draft';
}

function caseHasAssignedOwner(c) {
  return [
    c.assigned_to,
    c.assignedTo,
    c.assignee_id,
    c.assigneeId,
    c.created_by,
    c.createdByUserId,
    c.createdByUsername,
    c.ownerUserId,
    c.ownerUsername,
    c.data?.assignee,
  ].some(v => v !== undefined && v !== null && String(v).trim() !== '');
}

function caseOwnerIsMine(c) {
  return [
    c.assigned_to,
    c.assignedTo,
    c.assignee_id,
    c.assigneeId,
    c.created_by,
    c.createdByUserId,
    c.createdByUsername,
    c.ownerUserId,
    c.ownerUsername,
    c.data?.assignee,
    c.data?.supervisor,
  ].some(v => _isMine(v));
}

function getCaseOwnerLabel(c) {
  const user = (typeof getAuthUser === 'function') ? getAuthUser() : null;
  if (caseOwnerIsMine(c)) return '我负责';
  const raw = c.assigned_to_name || c.assignedByUsername || c.assignee_name || c.data?.assignee || c.data?.supervisor || c.created_by_username || c.createdByUsername || c.ownerUsername || '';
  if (raw) return String(raw).slice(0, 12);
  if (caseHasAssignedOwner(c)) return '已指派';
  if (user && _isMine(c.created_by)) return '我发起';
  return '未指派';
}

function caseHasAdmissionSource(c) {
  const tags = getCaseTags(c);
  return Boolean(
    c.admissionTaskId ||
    c.admission_task_id ||
    c.data?.source_admission_task_id ||
    c.data?.admission_task_id ||
    tags.includes('住院条分发') ||
    tags.includes('待录入')
  );
}

function normalizeFollowupState(c) {
  const stage = getCaseStageSummary(c);
  if (stage.status === '已完成' || stage.review === '审核通过') return 'complete';
  if (caseMatchesFolder(c, 'followup')) return 'needed';
  return 'none';
}

function caseMatchesModuleFilter(c, filter = currentModuleFilter || 'all') {
  return !filter || filter === 'all' || c.module === filter;
}

function normalizeCaseSearchText(value) {
  return String(value || '').toLowerCase().replace(/\s+/g, '');
}

function moduleChipLabel(mod, code = '') {
  const raw = String(mod?.name || code || '');
  return raw.split(/\s|\(/)[0].replace(/（.*$/, '') || code;
}

function caseSearchHaystack(c) {
  const mod = MODULES[c.module] || {};
  const data = c.data || {};
  const values = [
    displayPatientName(c),
    c.patient_name,
    c.patientName,
    data.basic_name,
    data.name,
    data.patient_name,
    data.basic_caseid,
    data.caseid,
    data.case_id,
    data.medical_record_no,
    data.hospital_no,
    data.admission_no,
    c.caseNo,
    c.case_no,
    c.backendId,
    c.id,
    c.module,
    mod.name,
    moduleChipLabel(mod, c.module),
    mod.category,
    ...(Array.isArray(c.tags) ? c.tags : []),
  ];
  return normalizeCaseSearchText(values.filter(v => v !== undefined && v !== null).join(' '));
}

function caseMatchesDashboardSearch(c) {
  const query = normalizeCaseSearchText(currentDashboardSearch || '');
  if (!query) return true;
  return caseSearchHaystack(c).includes(query);
}

function caseMatchesFacetSelection(c, filters = currentFacetFilters || {}) {
  const owner = filters.owner || 'all';
  const review = filters.review || 'all';
  const admission = filters.admission || 'all';
  const followup = filters.followup || 'all';

  if (owner === 'mine' && !caseOwnerIsMine(c)) return false;
  if (owner === 'assigned' && !caseHasAssignedOwner(c)) return false;
  if (owner === 'unassigned' && caseHasAssignedOwner(c)) return false;

  if (review !== 'all' && normalizeReviewState(c) !== review) return false;

  const fromAdmission = caseHasAdmissionSource(c);
  if (admission === 'admission' && !fromAdmission) return false;
  if (admission === 'manual' && fromAdmission) return false;

  if (followup !== 'all' && normalizeFollowupState(c) !== followup) return false;
  return true;
}

function caseMatchesFacetFilters(c) {
  return caseMatchesFacetSelection(c, currentFacetFilters || {});
}

function caseMatchesDashboardFilters(c, filter = currentModuleFilter || 'all') {
  return caseMatchesModuleFilter(c, filter)
    && caseMatchesDashboardSearch(c)
    && caseMatchesWorkbenchQueue(c)
    && caseMatchesScope(c, currentWorkScope)
    && caseMatchesCenter(c)
    && caseMatchesFolder(c, currentFolderFilter)
    && caseMatchesFacetFilters(c);
}

function setDashboardSearch(value) {
  currentDashboardSearch = String(value || '');
  renderSidebar();
  renderDashboard(currentModuleFilter || 'all');
}

function setCaseFacetFilter(kind, value) {
  if (!currentFacetFilters || typeof currentFacetFilters !== 'object') {
    currentFacetFilters = { owner: 'all', review: 'all', admission: 'all', followup: 'all', category: 'all' };
  }
  currentFacetFilters[kind] = value || 'all';
  renderSidebar();
  renderDashboard(currentModuleFilter || 'all');
}

function renderCaseFacetChips(cases) {
  const wrap = document.getElementById('caseFacetChips');
  if (!wrap) return;
  const filters = currentFacetFilters || {};
  wrap.innerHTML = CASE_FACET_GROUPS.map(group => {
    const buttons = group.options.map(opt => {
      const active = (filters[group.key] || 'all') === opt.id ? ' active' : '';
      const count = opt.id === 'all'
        ? cases.length
        : cases.filter(c => caseMatchesFacetSelection(c, { ...filters, [group.key]: opt.id })).length;
      const badge = opt.id === 'all' ? '' : `<em>${count}</em>`;
      return `<button class="filter-chip${active}" data-action="set-case-facet-filter" data-facet-key="${escapeHtml(group.key)}" data-facet-id="${escapeHtml(opt.id)}">${opt.label}${badge}</button>`;
    }).join('');
    return `<span class="filter-cat-label">${group.label}</span>${buttons}`;
  }).join('');
}

function renderModuleFilterBar(cases) {
  const bar = document.getElementById('filterBar');
  if (!bar) return;
  const byModule = {};
  cases.forEach(c => { byModule[c.module] = (byModule[c.module] || 0) + 1; });
  const active = currentModuleFilter || 'all';
  const rows = [
    `<div class="filter-cat-row">
      <button class="filter-chip${active === 'all' ? ' active' : ''}" data-action="filter-cases" data-filter="all" id="filter-all">全部病种 <em class="filter-count">${cases.length}</em></button>
    </div>`
  ];
  const categories = Array.isArray(MODULE_CATEGORIES) ? [...MODULE_CATEGORIES] : [];
  Object.values(MODULES || {}).forEach(mod => {
    const cat = mod.category || '其他';
    if (!categories.includes(cat)) categories.push(cat);
  });
  categories.forEach(cat => {
    const items = Object.entries(MODULES || {}).filter(([, mod]) => (mod.category || '其他') === cat);
    if (!items.length) return;
    const chips = items.map(([code, mod]) => {
      const isActive = active === code;
      return `<button class="filter-chip${isActive ? ' active' : ''}" data-action="filter-cases" data-filter="${escapeHtml(code)}" id="filter-${escapeHtml(code)}" title="${escapeHtml(code + ' ' + (mod.name || ''))}">${escapeHtml(moduleChipLabel(mod, code))} <em class="filter-count">${byModule[code] || 0}</em></button>`;
    }).join('');
    rows.push(`<div class="filter-cat-row"><span class="filter-cat-label">${escapeHtml(cat.replace(/^足踝/, ''))}</span>${chips}</div>`);
  });
  bar.innerHTML = rows.join('');
}

function renderCaseSignalTags(c, { compact = false } = {}) {
  const stage = getCaseStageSummary(c);
  const items = [
    { text: getCaseOwnerLabel(c), className: caseOwnerIsMine(c) ? 'case-tag-accent' : 'case-tag-muted' },
    { text: stage.review, className: normalizeReviewState(c) === 'rejected' ? 'case-tag-danger' : 'case-tag-muted' },
    { text: caseHasAdmissionSource(c) ? '住院条' : '手工建档', className: caseHasAdmissionSource(c) ? 'case-tag-accent' : 'case-tag-muted' },
    { text: normalizeFollowupState(c) === 'needed' ? '需随访' : normalizeFollowupState(c) === 'complete' ? '随访完成' : '未随访', className: normalizeFollowupState(c) === 'needed' ? 'case-tag-success' : 'case-tag-muted' },
  ];
  const visible = compact ? items.slice(0, 3) : items;
  return visible.map(item => `<span class="case-tag ${item.className}">${escapeHtml(item.text)}</span>`).join('');
}

// ── 个人工作台范围（闭环协作）：把共享/指派/审核/驳回/随访收敛成"以我为中心"的视图 ──
const WORK_SCOPES = [
  { id: 'all',       label: '全部病例' },
  { id: 'mine_todo', label: '我的待办' },
  { id: 'created',   label: '我发起的' },
  { id: 'to_review', label: '待我审核' },
  { id: 'rejected',  label: '被驳回' },
  { id: 'followup',  label: '需随访' },
];
function _myUserId() { const u = (typeof getAuthUser === 'function') ? getAuthUser() : null; return u ? (u.id != null ? u.id : u.username) : null; }
function _isMine(v) {
  const user = (typeof getAuthUser === 'function') ? getAuthUser() : null;
  const keys = [user?.id, user?.username].filter(item => item !== undefined && item !== null).map(String);
  return v != null && keys.includes(String(v));
}
function caseMatchesScope(c, scope) {
  if (!scope || scope === 'all') return true;
  const stage = getCaseStageSummary(c);
  const wf = c.workflow_state || '';
  switch (scope) {
    case 'mine_todo': {
      const ownerOrAssignee = _isMine(c.assigned_to) || _isMine(c.created_by);
      const done = stage.review === '审核通过' || stage.status === '已完成';
      return ownerOrAssignee && !done;
    }
    case 'created':
      return _isMine(c.created_by);
    case 'to_review':
      if (_isMine(c.current_reviewer_id)) return true;
      return (typeof isChiefClinical === 'function' && isChiefClinical()) && ['submitted', 'reviewing'].includes(wf);
    case 'rejected': {
      const rejected = wf === 'rejected' || stage.review === '驳回修改';
      return rejected && (_isMine(c.created_by) || _isMine(c.assigned_to));
    }
    case 'followup':
      return caseMatchesFolder(c, 'followup');
    default:
      return true;
  }
}
function setWorkScope(scope) {
  currentWorkbenchQueueFilter = 'all';
  currentWorkScope = scope;
  renderSidebar();
  renderDashboard(currentModuleFilter || 'all');
}

function setDashboardQueue(queue) {
  currentWorkbenchQueueFilter = queue || 'all';
  currentFolderFilter = 'all';
  currentWorkScope = 'all';
  renderSidebar();
  renderDashboard(currentModuleFilter || 'all');
}

function caseMatchesWorkbenchQueue(c) {
  if (!currentWorkbenchQueueFilter || currentWorkbenchQueueFilter === 'all') return true;
  const mod = MODULES[c.module];
  const stage = getCaseStageSummary(c);
  if (currentWorkbenchQueueFilter === 'pending') return calcOverallProgress(c) < 100;
  if (currentWorkbenchQueueFilter === 'review') return ['待审核', '审核中'].includes(stage.review);
  if (currentWorkbenchQueueFilter === 'rejected') return c.workflow_state === 'rejected' || stage.review === '驳回修改';
  if (currentWorkbenchQueueFilter === 'followup') return caseMatchesFolder(c, 'followup');
  return true;
}

function getWorkbenchQueues(cases) {
  const pending = cases.filter(c => calcOverallProgress(c) < 100);
  const review = cases.filter(c => {
    const stage = getCaseStageSummary(c);
    return ['待审核', '审核中'].includes(stage.review);
  });
  const rejected = cases.filter(c => {
    const stage = getCaseStageSummary(c);
    return c.workflow_state === 'rejected' || stage.review === '驳回修改';
  });
  const followup = cases.filter(c => caseMatchesFolder(c, 'followup'));
  return [
    { id: 'pending', label: '待补资料', value: pending.length, hint: pending.length ? '优先补齐核心字段和影像' : '暂无缺口', tone: 'warning' },
    { id: 'review', label: '待审/审核中', value: review.length, hint: review.length ? '需要二线或主任处理' : '审核队列清空', tone: 'primary' },
    { id: 'rejected', label: '驳回修改', value: rejected.length, hint: rejected.length ? '按意见补充后重提' : '暂无驳回', tone: 'danger' },
    { id: 'followup', label: '随访队列', value: followup.length, hint: followup.length ? '核对复诊与结局指标' : '暂无随访提醒', tone: 'success' },
  ];
}

function renderWorkbenchQueues(cases) {
  const wrap = document.getElementById('workbenchQueueGrid');
  if (!wrap) return;
  wrap.innerHTML = getWorkbenchQueues(cases).map(q => `
    <button class="workbench-queue-card tone-${q.tone}${currentWorkbenchQueueFilter === q.id ? ' active' : ''}" data-action="set-dashboard-queue" data-queue-id="${escapeHtml(q.id)}">
      <span>${q.label}</span>
      <strong>${q.value}</strong>
      <small>${q.hint}</small>
    </button>
  `).join('');
}

function setTodoScope(scope) {
  currentTodoScope = scope || 'mine_todo';
  renderTodoWorkbench();
}

function getTodoScopeCases(cases, scope) {
  return cases.filter(c => caseMatchesScope(c, scope));
}

function getTodoPrimaryAction(c, scope) {
  const next = getCaseNextAction(c, MODULES[c.module]);
  if (scope === 'to_review') return { label: '进入审核', phase: 'preop' };
  if (scope === 'followup') return { label: '补随访', phase: 'followup' };
  if (scope === 'rejected') return { label: '按意见补资料', phase: 'preop' };
  if (next.tone === 'warning' && next.label.includes('影像')) return { label: '补影像', phase: 'preop' };
  return { label: '打开处理', phase: 'preop' };
}

function openCaseAtPhase(caseId, phase = 'preop') {
  openCase(caseId).then(() => {
    currentCasePhase = phase || currentCasePhase;
    if (currentCaseId === caseId) renderCaseForm(caseId);
  });
}

function renderTodoWorkbench() {
  const wrap = document.getElementById('todoWorkbench');
  if (!wrap) return;
  const cases = getVisibleCases();
  const scopes = WORK_SCOPES.filter(s => s.id !== 'all');
  if (!currentTodoScope || currentTodoScope === 'all') currentTodoScope = 'mine_todo';
  const selected = scopes.some(s => s.id === currentTodoScope) ? currentTodoScope : 'mine_todo';
  currentTodoScope = selected;
  const selectedScope = scopes.find(s => s.id === selected);
  const selectedCases = getTodoScopeCases(cases, selected);
  const admissionTasks = (typeof loadAdmissionTasks === 'function')
    ? loadAdmissionTasks().filter(t => t.status !== '已建档')
    : [];
  const tabs = scopes.map(scope => {
    const count = getTodoScopeCases(cases, scope.id).length;
    return `<button class="todo-tab${selected === scope.id ? ' active' : ''}" data-action="set-todo-scope" data-scope-id="${escapeHtml(scope.id)}">
      <span>${scope.label}</span><strong>${count}</strong>
    </button>`;
  }).join('');
  const listHtml = selectedCases.length ? selectedCases.map(c => {
    const mod = MODULES[c.module];
    const stage = getCaseStageSummary(c);
    const action = getTodoPrimaryAction(c, selected);
    const required = getRequiredFieldStatus(c, mod);
    const mediaPct = calcMediaProgress(c, mod);
    const safeId = escapeJsArg(c.id);
    const phase = action.phase || 'preop';
    return `<article class="todo-card">
      <div class="todo-card-main">
        <div class="todo-card-kicker">${escapeHtml(mod?.name || c.module)} · ${escapeHtml(stage.review)}</div>
        <h3>${escapeHtml(displayPatientName(c))}</h3>
        <div class="todo-card-meta">
          <span>${escapeHtml(getCaseOwnerLabel(c))}</span>
          <span>核心 ${required.filled}/${required.total}</span>
          <span>影像 ${mediaPct.filled}/${mediaPct.total}</span>
          ${c.center_id || c.centerId ? `<span>${escapeHtml(getCenterName(c.center_id || c.centerId))}</span>` : ''}
        </div>
        ${selected === 'rejected' && (c.data?.workflow_reject_reason || c.rejected_reason)
          ? `<div class="todo-reject-note">${escapeHtml(c.data?.workflow_reject_reason || c.rejected_reason)}</div>`
          : ''}
      </div>
      <div class="todo-card-actions">
        <button class="case-action-btn primary" data-action="open-case-at-phase" data-case-id="${safeId}" data-phase-id="${escapeHtml(phase)}">${action.label}</button>
        <button class="case-action-btn" data-action="open-case" data-case-id="${safeId}">查看病例</button>
      </div>
    </article>`;
  }).join('') : `<div class="todo-empty">
    <div class="todo-empty-title">${escapeHtml(selectedScope?.label || '待办')}已清空</div>
    <div class="todo-empty-sub">当前账号下没有匹配此标签的病例。</div>
  </div>`;

  const admissionHtml = admissionTasks.length ? `
    <section class="todo-admission">
      <div class="todo-section-head">
        <div>
          <strong>住院条待录入</strong>
          <span>${admissionTasks.length} 条任务尚未建档</span>
        </div>
        <button class="case-action-btn" data-action="open-admission-scan">分发住院条</button>
      </div>
      <div class="todo-admission-grid">
        ${admissionTasks.slice(0, 6).map(task => `<button class="task-item" data-action="open-admission-scan">
          <span>${escapeHtml(task.status || '待录入')}</span><strong>${escapeHtml(task.patientName || task.name || '未命名住院条')}</strong>
        </button>`).join('')}
      </div>
    </section>` : '';

  wrap.innerHTML = `
    <div class="todo-head">
      <div>
        <h1>待办台</h1>
        <p>按个人任务、发起、审核、驳回和随访收拢病例，计数与列表实时联动。</p>
      </div>
      <button class="btn btn-primary" data-action="show-new-case">＋ 新建病例</button>
    </div>
    <div class="todo-tabs" role="tablist" aria-label="待办范围">${tabs}</div>
    <section class="todo-list" aria-label="${escapeHtml(selectedScope?.label || '待办列表')}">${listHtml}</section>
    ${admissionHtml}
  `;
}

// 渲染工作台范围 chip（带实时计数）。未登录时该行隐藏（个人视图无意义）。
function renderWorkScopeChips(cases) {
  const wrap = document.getElementById('workScopeChips');
  if (!wrap) return;
  const logged = (typeof isLoggedIn === 'function') && isLoggedIn();
  if (!logged) { wrap.hidden = true; wrap.innerHTML = ''; return; }
  wrap.hidden = false;
  wrap.innerHTML = `<span class="filter-cat-label">工作台</span>` + WORK_SCOPES.map(s => {
    const count = s.id === 'all' ? cases.length : cases.filter(c => caseMatchesScope(c, s.id)).length;
    const active = currentWorkScope === s.id ? ' active' : '';
    const badge = (s.id !== 'all' && count) ? `<em class="scope-count">${count}</em>` : '';
    return `<button class="filter-chip${active}" id="scope-${s.id}" data-action="set-work-scope" data-scope-id="${escapeHtml(s.id)}">${s.label}${badge}</button>`;
  }).join('');
}

function renderFolderBoard(cases) {
  const folders = [
    { id: 'all', count: cases.length },
    { id: 'pending', count: cases.filter(c => caseMatchesFolder(c, 'pending')).length },
    { id: 'review', count: cases.filter(c => caseMatchesFolder(c, 'review')).length },
    { id: 'followup', count: cases.filter(c => caseMatchesFolder(c, 'followup')).length },
    { id: 'starred', count: cases.filter(c => caseMatchesFolder(c, 'starred')).length },
    { id: 'archived', count: cases.filter(c => caseMatchesFolder(c, 'archived')).length },
  ];
  folders.forEach(folder => {
    const el = document.getElementById(`folder-${folder.id}`);
    if (!el) return;
    el.classList.toggle('active', currentFolderFilter === folder.id);
    const strong = el.querySelector('strong');
    if (strong) strong.innerHTML = `${strong.textContent.replace(/（.*$/, '')}<em>（${folder.count}）</em>`;
  });
  document.querySelectorAll('.folder-quick-filter button').forEach(btn => {
    const target = btn.dataset.folder || '';
    btn.classList.toggle('active', target === currentFolderFilter);
  });
}

function deriveCaseTasks(c) {
  const mod = MODULES[c.module];
  const pct = calcProgress(c);
  const mediaPct = calcMediaProgress(c, mod);
  const stage = getCaseStageSummary(c);
  const tasks = [];
  const name = displayPatientName(c);
  if (pct < 100) tasks.push({ type: '补资料', text: `${name}：核心字段完成 ${pct}%，需要补齐必填资料。`, caseId: c.id });
  if (mediaPct.pct < MEDIA_SUFFICIENT_PCT) tasks.push({ type: '补影像', text: `${name}：影像槽位 ${mediaPct.filled}/${mediaPct.total}，建议补充标准影像。`, caseId: c.id });
  if (['待审核', '审核中', '驳回修改'].includes(stage.review)) tasks.push({ type: '审核', text: `${name}：当前审核状态为“${stage.review}”，目标角色：${stage.reviewer}。`, caseId: c.id });
  if (stage.stage === '随访' || getCaseTags(c).includes('随访病例')) tasks.push({ type: '随访', text: `${name}：已进入随访队列，请核对复诊时间和疗效评分。`, caseId: c.id });
  if (c.favorite) tasks.push({ type: '星标', text: `${name}：星标病例，适合纳入典型病例或科研素材整理。`, caseId: c.id });
  return tasks;
}

function getCaseNextAction(c, mod = MODULES[c.module]) {
  const required = getRequiredFieldStatus(c, mod);
  const mediaPct = calcMediaProgress(c, mod);
  const stage = getCaseStageSummary(c);
  if (stage.review === '驳回修改') return { tone: 'danger', label: '按驳回意见补充后重提' };
  if (required.missing.length > 0) return { tone: 'warning', label: `补齐 ${required.missing.length} 项核心字段` };
  if (mediaPct.pct < MEDIA_SUFFICIENT_PCT) return { tone: 'warning', label: `补齐影像 ${mediaPct.filled}/${mediaPct.total}` };
  if (stage.review === '待审核') return { tone: 'primary', label: `等待${stage.reviewer}审核` };
  if (stage.review === '审核中') return { tone: 'primary', label: '审核中，关注审核结论' };
  if (stage.stage === '随访' || getCaseTags(c).includes('随访病例')) return { tone: 'success', label: '补充随访评分与复查影像' };
  if (stage.review === '审核通过') return { tone: 'success', label: '可归档或导出科研包' };
  return { tone: 'neutral', label: '资料可继续完善' };
}

function updateTaskPanelVisibility(cases) {
  const panel = document.getElementById('taskPanel');
  if (!panel) return;
  const taskCount = cases.flatMap(deriveCaseTasks).length;
  const admissionCount = (typeof loadAdmissionTasks === 'function')
    ? loadAdmissionTasks().filter(t => t.status !== '已建档').length
    : 0;
  const total = taskCount + admissionCount;
  panel.hidden = total === 0;
  const countEl = document.getElementById('taskPanelCount');
  if (countEl) countEl.textContent = total > 0 ? `${total} 项` : '';
  const admWrap = document.getElementById('admissionTaskBoardWrap');
  if (admWrap) admWrap.hidden = admissionCount === 0;
}

function renderTaskBoard(cases) {
  const board = document.getElementById('taskBoard');
  if (!board) return;
  const tasks = cases.flatMap(deriveCaseTasks).slice(0, 8);
  if (tasks.length === 0) {
    board.innerHTML = '';
    return;
  }
  board.innerHTML = `
    <div class="task-list">
      ${tasks.map(task => `<button class="task-item" data-action="open-case" data-case-id="${escapeHtml(task.caseId)}">
        <span>${escapeHtml(task.type)}</span><strong>${escapeHtml(task.text)}</strong>
      </button>`).join('')}
    </div>
  `;
}

function renderCaseFolderTools(c, mod) {
  const tags = getCaseTags(c);
  return `
    <div class="case-folder-tools">
      <div>
        <div class="case-folder-title">病例夹管理</div>
        <div class="case-folder-subtitle">用于典型病例、科研素材、随访病例和审核分流。</div>
      </div>
      <div class="tag-list">
        <button class="tag-action ${c.favorite ? 'active' : ''}" data-action="toggle-favorite" data-case-id="${escapeHtml(c.id)}">${c.favorite ? '取消星标' : '设为星标'}</button>
        ${tags.length ? tags.map(tag => `<button class="tag-chip" data-action="remove-case-tag" data-case-id="${escapeHtml(c.id)}" data-tag="${escapeHtml(tag)}" title="点击移除标签">${escapeHtml(tag)} ×</button>`).join('') : '<span class="tag-placeholder">暂无标签</span>'}
        <button class="tag-add-btn" data-action="prompt-add-tag" data-case-id="${escapeHtml(c.id)}">＋ 添加标签</button>
        <button class="tag-add-btn" data-action="export-anonymized-case" data-case-id="${escapeHtml(c.id)}">导出匿名化病例包</button>
      </div>
    </div>
  `;
}

function renderCaseTimeline(c, mod) {
  const required = getRequiredFieldStatus(c, mod);
  const mediaPct = calcMediaProgress(c, mod);
  const stage = getCaseStageSummary(c);
  const aiCount = c.aiGenCount || 0;
  const items = [
    { label: '建档', value: c.createdAt || '未记录', done: true },
    { label: '结构化采集', value: `核心必填 ${required.filled}/${required.total}`, done: required.missing.length === 0 },
    { label: '影像证据链', value: `槽位 ${mediaPct.filled}/${mediaPct.total}`, done: mediaPct.pct >= MEDIA_SUFFICIENT_PCT },
    { label: 'AI 文书', value: aiCount ? `已生成 ${aiCount} 次` : '尚未生成', done: aiCount > 0 },
    { label: '审核协作', value: stage.review, done: stage.review === '审核通过' },
    { label: '随访归档', value: stage.stage === '随访' ? '已进入随访' : stage.deadline, done: stage.status === '已完成' },
  ];
  return `
    <div class="case-timeline">
      <div class="timeline-title">病例时间轴</div>
      <div class="timeline-items">
        ${items.map(item => `<div class="timeline-item ${item.done ? 'done' : ''}">
          <span></span><strong>${item.label}</strong><small>${item.value}</small>
        </div>`).join('')}
      </div>
    </div>
  `;
}

function calcProgress(c) {
  const mod = MODULES[c.module];
  if (!mod) return 0;
  // 复用唯一权威的核心必填判定（getRequiredFieldStatus）：
  //   - 按 isFieldVisible 过滤掉当前不适用的条件字段（如糖尿病补充段、F 模块畸形相关术式、hideIf 字段），
  //     否则进度条会把用户根本看不到/填不了的隐藏字段算进分母，导致永远不到 100%；
  //   - 数值 0 / false 视为“已填”（旧逻辑用真值判断把 0 误判为空）；
  //   - 多选数组按“非空才算填”（旧逻辑把空数组 [] 误判为已填）。
  // 这样进度条与时间轴「核心必填 X/Y」、缺失字段面板三处口径完全一致。
  const { total, filled } = getRequiredFieldStatus(c, mod);
  return total === 0 ? 100 : Math.round((filled / total) * 100);
}

function getMediaSection(mod) {
  return (mod?.sections || []).find(s => s.type === 'media');
}

function calcMediaProgress(c, mod) {
  const mediaSection = getMediaSection(mod);
  if (!mediaSection) return { filled: 0, total: 0, pct: 100 };
  const metas = (mediaSection.slots || []).map((slot, i) => getSlotMeta(slot, i));
  const total = metas.length;
  let filled = 0;
  metas.forEach(meta => {
    // 槽位「有图」= 数组不为空（兼容旧的单对象格式）
    const hasFile = getSlotImages(c, meta.key, meta.legacyKey).length > 0;
    if (hasFile) filled++;
  });
  const pct = total === 0 ? 100 : Math.round((filled / total) * 100);
  return { filled, total, pct };
}

// 资料完整度（综合评价）：以影像资料完整度为主要维度（占 70%），核心必填字段为辅（占 30%）。
// 用于仪表盘卡片、侧栏、统计等“单一完整度”展示；病例表单页仍分别显示「核心信息」与
// 「影像资料」两条进度，便于精确补全。无影像模块时退化为纯字段完整度，避免恒为满分失真。
function calcOverallProgress(c) {
  const mod = MODULES[c.module];
  if (!mod) return 0;
  const fieldPct = calcProgress(c);
  const mediaSection = getMediaSection(mod);
  if (!mediaSection) return fieldPct;
  const media = calcMediaProgress(c, mod);
  if (media.total === 0) return fieldPct;
  return Math.round(media.pct * 0.7 + fieldPct * 0.3);
}

function getFieldRequirementLevel(field) {
  if (field.req) return { key: 'core', label: '核心必填' };
  const recommendedIds = new Set([
    'bmi', 'pain_vas', 'pain_vas_active', 'walking_distance', 'comorbidity',
    'aofas_pain', 'aofas_function', 'aofas_alignment', 'faam_adl', 'faam_sport',
    'vas_satisfaction', 'physical_notes', 'imaging_notes', 'plan_notes',
    'surgeon', 'anesthesia', 'deadline',
  ]);
  if (recommendedIds.has(field.id)) return { key: 'recommended', label: '推荐填写' };
  return { key: 'optional', label: '可选补充' };
}

function isFieldVisible(field, section, c) {
  if (c.module === 'F') {
    const deformity = c.data?.basic_deformity_type || c.data?.deformity_type || '';
    if (field.id === 'procedure_flat') return !deformity || deformity.includes('平足');
    if (field.id === 'procedure_cavus') return !deformity || deformity.includes('高弓');
  }
  if (section.id === 'implant' && c.module === 'B') return true;
  // Declarative hideIf: {full_data_key: value | [values]} — 任一命中即隐藏
  if (field.hideIf) {
    for (const [dataKey, expected] of Object.entries(field.hideIf)) {
      const value = c.data?.[dataKey];
      const match = Array.isArray(expected) ? expected.includes(value) : value === expected;
      if (match) return false;
    }
  }
  return true;
}

function getVisibleFields(section, c) {
  return (section.fields || []).filter(field => isFieldVisible(field, section, c));
}

function getRequiredFieldStatus(c, mod) {
  const missing = [];
  let total = 0;
  let filled = 0;
  [...(mod?.sections || []), ...getConditionalSections(c)].forEach(section => {
    if (section.type === 'media') return;
    getVisibleFields(section, c).forEach(field => {
      if (getFieldRequirementLevel(field).key !== 'core') return;
      total++;
      const key = `${section.id}_${field.id}`;
      const value = c.data?.[key];
      const isFilled = Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null && value !== '';
      if (isFilled) {
        filled++;
      } else {
        missing.push({ section: section.title, label: field.label, key });
      }
    });
  });
  return { total, filled, missing };
}

function getMediaPhaseId(meta) {
  if (meta.phase === '术中资料') return 'intraop';
  if (meta.phase === '术后资料') return 'postop';
  return 'preop';
}

function getSectionsForPhase(c, mod, phaseId = currentCasePhase) {
  const allSections = mod?.sections || [];
  const workflow = allSections.find(sec => sec.id === 'workflow') || getWorkflowSection();
  const mediaSections = allSections.filter(sec => sec.type === 'media');
  const normalSections = allSections.filter(sec => sec.type !== 'media' && sec.id !== 'workflow');
  if (phaseId === 'preop') return [workflow, ...normalSections, ...getConditionalSections(c), ...mediaSections];
  return [workflow, ...mediaSections];
}

function getPhaseRequiredStatus(c, mod, phaseId) {
  const sections = getSectionsForPhase(c, mod, phaseId);
  const missing = [];
  let total = 0;
  let filled = 0;
  sections.forEach(section => {
    if (section.type === 'media') {
      const metas = (section.slots || []).map((slot, i) => getSlotMeta(slot, i));
      metas.forEach(meta => {
        const metaPhase = getMediaPhaseId(meta);
        const visible = phaseId === 'followup' ? metaPhase === 'postop' : metaPhase === phaseId;
        if (!visible) return;
        total++;
        const hasFile = getSlotImages(c, meta.key, meta.legacyKey).length > 0;
        if (hasFile) filled++;
        else missing.push({ type: 'media', sectionId: `media_${section.id}`, key: meta.key, label: meta.label, section: section.title });
      });
      return;
    }
    getVisibleFields(section, c).forEach(field => {
      if (getFieldRequirementLevel(field).key !== 'core') return;
      total++;
      const key = `${section.id}_${field.id}`;
      const value = c.data?.[key];
      const isFilled = Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null && value !== '';
      if (isFilled) filled++;
      else missing.push({ type: 'field', sectionId: section.id, key, label: field.label, section: section.title });
    });
  });
  return { total, filled, missing, pct: total === 0 ? 100 : Math.round((filled / total) * 100) };
}

function renderPhaseProgressStrip(c, mod) {
  return `<div class="phase-progress-strip">
    ${CASE_PHASES.map(phase => {
      const status = getPhaseRequiredStatus(c, mod, phase.id);
      const active = currentCasePhase === phase.id ? ' active' : '';
      const done = status.total > 0 && status.filled === status.total ? ' done' : '';
      return `<button class="phase-progress-card${active}${done}" data-action="set-case-phase" data-phase-id="${escapeHtml(phase.id)}">
        <span>${phase.label}</span>
        <strong>${status.filled}/${status.total}</strong>
        <i><b data-progress-width="${status.pct}"></b></i>
      </button>`;
    }).join('')}
  </div>`;
}

function renderPhaseMissingPanel(c, mod) {
  const status = getPhaseRequiredStatus(c, mod, currentCasePhase);
  const phase = CASE_PHASES.find(item => item.id === currentCasePhase);
  const rejectReason = c.data?.workflow_reject_reason || c.rejected_reason || '';
  if (!status.missing.length && !rejectReason) {
    return `<div class="phase-missing-panel ok">
      <strong>${escapeHtml(phase?.label || '当前阶段')}关键项已完成</strong>
      <span>继续补充推荐字段和影像可提升病例汇报质量。</span>
    </div>`;
  }
  const missingButtons = status.missing.slice(0, 8).map(item => `
    <button class="phase-missing-chip" data-action="focus-phase-missing" data-phase-type="${escapeHtml(item.type)}" data-section-id="${escapeHtml(item.sectionId)}" data-field-key="${escapeHtml(item.key)}">
      ${escapeHtml(item.section)} · ${escapeHtml(item.label)}
    </button>`).join('');
  const more = status.missing.length > 8 ? `<span class="phase-missing-more">另有 ${status.missing.length - 8} 项待补</span>` : '';
  return `<div class="phase-missing-panel">
    <div class="phase-missing-head">
      <strong>${escapeHtml(phase?.label || '当前阶段')}待补 ${status.missing.length} 项</strong>
      <span>点击条目可直接跳转到字段或影像槽位。</span>
    </div>
    ${rejectReason ? `<div class="phase-reject-note"><b>审核驳回意见</b><span>${escapeHtml(rejectReason)}</span></div>` : ''}
    <div class="phase-missing-list">${missingButtons}${more}</div>
  </div>`;
}

function focusPhaseMissing(type, sectionId, key) {
  focusFormSection(sectionId);
  setTimeout(() => {
    const target = type === 'media'
      ? document.getElementById(`slot_${key}`)
      : document.getElementById(`f_${key}`) || document.querySelector(`[id^="cb_${key}_"]`);
    if (!target) return;
    target.classList.add('is-current-missing');
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (target.focus) target.focus();
    setTimeout(() => target.classList.remove('is-current-missing'), 1800);
  }, 160);
}

function getCaseStageSummary(c) {
  const mod = MODULES[c.module];
  const stage = c.data?.workflow_case_stage || c.data?.case_stage || '未填写';
  const status = inferStageStatus(c, mod);
  const review = c.data?.workflow_review_status || c.data?.review_status || '未填写';
  const reviewer = inferReviewerRole(c);
  const deadline = c.data?.workflow_deadline || c.data?.deadline || '未设置';
  return { stage, status, review, reviewer, deadline };
}

function getUserRoleLabel(role) {
  const roleMap = {
    admin: '系统管理员',
    member: '普通成员',
    deleted: '已删除账户'
  };
  return roleMap[role] || '未登录';
}

function getClinicalTitleLabel(title) {
  const titleMap = {
    resident: '住院医',
    fellow: '进修/Fellow',
    attending: '主治',
    secondline: '二线/副主任',
    chief: '主任',
    nurse: '管床护士',
  };
  return title ? (titleMap[title] || title) : '';
}

function inferReviewerRole(c) {
  const user = getAuthUser();
  const savedReviewer = c.data?.workflow_reviewer_role || c.data?.reviewer_role;
  if (savedReviewer) return savedReviewer;
  const titleLabels = {
    resident: '住院医',
    fellow: '进修/Fellow',
    attending: '主治',
    secondline: '二线',
    chief: '主任',
    nurse: '管床护士',
  };
  if (user?.clinical_title && titleLabels[user.clinical_title]) {
    return titleLabels[user.clinical_title];
  }
  return user?.role === 'admin' ? '系统管理员' : '未指定临床职称';
}

function inferStageStatus(c, mod = MODULES[c.module]) {
  const required = getRequiredFieldStatus(c, mod);
  const mediaPct = calcMediaProgress(c, mod);
  const review = c.data?.workflow_review_status || c.data?.review_status || '';
  if (review === '审核通过') return '已完成';
  if (required.missing.length > 0 || mediaPct.pct < MEDIA_SUFFICIENT_PCT) return '资料待补';
  if (review === '审核中' || review === '待审核') return '待审核';
  return '进行中';
}

function getQualityChecks(c, mod, required, mediaPct) {
  const checks = [];
  if (required.missing.length) {
    checks.push({ level: 'warning', text: `还有 ${required.missing.length} 项核心必填资料未完成，暂不建议提交审核。` });
  } else {
    checks.push({ level: 'ok', text: '核心必填资料已完成，可以进入审核或生成文书。' });
  }
  if (mediaPct.filled === 0) {
    checks.push({ level: 'warning', text: '尚未上传影像资料，病例汇报和 AI 文书证据不足。' });
  } else if (mediaPct.pct < MEDIA_SUFFICIENT_PCT) {
    checks.push({ level: 'info', text: '影像资料已开始上传，建议补齐术前标准影像槽位。' });
  }

  const age = Number(c.data?.basic_age || c.data?.age);
  if (age && (age < 0 || age > 120)) checks.push({ level: 'danger', text: '年龄数值超出常见范围，请核对。' });

  const bmi = Number(c.data?.basic_bmi || c.data?.bmi);
  if (bmi && (bmi < 12 || bmi > 55)) checks.push({ level: 'warning', text: 'BMI 数值异常，请确认单位和录入是否正确。' });

  collectNumericQualityChecks(c, mod).forEach(check => checks.push(check));

  const hva = Number(c.data?.imaging_hva_xray || c.data?.hva_xray);
  const ima = Number(c.data?.imaging_ima_xray || c.data?.ima_xray);
  if (c.module === 'A' && hva && ima && hva >= 40 && ima < 10) {
    checks.push({ level: 'warning', text: 'HVA 较大但 IMA 较低，建议复核拇外翻测量值。' });
  }

  const comorbidity = Object.entries(c.data || {})
    .filter(([key]) => key.includes('comorbidity'))
    .flatMap(([, value]) => Array.isArray(value) ? value : [value])
    .filter(Boolean);
  if (comorbidity.includes('糖尿病')) {
    checks.push({ level: 'info', text: '已记录糖尿病，建议补充血糖控制、感染风险和伤口管理要点。' });
  }

  const procedureValue = Object.entries(c.data || {}).find(([key]) => key.includes('procedure') || key.includes('plan'))?.[1];
  if (procedureValue && mediaPct.filled < 2) {
    checks.push({ level: 'warning', text: '已选择手术方案，但影像证据不足，建议至少上传术前 X线和 CT/大体相。' });
  }

  if (currentCasePhase === 'followup') {
    checks.push({ level: 'info', text: '随访阶段建议补充随访时间、术后评分、影像愈合情况和并发症记录。' });
  }
  const stage = getCaseStageSummary(c);
  if ((currentCasePhase === 'followup' || stage.stage === '随访') && getPostopMediaCount(c, mod) === 0) {
    checks.push({ level: 'warning', text: '病例已进入随访，但尚未上传术后/随访影像，结局评价证据不足。' });
  }
  return checks;
}

function renderQualityChecks(checks) {
  const visible = checks.slice(0, 9);
  const more = checks.length > visible.length ? `<li class="quality-item quality-info">另有 ${checks.length - visible.length} 条质控提示，建议分批核对。</li>` : '';
  return visible.map(check => `<li class="quality-item quality-${check.level}">${check.text}</li>`).join('') + more;
}

function collectNumericQualityChecks(c, mod) {
  const checks = [];
  const seen = new Set();
  [...(mod?.sections || []), ...getConditionalSections(c)].forEach(section => {
    if (section.type === 'media') return;
    getVisibleFields(section, c).forEach(field => {
      if (field.type !== 'number') return;
      const key = `${section.id}_${field.id}`;
      const raw = c.data?.[key];
      if (raw === undefined || raw === null || raw === '') return;
      const value = Number(raw);
      if (!Number.isFinite(value)) {
        checks.push({ level: 'danger', text: `${field.label} 不是有效数字，请核对录入。` });
        return;
      }
      const rule = getNumericQualityRule(field);
      if (!rule) return;
      const id = `${key}:${rule.min}:${rule.max}`;
      if (seen.has(id)) return;
      seen.add(id);
      if (value < rule.min || value > rule.max) {
        checks.push({ level: rule.level || 'warning', text: `${field.label} 为 ${value}，超出建议范围 ${rule.min}-${rule.max}${rule.unit || ''}。` });
      }
    });
  });
  return checks;
}

function getNumericQualityRule(field) {
  const id = String(field.id || '').toLowerCase();
  const label = String(field.label || '').toLowerCase();
  const unit = String(field.unit || '');
  const text = `${id} ${label} ${unit}`;
  if (id.includes('age')) return { min: 0, max: 120, unit: '岁', level: 'danger' };
  if (id.includes('bmi')) return { min: 12, max: 55, unit: 'kg/m2' };
  if (text.includes('aofas') || text.includes('faam') || text.includes('visa') || text.includes('kt_score')) return { min: 0, max: 100, unit: '分' };
  if (text.includes('cait')) return { min: 0, max: 30, unit: '分' };
  if (text.includes('beighton')) return { min: 0, max: 9, unit: '分' };
  if (text.includes('vas')) return { min: 0, max: 10, unit: '分' };
  if (unit.includes('%') || text.includes('pct')) return { min: 0, max: 100, unit: '%' };
  if (unit.includes('°')) return { min: -90, max: 180, unit: '°' };
  if (unit.includes('mm')) return { min: -50, max: 200, unit: 'mm' };
  if (unit.includes('cm')) return { min: 0, max: 50, unit: 'cm' };
  if (unit.includes('月')) return { min: 0, max: 240, unit: '月' };
  return null;
}

function getPostopMediaCount(c, mod) {
  const mediaSection = getMediaSection(mod);
  const metas = (mediaSection?.slots || []).map((slot, i) => getSlotMeta(slot, i));
  return metas
    .filter(meta => meta.phase === '术后资料')
    .reduce((sum, meta) => sum + getSlotImages(c, meta.key, meta.legacyKey).length, 0);
}

function getAISuggestions(c, mod, required, mediaPct) {
  const suggestions = [];
  if (required.missing.length) suggestions.push('先补齐核心必填字段，AI 文书会更完整。');
  if (mediaPct.filled === 0) suggestions.push('上传术前影像后，可生成更适合病例汇报的摘要。');
  suggestions.push('可生成“科室汇报版、论文版、患者沟通版、主任审核版”等不同输出。');
  suggestions.push(`当前病种为 ${mod.name}，AI 会优先围绕该病种组织诊断、术式和风险。`);
  return suggestions;
}

function renderAISuggestions(suggestions) {
  return suggestions.map(text => `<li>${text}</li>`).join('');
}

function getEvidenceChain(c, mod) {
  const mediaSection = getMediaSection(mod);
  const metas = (mediaSection?.slots || []).map((slot, i) => getSlotMeta(slot, i));
  const phaseMap = [
    { key: '术前资料', label: '术前证据' },
    { key: '术中资料', label: '术中证据' },
    { key: '术后资料', label: '术后/随访证据' },
  ];
  return phaseMap.map(phase => {
    const phaseSlots = metas.filter(meta => meta.phase === phase.key);
    const filled = phaseSlots.filter(meta => getSlotImages(c, meta.key, meta.legacyKey).length > 0).length;
    return { ...phase, filled, total: phaseSlots.length };
  });
}

function renderEvidenceChain(c, mod) {
  const chain = getEvidenceChain(c, mod);
  return `
    <div class="evidence-chain">
      <div class="evidence-title">影像证据链</div>
      <div class="evidence-steps">
        ${chain.map(item => `<div class="evidence-step ${item.filled ? 'has-data' : ''}">
          <strong>${item.label}</strong>
          <span>${item.filled ? '已上传' : '待补充'}</span>
        </div>`).join('')}
      </div>
    </div>
  `;
}

function renderPhaseTabs(c) {
  return `
    <div class="phase-tabs">
      ${CASE_PHASES.map(phase => {
        const mod = MODULES[c.module];
        const status = getPhaseRequiredStatus(c, mod, phase.id);
        return `<button class="phase-tab ${currentCasePhase === phase.id ? 'active' : ''}" data-action="set-case-phase" data-phase-id="${escapeHtml(phase.id)}">
        <strong>${phase.label}</strong>
        <span>${phase.hint}</span>
        <em>${status.filled}/${status.total}</em>
      </button>`;
      }).join('')}
    </div>
  `;
}

function caseHasComorbidity(c, label) {
  return Object.entries(c.data || {})
    .filter(([key]) => key.includes('comorbidity'))
    .some(([, value]) => Array.isArray(value) ? value.includes(label) : value === label);
}

function getConditionalSections(c) {
  const sections = [];
  if (caseHasComorbidity(c, '糖尿病')) {
    sections.push({
      id: 'risk_diabetes',
      icon: '🩸',
      title: '糖尿病围手术期风险补充',
      fields: [
        { id: 'hba1c', label: '最近 HbA1c', type: 'number', unit: '%', req: true },
        { id: 'glucose_control', label: '血糖控制情况', type: 'select', opts: ['控制良好', '控制一般', '控制不佳', '待完善检查'], req: true },
        { id: 'infection_risk', label: '感染风险评估', type: 'select', opts: ['低风险', '中风险', '高风险'] },
        { id: 'wound_plan', label: '切口/伤口管理计划', type: 'textarea', full: true, placeholder: '如：围手术期血糖目标、抗感染策略、换药频率等' },
      ],
    });
  }
  return sections;
}

function renderFollowupPanel(c, mod) {
  const stage = getCaseStageSummary(c);
  const postopCount = getPostopMediaCount(c, mod);
  return `
    <div class="section-block">
      <div class="section-header" data-action="toggle-section" data-section-id="followup_panel">
        <span class="section-icon">🕒</span>
        <span class="section-title">随访追踪任务</span>
        <span class="section-chevron" id="chev-followup_panel">▼</span>
      </div>
      <div class="section-body open" id="sbody-followup_panel">
        <div class="followup-grid">
          <div class="followup-card"><strong>6周随访</strong><span>切口、疼痛、早期影像、并发症</span></div>
          <div class="followup-card"><strong>3月随访</strong><span>功能恢复、负重情况、影像愈合</span></div>
          <div class="followup-card"><strong>6月随访</strong><span>评分量表、步态/活动视频、复查影像</span></div>
          <div class="followup-card"><strong>12月随访</strong><span>最终疗效、并发症、科研结局指标</span></div>
        </div>
        <div class="followup-quality-strip ${postopCount ? 'ok' : 'warning'}">
          <strong>${postopCount ? '随访影像已开始归档' : '随访影像待补充'}</strong>
          <span>${postopCount ? `已上传 ${postopCount} 份术后/随访影像资料。` : '建议至少上传术后正侧位影像或功能视频，便于结局评价。'}</span>
        </div>
        <p class="followup-note">当前病例阶段：${stage.stage}。若进入随访，请在“流程阶段与审核”中将病例阶段改为“随访”，并补充术后评分和影像对照。</p>
      </div>
    </div>
  `;
}

function renderCaseWorkbench(c, mod, pct, mediaPct) {
  const required = getRequiredFieldStatus(c, mod);
  const stage = getCaseStageSummary(c);
  const checks = getQualityChecks(c, mod, required, mediaPct);
  const aiSuggestions = getAISuggestions(c, mod, required, mediaPct);
  const missingHtml = required.missing.length
    ? required.missing.slice(0, 5).map(item => `<li>${item.section}：${item.label}</li>`).join('')
    : '<li>核心必填资料已完成，可以继续补充影像或生成文书。</li>';
  const moreMissing = required.missing.length > 5
    ? `<li>另有 ${required.missing.length - 5} 项必填资料待补充。</li>`
    : '';

  return `
    <div class="case-workbench">
      <div class="workbench-main">
        <div class="workbench-title">病例工作台</div>
        <div class="workbench-subtitle">这里汇总当前病例的阶段、完成度、影像上传和下一步建议。</div>
        <div class="workbench-status-grid">
          <div class="workbench-status-item"><span>病例阶段</span><strong>${stage.stage}</strong></div>
          <div class="workbench-status-item"><span>阶段状态</span><strong>${stage.status}</strong></div>
          <div class="workbench-status-item"><span>审核状态</span><strong>${stage.review}</strong></div>
          <div class="workbench-status-item"><span>目标审核角色</span><strong>${stage.reviewer}</strong></div>
          <div class="workbench-status-item"><span>截止日期</span><strong>${stage.deadline}</strong></div>
          <div class="workbench-status-item"><span>AI生成次数</span><strong>${c.aiGenCount || 0} 次</strong></div>
        </div>
      </div>
      <div class="workbench-side">
        <div class="todo-panel-title">待完善清单</div>
        <ul class="todo-list">
          <li>结构化资料：${required.filled}/${required.total} 项核心必填已完成，完整度 ${pct}%。</li>
          <li>影像资料：${mediaPct.filled}/${mediaPct.total} 个标准槽位已上传，上传进度 ${mediaPct.pct}%。</li>
          ${missingHtml}
          ${moreMissing}
        </ul>
      </div>
      <div class="workbench-side">
        <div class="todo-panel-title">智能质控与 AI 建议</div>
        <ul class="quality-list">${renderQualityChecks(checks)}</ul>
        <div class="ai-advisor-title">AI 反向补全建议</div>
        <ul class="todo-list">${renderAISuggestions(aiSuggestions)}</ul>
      </div>
    </div>
    ${renderEvidenceChain(c, mod)}
    <div class="case-action-strip">
      <button class="case-action-btn" data-action="toggle-section" data-section-id="media_media">进入影像资料上传</button>
      <button class="case-action-btn" data-action="toggle-section" data-section-id="workflow">填写流程与审核</button>
      <button class="case-action-btn" data-action="open-scan-modal" data-case-id="${escapeHtml(c.id)}">拍照扫描自动录入</button>
      <button class="case-action-btn" data-action="generate-ai" data-case-id="${escapeHtml(c.id)}" data-ai-type="discharge_summary">生成术前小结</button>
      <button class="case-action-btn" data-action="export-anonymized-case" data-case-id="${escapeHtml(c.id)}">导出匿名化病例包</button>
      <button class="case-action-btn" data-action="save-case" data-case-id="${escapeHtml(c.id)}">保存当前病例</button>
    </div>
  `;
}

function renderEntryHeader(c, mod, pct, mediaPct) {
  const stage = getCaseStageSummary(c);
  const name = displayPatientName(c);
  const revealHint = isPiiRevealed() ? '点击隐藏患者信息' : '点击显示患者姓名';
  return `
    <button class="entry-back-btn" data-action="go-back" aria-label="返回病例总览" title="返回病例总览">
      <span class="entry-back-arrow" aria-hidden="true">‹</span> 返回总览
    </button>
    <div class="entry-header">
      <div class="entry-title-block">
        <div class="entry-eyebrow">${mod.icon} ${mod.name}</div>
        <div class="entry-title pii-reveal-title" data-action="toggle-pii-reveal" title="${revealHint}">${escapeHtml(name)}<span class="pii-eye" aria-hidden="true">${isPiiRevealed() ? ' 🙈' : ' 👁'}</span></div>
        <div class="entry-subtitle">病例ID: ${escapeHtml(c.id)} · 创建于 ${escapeHtml(c.createdAt || '')}</div>
      </div>
      <div class="entry-header-actions">
        <span class="autosave-indicator" id="autoSaveIndicator">已自动保存</span>
        <button class="btn btn-ghost" data-action="open-scan-modal" data-case-id="${escapeHtml(c.id)}">📷 拍照扫描录入</button>
        <button class="btn btn-ghost" data-action="prompt-change-case-module" data-case-id="${escapeHtml(c.id)}" title="修正病种诊断分类（已填资料与影像保留）">🔖 更改病种</button>
        <button class="btn btn-ghost" data-action="export-anonymized-case" data-case-id="${escapeHtml(c.id)}">匿名化导出</button>
        <button class="btn btn-ghost" data-action="window-print" title="打印当前病例">打印</button>
        <button class="btn btn-ghost" data-action="delete-case" data-case-id="${escapeHtml(c.id)}">删除</button>
        <button class="btn btn-primary" data-action="save-case" data-case-id="${escapeHtml(c.id)}">保存</button>
      </div>
    </div>
    <div class="entry-summary-grid">
      <div class="entry-summary-card"><span>资料完整度</span><strong id="progressPct">${pct}%</strong><div class="mini-progress"><i id="progressFill" data-progress-width="${pct}"></i></div></div>
      <div class="entry-summary-card"><span>影像上传</span><strong id="mediaProgressPct">${mediaPct.pct}%</strong><div class="mini-progress"><i id="mediaProgressFill" data-progress-width="${mediaPct.pct}"></i></div></div>
      <div class="entry-summary-card"><span>病例阶段</span><strong>${escapeHtml(stage.stage)}</strong><small>${escapeHtml(stage.status)}</small></div>
      <div class="entry-summary-card"><span>审核状态</span><strong>${escapeHtml(stage.review)}</strong><small>${escapeHtml(stage.reviewer)}</small></div>
    </div>
    ${renderPhaseProgressStrip(c, mod)}
  `;
}

function renderEntryNav(c, mod, renderedSections) {
  return `
    <aside class="entry-nav-panel">
      <div class="entry-panel-title">录入步骤</div>
      ${renderPhaseTabs(c)}
      <div class="entry-section-nav">
        ${renderedSections.map(section => `<button data-action="focus-form-section" data-target-section="${escapeHtml(section.type === 'media' ? `media_${section.id}` : section.id)}">
          <span>${section.icon || '•'}</span><strong>${section.title}</strong>
        </button>`).join('')}
      </div>
      ${renderCaseFolderTools(c, mod)}
    </aside>
  `;
}

function renderEntryAssistant(c, mod, pct, mediaPct) {
  const required = getRequiredFieldStatus(c, mod);
  const checks = getQualityChecks(c, mod, required, mediaPct);
  const aiSuggestions = getAISuggestions(c, mod, required, mediaPct);
  const missingItems = required.missing.length
    ? required.missing.slice(0, 6).map(item => `<li>${escapeHtml(item.section)}：${escapeHtml(item.label)}</li>`).join('')
    : '<li>核心必填资料已完成。</li>';
  const more = required.missing.length > 6 ? `<li>另有 ${required.missing.length - 6} 项待补。</li>` : '';
  // 移动端单列堆叠时辅助卡片默认收起（审核面板除外），桌面侧栏保持展开。
  const isNarrow = typeof window !== 'undefined' && window.innerWidth <= 900;
  const open = isNarrow ? '' : ' open';
  const card = (title, inner, badge = '') => `
      <details class="entry-panel-card entry-card-collapsible"${open}>
        <summary class="entry-panel-title">${title}${badge}</summary>
        ${inner}
      </details>`;
  const todoBadge = required.missing.length
    ? ` <span class="section-progress warn">缺 ${required.missing.length}</span>`
    : ' <span class="section-progress done">✓</span>';
  return `
    <aside class="entry-assistant-panel">
      ${card('待办与质控', `
        <div class="auto-status-note">阶段状态由核心字段、影像完整度和审核状态自动识别；审核角色来自当前登录账号身份。</div>
        <ul class="todo-list">
          <li>结构化资料：${required.filled}/${required.total} 项核心必填，完整度 ${pct}%。</li>
          <li>影像资料：${mediaPct.filled}/${mediaPct.total} 个标准槽位，上传进度 ${mediaPct.pct}%。</li>
          ${missingItems}
          ${more}
        </ul>
        ${required.missing.length ? `
          <button class="case-action-btn" data-action="focus-missing-field" data-case-id="${escapeHtml(c.id)}" data-mode="first">定位第一个待补字段</button>
          <button class="case-action-btn" data-action="focus-missing-field" data-case-id="${escapeHtml(c.id)}" data-mode="next">下一项待补</button>
        ` : ''}
        <ul class="quality-list">${renderQualityChecks(checks)}</ul>`, todoBadge)}
      ${card('影像证据链', renderEvidenceChain(c, mod))}
      ${renderReviewActionPanel(c)}
      ${card('快捷操作', `
        <div class="entry-action-grid">
          <button class="case-action-btn" data-action="open-scan-modal" data-case-id="${escapeHtml(c.id)}">拍照扫描录入</button>
          <button class="case-action-btn" data-action="focus-form-section" data-target-section="media_media">上传影像资料</button>
          <button class="case-action-btn" data-action="focus-form-section" data-target-section="workflow">填写审核流程</button>
          <button class="case-action-btn" data-action="generate-ai" data-case-id="${escapeHtml(c.id)}" data-ai-type="discharge_summary">生成术前小结</button>
          <button class="case-action-btn" data-action="save-case" data-case-id="${escapeHtml(c.id)}">保存当前病例</button>
        </div>`)}
      ${card('AI 补全建议', `<ul class="todo-list">${renderAISuggestions(aiSuggestions)}</ul>`)}
      ${card('病例时间线', renderCaseTimeline(c, mod))}
    </aside>
  `;
}

function renderReviewActionPanel(c) {
  const stage = getCaseStageSummary(c);
  const rejectReason = c.data?.workflow_reject_reason || '';
  const user = getAuthUser() || {};
  const allActionsByStatus = {
    '未填写': [{ label: '提交审核', status: '待审核', tone: 'primary', roles: ['self'] }],
    '驳回修改': [{ label: '重新提交审核', status: '待审核', tone: 'primary', roles: ['self'] }],
    '待审核': [
      { label: '开始审核', status: '审核中', tone: 'primary', roles: ['attending+'] },
      { label: '驳回补充', status: '驳回修改', tone: 'danger', roles: ['attending+'] }
    ],
    '审核中': [
      { label: '审核通过', status: '审核通过', tone: 'success', roles: ['chief+'] },
      { label: '驳回补充', status: '驳回修改', tone: 'danger', roles: ['attending+'] }
    ],
    '审核通过': [{ label: '退回重新审核', status: '审核中', tone: 'warning', roles: ['chief+', 'admin'] }]
  };

  // 临床职称等级
  const titleRank = { resident: 1, fellow: 1, attending: 2, secondline: 3, chief: 4 };
  const userRank = titleRank[user.clinical_title] || 0;
  const isAdmin = user.role === 'admin';
  const isSelf = String(user.id) === String(c.created_by) || String(user.id) === String(c.assigned_to);

  function userCan(actionRoles) {
    if (isAdmin) return true;
    if (actionRoles.includes('self') && isSelf) return true;
    if (actionRoles.includes('attending+') && userRank >= titleRank.attending) return true;
    if (actionRoles.includes('chief+') && userRank >= titleRank.chief) return true;
    if (actionRoles.includes('admin')) return false;  // 仅 admin 已在上面处理
    return false;
  }

  const actions = (allActionsByStatus[stage.review] || []).filter(a => userCan(a.roles));
  return `
    <div class="entry-panel-card review-action-panel">
      <div class="entry-panel-title">审核动作</div>
      <div class="review-state-box">
        <span>当前状态</span>
        <strong>${escapeHtml(stage.review)}</strong>
        <small>当前账号身份：${escapeHtml(stage.reviewer)}</small>
      </div>
      ${rejectReason ? `<div class="review-reason">驳回原因：${escapeHtml(rejectReason)}</div>` : ''}
      <div class="review-action-grid">
        ${actions.length
          ? actions.map(action => `<button class="review-action-btn ${action.tone}" data-action="set-review-status" data-case-id="${escapeHtml(c.id)}" data-review-status="${escapeHtml(action.status)}">${action.label}</button>`).join('')
          : `<div class="task-empty">当前账号（临床职称：${escapeHtml(user.clinical_title || '未设置')}）暂无可执行的审核动作</div>`}
      </div>
    </div>
  `;
}

function focusFormSection(id) {
  const body = document.getElementById(`sbody-${id}`);
  if (body && !body.classList.contains('open')) toggleSection(id);
  const block = body?.closest('.section-block') || body;
  block?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

let _lastFocusedMissingKey = '';
function focusMissingField(caseId, mode = 'first') {
  const c = loadDB().find(x => x.id === caseId);
  if (!c) return;
  if (currentCaseId !== caseId) {
    openCase(caseId).then(() => setTimeout(() => focusMissingField(caseId, mode), 180));
    return;
  }
  const mod = MODULES[c.module];
  const required = getRequiredFieldStatus(c, mod);
  if (!required.missing.length) {
    showToast('✅', '核心必填项已完成');
    return;
  }
  let targetIndex = 0;
  if (mode === 'next' && _lastFocusedMissingKey) {
    const currentIndex = required.missing.findIndex(item => item.key === _lastFocusedMissingKey);
    targetIndex = currentIndex >= 0 ? Math.min(currentIndex + 1, required.missing.length - 1) : 0;
  }
  const first = required.missing[targetIndex] || required.missing[0];
  _lastFocusedMissingKey = first.key;
  if (typeof markRequiredFieldErrors === 'function') markRequiredFieldErrors(required.missing);
  markActiveRequiredField(first.key);
  const input = document.getElementById(`f_${first.key}`);
  const sectionId = first.key.split('_')[0];
  focusFormSection(sectionId);
  setTimeout(() => {
    input?.focus?.();
    input?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, 120);
  showToast('🧭', `已定位到待补项：${first.label}`);
}

function updateStats() {
  const cases = getVisibleCases();
  document.getElementById('stat-total').textContent = cases.length;
  document.getElementById('stat-complete').textContent = cases.filter(c => calcOverallProgress(c) === 100).length;
  document.getElementById('stat-pending').textContent = cases.filter(c => calcOverallProgress(c) < 100).length;
  document.getElementById('stat-ai').textContent = getAICount();
}

function renderDashboard(filter = currentModuleFilter || 'all') {
  const cases = getVisibleCases();
  const grid = document.getElementById('dashGrid');
  currentModuleFilter = filter;
  const sortEl = document.getElementById('caseSortMode');
  if (sortEl) sortEl.value = currentSortMode;
  const searchEl = document.getElementById('caseSearchInput');
  if (searchEl && searchEl.value !== (currentDashboardSearch || '')) searchEl.value = currentDashboardSearch || '';
  renderWorkbenchQueues(cases);
  renderWorkScopeChips(cases);
  renderCenterFilterChips();
  renderCaseFacetChips(cases);
  renderModuleFilterBar(cases);
  renderFolderBoard(cases);
  renderTaskBoard(cases);
  renderAdmissionTaskBoard();
  updateTaskPanelVisibility(cases);
  const filtered = cases.filter(c => caseMatchesDashboardFilters(c, filter));
  const sorted = sortCasesForDashboard(filtered);

  if (sorted.length === 0) {
    grid.innerHTML = `<div class="dash-empty-state">
      <div class="dash-empty-icon">📋</div>
      <div class="dash-empty-title">当前筛选下暂无病例</div>
      <button class="btn btn-primary dash-empty-action" data-action="show-new-case">＋ 新建病例</button>
    </div>`;
    return;
  }

  grid.innerHTML = sorted.map(c => {
    const mod = MODULES[c.module];
    const pct = calcOverallProgress(c);
    const required = getRequiredFieldStatus(c, mod);
    const name = displayPatientName(c);
    const stage = getCaseStageSummary(c);
    const nextAction = getCaseNextAction(c, mod);
    const tags = getCaseTags(c);
    const safeId = escapeJsArg(c.id);
    return `<div class="dash-card" data-case-id="${safeId}" data-action="open-case"
      data-action-contextmenu="case-card"
      data-action-touchstart="case-card"
      data-action-touchend="case-card" data-action-touchmove="case-card">
      <div class="dash-card-header">
        <div>
          <div class="dash-card-name">${c.favorite ? '★ ' : ''}${escapeHtml(name)}</div>
          <div class="dash-card-module">${mod?.icon || ''} ${mod?.name || c.module}</div>
        </div>
        <div class="dash-card-date">${c.createdAt || ''}</div>
        <button class="case-card-menu-btn" aria-haspopup="menu" aria-label="病例快捷操作"
          data-action="case-card-menu" data-case-id="${safeId}">⋯</button>
      </div>
      <div class="dash-card-progress-bar"><div class="dash-card-progress-fill" data-progress-width="${pct}"></div></div>
      <div class="dash-card-progress-text ${pct === 100 ? 'complete' : 'pending'}">
        ${pct === 100 ? '✓ 资料完整' : `⟳ ${pct}% 完整`}
      </div>
      <div class="dash-card-next tone-${nextAction.tone}">
        <span>下一步</span><strong>${escapeHtml(nextAction.label)}</strong>
      </div>
      ${required.missing.length ? `<button class="case-card-inline-action" data-action="focus-missing-stop" data-case-id="${safeId}">补齐 ${required.missing.length} 项缺失</button>` : ''}
      <div class="dash-card-footer">
        <span class="case-tag tag-${c.module}">${mod?.name?.split(' ')[0] || c.module}</span>
        <span class="case-tag case-tag-muted">${stage.stage}</span>
        ${renderCaseSignalTags(c)}
        ${c.data?.basic_side || c.data?.side ? `<span class="case-tag case-tag-muted">${escapeHtml(c.data.basic_side || c.data.side || '')}</span>` : ''}
        ${c.center_id || c.centerId ? `<span class="case-tag case-tag-muted">${escapeHtml(getCenterName(c.center_id || c.centerId))}</span>` : ''}
        ${tags.slice(0, 2).map(tag => `<span class="case-tag case-tag-muted">${escapeHtml(tag)}</span>`).join('')}
        ${c.aiGenCount ? `<span class="case-tag case-tag-ai">AI×${c.aiGenCount}</span>` : ''}
      </div>
    </div>`;
  }).join('');
  hydrateProgressWidths(grid);
}

function setCaseSortMode(mode) {
  currentSortMode = mode || 'updated_desc';
  renderDashboard(currentModuleFilter || 'all');
}

function caseTimeValue(c, field) {
  const raw = c[field] || c.updated_at || c.updatedAt || c.cloudSyncedAt || c.createdAt || '';
  const t = Date.parse(raw);
  return Number.isFinite(t) ? t : 0;
}

function sortCasesForDashboard(items) {
  const copy = [...items];
  copy.sort((a, b) => {
    if (currentSortMode === 'created_desc') return caseTimeValue(b, 'createdAt') - caseTimeValue(a, 'createdAt');
    if (currentSortMode === 'progress_asc') return calcOverallProgress(a) - calcOverallProgress(b) || caseTimeValue(b, 'cloudSyncedAt') - caseTimeValue(a, 'cloudSyncedAt');
    if (currentSortMode === 'progress_desc') return calcOverallProgress(b) - calcOverallProgress(a) || caseTimeValue(b, 'cloudSyncedAt') - caseTimeValue(a, 'cloudSyncedAt');
    return caseTimeValue(b, 'cloudSyncedAt') - caseTimeValue(a, 'cloudSyncedAt') || caseTimeValue(b, 'createdAt') - caseTimeValue(a, 'createdAt');
  });
  return copy;
}

function setCenterFilter(filter) {
  currentCenterFilter = filter || 'all';
  renderSidebar();
  renderDashboard(currentModuleFilter || 'all');
}

function renderCenterFilterChips() {
  const wrap = document.getElementById('centerChips');
  if (!wrap) return;
  const centers = getCachedCenters();
  const show = isCloudMode() && isLoggedIn() && centers.length > 0;
  if (!show) {
    wrap.hidden = true;
    wrap.innerHTML = '';
    currentCenterFilter = 'all';
    return;
  }
  wrap.hidden = false;
  const chips = [
    `<span class="filter-cat-label">中心</span>`,
    `<button class="filter-chip${currentCenterFilter === 'all' ? ' active' : ''}" data-action="set-center-filter" data-center-key="all">全部中心</button>`,
    `<button class="filter-chip${currentCenterFilter === 'none' ? ' active' : ''}" data-action="set-center-filter" data-center-key="none">未归属</button>`,
    ...centers.map(c => {
      const key = `center:${c.id}`;
      return `<button class="filter-chip${currentCenterFilter === key ? ' active' : ''}" data-action="set-center-filter" data-center-key="${escapeHtml(key)}">${escapeHtml(c.name)}</button>`;
    }),
  ];
  wrap.innerHTML = chips.join('');
}

function renderCaseForm(caseId, { preserveScroll = false } = {}) {
  const cases = getVisibleCases();
  const c = cases.find(x => x.id === caseId);
  if (!c) return;

  const mod = MODULES[c.module];
  const pct = calcProgress(c);
  const mediaPct = calcMediaProgress(c, mod);
  const page = document.getElementById('page-form');

  // 滚动容器是 #page-form 的就近可滚动祖先 <main class="main">（overflow-y:auto，
  // 高度受 calc(100vh-60px) 约束）。整页 innerHTML 重建会把它重置到顶部 → 闪屏。
  // 同阶段字段联动重建（comorbidity / deformity）需保留滚动位置；切阶段/首次打开回顶。
  const scroller = preserveScroll ? page.closest('.main') : null;
  const savedScrollTop = scroller ? scroller.scrollTop : 0;

  const renderedSections = getSectionsForPhase(c, mod, currentCasePhase);
  let formSectionsHtml = '';
  renderedSections.forEach(sec => {
    if (sec.type === 'media') {
      formSectionsHtml += renderMediaSection(sec, c);
    } else {
      formSectionsHtml += renderFormSection(sec, c);
    }
  });
  if (currentCasePhase === 'followup') formSectionsHtml += renderFollowupPanel(c, mod);

  const html = `
    ${renderEntryHeader(c, mod, pct, mediaPct)}
    <div class="entry-workspace">
      ${renderEntryNav(c, mod, renderedSections)}
      <main class="entry-form-panel">
        <div class="entry-panel-heading">
          <div>
            <strong>结构化录入区</strong>
            <span>当前阶段：${escapeHtml(getCurrentPhaseLabel())}。核心必填、推荐填写和可选补充已分级标记。</span>
          </div>
          <div class="entry-heading-tools">
            <button class="btn btn-ghost" data-action="expand-all-sections">⊞ 全部展开</button>
            <button class="btn btn-ghost" data-action="collapse-all-sections">⊟ 全部收起</button>
          </div>
        </div>
        ${renderPhaseMissingPanel(c, mod)}
        ${formSectionsHtml}
        ${renderAIPanel(c)}
        ${renderPatientMessagePanel(c)}
      </main>
      ${renderEntryAssistant(c, mod, pct, mediaPct)}
    </div>
  `;
  page.innerHTML = html;
  hydrateProgressWidths(page);

  // restore values
  renderedSections.forEach(sec => {
    if (sec.type === 'media') return;
    (sec.fields || []).forEach(f => {
      const key = `${sec.id}_${f.id}`;
      const val = c.data?.[key] || '';
      if (f.type === 'checkboxes') {
        const vals = Array.isArray(val) ? val : [];
        vals.forEach(v => {
          const cb = document.getElementById(`cb_${key}_${v}`);
          if (cb) { cb.checked = true; cb.closest('.checkbox-item')?.classList.add('checked'); }
        });
      } else {
        const el = document.getElementById(`f_${key}`);
        if (el && val) {
          el.value = val;
          if (el.tagName === 'TEXTAREA') autoGrowTextarea(el);
        }
      }
    });
  });

  // 恢复展开状态：本会话记忆 → 第一个有核心缺失的区块 → 第一个区块。
  // （此前固定只开第一个，字段联动重渲染会把医生开着的区块全部收起。）
  const remembered = (typeof _openSectionsFor === 'function') ? _openSectionsFor(caseId) : null;
  const sectionDomId = (sec) => sec.type === 'media' ? `media_${sec.id}` : sec.id;
  if (remembered && remembered.size) {
    [...remembered].forEach(id => toggleSection(id, true));
  } else {
    let target = null;
    try {
      const status = getPhaseRequiredStatus(c, mod, currentCasePhase);
      const firstMissing = (status.missing || [])[0];
      if (firstMissing) {
        target = renderedSections.find(sec => sectionDomId(sec) === firstMissing.sectionId || sec.id === firstMissing.sectionId);
      }
    } catch (_) { /* 统计失败回退第一个 */ }
    const sec = target || renderedSections[0];
    if (sec) toggleSection(sectionDomId(sec), true);
  }
  if (typeof hydrateAuthenticatedMedia === 'function') hydrateAuthenticatedMedia(page);
  if (typeof hydratePatientMessagePanel === 'function') hydratePatientMessagePanel(c);

  // 恢复滚动位置（仅字段联动重建）。rAF 等本帧 reflow/布局完成后再设，避免抖动。
  if (scroller && savedScrollTop) {
    requestAnimationFrame(() => { scroller.scrollTop = savedScrollTop; });
  }
}

// 折叠头部完成度徽标：已填 n/m；有核心必填缺失时红色提示「缺核心 x」。
// 让医生不展开也能判断每个区块还要不要补。
function _sectionProgressBadge(sec, c) {
  const fields = getVisibleFields(sec, c);
  if (!fields.length) return '';
  let filled = 0, coreMissing = 0;
  fields.forEach(f => {
    const v = c.data?.[`${sec.id}_${f.id}`];
    const has = Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null && v !== '';
    if (has) filled++;
    else if (getFieldRequirementLevel(f).key === 'core') coreMissing++;
  });
  if (coreMissing > 0) {
    return `<span class="section-progress warn">已填 ${filled}/${fields.length} · 缺核心 ${coreMissing}</span>`;
  }
  if (filled === fields.length) {
    return `<span class="section-progress done">✓ ${filled}/${fields.length}</span>`;
  }
  return `<span class="section-progress">已填 ${filled}/${fields.length}</span>`;
}

function _mediaProgressBadge(sec, c) {
  const metas = (sec.slots || []).map((slot, i) => getSlotMeta(slot, i)).filter(isMediaSlotVisibleForPhase);
  if (!metas.length) return '';
  const filled = metas.filter(meta => getSlotImages(c, meta.key, meta.legacyKey).length > 0).length;
  if (filled === metas.length) return `<span class="section-progress done">✓ ${filled}/${metas.length}</span>`;
  return `<span class="section-progress">已传 ${filled}/${metas.length}</span>`;
}

function renderFormSection(sec, c) {
  const fieldRows = getVisibleFields(sec, c).map(f => renderField(f, sec.id, c)).join('');
  return `
    <div class="section-block">
      <div class="section-header" data-action="toggle-section" data-section-id="${escapeHtml(sec.id)}">
        <span class="section-icon">${sec.icon}</span>
        <span class="section-title">${sec.title}</span>
        ${_sectionProgressBadge(sec, c)}
        <span class="section-chevron" id="chev-${sec.id}">▼</span>
      </div>
      <div class="section-body" id="sbody-${sec.id}">
        <div class="form-grid">${fieldRows}</div>
        ${sec.id === 'imaging' ? renderFamMeasurementPanel(c) : ''}
      </div>
    </div>
  `;
}

// —— FAM 互联 Phase B/C：影像数据区「FAM 测量结果」卡片 ——
// 仅 A 模块；惰性拉取（点按钮才请求），医生"采纳回填"才写表单字段，绝不自动覆盖。
const FAM_VIEW_LABELS = {
  WB_AP: '负重正位', WB_LAT: '负重侧位',
  POSTOP_HV: '术后拇外翻正位', POSTOP_HV_AP: '术后拇外翻正位', POSTOP_HV_LAT: '术后拇外翻侧位',
};
// 采纳回填映射：FAM 指标 → A 模块影像数据字段（仅数值含义一一对应的三项，
// 籽骨分级等语义需医生判断的不自动回填）。
const FAM_ADOPT_FIELD_MAP = { HVA: 'imaging_hva_xray', IMA: 'imaging_ima_xray', DMAA: 'imaging_dmaa' };

function renderFamMeasurementPanel(c) {
  if (!c || c.module !== 'A') return '';
  return `
    <div class="fam-panel fam-measure-panel" id="fam-measure-panel-${escapeHtml(c.id)}">
      <div class="fam-panel-row">
        <span class="fam-panel-label">📐 FAM 测量结果</span>
        <button class="btn btn-secondary fam-btn-sm" data-action="fam-load-measurements" data-case-id="${escapeHtml(c.id)}">查看测量结果</button>
        <span class="fam-panel-hint">FAM 平台审核通过的测量自动回传到这里，采纳后回填上方字段</span>
      </div>
      <div id="fam-measure-list-${escapeHtml(c.id)}"></div>
    </div>`;
}

async function famLoadMeasurements(caseId) {
  const box = document.getElementById(`fam-measure-list-${caseId}`);
  if (!box) return;
  box.innerHTML = '<div class="fam-measure-status">加载中…</div>';
  try {
    const backendId = await _pmResolveBackendId(caseId);
    const res = await backendRequest(`/api/cases/${backendId}/fam-measurements`);
    const items = res.items || [];
    if (!items.length) {
      box.innerHTML = '<div class="fam-measure-status">暂无回传记录。先在影像资料区同步 X 线，待 FAM 平台标注并审核通过后这里会出现测量结果。</div>';
      return;
    }
    window._famMeasureCache = window._famMeasureCache || {};
    window._famMeasureCache[caseId] = items;
    box.innerHTML = items.map(item => {
      const label = FAM_VIEW_LABELS[item.view_type] || item.view_type;
      const metricRows = Object.entries(item.metrics || {})
        .filter(([, v]) => typeof v === 'number' || typeof v === 'string')
        .map(([k, v]) => `<span class="fam-metric"><b>${escapeHtml(k)}</b> ${escapeHtml(String(v))}</span>`)
        .join('');
      const adoptable = Object.keys(item.metrics || {}).some(k => FAM_ADOPT_FIELD_MAP[k] !== undefined);
      const adopted = item.adopted_at ? `<span class="fam-measure-adopted">✓ 已采纳 ${escapeHtml((item.adopted_at || '').slice(0, 10))}</span>` : '';
      const adoptBtn = adoptable && !item.adopted_at
        ? `<button class="btn btn-secondary fam-adopt-btn" data-action="fam-adopt-measurement" data-case-id="${escapeHtml(caseId)}" data-measurement-id="${item.id}">采纳回填 HVA/IMA/DMAA</button>`
        : adopted;
      return `<div class="fam-measure-item">
        <div class="fam-measure-head">
          <b>${escapeHtml(label)}</b>
          <span class="fam-measure-meta">${escapeHtml(item.timepoint || '')} ${escapeHtml(item.side || '')} · FAM 审核 ${escapeHtml((item.reviewed_at || '').slice(0, 10))}</span>
          ${adoptBtn}
        </div>
        <div class="fam-metric-row">${metricRows || '<span class="fam-panel-hint">（无数值指标）</span>'}</div>
      </div>`;
    }).join('');
  } catch (e) {
    box.innerHTML = `<div class="fam-measure-status error">${escapeHtml(e?.status === 503 ? 'FAM 互联尚未在服务器启用' : (e?.message || '加载失败'))}</div>`;
  }
}

async function famAdoptMeasurement(caseId, measurementId) {
  const items = (window._famMeasureCache || {})[caseId] || [];
  const item = items.find(x => String(x.id) === String(measurementId));
  if (!item) { showToast('ℹ️', '请先点「查看测量结果」刷新列表'); return; }
  const fills = Object.entries(item.metrics || {})
    .filter(([k, v]) => FAM_ADOPT_FIELD_MAP[k] && (typeof v === 'number' || (typeof v === 'string' && v !== '')))
    .map(([k, v]) => ({ key: FAM_ADOPT_FIELD_MAP[k], metric: k, value: v }));
  if (!fills.length) { showToast('ℹ️', '该记录没有可回填的 HVA/IMA/DMAA 数值'); return; }
  const summary = fills.map(f => `${f.metric}=${f.value}`).join('，');
  const okAdopt = await showAppConfirm({ title: '采纳 FAM 测量值', message: `将以下测量值回填到影像数据字段（覆盖现有值）：\n${summary}`, confirmText: '采纳回填' });
  if (!okAdopt) return;
  try {
    const backendId = await _pmResolveBackendId(caseId);
    await backendRequest(`/api/cases/${backendId}/fam-measurements/${measurementId}/adopt`, { method: 'POST' });
    // 写表单字段并走正常自动保存链路（autoSaveField），医生仍可改后再保存
    let applied = 0;
    fills.forEach(f => {
      const input = document.querySelector(`input[data-field-key="${f.key}"]`);
      if (input) { input.value = f.value; applied++; }
      if (typeof autoSaveField === 'function') autoSaveField(f.key, String(f.value));
    });
    showToast('✅', `已采纳回填 ${applied} 项（${summary}），请确认后保存病例`);
    famLoadMeasurements(caseId);
  } catch (e) {
    showToast('❌', `采纳失败：${e?.message || '请稍后重试'}`);
  }
}

function renderField(f, secId, c) {
  const key = `${secId}_${f.id}`;
  const full = f.full ? 'form-full' : '';
  const level = getFieldRequirementLevel(f);
  const req = `<span class="field-level level-${level.key}">${level.label}</span>`;

  if (f.type === 'checkboxes') {
    const opts = f.opts.map(o => `
      <label class="checkbox-item" id="cbl_${key}_${o}">
        <input type="checkbox" id="cb_${key}_${o}" data-action-change="handle-checkbox" data-field-key="${escapeHtml(key)}" data-opt="${escapeHtml(o)}"> ${o}
      </label>`).join('');
    return `<div class="field-group form-full">
      <label class="field-label">${f.label} ${req}</label>
      <div class="checkbox-grid">${opts}</div>
    </div>`;
  }

  if (f.type === 'textarea') {
    return `<div class="field-group ${full}">
      <label class="field-label">${f.label} ${req}</label>
      <textarea class="field-input" id="f_${key}" rows="3" placeholder="${f.placeholder||''}" autocomplete="off" data-action-input="autosave-field-grow" data-field-key="${escapeHtml(key)}">${''}</textarea>
    </div>`;
  }

  if (f.type === 'select') {
    const opts = (f.opts||[]).map(o=>`<option value="${o}">${o}</option>`).join('');
    return `<div class="field-group ${full}">
      <label class="field-label">${f.label} ${req}</label>
      <select class="field-input" id="f_${key}" data-action-change="autosave-field-change" data-field-key="${escapeHtml(key)}">
        <option value="">请选择</option>${opts}
      </select>
    </div>`;
  }

  const unit = f.unit ? `<span class="field-unit">${f.unit}</span>` : '';
  const inputAttrs = getFieldInputAttrs(f);
  return `<div class="field-group ${full}">
    <label class="field-label">${f.label} ${req}</label>
    <div class="field-input-wrap">
      <input class="field-input${f.unit ? ' has-unit' : ''}" id="f_${key}" type="${f.type||'text'}" placeholder="${f.placeholder||''}"
        ${inputAttrs}
        data-action-input="autosave-field" data-field-key="${escapeHtml(key)}" />
      ${unit}
    </div>
  </div>`;
}

function getFieldInputAttrs(f) {
  const attrs = ['autocomplete="off"'];
  if (f.type === 'number') {
    attrs.push('inputmode="decimal"', 'step="any"');
    if (/age/i.test(f.id || '')) attrs.push('min="0"', 'max="120"');
  }
  if (f.type === 'date') attrs.push('inputmode="numeric"');
  return attrs.join(' ');
}

function autoGrowTextarea(el) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = Math.min(el.scrollHeight, 220) + 'px';
}

function renderMediaSection(sec, c) {
  const slots = sec.slots || [];
  const grouped = {};

  slots.forEach((slot, i) => {
    const meta = getSlotMeta(slot, i);
    if (!isMediaSlotVisibleForPhase(meta)) return;
    const phaseKey = meta.phase;
    const groupKey = meta.group;
    if (!grouped[phaseKey]) grouped[phaseKey] = {};
    if (!grouped[phaseKey][groupKey]) grouped[phaseKey][groupKey] = [];
    grouped[phaseKey][groupKey].push(meta);
  });

  const phaseHtml = Object.entries(grouped).map(([phase, groups]) => {
    const groupHtml = Object.entries(groups).map(([group, metas]) => {
      const slotHtml = metas.map((meta) => {
        const images = getSlotImages(c, meta.key, meta.legacyKey);
        // 已有图片的缩略卡片（每张都有独立删除按钮）
        const thumbsHtml = images.map((img, idx) => `
          <div class="image-thumb" id="thumb_${meta.key}_${idx}">
            ${renderMediaPreview(img, meta.label)}
            <div class="img-label">${meta.label}${images.length > 1 ? `<span class="img-count">${idx + 1}/${images.length}</span>` : ''}</div>
            <button class="img-move" draggable="true" title="拖动或点按移动到其它槽位"
              data-action-dragstart="thumb-drag"
              data-action="show-move-slot-picker"
              data-case-id="${escapeHtml(c.id)}" data-media-key="${escapeHtml(meta.key)}" data-media-idx="${idx}">⇄</button>
            <button class="img-remove" data-action="remove-media" data-case-id="${escapeHtml(c.id)}" data-media-key="${escapeHtml(meta.key)}" data-media-idx="${idx}">✕</button>
          </div>`).join('');
        // "＋添加" 按钮卡片（永远显示在已有图片后面）
        const addBtnHtml = `
          <label class="image-thumb image-thumb-add" for="upinput_${escapeHtml(meta.key)}">
            <div class="upload-add-content">
              <span class="upload-add-icon">＋</span>
              <span class="upload-add-label">${images.length ? '再添一张' : meta.label}</span>
            </div>
            <input class="visually-hidden-file" type="file" id="upinput_${escapeHtml(meta.key)}" accept="${escapeHtml(meta.accept)}" multiple
              data-action-change="handle-upload" data-case-id="${escapeHtml(c.id)}" data-media-key="${escapeHtml(meta.key)}" data-media-label="${escapeHtml(meta.label)}">
          </label>`;
        return `<div class="media-slot-group" id="slot_${meta.key}"
          data-action-dragover="slot-drag-over" data-action-dragleave="slot-drag-leave" data-action-drop="slot-drop"
          data-case-id="${escapeHtml(c.id)}" data-media-key="${escapeHtml(meta.key)}">${thumbsHtml}${addBtnHtml}</div>`;
      }).join('');
      return `<div>
        <div class="media-group-title">${group}</div>
        <div class="image-grid">${slotHtml}</div>
      </div>`;
    }).join('');

    return `<div>
      <div class="media-phase-title">${phase}</div>
      ${groupHtml}
    </div>`;
  }).join('');

  return `
    <div class="section-block">
      <div class="section-header" data-action="toggle-section" data-section-id="media_${escapeHtml(sec.id)}">
        <span class="section-icon">${sec.icon}</span>
        <span class="section-title">${sec.title}</span>
        ${_mediaProgressBadge(sec, c)}
        <span class="section-chevron" id="chev-media_${sec.id}">▼</span>
      </div>
      <div class="section-body" id="sbody-media_${sec.id}">
        <p class="media-phase-hint">当前显示：${getCurrentPhaseLabel()}。按时间线补齐影像证据链，后续 AI 文书会引用这些资料。</p>
        ${renderFamSyncBar(c)}
        ${phaseHtml}
      </div>
    </div>
  `;
}

// —— FAM(FootAutoMeasure) 互联 Phase A：拇外翻 X 线手动同步 ——
// 仅 A 模块显示；后端未配置互联时点击会得到 503 提示，不影响其它功能。
function renderFamSyncBar(c) {
  if (!c || c.module !== 'A') return '';
  return `
    <div class="fam-panel fam-sync-bar" id="fam-sync-bar-${escapeHtml(c.id)}">
      <span class="fam-panel-label">📐 FAM 测量平台</span>
      <button class="btn btn-secondary fam-btn-sm" data-action="fam-sync" data-case-id="${escapeHtml(c.id)}">同步 X 线到 FAM 测量</button>
      <span id="fam-sync-status-${escapeHtml(c.id)}" class="fam-panel-hint">仅推送负重正/侧位与术后 X 线，去标识化（不含姓名等个人信息）</span>
    </div>`;
}

async function famSyncCase(caseId) {
  const t = showToast('📐', '正在同步 X 线到 FAM 测量平台…', 0);
  try {
    const backendId = await _pmResolveBackendId(caseId);
    const res = await backendRequest(`/api/cases/${backendId}/fam-sync`, { method: 'POST' });
    const parts = [];
    if (res.synced?.length) parts.push(`新同步 ${res.synced.length} 张`);
    const already = (res.skipped || []).filter(s => s.reason === '已同步').length;
    if (already) parts.push(`已同步 ${already} 张`);
    const otherSkip = (res.skipped || []).length - already;
    if (otherSkip > 0) parts.push(`跳过 ${otherSkip} 项`);
    if (res.errors?.length) parts.push(`失败 ${res.errors.length} 张`);
    const summary = parts.length ? parts.join('，') : '无符合条件的 X 线（请先上传负重正/侧位片）';
    const statusEl = document.getElementById(`fam-sync-status-${caseId}`);
    if (statusEl) statusEl.textContent = `FAM 病例 ${res.case_code}：${summary}`;
    showToast(res.errors?.length ? '⚠️' : '✅', `FAM 同步完成：${summary}`);
  } catch (e) {
    if (e?.status === 503) showToast('ℹ️', 'FAM 互联尚未在服务器启用（需配置 FAM_INTEGRATION_URL/TOKEN）');
    else showToast('❌', `FAM 同步失败：${e?.message || '请稍后重试'}`);
  } finally { if (t) t.remove(); }
}

function isMediaSlotVisibleForPhase(meta) {
  if (currentCasePhase === 'preop') return meta.phase === '术前资料';
  if (currentCasePhase === 'intraop') return meta.phase === '术中资料';
  if (currentCasePhase === 'postop') return meta.phase === '术后资料';
  if (currentCasePhase === 'followup') return meta.phase === '术后资料';
  return true;
}

function getCurrentPhaseLabel() {
  return CASE_PHASES.find(phase => phase.id === currentCasePhase)?.label || '全部资料';
}

function renderMediaPreview(existing, fallbackLabel) {
  const isVideo = existing?.mime?.startsWith('video/') || /\.(mp4|mov|avi|m4v|webm)$/i.test(existing?.filename || '');
  const isHeic = /image\/hei[cf]/i.test(existing?.mime || '') || /\.(heic|heif)$/i.test(existing?.filename || '');
  const src = mediaPreviewSrc(existing?.data || '');
  const authAttrs = mediaAuthAttrs(existing?.data || '', fallbackLabel || '', isVideo);
  if (isHeic) {
    const safe = (src || '').replace(/"/g, '&quot;');
    return `<div class="thumb-heic-ph"${authAttrs} data-action="open-window" data-url="${safe}" title="HEIC 原图，点击下载查看"><span>HEIC</span><small>已上传·点击下载原图</small></div>`;
  }
  if (isVideo) {
    const safe = (src || '').replace(/"/g, '&quot;');
    const label = (fallbackLabel || '').replace(/"/g, '&quot;');
    return `<video class="media-preview-video" src="${safe}"${authAttrs} controls title="${label}"
              data-action="open-lightbox" data-lightbox-label="${label}" data-lightbox-video="1"></video>`;
  }
  const safe = (src || '').replace(/"/g, '&quot;');
  const label = (fallbackLabel || '').replace(/"/g, '&quot;');
  return `<img src="${safe}"${authAttrs} alt="${label}" loading="lazy" decoding="async" data-action-error="mark-media-error" data-action="open-lightbox" data-lightbox-label="${label}">`;
}

function renderAIPanel(c) {
  const hasKey = !!getAPIKey();
  const hasBackend = isCloudMode();
  const provider = getAIProvider();
  const model = getAIModel();
  const providerOptions = ['deepseek', 'anthropic', 'openai', 'glm', 'gemini', 'banana', 'compatible']
    .map(value => `<option value="${value}" ${provider === value ? 'selected' : ''}>${getProviderLabel(value)}</option>`)
    .join('');
  // 云端模式：模型留空 = 「🤖 自动（按文书类型智能选模型）」，由后端按 document_type 分层。
  // 把"自动"作为置顶选项，并将输入框默认值置空，使分层默认生效。
  // 本地演示模式无后端分层，回退到具体模型名（getAIModel）。
  const autoOption = hasBackend
    ? `<option value="">🤖 自动（按文书类型智能选模型）</option>`
    : '';
  const modelDefault = hasBackend ? '' : model;
  const modelPlaceholder = hasBackend
    ? '留空＝自动按文书类型选模型，或输入/选择具体模型覆盖'
    : '输入或选择模型名';
  const modelOptions = autoOption + getGenerationModelOptions().map(item =>
    `<option value="${escapeHtml(item.model)}">${escapeHtml(getProviderLabel(item.provider))} · ${escapeHtml(item.label || item.model)}</option>`
  ).join('');
  const modelShort = `${getProviderLabel(provider)} · ${model}`;
  const keyStatus = hasBackend
    ? `<span class="ai-key-status ok">✓ 服务端生成已启用</span>`
    : hasKey
    ? `<span class="ai-key-status ok">✓ API Key 已配置</span>`
    : `<span class="ai-key-status warn" data-action="show-settings-modal">⚠ 未配置后端或 API Key — 点击设置</span>`;
  const modelBadge = hasBackend ? `服务端调用 · 自动按文书类型选模型` : modelShort;

  return `
    <div class="ai-panel">
      <div class="ai-panel-header ai-panel-header-toggle" data-action="toggle-section" data-section-id="aipanel">
        <span class="ai-panel-icon">🤖</span>
        <div class="ai-panel-main">
          <div class="ai-panel-title">AI 辅助生成</div>
          <div class="ai-panel-subtitle ai-panel-subtitle-row">
            ${keyStatus}
            <span class="model-badge">⚡ ${modelBadge}</span>
          </div>
        </div>
        <button class="btn btn-ghost ai-settings-btn" data-action="show-settings-modal">⚙️ 设置</button>
        <span class="section-chevron" id="chev-aipanel">▼</span>
      </div>
      <div class="section-body" id="sbody-aipanel">
      <div class="ai-model-selector">
        <label>
          <span>生成平台</span>
          <select class="field-input" id="ai-provider-${c.id}">${providerOptions}</select>
        </label>
        <label>
          <span>生成模型</span>
          <input class="field-input" id="ai-model-${c.id}" list="ai-model-list-${c.id}" value="${escapeHtml(modelDefault)}" placeholder="${escapeHtml(modelPlaceholder)}">
          <datalist id="ai-model-list-${c.id}">${modelOptions}</datalist>
        </label>
      </div>
      <div class="ai-route-note ai-entry-guide">
        <strong>AI 入口导览：</strong>
        <span>① <b>扫描/补全</b>（拍照自动填表·低成本 OCR）</span>
        <span>② <b>文书生成</b>（下方按钮·一键成稿）</span>
        <span>③ <b>对话助手</b>（自由追问·多轮）</span>
        <span>④ <b>导出简报</b>（病例右键菜单·HTML/PDF）</span>
      </div>
      <div class="ai-gen-group-label">📋 病历文书 · 日常临床</div>
      <div class="ai-generate-grid">
        <button class="ai-gen-btn" data-action="generate-ai" data-case-id="${escapeHtml(c.id)}" data-ai-type="discharge_summary">
          <span class="gen-icon">🏥</span>
          生成术前小结
          <span class="gen-label">病历摘要标准格式</span>
        </button>
        <button class="ai-gen-btn" data-action="generate-ai" data-case-id="${escapeHtml(c.id)}" data-ai-type="operative_note">
          <span class="gen-icon">⚕️</span>
          手术记录草稿
          <span class="gen-label">标准手术记录格式</span>
        </button>
        <button class="ai-gen-btn" data-action="generate-ai" data-case-id="${escapeHtml(c.id)}" data-ai-type="consent_form">
          <span class="gen-icon">📝</span>
          知情同意要点
          <span class="gen-label">术前谈话核心要点</span>
        </button>
      </div>
      <div class="ai-gen-group-label">🎓 学术汇报 · 科研讲课</div>
      <div class="ai-generate-grid">
        <button class="ai-gen-btn" data-action="generate-ai" data-case-id="${escapeHtml(c.id)}" data-ai-type="case_report">
          <span class="gen-icon">📄</span>
          生成个案报道
          <span class="gen-label">符合期刊投稿格式</span>
        </button>
        <button class="ai-gen-btn" data-action="generate-ai" data-case-id="${escapeHtml(c.id)}" data-ai-type="html_case_brief">
          <span class="gen-icon">🧾</span>
          HTML病例简报
          <span class="gen-label">可保存/打印的网页简报</span>
        </button>
        <button class="ai-gen-btn" data-action="generate-ai" data-case-id="${escapeHtml(c.id)}" data-ai-type="academic_presentation">
          <span class="gen-icon">🎓</span>
          学术交流稿
          <span class="gen-label">学术会议发言稿</span>
        </button>
        <button class="ai-gen-btn" data-action="generate-ai" data-case-id="${escapeHtml(c.id)}" data-ai-type="wechat_article">
          <span class="gen-icon">📱</span>
          公众号科普文
          <span class="gen-label">面向大众的科普文章（高级模型）</span>
        </button>
        <button class="ai-gen-btn" data-action="generate-ai" data-case-id="${escapeHtml(c.id)}" data-ai-type="case_ppt">
          <span class="gen-icon">📊</span>
          病例简报PPT
          <span class="gen-label">生成可演示的HTML幻灯片（jeniya高级模型）</span>
        </button>
        <button class="ai-gen-btn" data-action="export-case-pptx" data-case-id="${escapeHtml(c.id)}">
          <span class="gen-icon">📥</span>
          导出简报PPT（.pptx）
          <span class="gen-label">真PPT文件 · 影像内嵌离线可看 · 确定性排版零AI</span>
        </button>
        <button class="ai-gen-btn" data-action="export-case-narrative-pptx" data-case-id="${escapeHtml(c.id)}">
          <span class="gen-icon">🤖</span>
          AI 生成汇报型 PPT
          <span class="gen-label">模型聚合病例信息 · 服务端确定性渲染 .pptx</span>
        </button>
        <button class="ai-gen-btn" data-action="export-case-narrative-pptx-expanded" data-case-id="${escapeHtml(c.id)}">
          <span class="gen-icon">🖼️</span>
          AI 汇报型 PPT（全图展开）
          <span class="gen-label">完整影像分页展开 · 适合术中流程复盘</span>
        </button>
      </div>
      <div class="ai-chat-entry">
        <button class="ai-chat-entry-btn" data-action="open-ai-chat" data-case-id="${escapeHtml(c.id)}">
          <span class="ai-chat-entry-icon">💬</span>
          <div class="ai-chat-entry-copy">
            <div class="ai-chat-entry-title">AI 对话助手</div>
            <div class="ai-chat-entry-subtitle">自由描述需求，支持多轮追问</div>
          </div>
          <span class="ai-chat-entry-arrow">›</span>
        </button>
      </div>
      </div>
    </div>
  `;
}

// ===================== 患者通知（出院后注意事项）Phase 1 =====================
// 本地闭环：生成 → 编辑 → 提交审核 → （主治及以上）审核通过/驳回 → 预览患者版 + 可复制短信文案。
// Phase 1 不直接发送短信。后端 API 见 docs/api-contract.md `/api/cases/{id}/patient-message`。
// 草稿内容仅 de-identified 文本；真实姓名仅在 GET /preview 的 greeting 出现。

// 内存缓存当前面板的后端返回，供编辑/提交/审核动作复用（按本地 caseId 索引）。
const _patientMsgState = {};

// 状态机：后端 draft.status ∈ {draft, pending_review, approved}（或 null = 尚无草稿）。
function _pmStatusMeta(status) {
  switch (status) {
    case 'approved':       return { label: '已审核', tone: 'success' };
    case 'pending_review': return { label: '待审核', tone: 'primary' };
    case 'draft':          return { label: '草稿', tone: 'warning' };
    default:               return { label: '未生成', tone: 'muted' };
  }
}

// 当前登录用户是否具备审核权（前端显示用；后端 403 仍是权威）。
// 与 api-contract「Approval authority」一致：admin，或 clinical_title ∈ {attending, secondline, chief}。
function _pmCanApprove(user = getAuthUser()) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return ['attending', 'secondline', 'chief'].includes(user.clinical_title);
}

// 面板入口：同步渲染骨架容器（不阻塞表单），实际状态由 hydratePatientMessagePanel 异步填充。
function renderPatientMessagePanel(c) {
  return `
    <div class="ai-panel patient-msg-panel" id="patient-msg-panel-${escapeHtml(c.id)}" data-case-id="${escapeHtml(c.id)}">
      <div class="ai-panel-header ai-panel-header-toggle" data-action="toggle-section" data-section-id="pmpanel">
        <span class="ai-panel-icon">📨</span>
        <div class="ai-panel-main">
          <div class="ai-panel-title">患者通知 · 出院后注意事项</div>
          <div class="ai-panel-subtitle ai-panel-subtitle-row">
            <span class="model-badge">Phase 1 · 本地闭环，暂不直接发送短信</span>
          </div>
        </div>
        <span class="section-chevron" id="chev-pmpanel">▼</span>
      </div>
      <div class="section-body" id="sbody-pmpanel">
        <div class="patient-msg-body" id="patient-msg-body-${escapeHtml(c.id)}">
          <div class="task-empty">正在加载患者通知状态…</div>
        </div>
      </div>
    </div>
  `;
}

// 拉取后端状态并渲染面板主体。云端未登录/本地模式下提示不可用。
async function hydratePatientMessagePanel(c) {
  const body = document.getElementById(`patient-msg-body-${c.id}`);
  if (!body) return;
  if (!isCloudMode() || !isLoggedIn()) {
    body.innerHTML = `<div class="task-empty">患者通知需登录云端账号后使用。</div>`;
    return;
  }
  let backendId = c.backendId;
  if (!backendId) {
    const persisted = loadDB().find(x => x.id === c.id);
    backendId = persisted?.backendId;
  }
  if (!backendId) {
    body.innerHTML = `<div class="task-empty">请先保存病例（上传影像或保存后会建立云端记录），再使用患者通知。</div>`;
    return;
  }
  try {
    const state = await backendRequest(`/api/cases/${backendId}/patient-message`);
    _patientMsgState[c.id] = { backendId, state };
    _renderPatientMsgBody(c.id);
  } catch (e) {
    body.innerHTML = `<div class="task-empty">加载患者通知失败：${escapeHtml(e.friendly || e.message || '未知错误')}</div>`;
  }
}

// 取当前草稿最新版本内容（de-identified 文本）。
function _pmLatestVersion(state) {
  const versions = Array.isArray(state?.versions) ? state.versions : [];
  if (!versions.length) return null;
  // 按 version_no 取最大；后端 versions 顺序不保证，显式排序。
  return versions.slice().sort((a, b) => (b.version_no || 0) - (a.version_no || 0))[0];
}

// 防御性清洗：去掉模型/历史残留的 Markdown 符号与 keycap emoji 噪声，保留正文与换行。
// 保守原则——只动明确的标记符号，不触碰中文/数字/单位（° % mg 等）。空安全、纯函数。
function _stripMarkdownNoise(text) {
  if (text == null) return '';
  let s = String(text);
  // 统一换行，便于按行处理
  s = s.replace(/\r\n?/g, '\n');
  // keycap 序列（如 #️⃣ = U+0023 U+FE0F U+20E3，其它数字 keycap 同理）：连同其后的单个空格一起删除，避免留下前导空格
  s = s.replace(/[#*0-9]️?⃣[ \t]?/g, '');
  // 行首 Markdown 标题井号：# ~ ######
  s = s.replace(/^[ \t]*#{1,6}[ \t]*/gm, '');
  // 行首引用标记：>
  s = s.replace(/^[ \t]*>[ \t]?/gm, '');
  // 行首无序列表标记：- / * / +（要求后随空白，避免误删正文中的减号/星号）
  s = s.replace(/^[ \t]*[-*+][ \t]+/gm, '');
  // 成对强调标记：**x** / __x__ / *x* / _x_ → x（去标记保留内容，避免跨行贪婪）
  s = s.replace(/\*\*([^*\n]+?)\*\*/g, '$1');
  s = s.replace(/__([^_\n]+?)__/g, '$1');
  s = s.replace(/\*([^*\n]+?)\*/g, '$1');
  s = s.replace(/(^|[\s(])_([^_\n]+?)_(?=[\s).,!?；。，！？]|$)/g, '$1$2');
  // 去掉每行行尾残留空白（保留行首缩进与行间空行结构，避免误删正文格式）
  s = s.replace(/[ \t]+$/gm, '');
  // 折叠 3+ 连续空行为 1 个空行
  s = s.replace(/\n{3,}/g, '\n\n');
  return s;
}

// 将清洗后的纯文本按行写入容器：textContent 逐行 + <br>，绝不 innerHTML 注入原始文本（XSS 安全）。
function _setTextWithLineBreaks(el, text) {
  if (!el) return;
  el.textContent = '';
  const lines = String(text == null ? '' : text).split('\n');
  lines.forEach((line, i) => {
    if (i > 0) el.appendChild(document.createElement('br'));
    el.appendChild(document.createTextNode(line));
  });
}

// 渲染面板主体（状态徽标 + 联系方式/同意 + 文书编辑 + 状态机动作 + 预览）。
function _renderPatientMsgBody(caseId) {
  const entry = _patientMsgState[caseId];
  const body = document.getElementById(`patient-msg-body-${caseId}`);
  if (!entry || !body) return;
  const st = entry.state || {};
  const status = st.status || null;
  const meta = _pmStatusMeta(status);
  const consent = st.consent || {};
  const latest = _pmLatestVersion(st);
  const content = latest ? latest.content : '';
  const canApprove = _pmCanApprove();
  const editable = status !== 'pending_review'; // 待审核时锁定编辑，避免审核中被改

  // —— 联系方式 + 知情同意 ——
  const phoneMasked = consent.phone_masked || '';
  const phonePlaceholder = consent.phone_on_file ? `已存：${escapeHtml(phoneMasked)}（编辑请输入完整手机号）` : '请输入 11 位手机号';
  const consentInfo = consent.granted
    ? `<div class="review-reason">已记录同意${consent.consent_at ? ` · ${escapeHtml(consent.consent_at)}` : ''}${consent.consent_by != null ? ` · 操作者 #${escapeHtml(consent.consent_by)}` : ''}</div>`
    : '';

  // —— 状态机动作按钮 ——
  const actionBtns = [];
  if (status === 'draft' || status === null) {
    actionBtns.push(`<button class="review-action-btn primary" data-action="pm-submit" data-case-id="${escapeHtml(caseId)}">提交审核</button>`);
  } else if (status === 'pending_review') {
    if (canApprove) {
      actionBtns.push(`<button class="review-action-btn success" data-action="pm-approve" data-case-id="${escapeHtml(caseId)}">审核通过</button>`);
      actionBtns.push(`<button class="review-action-btn danger" data-action="pm-reject" data-case-id="${escapeHtml(caseId)}">驳回</button>`);
    } else {
      actionBtns.push(`<button class="review-action-btn" disabled title="需主治及以上审核">审核通过（需主治及以上）</button>`);
    }
  } else if (status === 'approved') {
    actionBtns.push(`<span class="model-badge">已审核 · 可预览并复制文案</span>`);
  }

  const editHint = status === 'approved'
    ? '已审核。如需修改，编辑并保存草稿将生成新版本并退回草稿状态。'
    : status === 'pending_review'
    ? '待审核中，已锁定编辑。审核通过或驳回后可继续修改。'
    : '可编辑后保存草稿；满意后提交审核。';

  body.innerHTML = `
    <div class="entry-panel-card">
      <div class="review-state-box">
        <span>当前状态</span>
        <strong class="case-tag tone-${meta.tone}">${escapeHtml(meta.label)}</strong>
        ${latest ? `<small>版本 v${escapeHtml(latest.version_no)}</small>` : ''}
      </div>

      <div class="entry-panel-title">① 联系方式与知情同意</div>
      <div class="ai-model-selector">
        <label>
          <span>患者手机号</span>
          <input class="field-input" id="pm-phone-${escapeHtml(caseId)}" type="tel" inputmode="numeric"
                 autocomplete="off" placeholder="${phonePlaceholder}">
        </label>
        <label class="patient-msg-consent">
          <input type="checkbox" id="pm-consent-${escapeHtml(caseId)}" ${consent.granted ? 'checked' : ''}>
          <span>患者已同意接收随访/出院提醒</span>
        </label>
      </div>
      ${consentInfo}
      <div class="review-action-grid">
        <button class="case-action-btn" data-action="pm-save-consent" data-case-id="${escapeHtml(caseId)}">保存联系方式</button>
      </div>

      <div class="entry-panel-title">② 出院说明文书</div>
      <div class="auto-status-note">${escapeHtml(editHint)} 内容已脱敏（不含真实姓名/手机号），真实姓名仅在预览的称呼问候中本地填充。</div>
      <div class="review-action-grid">
        <button class="case-action-btn" data-action="pm-generate" data-case-id="${escapeHtml(caseId)}">🤖 生成出院说明</button>
      </div>
      <textarea class="field-input patient-msg-textarea" id="pm-content-${escapeHtml(caseId)}" rows="10"
                ${editable ? '' : 'readonly'} placeholder="点击“生成出院说明”由 AI 起草，或在此手动撰写…"></textarea>
      <div class="review-action-grid">
        <button class="case-action-btn" data-action="pm-save-draft" data-case-id="${escapeHtml(caseId)}" ${editable ? '' : 'disabled'}>保存草稿</button>
        ${actionBtns.join('')}
      </div>

      <div class="entry-panel-title">③ 患者版预览</div>
      <div class="review-action-grid">
        <button class="case-action-btn primary" data-action="pm-preview" data-case-id="${escapeHtml(caseId)}">预览患者版 + 短信文案</button>
      </div>
    </div>
  `;

  // textContent 安全填充（避免把模型输出当 HTML 注入）
  const ta = document.getElementById(`pm-content-${caseId}`);
  if (ta) {
    // 清洗历史/模型残留的 Markdown 噪声后填充（textarea 自身保留换行、可编辑）。
    ta.value = _stripMarkdownNoise(content);
    if (typeof autoGrowTextarea === 'function') autoGrowTextarea(ta);
  }
}

// 解析后端 case 记录 id（已建档才有；与上传影像等共用 ensureBackendCase 去重逻辑）。
async function _pmResolveBackendId(caseId) {
  const entry = _patientMsgState[caseId];
  if (entry?.backendId) return entry.backendId;
  const c = loadDB().find(x => x.id === caseId);
  if (!c) throw new Error('未找到病例');
  if (c.backendId) return c.backendId;
  return await ensureBackendCase(c);
}

// 统一处理后端返回（generate/draft/submit/approve/reject 同形），刷新面板。
function _pmApplyResult(caseId, backendId, state) {
  _patientMsgState[caseId] = { backendId, state };
  _renderPatientMsgBody(caseId);
}

// 友好错误：403 → 无审核权；422 → 手机/参数格式；其余走 backendRequest 的 friendly。
function _pmToastError(e) {
  if (e?.status === 403) { showToast('🔒', '需主治及以上医师审核（你当前无审核权限）'); return; }
  showToast('⚠️', e?.friendly || e?.message || '操作失败，请稍后重试');
}

async function pmSaveConsent(caseId) {
  const phoneEl = document.getElementById(`pm-phone-${caseId}`);
  const consentEl = document.getElementById(`pm-consent-${caseId}`);
  const phone = (phoneEl?.value || '').trim();
  const consent = !!consentEl?.checked;
  if (!consent) { showToast('ℹ️', '请先勾选“患者已同意接收随访/出院提醒”'); return; }
  if (!/^1[3-9]\d{9}$/.test(phone)) { showToast('⚠️', '请输入有效的 11 位手机号'); return; }
  const t = showToast('💾', '正在保存联系方式…', 0);
  try {
    const backendId = await _pmResolveBackendId(caseId);
    const state = await backendRequest(`/api/cases/${backendId}/patient-message/consent`, {
      method: 'PUT',
      body: JSON.stringify({ phone, consent: true }),
    });
    _pmApplyResult(caseId, backendId, state);
    showToast('✅', '联系方式与同意已保存');
  } catch (e) { _pmToastError(e); }
  finally { if (t) t.remove(); }
}

async function pmGenerate(caseId) {
  const t = showToast('🤖', '正在生成出院说明（境内模型，已脱敏）…', 0);
  try {
    const backendId = await _pmResolveBackendId(caseId);
    const state = await backendRequest(`/api/cases/${backendId}/patient-message/generate`, { method: 'POST' });
    _pmApplyResult(caseId, backendId, state);
    showToast('✅', '已生成出院说明草稿，可编辑后提交审核');
  } catch (e) { _pmToastError(e); }
  finally { if (t) t.remove(); }
}

async function pmSaveDraft(caseId) {
  const ta = document.getElementById(`pm-content-${caseId}`);
  const content = (ta?.value || '').trim();
  if (!content) { showToast('ℹ️', '内容为空，请先撰写或生成出院说明'); return; }
  const t = showToast('💾', '正在保存草稿…', 0);
  try {
    const backendId = await _pmResolveBackendId(caseId);
    const state = await backendRequest(`/api/cases/${backendId}/patient-message/draft`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    });
    _pmApplyResult(caseId, backendId, state);
    showToast('✅', '草稿已保存为新版本');
  } catch (e) { _pmToastError(e); }
  finally { if (t) t.remove(); }
}

async function pmSubmit(caseId) {
  const ta = document.getElementById(`pm-content-${caseId}`);
  const content = (ta?.value || '').trim();
  if (!content) { showToast('ℹ️', '内容为空，请先撰写或生成出院说明'); return; }
  const t = showToast('📤', '正在提交审核…', 0);
  try {
    const backendId = await _pmResolveBackendId(caseId);
    // 先保存当前编辑，确保提交的是最新内容，再提交。
    await backendRequest(`/api/cases/${backendId}/patient-message/draft`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    });
    const state = await backendRequest(`/api/cases/${backendId}/patient-message/submit`, { method: 'POST' });
    _pmApplyResult(caseId, backendId, state);
    showToast('✅', '已提交审核，等待主治及以上医师处理');
  } catch (e) { _pmToastError(e); }
  finally { if (t) t.remove(); }
}

async function pmApprove(caseId) {
  const t = showToast('✅', '正在通过审核…', 0);
  try {
    const backendId = await _pmResolveBackendId(caseId);
    const state = await backendRequest(`/api/cases/${backendId}/patient-message/approve`, { method: 'POST' });
    _pmApplyResult(caseId, backendId, state);
    showToast('✅', '审核通过，可预览患者版并复制短信文案');
  } catch (e) { _pmToastError(e); }
  finally { if (t) t.remove(); }
}

async function pmReject(caseId) {
  const raw = await showAppPrompt({ title: '驳回出院说明', message: '请输入驳回意见（可选，≤200 字）', maxLength: 200, confirmText: '确认驳回' });
  if (raw === null) return;
  const note = raw.trim();
  const t = showToast('↩️', '正在驳回…', 0);
  try {
    const backendId = await _pmResolveBackendId(caseId);
    const state = await backendRequest(`/api/cases/${backendId}/patient-message/reject`, {
      method: 'POST',
      body: JSON.stringify({ note: note.slice(0, 200) }),
    });
    _pmApplyResult(caseId, backendId, state);
    showToast('✅', '已驳回，退回草稿状态');
  } catch (e) { _pmToastError(e); }
  finally { if (t) t.remove(); }
}

let _pmPreviewSmsText = '';
async function pmPreview(caseId) {
  const t = showToast('👁', '正在生成患者版预览…', 0);
  try {
    const backendId = await _pmResolveBackendId(caseId);
    const data = await backendRequest(`/api/cases/${backendId}/patient-message/preview`);
    _openPatientMsgPreviewModal(data);
  } catch (e) { _pmToastError(e); }
  finally { if (t) t.remove(); }
}

// 患者版预览弹窗。内容是脱敏文本（非完整 HTML），全部用 textContent / 安全 DOM 渲染，绝不 innerHTML 注入模型输出。
let _pmPreviewKeyHandler = null;
function _openPatientMsgPreviewModal(data) {
  closePatientMsgPreviewModal();
  const page = data?.patient_page || {};
  const isDraft = !!data?.is_draft_preview;
  _pmPreviewSmsText = data?.sms_text || '';

  const ov = document.createElement('div');
  ov.id = 'patientMsgPreviewOverlay';
  ov.className = 'report-preview-overlay';
  // 仅注入固定的、可信的结构骨架；所有患者/模型文本随后用 textContent 填充。
  ov.innerHTML = `
    <div class="report-preview-dialog" role="dialog" aria-modal="true" aria-label="患者版预览">
      <div class="report-preview-bar">
        <span class="report-preview-title">📨 患者版预览 · <span id="pm-preview-status-badge"></span></span>
        <button class="report-preview-close" type="button" aria-label="关闭预览" data-action="close-patient-msg-preview">✕</button>
      </div>
      <div class="patient-msg-preview-scroll">
        <div class="patient-msg-preview-card">
          <div class="patient-msg-preview-greeting" id="pm-preview-greeting"></div>
          <h3 class="patient-msg-preview-h" id="pm-preview-title"></h3>
          <div class="patient-msg-preview-content" id="pm-preview-body" style="white-space:pre-wrap"></div>
        </div>
        <div class="patient-msg-preview-card">
          <div class="entry-panel-title">可复制短信文案</div>
          <div class="auto-status-note">Phase 1 暂不直接发送，仅提供可复制文案；其中 {说明链接} 为占位，Phase 2 接入真实短链与发送。</div>
          <pre class="patient-msg-sms-text" id="pm-preview-sms"></pre>
          <div class="review-action-grid">
            <button class="case-action-btn primary" data-action="copy-patient-msg-sms">📋 一键复制短信文案</button>
          </div>
        </div>
      </div>
    </div>`;
  document.body.appendChild(ov);

  // 安全填充（textContent，不解析 HTML）
  const badge = ov.querySelector('#pm-preview-status-badge');
  if (badge) badge.textContent = isDraft ? '未审核草稿预览' : '已审核';
  const g = ov.querySelector('#pm-preview-greeting');
  if (g) g.textContent = page.greeting || '';
  const ti = ov.querySelector('#pm-preview-title');
  if (ti) ti.textContent = page.title || '出院后注意事项';
  const bodyEl = ov.querySelector('#pm-preview-body');
  // 清洗 Markdown 噪声后逐行 textContent + <br> 渲染：保留换行且 XSS 安全（绝不 innerHTML 注入）。
  // 容器同时设 white-space:pre-wrap 作为双保险，避免浏览器折叠空白。
  if (bodyEl) _setTextWithLineBreaks(bodyEl, _stripMarkdownNoise(page.body || ''));
  const smsEl = ov.querySelector('#pm-preview-sms');
  if (smsEl) smsEl.textContent = _pmPreviewSmsText;

  ov.addEventListener('click', (e) => { if (e.target === ov) closePatientMsgPreviewModal(); });
  _pmPreviewKeyHandler = (e) => { if (e.key === 'Escape') closePatientMsgPreviewModal(); };
  document.addEventListener('keydown', _pmPreviewKeyHandler, true);
}

function closePatientMsgPreviewModal() {
  const ov = document.getElementById('patientMsgPreviewOverlay');
  if (ov) ov.remove();
  if (_pmPreviewKeyHandler) {
    document.removeEventListener('keydown', _pmPreviewKeyHandler, true);
    _pmPreviewKeyHandler = null;
  }
}

function copyPatientMsgSms() {
  const text = _pmPreviewSmsText || '';
  if (!text) { showToast('ℹ️', '暂无可复制文案'); return; }
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text)
      .then(() => showToast('📋', '短信文案已复制'))
      .catch(() => showToast('⚠️', '复制失败，请手动选择文本复制'));
  } else {
    showToast('⚠️', '当前环境不支持自动复制，请手动选择文本复制');
  }
}



/* assets/case-actions.js */
// ===================== INTERACTIONS =====================
// ── 折叠状态记忆（本会话内按病例记住展开了哪些区块，重渲染不再被重置） ──
let _openSectionState = { caseId: null, set: new Set() };

function _openSectionsFor(caseId) {
  if (_openSectionState.caseId !== caseId) {
    _openSectionState = { caseId, set: new Set() };
  }
  return _openSectionState.set;
}

function toggleSection(id, forceOpen) {
  const body = document.getElementById(`sbody-${id}`);
  const chev = document.getElementById(`chev-${id}`);
  if (!body) return;
  const isOpen = body.classList.contains('open');
  const next = forceOpen === undefined ? !isOpen : !!forceOpen;
  body.classList.toggle('open', next);
  if (chev) chev.classList.toggle('open', next);
  if (typeof currentCaseId !== 'undefined' && currentCaseId) {
    const set = _openSectionsFor(currentCaseId);
    if (next) set.add(id); else set.delete(id);
  }
}

/** dispatcher: 展开/收起当前表单页全部区块。 */
function toggleAllSections(expand) {
  document.querySelectorAll('#page-form .section-body[id^="sbody-"]').forEach(body => {
    toggleSection(body.id.replace(/^sbody-/, ''), !!expand);
  });
}

function handleCheckbox(key, val, checked, el) {
  setAutoSaveState('saving', '保存中...');
  const cases = loadDB();
  const c = cases.find(x => x.id === currentCaseId);
  if (!c) return;
  c.data = c.data || {};
  let arr = Array.isArray(c.data[key]) ? [...c.data[key]] : [];
  if (checked) { if (!arr.includes(val)) arr.push(val); }
  else { arr = arr.filter(v => v !== val); }
  c.data[key] = arr;
  el.closest('.checkbox-item').classList.toggle('checked', checked);
  saveDB(cases);
  setAutoSaveState('saved');
  updateProgressUI(c);
  if (key.includes('comorbidity')) renderCaseForm(currentCaseId, { preserveScroll: true });
}

// ── 输入防抖批量保存 ────────────────────────────────────────────
// 旧实现每个击键全量 JSON.parse + stringify 整个病例库（localStorage），
// 病例多了输入明显发卡。改为 250ms 窗口内的连续输入合并为一次落库；
// 切病例 / 翻页 / 关页前 flush 兜底，不丢数据。
let _autoSaveTimer = null;
let _autoSavePending = null;   // { caseId, fields: Map<key,value> }

function autoSaveField(key, value) {
  setAutoSaveState('saving', '保存中...');
  if (!_autoSavePending || _autoSavePending.caseId !== currentCaseId) {
    flushAutoSave();   // 病例切换间隙的残留先落库
    _autoSavePending = { caseId: currentCaseId, fields: new Map() };
  }
  _autoSavePending.fields.set(key, value);
  clearTimeout(_autoSaveTimer);
  _autoSaveTimer = setTimeout(flushAutoSave, 250);
}

function flushAutoSave() {
  clearTimeout(_autoSaveTimer);
  _autoSaveTimer = null;
  const pending = _autoSavePending;
  _autoSavePending = null;
  if (!pending || !pending.fields.size) return;
  const cases = loadDB();
  const c = cases.find(x => x.id === pending.caseId);
  if (!c) return;
  c.data = c.data || {};
  let nameChanged = false, deformityChanged = false, lastName = '';
  pending.fields.forEach((value, key) => {
    c.data[key] = value;
    // 姓名是病例卡片/标题的显示来源，但读取处优先用反范式字段 c.data.name
    // （创建/扫描/导入时写入一次）。编辑表单只写 basic_name，这里保持同步。
    if (key === 'basic_name') { c.data.name = value; nameChanged = true; lastName = value; }
    if (key === 'basic_deformity_type' || key === 'deformity_type') deformityChanged = true;
    // #13 — editing a previously-flagged required field clears its inline error.
    const isFilled = Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null && value !== '';
    if (isFilled && typeof clearFieldError === 'function') clearFieldError(key);
  });
  saveDB(cases);
  updateProgressUI(c);
  if (nameChanged) { renderSidebar(); _syncPatientNameToCloud(c.id, lastName); }
  setAutoSaveState('saved');
  if (deformityChanged && pending.caseId === currentCaseId) {
    renderCaseForm(currentCaseId, { preserveScroll: true });
  }
}

// 离开页面 / 切后台时兜底落库，防 250ms 窗口内的输入丢失。
window.addEventListener('beforeunload', flushAutoSave);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') flushAutoSave();
});

let _autoSaveStateTimer = null;
function setAutoSaveState(state = 'saved', text = '') {
  const el = document.getElementById('autoSaveIndicator');
  if (!el) return;
  clearTimeout(_autoSaveStateTimer);
  el.classList.remove('saving', 'saved');
  el.classList.add(state);
  if (state === 'saving') {
    el.textContent = text || '保存中...';
    return;
  }
  const now = new Date();
  const time = now.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
  el.textContent = text || `已自动保存 ${time}`;
  _autoSaveStateTimer = setTimeout(() => {
    if (el.classList.contains('saved')) el.textContent = '已自动保存';
  }, 2600);
}

// 云端姓名同步（防抖）：姓名输入框是 oninput（每击键触发），这里把 800ms 内的
// 连续输入合并为一次 PUT /api/cases/{id} { patient_name }，更新关联 Patient.name。
// 仅对已在云端建档的病例（有 backendId）生效；本地草稿（无云端记录）跳过。
let _patientNameSyncTimer = null;
function _syncPatientNameToCloud(caseId, value) {
  if (!isCloudMode() || !isLoggedIn()) return;
  clearTimeout(_patientNameSyncTimer);
  _patientNameSyncTimer = setTimeout(async () => {
    const c = loadDB().find(x => x.id === caseId);
    if (!c || !c.backendId) return;          // 尚无云端病例记录（未上传过影像）→ 无可更新对象
    const name = (value || '').trim();
    if (!name) return;                        // 空名不推送（后端也会忽略，省一次请求）
    try {
      await backendRequest(`/api/cases/${c.backendId}`, {
        method: 'PUT',
        body: JSON.stringify({ patient_name: name })
      });
    } catch (_) { /* 本地已保存；云端同步失败静默，下次编辑/保存按钮会再试 */ }
  }, 800);
}

// 云端 data 回写（防抖）：建档后表单字段编辑只落 localStorage，必须把【完整 data blob】
// PUT 回 /api/cases/{id}，否则 cases.data 冻结在建档快照（name/gender/age 等 patient 列字段
// 除外）——侧别等 data-only 字段因此永不进库（本次根因）。
// 后端是浅合并（cases.py:689-691），整包发避免漏字段；无 backendId 时先 ensureBackendCase
// 建档（即便没传图也要能存上云）。防抖合并成一次，last-write-wins。
let _caseDataSyncTimer = null;
let _caseDataSyncPendingId = null;
function syncCaseDataToCloud(caseId) {
  if (!isCloudMode() || !isLoggedIn()) return;   // 纯本地模式：localStorage 即事实来源
  _caseDataSyncPendingId = caseId || currentCaseId;
  clearTimeout(_caseDataSyncTimer);
  _caseDataSyncTimer = setTimeout(flushCaseDataToCloud, 800);
}

async function flushCaseDataToCloud() {
  clearTimeout(_caseDataSyncTimer);
  _caseDataSyncTimer = null;
  const caseId = _caseDataSyncPendingId;
  _caseDataSyncPendingId = null;
  if (!caseId || !isCloudMode() || !isLoggedIn()) return;
  const c = loadDB().find(x => x.id === caseId);
  if (!c) return;
  try {
    const backendId = await ensureBackendCase(c);   // 无 backendId → 先建档（POST 已带 data）
    if (!backendId) return;
    // 重新读最新（ensureBackendCase 期间可能有并发写入），整包回写当前 data。
    const fresh = loadDB().find(x => x.id === caseId) || c;
    await backendRequest(`/api/cases/${backendId}`, {
      method: 'PUT',
      body: JSON.stringify({ data: fresh.data || {} }),
    });
  } catch (_) {
    // 本地已保存；云端失败静默，下次「保存」会再试，不阻塞医生录入。
  }
}

function updateProgressUI(c) {
  const pct = calcProgress(c);
  const mod = MODULES[c.module];
  const mediaPct = calcMediaProgress(c, mod);
  const fill = document.getElementById('progressFill');
  const pctEl = document.getElementById('progressPct');
  const mediaFill = document.getElementById('mediaProgressFill');
  const mediaPctEl = document.getElementById('mediaProgressPct');
  const mediaCount = document.getElementById('mediaProgressCount');
  if (fill) fill.style.width = pct + '%';
  if (pctEl) pctEl.textContent = pct + '%';
  if (mediaFill) mediaFill.style.width = mediaPct.pct + '%';
  if (mediaPctEl) mediaPctEl.textContent = mediaPct.pct + '%';
  if (mediaCount) mediaCount.textContent = `${mediaPct.filled}/${mediaPct.total}`;
}

function setCasePhase(phase) {
  currentCasePhase = phase;
  if (currentCaseId) renderCaseForm(currentCaseId);
}

// 中文状态 → 后端状态机
const WORKFLOW_STATE_TO_BACKEND = {
  '待审核': 'submitted',
  '审核中': 'reviewing',
  '审核通过': 'approved',
  '驳回修改': 'rejected',
  '未填写': 'draft',
};
const WORKFLOW_STATE_FROM_BACKEND = {
  draft: '未填写',
  submitted: '待审核',
  reviewing: '审核中',
  approved: '审核通过',
  rejected: '驳回修改',
};

async function setReviewStatus(caseId, status) {
  const cases = loadDB();
  const c = cases.find(x => x.id === caseId);
  if (!c) return;
  c.data = c.data || {};

  let reason = '';
  if (status === '驳回修改') {
    reason = (await showAppPrompt({
      title: '驳回修改',
      message: '请输入驳回原因或需要补充的资料',
      defaultValue: c.data.workflow_reject_reason || '',
      maxLength: 200,
      confirmText: '确认驳回',
    })) || '';
    if (!reason.trim()) {
      showToast('⚠️', '驳回必须填写原因');
      return;
    }
    reason = reason.trim();
  }

  // #13 — submitting for review: flag empty core-required fields inline first.
  if (status === '待审核' && typeof getRequiredFieldStatus === 'function') {
    const mod = MODULES[c.module];
    const required = getRequiredFieldStatus(c, mod);
    if (required.missing.length > 0) {
      if (typeof markRequiredFieldErrors === 'function') markRequiredFieldErrors(required.missing);
      showToast('⚠️', `还有 ${required.missing.length} 项核心必填资料未完成`);
      const proceed = await showAppConfirm({
        title: '提交审核',
        message: `还有 ${required.missing.length} 项核心必填资料未完成，确定仍要提交审核吗？`,
        confirmText: '仍要提交',
      });
      if (!proceed) return;
    } else if (typeof clearAllFieldErrors === 'function') {
      clearAllFieldErrors();
    }
  }

  // 优先走后端：状态写入数据库，跨账号一致
  if (isCloudMode() && isLoggedIn() && c.backendId) {
    const toState = WORKFLOW_STATE_TO_BACKEND[status] || status;
    try {
      const result = await backendRequest(`/api/cases/${c.backendId}/transitions`, {
        method: 'POST',
        body: JSON.stringify({ to_state: toState, reason }),
      });
      // 用后端权威值更新本地缓存
      c.data.workflow_review_status = WORKFLOW_STATE_FROM_BACKEND[result.workflow_state] || status;
      c.data.workflow_reject_reason = result.rejected_reason || '';
      c.workflow_state = result.workflow_state;
      c.current_reviewer_id = result.current_reviewer_id;
      saveDB(cases);
      renderCaseForm(caseId);
      renderSidebar();
      updateStats();
      showToast('☁️', `审核状态已更新：${status}`);
      return;
    } catch (err) {
      showToast('⚠️', `云端流转失败：${err.message}`);
      return;
    }
  }

  // 本地降级
  c.data.workflow_review_status = status;
  c.data.workflow_reject_reason = status === '驳回修改' ? reason : '';
  saveDB(cases);
  renderCaseForm(caseId);
  renderSidebar();
  updateStats();
  showToast('✅', `审核状态已更新（本地）：${status}`);
}

function setFolderFilter(folder) {
  currentWorkbenchQueueFilter = 'all';
  currentFolderFilter = folder;
  renderSidebar();
  renderDashboard(currentModuleFilter || 'all');
}

function saveCase(id) {
  flushAutoSave();   // 把 250ms 防抖窗口内的输入先落库（保证下面回写的是最新值）
  syncCaseDataToCloud(id);   // 再把完整 data blob 回写云端（防抖、last-write-wins）
  setAutoSaveState('saved', '已保存当前病例');
  showToast('✅', '已保存到本地存储');
  renderSidebar();
  updateStats();
  renderDashboard(currentModuleFilter || 'all');
}

async function deleteCase(id) {
  const c = loadDB().find(x => x.id === id);
  const name = (c?.data?.name || c?.data?.basic_name || '未命名患者');
  const ok = await showAppConfirm({
    title: '删除病例',
    message: `确定删除病例“${name}”吗？此操作不可恢复。`,
    confirmText: '删除',
    danger: true,
  });
  if (!ok) return;
  // 云端病例必须先删后端（软删）。否则只删本地，刷新时 syncCloudCasesToLocal
  // 会从 /api/cases 把它重新同步回来，造成“删了又出现”的错觉。
  if (c && c.backendId && isCloudMode() && isLoggedIn()) {
    try {
      await backendRequest(`/api/cases/${c.backendId}`, { method: 'DELETE' });
    } catch (err) {
      // 404 = 后端其实已删，按成功处理；其余错误则中止，不动本地，避免不一致。
      if (err && err.status !== 404) {
        showToast('⚠️', `云端删除失败：${err.message || '请重试'}`);
        return;
      }
    }
  }
  // 删除前播放“被划走”动效（卡片在仪表盘可见时）；不可见则立即返回。
  await _animateCardRemoval(id);
  // 重新读取（await 期间可能有并发写入），按 id 过滤后落盘。
  const cases = loadDB().filter(x => x.id !== id);
  saveDB(cases);
  currentCaseId = null;
  showPage('dashboard');
  showToast('🗑️', '病例已删除');
}

// 删除小特效：把目标卡片向右滑出并淡出，动画结束（或 ~400ms 兜底）后 resolve。
// 找不到对应卡片（如不在仪表盘）时立即 resolve，不阻塞删除。
function _animateCardRemoval(caseId) {
  return new Promise(resolve => {
    let el = null;
    try {
      const sel = (window.CSS && CSS.escape) ? CSS.escape(caseId) : caseId;
      el = document.querySelector(`.dash-card[data-case-id="${sel}"]`);
    } catch (_) { /* 选择器异常忽略 */ }
    if (!el) { resolve(); return; }
    let done = false;
    const finish = () => { if (done) return; done = true; resolve(); };
    el.addEventListener('animationend', finish, { once: true });
    el.classList.add('dash-card--removing');
    setTimeout(finish, 400);   // 兜底：animationend 未触发也不卡住删除
  });
}

function triggerUpload(key) {
  document.getElementById(`upinput_${key}`)?.click();
}

/* ── 大图预览 Lightbox ──────────────────────────────────────── */
function openLightbox(src, label, isVideo) {
  let ov = document.getElementById('lightboxOverlay');
  if (!ov) {
    ov = document.createElement('div');
    ov.id = 'lightboxOverlay';
    ov.innerHTML = `
      <button id="lightboxClose" data-action="close-lightbox">✕</button>
      <img id="lightboxImg" src="" alt="">
      <video id="lightboxVideo" src="" controls playsinline></video>
      <div id="lightboxLabel"></div>`;
    ov.addEventListener('click', (e) => { if (e.target === ov) closeLightbox(); });
    document.body.appendChild(ov);
    // 键盘 ESC 关闭
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeLightbox(); });
  }
  const img = document.getElementById('lightboxImg');
  const vid = document.getElementById('lightboxVideo');
  if (isVideo) {
    img.style.display = 'none';
    vid.style.display = 'block';
    vid.src = mediaPreviewSrc(src);
    if (isAuthenticatedMediaApiUrl(src)) {
      fetchAuthenticatedMediaObjectUrl(src).then(url => { vid.src = url; }).catch(() => {});
    }
    vid.play().catch(() => {});
  } else {
    vid.style.display = 'none'; vid.pause(); vid.src = '';
    img.style.display = 'block';
    img.src = mediaPreviewSrc(src);
    if (isAuthenticatedMediaApiUrl(src)) {
      fetchAuthenticatedMediaObjectUrl(src).then(url => { img.src = url; }).catch(() => {});
    }
  }
  document.getElementById('lightboxLabel').textContent = label || '';
  ov.classList.add('active');
  document.body.style.overflow = 'hidden';
}
function closeLightbox() {
  const ov = document.getElementById('lightboxOverlay');
  if (!ov) return;
  ov.classList.remove('active');
  document.body.style.overflow = '';
  const vid = document.getElementById('lightboxVideo');
  if (vid) { vid.pause(); vid.src = ''; }
}

// 并发去重：同一个本地病例的"建云端病例"请求只发一次。
// 关键 bug 背景：一次多选上传时，N 张图各自 loadDB() 拿到不同的 c 对象实例，
// 若都看到 c.backendId 为空，会各自 POST /api/cases/ → 创建 N 份重复病例。
// 用 caseId 作为键的进行中 Promise 表，让并发调用共享同一次建病例请求。
const _ensureCasePromises = {};

// B —— 本会话刚上传图片的本地 blob 预览，按 backendMediaId 索引。
// 仅用于云端预签名图加载失败时的 onerror 兜底显示，不持久化（blob URL 跨刷新失效）。
const _freshUploadPreviews = {};

async function ensureBackendCase(c) {
  if (!isCloudMode()) return null;
  if (!isLoggedIn()) throw new Error('请先登录云端账号，再上传影像到对象存储');
  if (c.backendId) return c.backendId;

  // 别的并发任务可能刚把 backendId 持久化到 localStorage —— 直接复用，绝不重复建。
  const persisted = loadDB().find(x => x.id === c.id);
  if (persisted?.backendId) {
    c.backendId = persisted.backendId;
    c.backendPatientId = persisted.backendPatientId;
    return c.backendId;
  }

  // 已有进行中的建病例请求 → 等它，不再发第二次。
  if (_ensureCasePromises[c.id]) {
    const result = await _ensureCasePromises[c.id];
    c.backendId = result.id;
    c.backendPatientId = result.patient_id;
    return c.backendId;
  }

  const mod = MODULES[c.module];
  const data = c.data || {};
  _ensureCasePromises[c.id] = backendRequest('/api/cases/', {
    method: 'POST',
    body: JSON.stringify({
      patient: {
        name: data.basic_name || data.name || '未命名患者',
        gender: data.basic_gender || data.gender || null,
        age: data.basic_age ? Number(data.basic_age) : null,
        bmi: data.basic_bmi ? Number(data.basic_bmi) : null
      },
      module: c.module,
      module_name: mod?.name || c.module,
      center_id: c.center_id || c.centerId || getDefaultCenterId() || null,
      data
    })
  });

  try {
    const result = await _ensureCasePromises[c.id];
    c.backendId = result.id;
    c.backendPatientId = result.patient_id;
    c.center_id = result.center_id || c.center_id || null;
    c.centerId = c.center_id;
    c.cloudSyncedAt = new Date().toISOString();
    // 立即把 backendId 持久化，后续并发任务读 localStorage 即可复用，避免重复建病例。
    try {
      const cases = loadDB();
      const idx = cases.findIndex(x => x.id === c.id);
      if (idx >= 0) {
        cases[idx].backendId = c.backendId;
        cases[idx].backendPatientId = c.backendPatientId;
        cases[idx].center_id = c.center_id;
        cases[idx].centerId = c.centerId;
        cases[idx].cloudSyncedAt = c.cloudSyncedAt;
        saveDB(cases);
      }
    } catch { /* 持久化失败不阻塞上传，内存里的 c.backendId 仍可用 */ }
    return c.backendId;
  } finally {
    delete _ensureCasePromises[c.id];
  }
}

async function uploadMediaToBackend(c, key, slot, file) {
  const backendCaseId = await ensureBackendCase(c);
  const formData = new FormData();
  formData.append('slot_name', key);
  formData.append('file', file);
  const result = await backendRequest(`/api/cases/${backendCaseId}/media`, {
    method: 'POST',
    body: formData
  });
  return {
    name: slot,
    data: resolveBackendAssetUrl(result.access_url || `/api/media/${result.id}`),
    filename: file.name,
    mime: result.file_type || file.type || '',
    cloud: true,
    backendMediaId: result.id,
    backendCaseId,
    fileSize: result.file_size,
    uploadedAt: result.uploaded_at
  };
}

// ── 影像上传：体验强化（类型/大小校验 · HEIC 识别 · 按需压缩 · 并发节流 · 重试 · 中断守卫）──
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;   // 与后端 MAX_FILE_SIZE 一致（10MB）
const UPLOAD_CONCURRENCY = 3;                 // 批量上传并发上限（2GB 云主机友好）
let _inFlightUploads = 0;                     // 在途上传数，用于离开守卫
const _uploadRetryCtx = {};                   // tempId → {caseId,key,slot,file} 供失败重试
const _uploadQueue = [];
let _uploadActiveCount = 0;

function _isHeicFile(file) {
  const t = (file.type || '').toLowerCase();
  if (t === 'image/heic' || t === 'image/heif') return true;
  return /\.(heic|heif)$/i.test(file.name || '');
}
function _isImageFile(file) {
  return (file.type || '').startsWith('image/') || /\.(jpe?g|png|webp|gif|heic|heif)$/i.test(file.name || '');
}
function _loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('无法读取图像内容')); };
    img.src = url;
  });
}
// 仅在用户明确同意（>10MB）时调用：画布降采样/降质迭代直到 < maxBytes。
async function _compressImageUnder(file, maxBytes) {
  const img = await _loadImageFromFile(file);
  let scale = 1, quality = 0.9;
  const longEdge = Math.max(img.width, img.height);
  if (longEdge > 4096) scale = 4096 / longEdge;   // 先限制超大分辨率
  for (let attempt = 0; attempt < 9; attempt++) {
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    canvas.getContext('2d').drawImage(img, 0, 0, w, h);
    const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', quality));
    if (blob && blob.size <= maxBytes) {
      const name = (file.name || 'image').replace(/\.[^.]+$/, '') + '.jpg';
      return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
    }
    if (quality > 0.6) quality -= 0.1; else scale *= 0.85;   // 先降质，再降尺寸
  }
  throw new Error('图像过大，压缩后仍超过 10MB，请改用更小的图片。');
}

// 上传前统一预处理：HEIC 提示 · 大小校验 · 超 10MB 按需压缩。
// 返回处理后的 File；返回 null 表示用户取消；抛错表示无法上传（外层弹友好提示）。
async function _preprocessUploadFile(file) {
  if (_isHeicFile(file)) {
    // 保持 CSP 严格：不在浏览器转码，识别 + 提示，仍上传原图（后端支持存 HEIC）。
    showToast('📱', 'iPhone HEIC 原图可上传，但浏览器无法预览。建议手机「设置→相机→格式」选「兼容性最佳」，或先转 JPG。', 5200);
    if (file.size > MAX_UPLOAD_BYTES) {
      throw new Error(`HEIC 文件约 ${(file.size / 1024 / 1024).toFixed(1)}MB，超过 10MB 上限，且无法在浏览器压缩。请先转 JPG 或用更小的图片。`);
    }
    return file;
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    if (!_isImageFile(file)) {
      throw new Error(`文件约 ${(file.size / 1024 / 1024).toFixed(1)}MB，超过 10MB 上限，无法自动压缩。`);
    }
    const mb = (file.size / 1024 / 1024).toFixed(1);
    const ok = await showAppConfirm({
      title: '影像超出大小上限',
      message: `该影像约 ${mb}MB，超过 10MB 上限。\n压缩后上传可能降低图像细节，诊断影像请谨慎。`,
      confirmText: '压缩后上传',
      cancelText: '放弃这张',
    });
    if (!ok) return null;
    const t = showToast('🗜️', '正在压缩影像…', 0);
    try { return await _compressImageUnder(file, MAX_UPLOAD_BYTES); }
    finally { if (t) t.remove(); }
  }
  return file;
}

// 批量上传并发节流：一次最多 UPLOAD_CONCURRENCY 张，完成一张再补一张。
function _enqueueUploads(items) {
  if (items.length > 1 && typeof showToast === 'function') {
    showToast('📤', `已加入 ${items.length} 张影像上传队列，后台将自动并发上传`);
  }
  for (const it of items) _uploadQueue.push(it);
  _pumpUploadQueue();
}
function _pumpUploadQueue() {
  while (_uploadActiveCount < UPLOAD_CONCURRENCY && _uploadQueue.length) {
    const it = _uploadQueue.shift();
    _uploadActiveCount++;
    Promise.resolve(handleCloudUpload(it.caseId, it.key, it.slot, it.file))
      .finally(() => { _uploadActiveCount--; _pumpUploadQueue(); });
  }
}

// 失败卡片「↻ 重试」：用保存的上下文重新入队。
function retryUpload(tempId) {
  const ctx = _uploadRetryCtx[tempId];
  if (!ctx) return;
  delete _uploadRetryCtx[tempId];
  const el = document.getElementById(tempId);
  if (el) el.remove();
  _enqueueUploads([ctx]);
}

// 在途上传中离开页面时给出原生确认，避免误关导致上传中断。
window.addEventListener('beforeunload', (e) => {
  if (_inFlightUploads > 0) { e.preventDefault(); e.returnValue = '上传尚未完成，离开将中断上传。'; return e.returnValue; }
});

function handleUpload(caseId, key, slot, input) {
  const files = Array.from(input.files);
  input.value = '';  // 允许再次选同一文件
  if (!files.length) return;

  if (isCloudMode() && isLoggedIn()) {
    // 批量上传：入队 + 并发节流，互不阻塞但不淹没服务器
    _enqueueUploads(files.map(file => ({ caseId, key, slot, file })));
    return;
  }
  // 本地模式：逐个预处理后 base64 编码
  (async () => {
    let done = 0;
    for (const original of files) {
      let file;
      try { file = await _preprocessUploadFile(original); }
      catch (err) { showToast('⚠️', err.message || '文件无法添加'); continue; }
      if (!file) continue;   // 用户取消压缩
      const ok = await new Promise(resolve => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const cases = loadDB();
          const c = cases.find(x => x.id === caseId);
          if (!c) return resolve(false);
          c.media = c.media || {};
          const newItem = { name: slot, data: e.target.result, filename: file.name, mime: file.type || '' };
          c.media[key] = appendSlotImage(c, key, newItem);
          try { saveDB(cases); }
          catch { showToast('❌', '文件过大：本地模式浏览器存储容量有限，建议使用云端模式'); return resolve(false); }
          _refreshSlotUI(c, key);
          updateProgressUI(c);
          resolve(true);
        };
        reader.onerror = () => resolve(false);
        reader.readAsDataURL(file);
      });
      if (ok) done++;
    }
    if (done) showToast('📸', done > 1 ? `已添加 ${done} 张图片` : `${slot} 已添加`);
  })();
}

async function handleCloudUpload(caseId, key, slot, file) {
  const c0 = loadDB().find(x => x.id === caseId);
  if (!c0) return;

  // 0. 预处理：HEIC 识别提示 · 大小校验 · 超 10MB 按需压缩
  let prepared;
  try { prepared = await _preprocessUploadFile(file); }
  catch (err) { showToast('⚠️', err.message || '文件无法上传'); return; }
  if (!prepared) return;   // 用户取消压缩
  file = prepared;
  const isHeic = _isHeicFile(file);

  // 1. 立即显示本地预览缩略图（用户即时看到照片，无需等待上传）
  const previewUrl = URL.createObjectURL(file);
  const tempId = `uploading_${key}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  _insertUploadingThumb(key, tempId, previewUrl, file, isHeic);

  _inFlightUploads++;
  try {
    const backendCaseId = await ensureBackendCase(c0);
    if (!backendCaseId) throw new Error('无法创建云端病例记录');

    // 2. 上传：大文件走分块/断点续传，小文件走单次 XHR；均回报精确进度
    const onPct = (pct) => _setUploadProgress(tempId, pct);
    const result = (file.size >= CHUNK_UPLOAD_THRESHOLD)
      ? await _chunkedUpload(backendCaseId, key, file, onPct)
      : await _xhrUpload(backendCaseId, key, file, onPct);

    const media = {
      name: slot,
      data: resolveBackendAssetUrl(result.access_url || `/api/media/${result.id}`),
      filename: file.name,
      mime: result.file_type || file.type || '',
      cloud: true,
      backendMediaId: result.id,
      backendCaseId,
      fileSize: result.file_size,
      uploadedAt: result.uploaded_at,
    };

    // B —— 保留本次会话的本地 blob 预览，按 backendMediaId 索引。
    // 若云端预签名图偶发加载慢/失败，_refreshSlotUI 会用它做 onerror 兜底，
    // 确保用户上传后"一定看得到自己的图"，不会误判为失败而重传。
    if (result.id != null) _freshUploadPreviews[result.id] = previewUrl;

    // A —— 关键修复：读-改-写收敛到 await 之后，并发上传不再互相覆盖。
    // 旧 bug：上传前就持有 cases 快照，多张图并发完成时各自把"上传前的旧快照
    // + 自己这一张"写回，后完成者覆盖先完成者 → 本地只剩最后一张 → 看似没传上。
    // 这里重新 loadDB() 拿最新快照（已含其它并发任务追加的图），再追加本张。
    // loadDB→saveDB 之间无 await，单线程下是原子的读-改-写。
    const cases = loadDB();
    const c = cases.find(x => x.id === caseId) || c0;
    c.media = c.media || {};
    if (!c.backendId && backendCaseId) c.backendId = backendCaseId;
    c.media[key] = appendSlotImage(c, key, media);
    saveDB(cases);
    // 本张已落盘：先移除"自己"这张的"上传中"临时缩略图，再让 _refreshSlotUI
    // 按持久化数据重建。_refreshSlotUI 会保留其它仍在上传中的临时缩略图，
    // 因此多图并发时界面不会塌回成一张。
    const _tmp = document.getElementById(tempId);
    if (_tmp) _tmp.remove();
    delete _uploadRetryCtx[tempId];
    _refreshSlotUI(c, key);   // 替换临时缩略图为云端图
    updateProgressUI(c);
    renderSidebar();
  } catch (err) {
    URL.revokeObjectURL(previewUrl);
    // 保存上下文以便「↻ 重试」（用已预处理过的 file，重试时无需再压缩/转码）
    _uploadRetryCtx[tempId] = { caseId, key, slot, file };
    _setUploadError(tempId, key, c0, file.name, err.message || '网络错误，请重试');
  } finally {
    _inFlightUploads--;
  }
}

/**
 * XHR 上传，支持 onprogress 回调（0–100）。
 * fetch API 不提供上传进度，必须用 XHR。
 */
// ── 断点续传（分块）前端客户端 ───────────────────────────────────────
// ≥ 阈值的文件走分块上传：网络中断/重试时按服务端已收到的分块续传，不重头再来。
// 续传状态以"文件指纹→upload_token"存于 localStorage，支持刷新后重选同一文件续传。
const CHUNK_UPLOAD_THRESHOLD = 1.5 * 1024 * 1024;   // ≥1.5MB 启用分块
const UPLOAD_CHUNK_SIZE = 1024 * 1024;              // 每块 1MB
const _UPLOAD_STATE_KEY = 'foot_ankle_uploads_v1';

function _uploadFingerprint(caseId, slotKey, file) {
  return `up_${caseId}_${slotKey}_${file.name}_${file.size}_${file.lastModified || 0}`;
}
function _loadUploadState(fp) {
  try { return (JSON.parse(localStorage.getItem(_UPLOAD_STATE_KEY) || '{}'))[fp] || null; }
  catch { return null; }
}
function _saveUploadState(fp, st) {
  try {
    const m = JSON.parse(localStorage.getItem(_UPLOAD_STATE_KEY) || '{}');
    if (st) m[fp] = st; else delete m[fp];
    localStorage.setItem(_UPLOAD_STATE_KEY, JSON.stringify(m));
  } catch { /* 配额/隐私模式失败：续传退化为重传，不阻断 */ }
}

async function _chunkedUpload(backendCaseId, slotKey, file, onProgress) {
  const chunkSize = UPLOAD_CHUNK_SIZE;
  const totalChunks = Math.ceil(file.size / chunkSize);
  const fp = _uploadFingerprint(backendCaseId, slotKey, file);
  let token = null;
  const received = new Set();

  // 1) 尝试续传已有会话
  const saved = _loadUploadState(fp);
  if (saved && saved.token) {
    try {
      const st = await backendRequest(`/api/cases/${backendCaseId}/media/uploads/${saved.token}`);
      token = saved.token;
      (st.received || []).forEach(i => received.add(i));
    } catch (_) { token = null; }   // 会话失效 → 重新初始化
  }
  // 2) 新建会话
  if (!token) {
    const init = await backendRequest(`/api/cases/${backendCaseId}/media/uploads`, {
      method: 'POST',
      body: JSON.stringify({
        slot_name: slotKey,
        filename: file.name,
        file_type: file.type || 'application/octet-stream',
        total_size: file.size,
        chunk_size: chunkSize,
        total_chunks: totalChunks,
      }),
    });
    token = init.upload_token;
    (init.received || []).forEach(i => received.add(i));
    _saveUploadState(fp, { token, totalChunks, ts: Date.now() });
  }

  // 3) 顺序补传缺失分块（进度按已收到块数）
  const report = () => onProgress(Math.round(received.size / totalChunks * 100));
  report();
  for (let i = 0; i < totalChunks; i++) {
    if (received.has(i)) continue;
    const start = i * chunkSize;
    const blob = file.slice(start, Math.min(start + chunkSize, file.size));
    const fd = new FormData();
    fd.append('chunk', blob, 'chunk');
    await backendRequest(`/api/cases/${backendCaseId}/media/uploads/${token}/chunks/${i}`, {
      method: 'PUT', body: fd,
    });
    received.add(i);
    _saveUploadState(fp, { token, totalChunks, ts: Date.now() });
    report();
  }

  // 4) 合并落盘（与单次上传同一去重/存储/审计路径）
  const result = await backendRequest(`/api/cases/${backendCaseId}/media/uploads/${token}/complete`, {
    method: 'POST',
  });
  _saveUploadState(fp, null);   // 清除续传状态
  return result;
}

function _xhrUpload(backendCaseId, slotKey, file, onProgress) {
  // 单次 XHR，resolve/reject 包含 status 以便外层判断是否需要刷新 token
  const _doXhr = (token) => new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('slot_name', slotKey);
    formData.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round(e.loaded / e.total * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); }
        catch { reject(Object.assign(new Error('服务器返回格式异常'), { status: xhr.status })); }
      } else {
        let detail = `上传失败（${xhr.status}）`;
        try { detail = JSON.parse(xhr.responseText)?.detail || detail; } catch {}
        reject(Object.assign(new Error(detail), { status: xhr.status }));
      }
    };
    xhr.onerror = () => reject(new Error('网络错误，请检查连接后重试'));
    xhr.ontimeout = () => reject(new Error('上传超时（2分钟），请检查网络或文件大小'));
    xhr.timeout = 120000;

    xhr.open('POST', `${getBackendBase()}/api/cases/${backendCaseId}/media`);
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(formData);
  });

  // 主流程：失败时若 401 则尝试刷新 token 后重试一次
  return _doXhr(getAuthToken()).catch(async (err) => {
    if (err.status === 401) {
      const ok = await _tryRefreshToken();
      if (ok) {
        onProgress(0);  // 重置进度条
        return _doXhr(getAuthToken());
      }
      clearAuth();
      if (typeof renderAuthState === 'function') renderAuthState();
      throw new Error('登录已过期，请重新登录后重试上传');
    }
    throw err;
  });
}

/** 在槽位内插入一个"正在上传"的临时缩略图卡片。*/
function _insertUploadingThumb(key, tempId, previewUrl, file, isHeic) {
  const slotEl = document.getElementById(`slot_${key}`);
  if (!slotEl) return;
  const R = 18;  // SVG 圆弧半径
  const C = 2 * Math.PI * R;  // 圆周长
  const isVid = file?.type?.startsWith('video/');
  const el = document.createElement('div');
  el.className = 'image-thumb-uploading';
  el.id = tempId;
  // HEIC 浏览器无法解码 → 用占位代替会裂的 <img>
  const previewHtml = isHeic
    ? `<div class="thumb-preview thumb-heic-ph"><span>HEIC</span><small>上传中·完成后可下载原图</small></div>`
    : (isVid
        ? `<video src="${previewUrl}" muted class="thumb-preview" style="pointer-events:none;"></video>`
        : `<img src="${previewUrl}" class="thumb-preview">`);
  el.innerHTML = `
    ${previewHtml}
    <div class="upload-overlay">
      <div class="upload-progress-ring-wrap">
        <svg viewBox="0 0 44 44" width="44" height="44">
          <circle class="track" cx="22" cy="22" r="${R}"/>
          <circle class="fill" id="${tempId}_arc" cx="22" cy="22" r="${R}"
            stroke-dasharray="${C}" stroke-dashoffset="${C}"/>
        </svg>
      </div>
      <div class="upload-pct-text" id="${tempId}_pct">0%</div>
    </div>`;
  // 插到"+"按钮之前
  const addBtn = slotEl.querySelector('.image-thumb-add');
  slotEl.insertBefore(el, addBtn || null);
}

/** 更新临时缩略图上的进度百分比和圆弧。*/
function _setUploadProgress(tempId, pct) {
  const pctEl = document.getElementById(`${tempId}_pct`);
  const arc = document.getElementById(`${tempId}_arc`);
  if (pctEl) pctEl.textContent = pct + '%';
  if (arc) {
    const R = 18, C = 2 * Math.PI * R;
    arc.style.strokeDashoffset = C * (1 - pct / 100);
  }
}

/** 上传失败：将临时缩略图变成错误卡片，提供重试入口。*/
function _setUploadError(tempId, key, c, filename, errMsg) {
  const el = document.getElementById(tempId);
  if (!el) {
    // DOM 已被替换（罕见情况），用 toast 兜底
    showToast('❌', `上传失败：${errMsg}`);
    return;
  }
  el.className = 'image-thumb-error';
  const canRetry = !!_uploadRetryCtx[tempId];
  el.innerHTML = `
    <span class="err-icon">⚠️</span>
    <span class="err-text">${escapeHtml(filename || '文件')}<br>上传失败</span>
    <span class="err-text" style="color:#6b7280;font-size:9px;">${escapeHtml(errMsg || '')}</span>
    ${canRetry ? `<button class="img-retry" data-action="retry-upload" data-temp-id="${escapeHtml(tempId)}">↻ 重试</button>` : ''}
    <button class="img-remove" data-action="remove-error-thumb">✕</button>`;
  showToast('❌', `上传失败：${errMsg}`);
}

/** 局部刷新某个槽位的缩略图区域，避免整页重渲。*/
function _refreshSlotUI(c, key) {
  const slotEl = document.getElementById(`slot_${key}`);
  if (!slotEl) { openCase(c.id); return; }
  const mod = MODULES[c.module];
  const mediaSection = mod?.sections?.find(s => s.type === 'media');
  const slot = mediaSection?.slots?.find((s, i) => {
    const meta = getSlotMeta(s, i);
    return meta.key === key;
  });
  if (!slot) { openCase(c.id); return; }
  const idx = mediaSection.slots.indexOf(slot);
  const meta = getSlotMeta(slot, idx);
  const images = getSlotImages(c, meta.key, meta.legacyKey);
  const thumbsHtml = images.map((img, i) => {
    const isVid = img?.mime?.startsWith('video/') || /\.(mp4|mov|avi|m4v|webm)$/i.test(img?.filename || '');
    const isHeic = /image\/hei[cf]/i.test(img?.mime || '') || /\.(heic|heif)$/i.test(img?.filename || '');
    const displaySrc = mediaPreviewSrc(img?.data || '');
    const authAttrs = mediaAuthAttrs(img?.data || '', meta.label || '', isVid);
    const safe = (displaySrc || '').replace(/"/g, '&quot;');
    const safeLbl = (meta.label || '').replace(/"/g, '&quot;');
    // B —— 若该图刚在本会话上传，预签名图加载失败时回退到本地 blob 预览，
    // 保证用户上传后一定看得到自己的图（不会误判失败而重传）。
    const preview = (img?.backendMediaId != null) ? _freshUploadPreviews[img.backendMediaId] : '';
    // 失败回退到 blob 预览：用 data-action-error="media-fallback" + data-fallback-src 在 app.js dispatcher 里实现
    const onErr = preview
      ? ` data-action-error="media-fallback" data-fallback-src="${preview.replace(/"/g, '&quot;')}"`
      : ` data-action-error="mark-media-error"`;
    // HEIC 浏览器无法解码：显示「已上传·无法预览」占位，点击仍可下载原图
    const body = isHeic
      ? `<div class="thumb-heic-ph"${authAttrs} data-action="open-window" data-url="${safe}" title="HEIC 原图，点击下载查看"><span>HEIC</span><small>已上传·点击下载原图</small></div>`
      : (isVid
        ? `<video src="${safe}"${authAttrs} controls style="width:100%;height:100%;object-fit:cover;"
             data-action="open-lightbox" data-lightbox-label="${safeLbl}" data-lightbox-video="1"></video>`
        : `<img src="${safe}"${authAttrs} alt="${safeLbl}" loading="lazy" decoding="async"${onErr}
             data-action="open-lightbox" data-lightbox-label="${safeLbl}">`);
    return `
    <div class="image-thumb" id="thumb_${meta.key}_${i}">
      ${body}
      <div class="img-label">${meta.label}${images.length > 1 ? `<span style="color:var(--text3);margin-left:3px;">${i + 1}/${images.length}</span>` : ''}</div>
      <button class="img-move" draggable="true" title="拖动或点按移动到其它槽位"
        data-action-dragstart="thumb-drag"
        data-action="show-move-slot-picker"
        data-case-id="${escapeHtml(c.id)}" data-media-key="${escapeHtml(meta.key)}" data-media-idx="${i}">⇄</button>
      <button class="img-remove" data-action="remove-media" data-case-id="${escapeHtml(c.id)}" data-media-key="${escapeHtml(meta.key)}" data-media-idx="${i}">✕</button>
    </div>`;
  }).join('');
  const addBtnHtml = `
    <label class="image-thumb image-thumb-add" for="upinput_${escapeHtml(meta.key)}">
      <div class="upload-add-content">
        <span class="upload-add-icon">＋</span>
        <span class="upload-add-label">${images.length ? '继续添加' : meta.label}</span>
      </div>
      <input class="visually-hidden-file" type="file" id="upinput_${escapeHtml(meta.key)}" accept="${escapeHtml(meta.accept)}" multiple
        data-action-change="handle-upload" data-case-id="${escapeHtml(c.id)}" data-media-key="${escapeHtml(meta.key)}" data-media-label="${escapeHtml(meta.label)}">
    </label>`;
  // #3 修复：重建槽位 DOM 时，保留仍在进行中的"上传中"临时缩略图。
  // 旧 bug：innerHTML 重建只渲染已落盘图片，会顺手抹掉并发上传中的其它临时缩略图——
  // 多图同时上传时，先完成的一张触发重建，界面会从"多张上传中"瞬间塌回成"一张已传"，
  // 给人"没传成功"的错觉。失败卡片不跨刷新保留，避免重试成功后旧错误继续显示。
  const pendingNodes = Array.from(
    slotEl.querySelectorAll('.image-thumb-uploading')
  );
  pendingNodes.forEach(n => { if (n.parentNode === slotEl) slotEl.removeChild(n); });
  slotEl.innerHTML = thumbsHtml + addBtnHtml;
  const addBtnEl = slotEl.querySelector('.image-thumb-add');
  pendingNodes.forEach(n => slotEl.insertBefore(n, addBtnEl || null));
  if (typeof hydrateAuthenticatedMedia === 'function') hydrateAuthenticatedMedia(slotEl);
}

// ── 影像换槽位（拖拽 + 触屏"移动到…"选择器）──────────────────────────
let _dragMediaCtx = null;   // { caseId, fromKey, idx }

function _thumbDragStart(ev, caseId, fromKey, idx) {
  _dragMediaCtx = { caseId, fromKey, idx };
  try {
    ev.dataTransfer.effectAllowed = 'move';
    ev.dataTransfer.setData('text/plain', `${fromKey}#${idx}`);
  } catch (_) {}
}
function _slotDragOver(ev) {
  if (!_dragMediaCtx) return;
  ev.preventDefault();
  try { ev.dataTransfer.dropEffect = 'move'; } catch (_) {}
  const el = ev.currentTarget;
  if (el && el.classList) el.classList.add('drag-over');
}
function _slotDragLeave(ev) {
  const el = ev.currentTarget;
  if (el && el.classList) el.classList.remove('drag-over');
}
function _slotDrop(ev, caseId, toKey) {
  ev.preventDefault();
  const el = ev.currentTarget;
  if (el && el.classList) el.classList.remove('drag-over');
  const ctx = _dragMediaCtx;
  _dragMediaCtx = null;
  if (!ctx || ctx.caseId !== caseId || ctx.fromKey === toKey) return;
  moveMediaToSlot(caseId, ctx.fromKey, ctx.idx, toKey);
}

// 把第 idx 张影像从 fromKey 槽位移动到 toKey 槽位。
async function moveMediaToSlot(caseId, fromKey, idx, toKey) {
  if (fromKey === toKey) return;
  const cases = loadDB();
  const c = cases.find(x => x.id === caseId);
  if (!c) return;
  const fromArr = getSlotImages(c, fromKey);
  const img = fromArr[idx];
  if (!img) return;

  // 云端影像：先改后端 slot_name，再以后端为准重建并整页刷新（最稳）
  if (img.backendMediaId && isCloudMode() && isLoggedIn()) {
    try {
      await backendRequest(`/api/media/${img.backendMediaId}/slot`, {
        method: 'PATCH', body: JSON.stringify({ slot_name: toKey }),
      });
    } catch (err) { showToast('⚠️', `移动失败：${err.message || '请重试'}`); return; }
    try { await hydrateCloudCase(c); } catch (_) {}
    saveDB(cases);
    if (typeof renderCaseForm === 'function') renderCaseForm(caseId);
    showToast('✅', '已移动到新槽位');
    return;
  }

  // 本地影像：直接在 c.media 里挪
  const newFrom = fromArr.slice();
  newFrom.splice(idx, 1);
  const toArr = getSlotImages(c, toKey).slice();
  toArr.push(img);
  c.media = c.media || {};
  c.media[fromKey] = newFrom;
  c.media[toKey] = toArr;
  saveDB(cases);
  _refreshSlotUI(c, fromKey);
  _refreshSlotUI(c, toKey);
  updateProgressUI(c);
  showToast('✅', '已移动到新槽位');
}

// 触屏/点按：弹出"移动到…"槽位选择器（按阶段分组）
function showMoveSlotPicker(caseId, fromKey, idx) {
  closeMoveSlotPicker();
  const c = loadDB().find(x => x.id === caseId);
  if (!c) return;
  const mod = MODULES[c.module];
  const mediaSec = mod?.sections?.find(s => s.type === 'media');
  if (!mediaSec) return;
  const metas = (mediaSec.slots || []).map((s, i) => getSlotMeta(s, i)).filter(m => m.key !== fromKey);
  const byPhase = {};
  metas.forEach(m => { (byPhase[m.phase] = byPhase[m.phase] || []).push(m); });
  const body = Object.entries(byPhase).map(([phase, list]) => `
    <div class="move-picker-phase">${escapeHtml(phase)}</div>
    ${list.map(m => `<button class="move-picker-item" data-action="move-media-to-slot" data-case-id="${escapeHtml(caseId)}" data-from-key="${escapeHtml(fromKey)}" data-media-idx="${idx}" data-to-key="${escapeHtml(m.key)}">${escapeHtml(m.label)}</button>`).join('')}
  `).join('');
  const ov = document.createElement('div');
  ov.id = 'moveSlotPicker';
  ov.className = 'move-picker-overlay';
  ov.innerHTML = `<div class="move-picker-dialog" role="dialog" aria-label="移动影像到槽位">
      <div class="move-picker-head"><span>移动这张影像到…</span><button data-action="close-move-slot-picker" aria-label="关闭">✕</button></div>
      <div class="move-picker-body">${body || '<div style="padding:12px;color:var(--text3);">没有其它可选槽位</div>'}</div>
    </div>`;
  ov.addEventListener('click', e => { if (e.target === ov) closeMoveSlotPicker(); });
  document.body.appendChild(ov);
}
function closeMoveSlotPicker() {
  const ov = document.getElementById('moveSlotPicker');
  if (ov) ov.remove();
}

async function removeMedia(caseId, key, index) {
  const cases = loadDB();
  const c = cases.find(x => x.id === caseId);
  if (!c?.media) return;

  const images = getSlotImages(c, key);
  // index 缺省（undefined）= 删除整个槽位（向后兼容旧调用）
  const idx = (index !== undefined && index !== null) ? Number(index) : 0;
  const target = images[idx];

  // 云端：先删远端
  if (target?.cloud && target.backendMediaId && isCloudMode() && isLoggedIn()) {
    try {
      await backendRequest(`/api/media/${target.backendMediaId}`, { method: 'DELETE' });
    } catch (err) {
      showToast('⚠️', `云端影像删除失败：${err.message}`);
      return;
    }
  }

  images.splice(idx, 1);
  if (images.length === 0) {
    delete c.media[key];
  } else {
    c.media[key] = images;
  }
  saveDB(cases);
  _refreshSlotUI(c, key);
  updateProgressUI(c);
  renderSidebar();
}

// ===================== CASE MANAGEMENT =====================
function showPage(name, _slideDir) {
  if (typeof flushAutoSave === 'function') flushAutoSave();   // 翻页前落库防抖窗口内的输入
  document.querySelectorAll('.page').forEach(p => p.classList.remove('active'));
  const pageEl = document.getElementById('page-' + name);
  if (pageEl) {
    pageEl.classList.add('active');
    // 方向滑入动画（仅手势导航时传入方向；普通点击不加，避免到处闪动）
    if (_slideDir) {
      pageEl.classList.remove('page-slide-in-left', 'page-slide-in-right');
      void pageEl.offsetWidth;   // 强制重排，使重复同向导航也能重新触发动画
      pageEl.classList.add(_slideDir === 'back' ? 'page-slide-in-right' : 'page-slide-in-left');
    }
  }
  if (name === 'dashboard') {
    currentCaseId = null;
    renderSidebar();
    updateStats();
    renderDashboard();
  }
  _recordView(name);
}

// ===================== 视图历史（移动端 back/forward 手势） =====================
// 浏览器式前进/后退：记录 dashboard / form / todos 之间的跳转，左右滑手势重放。
let _viewHistory = [];
let _viewIndex = -1;
let _viewNavigating = false;   // 重放期间为 true，阻止 _recordView 再次入栈

function _snapshotView(name) {
  if (name === 'form') return { view: 'form', caseId: currentCaseId, phase: currentCasePhase };
  if (name === 'todos') return { view: 'todos', scope: currentTodoScope };
  return { view: 'dashboard' };
}

function _recordView(name) {
  if (_viewNavigating) return;
  const st = _snapshotView(name);
  const top = _viewHistory[_viewIndex];
  // 与栈顶同视图（同病例）则只更新，不重复入栈
  if (top && top.view === st.view && top.caseId === st.caseId && top.scope === st.scope) {
    _viewHistory[_viewIndex] = st;
    return;
  }
  _viewHistory = _viewHistory.slice(0, _viewIndex + 1);   // 截断 forward 分支
  _viewHistory.push(st);
  if (_viewHistory.length > 50) _viewHistory.shift();      // 上限保护
  _viewIndex = _viewHistory.length - 1;
}

async function _applyView(st, dir) {
  _viewNavigating = true;
  try {
    if (st.view === 'form' && st.caseId && loadDB().some(c => c.id === st.caseId)) {
      const c = loadDB().find(x => x.id === st.caseId);
      if (!canCurrentUserViewCase(c)) { await openDashboardView(); showPage('dashboard', dir); return; }
      await hydrateCloudCase(c);
      currentCaseId = st.caseId;
      currentCasePhase = st.phase || 'preop';
      showPage('form', dir);
      renderCaseForm(st.caseId);
      renderSidebar();
    } else if (st.view === 'todos') {
      _revokeFreshUploadPreviews();
      await syncCloudCasesToLocal();
      currentCaseId = null;
      if (st.scope) currentTodoScope = st.scope;
      showPage('todos', dir);
      renderSidebar();
      updateStats();
      if (typeof renderTodoWorkbench === 'function') renderTodoWorkbench();
    } else {
      _revokeFreshUploadPreviews();
      await syncCloudCasesToLocal();
      showPage('dashboard', dir);
    }
  } finally {
    _viewNavigating = false;
  }
}

// 后退：先让 goBack 关闭浮层（菜单/灯箱/弹窗/侧栏）；无浮层时回退视图历史。
function navBack() {
  if (_closeTopOverlay()) return true;
  if (_viewIndex > 0) {
    _viewIndex--;
    _applyView(_viewHistory[_viewIndex], 'back');
    return true;
  }
  return goBack();
}

// 前进：重放被后退出去的视图。有浮层打开时不前进（让后退先收起浮层）。
function navForward() {
  if (_anyOverlayOpen()) return false;
  if (_viewIndex >= 0 && _viewIndex < _viewHistory.length - 1) {
    _viewIndex++;
    _applyView(_viewHistory[_viewIndex], 'forward');
    return true;
  }
  return false;
}

function _anyOverlayOpen() {
  if (document.getElementById('caseContextMenu')) return true;
  const lb = document.getElementById('lightboxOverlay');
  if (lb && lb.classList.contains('active')) return true;
  if (document.querySelector('.modal-overlay.open')) return true;
  if (document.getElementById('reportPreviewOverlay')) return true;
  const sidebar = document.getElementById('sidebar');
  if (sidebar && sidebar.classList.contains('open')) return true;
  return false;
}

// 仅关闭最上层浮层（不动视图历史）。有浮层被关返回 true。
function _closeTopOverlay() {
  if (document.getElementById('caseContextMenu')) { closeCaseContextMenu(); return true; }
  const lb = document.getElementById('lightboxOverlay');
  if (lb && lb.classList.contains('active')) { closeLightbox(); return true; }
  const openModals = document.querySelectorAll('.modal-overlay.open');
  if (openModals.length) { openModals[openModals.length - 1].classList.remove('open'); return true; }
  const sidebar = document.getElementById('sidebar');
  if (sidebar && sidebar.classList.contains('open')) {
    if (typeof closeMobileSidebar === 'function') closeMobileSidebar();
    else sidebar.classList.remove('open');
    return true;
  }
  return false;
}

// 离开病例表单回到总览时，释放本会话累积的本地预览 blob，避免移动端内存堆积
// （CT 大图每张数 MB，一次门诊可能上传几十张）。
function _revokeFreshUploadPreviews() {
  for (const id in _freshUploadPreviews) {
    try { URL.revokeObjectURL(_freshUploadPreviews[id]); } catch { /* ignore */ }
    delete _freshUploadPreviews[id];
  }
}

async function openDashboardView() {
  _revokeFreshUploadPreviews();
  // #13 — show skeleton rows while the cloud list is in flight (only when there's
  // nothing to show yet, so we don't flash placeholders over existing content).
  if (isCloudMode() && isLoggedIn() && typeof renderSkeletonList === 'function') {
    const listEl = document.getElementById('caseList');
    if (listEl && !listEl.querySelector('.case-item')) {
      renderSkeletonList(listEl, 5);
    }
  }
  await syncCloudCasesToLocal();
  if (typeof refreshAdmissionTasks === 'function') await refreshAdmissionTasks();
  showPage('dashboard');
}

async function openTodoWorkbenchView(scope) {
  _revokeFreshUploadPreviews();
  await syncCloudCasesToLocal();
  if (typeof refreshAdmissionTasks === 'function') await refreshAdmissionTasks();
  if (scope) currentTodoScope = scope;
  currentCaseId = null;
  showPage('todos');
  renderSidebar();
  updateStats();
  if (typeof renderTodoWorkbench === 'function') renderTodoWorkbench();
}

async function syncCloudCasesToLocal() {
  if (!isCloudMode() || !isLoggedIn()) return;
  try {
    const result = await backendRequest('/api/cases/?limit=100');
    const cloudCases = Array.isArray(result?.items) ? result.items : [];
    if (!cloudCases.length) return;
    const cases = loadDB();
    cloudCases.forEach(item => {
      const localId = `cloud_${item.id}`;
      let c = cases.find(x => x.backendId === item.id || x.id === localId);
      if (!c) {
        c = { id: localId, data: {}, media: {}, aiGenCount: item.ai_gen_count || 0 };
        cases.push(c);
      }
      c.backendId = item.id;
      c.backendPatientId = item.patient_id;
      c.module = item.module;
      c.center_id = item.center_id || null;
      c.centerId = item.center_id || null;
      c.createdAt = (item.created_at || '').slice(0, 10);
      c.created_by = item.created_by;
      c.assigned_to = item.assigned_to;
      c.workflow_state = item.workflow_state || c.workflow_state;
      c.current_reviewer_id = item.current_reviewer_id ?? c.current_reviewer_id;
      c.cloudSyncedAt = new Date().toISOString();
      // 审核状态以后端 workflow_state 为权威（submitted/reviewing/approved/rejected），
      // 不能再用粗粒度的 status==='complete' 折叠——否则“审核中/驳回修改”刷新列表后
      // 会被错误地还原成“待审核”，造成显示与真实状态不符。
      // 仅当处于“真实评审态”时覆盖；draft/缺省沿用旧行为，避免把草稿大面积改标。
      const REVIEW_STATES = ['submitted', 'reviewing', 'approved', 'rejected'];
      const reviewStatus = REVIEW_STATES.includes(item.workflow_state)
        ? WORKFLOW_STATE_FROM_BACKEND[item.workflow_state]
        : (item.status === 'complete' ? '审核通过' : (c.data?.workflow_review_status || '待审核'));
      c.data = {
        ...(c.data || {}),
        basic_name: item.patient_name || c.data?.basic_name || '',
        name: item.patient_name || c.data?.name || '',
        workflow_review_status: reviewStatus,
        workflow_reject_reason: item.rejected_reason || c.data?.workflow_reject_reason || ''
      };
      c.aiGenCount = item.ai_gen_count || c.aiGenCount || 0;
    });
    saveDB(cases);
  } catch (err) {
    showToast('⚠️', `云端病例列表同步失败：${err.message}`);
  }
}

async function hydrateCloudCase(c) {
  if (!c?.backendId || !isCloudMode() || !isLoggedIn()) return c;
  try {
    const detail = await backendRequest(`/api/cases/${c.backendId}`);

    // ── 基本信息 ──────────────────────────────────────────────
    c.data = { ...(c.data || {}), ...(detail.data || {}) };
    c.data.basic_name = detail.patient_name || c.data.basic_name || c.data.name || '';
    c.data.name       = detail.patient_name || c.data.name || c.data.basic_name || '';
    c.module          = detail.module || c.module;
    c.created_by      = detail.created_by;
    c.assigned_to     = detail.assigned_to;
    c.center_id       = detail.center_id || null;
    c.centerId        = detail.center_id || null;

    // ── 影像：以后端为权威，按 slot_name 分组重建数组 ──────────
    //
    // 旧逻辑：c.media[slot] = singleObj   ← 多张时后者覆盖前者，只剩最后一张
    // 新逻辑：c.media[slot] = [obj, obj…] ← 按上传顺序保留全部
    //
    // 同时刷新 COS 预签名 URL（默认 1 小时过期，每次打开病例自动续期）。
    const newMedia = {};
    (detail.media_files || [])
      .sort((a, b) => a.id - b.id)          // 按上传先后排序
      .forEach(m => {
        const filename = (m.file_path || '').split('/').pop() || '';
        const item = {
          name:          m.description || m.slot_name,
          data:          resolveBackendAssetUrl(m.access_url || m.file_path),
          filename,
          mime:          m.file_type  || '',
          cloud:         true,
          backendMediaId: m.id,
          backendCaseId:  detail.id,
          fileSize:      m.file_size,
          uploadedAt:    m.uploaded_at,
        };
        if (!newMedia[m.slot_name]) newMedia[m.slot_name] = [];
        newMedia[m.slot_name].push(item);
      });
    c.media = newMedia;   // 完整替换：后端是唯一事实来源，不保留本地孤儿对象

    // ── 持久化到 localStorage ────────────────────────────────
    const cases = loadDB();
    const idx = cases.findIndex(x => x.id === c.id);
    if (idx >= 0) cases[idx] = c;
    saveDB(cases);
  } catch (err) {
    showToast('⚠️', `云端病例同步失败：${err.message}`);
  }
  return c;
}

async function openCase(id) {
  flushAutoSave();   // 防 250ms 窗口内的输入被云端 hydrate 覆盖
  const c = loadDB().find(item => item.id === id);
  if (!canCurrentUserViewCase(c)) {
    showToast('⚠️', '当前账号无权查看此病例');
    openDashboardView();
    return;
  }
  await hydrateCloudCase(c);
  currentCaseId = id;
  currentCasePhase = 'preop';
  showPage('form');
  renderCaseForm(id);
  renderSidebar();
}

// ===================== CASE CARD QUICK-ACTION MENU =====================
// 长按（触屏 ~500ms）/ 右键 / ⋯ 按钮 → 打开 .case-context-menu。
// 不影响卡片点击进入 openCase（这些入口都 stopPropagation）。
let _caseMenuOutsideHandler = null;
let _caseMenuKeyHandler = null;
let _caseLongPressTimer = null;
let _caseMenuSourceEl = null;   // 被长按/右键的卡片，菜单打开期间高亮，关闭时还原

function closeCaseContextMenu() {
  const menu = document.getElementById('caseContextMenu');
  if (menu) menu.remove();
  if (_caseMenuSourceEl) { _caseMenuSourceEl.classList.remove('dash-card--menu-active'); _caseMenuSourceEl = null; }
  if (_caseMenuOutsideHandler) {
    document.removeEventListener('click', _caseMenuOutsideHandler, true);
    document.removeEventListener('contextmenu', _caseMenuOutsideHandler, true);
    _caseMenuOutsideHandler = null;
  }
  if (_caseMenuKeyHandler) {
    document.removeEventListener('keydown', _caseMenuKeyHandler, true);
    _caseMenuKeyHandler = null;
  }
}

function openCaseContextMenu(caseId, x, y, sourceEl) {
  closeCaseContextMenu();
  const c = loadDB().find(item => item.id === caseId);
  if (!c) return;
  const fav = !!c.favorite;
  const safeId = escapeJsArg(caseId);

  // 高亮触发卡片，让人一眼看出菜单属于哪个病例。
  _caseMenuSourceEl = sourceEl || document.querySelector(`.dash-card[data-case-id="${(window.CSS && CSS.escape) ? CSS.escape(caseId) : caseId}"]`) || null;
  if (_caseMenuSourceEl) _caseMenuSourceEl.classList.add('dash-card--menu-active');

  // 菜单项不再用内联 onclick；dispatcher 通过 data-fn 调用对应全局函数（接收 caseId 一个参数）。
  const items = [
    { label: '📄 导出病例简报 HTML', fn: 'exportCaseBriefHtml' },
    { label: '🖨️ 导出病例简报 PDF', fn: 'exportCaseBriefPdf' },
    { label: '📊 导出简报 PPT (.pptx)', fn: 'exportCaseBriefPptx' },
    { label: '🤖 AI 生成汇报型 PPT', fn: 'exportCaseNarrativePptx' },
    { label: '🤖 AI 智能简报', fn: 'generateCaseBriefAI' },
    { label: fav ? '☆ 取消星标' : '★ 设为星标', fn: 'toggleFavorite' },
    { label: '📋 复制患者基本信息', fn: 'copyCaseBasicInfo' },
    { label: '🗑️ 删除病例', fn: 'deleteCase', danger: true },
  ];

  const menu = document.createElement('div');
  menu.id = 'caseContextMenu';
  menu.className = 'case-context-menu';
  menu.setAttribute('role', 'menu');
  menu.innerHTML = items.map((it, i) => `
    <button class="case-context-menu-item${it.danger ? ' danger' : ''}" role="menuitem" tabindex="${i === 0 ? '0' : '-1'}"
      data-action="case-context-menu-item" data-fn="${escapeHtml(it.fn)}" data-case-id="${safeId}">${escapeHtml(it.label)}</button>`).join('');
  document.body.appendChild(menu);

  // 定位：固定定位锚定在 (x,y)（视口坐标），超出视口则回拉。
  // 注意：必须显式 position:fixed —— 否则 left/top 在默认 static 下完全无效，
  // 菜单会掉到页面流末尾，离被操作的卡片十万八千里（本次修复的根因）。
  menu.style.position = 'fixed';
  const vw = window.innerWidth, vh = window.innerHeight;
  const rect = menu.getBoundingClientRect();
  let left = x, top = y;
  if (left + rect.width > vw - 8) left = Math.max(8, vw - rect.width - 8);
  if (top + rect.height > vh - 8) top = Math.max(8, vh - rect.height - 8);
  menu.style.left = left + 'px';
  menu.style.top = top + 'px';

  const firstItem = menu.querySelector('.case-context-menu-item');
  if (firstItem) firstItem.focus();

  _caseMenuOutsideHandler = (e) => { if (!menu.contains(e.target)) closeCaseContextMenu(); };
  _caseMenuKeyHandler = (e) => {
    const btns = [...menu.querySelectorAll('.case-context-menu-item')];
    const idx = btns.indexOf(document.activeElement);
    if (e.key === 'Escape') { e.preventDefault(); closeCaseContextMenu(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); (btns[idx + 1] || btns[0]).focus(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); (btns[idx - 1] || btns[btns.length - 1]).focus(); }
  };
  // 捕获阶段监听，避免菜单内点击在冒泡时被外部处理误关。
  setTimeout(() => {
    document.addEventListener('click', _caseMenuOutsideHandler, true);
    document.addEventListener('contextmenu', _caseMenuOutsideHandler, true);
    document.addEventListener('keydown', _caseMenuKeyHandler, true);
  }, 0);
}

// 右键卡片：阻止默认菜单，在卡片下方打开自定义菜单。
function handleCaseCardContextMenu(e, caseId) {
  e.preventDefault();
  e.stopPropagation();
  const card = e.currentTarget;
  const r = card.getBoundingClientRect();
  openCaseContextMenu(caseId, r.left, r.bottom + 4, card);
}

// ⋯ 按钮：在所属卡片下方打开菜单。
function handleCaseCardMenuBtn(e, caseId) {
  e.preventDefault();
  e.stopPropagation();
  const card = e.currentTarget.closest('.dash-card');
  const r = (card || e.currentTarget).getBoundingClientRect();
  openCaseContextMenu(caseId, r.left, r.bottom + 4, card);
}

// 长按（触屏）：500ms 后在卡片下方打开菜单；移动/抬手则取消。
// 锚定在卡片下方（而非手指落点），避免“忘记长按的是哪个病例”。
function handleCaseCardTouchStart(e, caseId) {
  const card = e.currentTarget;
  const r = card ? card.getBoundingClientRect() : null;
  clearTimeout(_caseLongPressTimer);
  _caseLongPressTimer = setTimeout(() => {
    _caseLongPressTimer = null;
    if (r) openCaseContextMenu(caseId, r.left, r.bottom + 4, card);
    else openCaseContextMenu(caseId, 0, 0);
  }, 500);
}
function handleCaseCardTouchEnd() {
  if (_caseLongPressTimer) { clearTimeout(_caseLongPressTimer); _caseLongPressTimer = null; }
}

// ===================== 移动端手势：左滑返回上一页 =====================
// 统一“返回上一层”：操作菜单 > 图片灯箱 > 模态框（最上层） > 病例表单页→总览。
// 已在顶层（病例总览）时返回 false（无副作用）。
function goBack() {
  if (document.getElementById('caseContextMenu')) { closeCaseContextMenu(); return true; }
  const lb = document.getElementById('lightboxOverlay');
  if (lb && lb.classList.contains('active')) { closeLightbox(); return true; }
  const openModals = document.querySelectorAll('.modal-overlay.open');
  if (openModals.length) { openModals[openModals.length - 1].classList.remove('open'); return true; }
  const form = document.getElementById('page-form');
  if (form && form.classList.contains('active')) { openDashboardView(); return true; }
  return false;
}

// 触屏左滑检测：仅在“以水平为主、距离/速度达标、且起点不在横向滚动容器/输入控件内”时触发。
let _swipeStartX = 0, _swipeStartY = 0, _swipeStartT = 0, _swipeTracking = false;

function _swipeStartsInScrollable(target) {
  let el = target;
  while (el && el !== document.body && el.nodeType === 1) {
    if (el.scrollWidth - el.clientWidth > 12) {
      const ox = getComputedStyle(el).overflowX;
      if (ox === 'auto' || ox === 'scroll') return true;
    }
    el = el.parentElement;
  }
  return false;
}

document.addEventListener('touchstart', (e) => {
  if (!e.touches || e.touches.length !== 1) { _swipeTracking = false; return; }
  const tgt = e.target;
  // 输入/可编辑/操作菜单/灯箱/弹窗/预览层内不拦截，避免与原生交互、弹窗滑动冲突
  if (tgt && tgt.closest && tgt.closest('input, textarea, select, [contenteditable="true"], .case-context-menu, #lightboxOverlay, .modal-overlay, .report-preview-overlay, .report-preview-frame, [data-no-swipe]')) {
    _swipeTracking = false; return;
  }
  if (_swipeStartsInScrollable(tgt)) { _swipeTracking = false; return; }
  const t = e.touches[0];
  _swipeStartX = t.clientX; _swipeStartY = t.clientY; _swipeStartT = Date.now();
  _swipeTracking = true;
}, { passive: true });

document.addEventListener('touchend', (e) => {
  if (!_swipeTracking) return;
  _swipeTracking = false;
  const t = e.changedTouches && e.changedTouches[0];
  if (!t) return;
  const dx = t.clientX - _swipeStartX;
  const dy = t.clientY - _swipeStartY;
  const dt = Date.now() - _swipeStartT;
  // 判定为有效横向滑动：位移足够、以水平为主、足够快
  const horizontal = Math.abs(dx) > 64 && Math.abs(dx) > Math.abs(dy) * 1.6 && dt < 700;
  if (!horizontal) return;
  if (dx > 0) {
    // 左→右滑：返回上一页（标准移动端「后退」手势）
    navBack();
  } else {
    // 右→左滑：进入下一页（重放被后退出去的视图）
    navForward();
  }
}, { passive: true });

function filterCases(mod) {
  currentModuleFilter = mod;
  document.querySelectorAll('#filterBar .filter-chip').forEach(b => b.classList.remove('active'));
  document.getElementById('filter-' + mod)?.classList.add('active');
  renderSidebar();
  renderDashboard(mod);
}

function toggleFavorite(caseId) {
  const cases = loadDB();
  const c = cases.find(x => x.id === caseId);
  if (!c) return;
  c.favorite = !c.favorite;
  saveDB(cases);
  renderSidebar();
  renderDashboard(currentModuleFilter || 'all');
  renderCaseForm(caseId);
  showToast(c.favorite ? '★' : '☆', c.favorite ? '已设为星标病例' : '已取消星标');
}

async function promptAddTag(caseId) {
  const tag = await showAppPrompt({
    title: '添加病例标签',
    message: '例如：典型病例、科研素材、随访病例、需主任审核、教学病例',
    placeholder: '输入标签（≤16 字）',
    maxLength: 16,
    confirmText: '添加',
  });
  if (!tag) return;
  const cleanTag = tag.trim().slice(0, 16);
  if (!cleanTag) return;
  const cases = loadDB();
  const c = cases.find(x => x.id === caseId);
  if (!c) return;
  c.tags = Array.isArray(c.tags) ? c.tags : [];
  if (!c.tags.includes(cleanTag)) c.tags.push(cleanTag);
  saveDB(cases);
  renderSidebar();
  renderDashboard(currentModuleFilter || 'all');
  renderCaseForm(caseId);
  showToast('🏷️', `已添加标签：${cleanTag}`);
}

function removeCaseTag(caseId, tag) {
  const cases = loadDB();
  const c = cases.find(x => x.id === caseId);
  if (!c) return;
  c.tags = getCaseTags(c).filter(item => item !== tag);
  saveDB(cases);
  renderSidebar();
  renderDashboard(currentModuleFilter || 'all');
  renderCaseForm(caseId);
  showToast('🏷️', `已移除标签：${tag}`);
}

function getModuleChangePromptText(currentModule) {
  return Object.entries(MODULES).map(([code, mod]) => {
    const mark = code === currentModule ? '（当前）' : '';
    return `${code}：${mod.name}${mark}`;
  }).join('\n');
}

async function promptChangeCaseModule(caseId) {
  const cases = loadDB();
  const c = cases.find(x => x.id === caseId);
  if (!c) return;
  const currentModule = c.module;
  const raw = await showAppPrompt({
    title: '更改病种分类',
    message: `请选择新的病种诊断分类代码：\n\n${getModuleChangePromptText(currentModule)}`,
    placeholder: '输入 A-O 中的代码',
    defaultValue: currentModule,
    maxLength: 1,
    confirmText: '下一步',
  });
  if (!raw) return;
  const nextModule = raw.trim().toUpperCase();
  if (!MODULES[nextModule]) {
    showToast('⚠️', '病种代码无效，请输入 A-O 中的代码');
    return;
  }
  if (nextModule === currentModule) {
    showToast('ℹ️', '病种分类未变化');
    return;
  }
  const oldName = MODULES[currentModule]?.name || currentModule;
  const nextName = MODULES[nextModule].name;
  const okChange = await showAppConfirm({
    title: '更改病种分类',
    message: `确定将该病例从“${oldName}”改为“${nextName}”吗？\n已填写资料和影像会保留，但表单模板会切换到新病种。`,
    confirmText: '确认更改',
  });
  if (!okChange) return;

  c.module = nextModule;
  c.moduleName = nextName;
  c.data = c.data || {};
  c.data.workflow_module_changed_from = currentModule;
  c.data.workflow_module_changed_at = new Date().toISOString();

  if (isCloudMode() && isLoggedIn() && c.backendId) {
    try {
      const updated = await backendRequest(`/api/cases/${c.backendId}`, {
        method: 'PUT',
        body: JSON.stringify({ module: nextModule }),
      });
      c.module = updated.module || nextModule;
      c.moduleName = updated.module_name || nextName;
      c.cloudSyncedAt = new Date().toISOString();
      showToast('☁️', `云端病种分类已改为：${c.moduleName}`);
    } catch (err) {
      showToast('⚠️', `云端重分类失败：${err.message}；本地已保留修改`);
    }
  } else {
    showToast('✅', `病种分类已改为：${nextName}`);
  }

  saveDB(cases);
  renderSidebar();
  renderDashboard(currentModuleFilter || 'all');
  renderCaseForm(caseId);
  updateStats();
}

// 新建病例的病种 picker 各大类标题图标（仅视觉，沿用原静态清单的 emoji）。
const NEW_CASE_CATEGORY_ICONS = {
  '足踝矫形': '🛠',
  '足踝创伤': '🚑',
  '运动医学': '🏃',
  '足踝其他/神经': '🦶',
};

// 从 MODULES + MODULE_CATEGORIES 动态生成病种 radio 列表，替换原写死的静态清单。
// 新增任何病种（如 O 腓骨肌腱滑脱）会自动出现；病种名直接取 MODULES[x].name，
// 不再有手写标签漂移（如旧清单把 E 误写成「跟腱断裂」）。
function renderNewCaseModulePicker() {
  const wrap = document.getElementById('ncModulePicker');
  if (!wrap || typeof MODULES === 'undefined') return;
  // 大类顺序：先按 MODULE_CATEGORIES，再兜底补上任何额外 category，保证没有病种被漏掉。
  const ordered = [];
  const seen = new Set();
  const pushCat = (c) => { if (c && !seen.has(c)) { seen.add(c); ordered.push(c); } };
  (typeof MODULE_CATEGORIES !== 'undefined' ? MODULE_CATEGORIES : []).forEach(pushCat);
  Object.values(MODULES).forEach(m => pushCat(m.category || '其他'));

  wrap.innerHTML = ordered.map(cat => {
    const items = Object.entries(MODULES).filter(([, m]) => (m.category || '其他') === cat);
    if (!items.length) return '';
    const opts = items.map(([code, m]) =>
      `<label class="module-pick-opt"><input type="radio" name="nc-module" value="${escapeHtml(code)}">` +
      `<span>${escapeHtml(m.icon || '')} ${escapeHtml(m.name || code)}</span></label>`
    ).join('');
    const catIcon = NEW_CASE_CATEGORY_ICONS[cat] || '';
    return `<div class="module-cat">` +
      `<div class="module-cat-title">${escapeHtml((catIcon ? catIcon + ' ' : '') + cat)}</div>` +
      `<div class="module-pick">${opts}</div></div>`;
  }).join('');
}

function showNewCaseModal() {
  if (!isLoggedIn()) {
    showToast('🔐', '请先登录账号后再新建病例');
    showAuthModal();
    return;
  }
  document.getElementById('newCaseModal').classList.add('open');
  renderNewCaseModulePicker();
  clearNewCaseErrors();
  const submitBtn = document.getElementById('newCaseSubmitBtn');
  if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = '创建并录入'; }
  // Set today's date
  const today = new Date().toISOString().split('T')[0];
  document.getElementById('nc-date').value = today;
  refreshNewCaseCenterOptions();
  setTimeout(() => document.getElementById('nc-name')?.focus(), 30);
}

function closeModal(id) {
  document.getElementById(id).classList.remove('open');
}

function showFeatureOverviewModal() {
  document.getElementById('featureOverviewModal')?.classList.add('open');
}

function clearNewCaseErrors() {
  document.querySelectorAll('#newCaseModal .field-input.is-invalid').forEach(el => el.classList.remove('is-invalid'));
  document.querySelectorAll('#newCaseModal .field-error[data-new-case-error]').forEach(el => el.remove());
  document.querySelectorAll('#newCaseModal .module-pick-categories.is-invalid').forEach(el => el.classList.remove('is-invalid'));
}

function markNewCaseError(el, message) {
  if (!el) return;
  el.classList.add('is-invalid');
  const target = el.closest('.field-group') || el.parentElement;
  if (!target?.querySelector('[data-new-case-error]')) {
    const err = document.createElement('div');
    err.className = 'field-error';
    err.dataset.newCaseError = '1';
    err.textContent = message;
    target?.appendChild(err);
  }
}

function refreshNewCaseCenterOptions() {
  const group = document.getElementById('newCaseCenterGroup');
  const select = document.getElementById('nc-center');
  if (!group || !select) return;
  const centers = getCachedCenters();
  const show = isCloudMode() && isLoggedIn() && centers.length > 0;
  group.hidden = !show;
  if (!show) return;
  const defaultId = getDefaultCenterId();
  select.innerHTML = '<option value="">使用默认中心</option>' + centers.map(c =>
    `<option value="${c.id}" ${String(defaultId || '') === String(c.id) ? 'selected' : ''}>${escapeHtml(c.name)}</option>`
  ).join('');
}

async function createCase() {
  const submitBtn = document.getElementById('newCaseSubmitBtn');
  const name = document.getElementById('nc-name').value.trim();
  const date = document.getElementById('nc-date').value;
  const gender = document.getElementById('nc-gender').value;
  const age = document.getElementById('nc-age').value;
  const caseid = document.getElementById('nc-caseid').value;
  const moduleEl = document.querySelector('input[name="nc-module"]:checked');
  const centerSelect = document.getElementById('nc-center');
  const selectedCenterId = centerSelect?.value ? Number(centerSelect.value) : getDefaultCenterId();

  clearNewCaseErrors();
  const nameEl = document.getElementById('nc-name');
  const moduleWrap = document.querySelector('#newCaseModal .module-pick-categories');
  if (!name || !moduleEl) {
    if (!name) markNewCaseError(nameEl, '请填写患者姓名');
    if (!moduleEl) markNewCaseError(moduleWrap, '请选择病例模块');
    (name ? moduleWrap : nameEl)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    (name ? moduleWrap : nameEl)?.focus?.();
    showToast('⚠️', '请补齐新建病例的必填项');
    return;
  }

  if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = '创建中...'; }

  try {
    const newCase = {
      id: 'case_' + Date.now(),
      module: moduleEl.value,
      createdAt: date || new Date().toISOString().split('T')[0],
      createdByUserId: getAuthUser()?.id || '',
      createdByUsername: getAuthUser()?.username || '',
      ownerRole: getAuthUser()?.role || '',
      center_id: selectedCenterId || null,
      centerId: selectedCenterId || null,
      data: {
        basic_name: name,
        name: name,
        basic_date: date,
        date: date,
        basic_gender: gender,
        gender: gender,
        basic_age: age,
        age: age,
        basic_caseid: caseid,
        caseid: caseid
      },
      media: {},
      aiGenCount: 0
    };

    const cases = loadDB();
    cases.unshift(newCase);
    if (isCloudMode() && isLoggedIn()) {
      try {
        await ensureBackendCase(newCase);
        showToast('☁️', '病例已同步到云端');
      } catch (err) {
        showToast('⚠️', `病例云端同步失败：${err.message}`);
      }
    }
    saveDB(cases);
    closeModal('newCaseModal');

    // reset form
    document.getElementById('nc-name').value = '';
    document.getElementById('nc-age').value = '';
    document.getElementById('nc-caseid').value = '';
    document.getElementById('nc-gender').value = '';
    if (centerSelect) centerSelect.value = getDefaultCenterId() || '';
    document.querySelectorAll('input[name="nc-module"]').forEach(r => r.checked = false);

    openCase(newCase.id);
    showToast('✅', `病例 ${name} 已创建`);
  } catch (err) {
    showToast('⚠️', err.message || '病例创建失败，请稍后重试');
  } finally {
    if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = '创建并录入'; }
  }
}



/* assets/agent-workflows.js */
// ===================== AGENT WORKFLOWS =====================
// 规则型自动化中心：默认不调用大模型，避免 7x24 工作流变成 token 黑洞。

let agentWorkflowState = {
  workflows: [],
  runs: [],
  lastLocalRun: null,
  loading: false,
};

function getDefaultAgentWorkflows() {
  return [
    {
      id: 'daily_quality_scan',
      name: '每日资料质控巡检',
      cadence: '建议每 1-6 小时',
      mode: 'rules_only',
      ai_invoked: false,
      token_estimate: 0,
      description: '检查病例完整度、影像槽位、随访截止、中心归属和科研导出准备度。',
    },
    {
      id: 'event_guardrail_scan',
      name: '事件触发安全闸门',
      cadence: '上传/导入/同步前触发',
      mode: 'rules_only',
      ai_invoked: false,
      token_estimate: 0,
      description: '用于同步云端、正式导出或批量处理前的低成本安全检查。',
    },
  ];
}

function getAgentModeLabel(mode) {
  return ({
    rules_only: '规则巡检',
    human_confirmed_ai: '人工确认 AI',
  })[mode] || mode || '未定义';
}

function normalizeAgentSeverity(severity) {
  return ({
    critical: '严重',
    warning: '提醒',
    info: '提示',
  })[severity] || severity || '提示';
}

function getAgentRunSummary(run) {
  const summary = run?.summary || {};
  return {
    caseCount: Number(summary.case_count || 0),
    findingCount: Number(summary.finding_count || 0),
    critical: Number(summary.severity_counts?.critical || 0),
    warning: Number(summary.severity_counts?.warning || 0),
    info: Number(summary.severity_counts?.info || 0),
    aiInvoked: !!(run?.ai_invoked || summary.ai_invoked),
    tokenEstimate: Number(run?.token_estimate ?? summary.token_estimate ?? 0),
  };
}

function renderAgentWorkflowPanel() {
  const metrics = document.getElementById('agentMetricsGrid');
  const workflowList = document.getElementById('agentWorkflowList');
  const runList = document.getElementById('agentRunList');
  const runBtn = document.getElementById('agentRunBtn');
  if (!metrics || !workflowList || !runList) return;

  const latestRun = agentWorkflowState.runs[0] || agentWorkflowState.lastLocalRun;
  const summary = getAgentRunSummary(latestRun);
  const cloudReady = isCloudMode() && isLoggedIn();
  const adminReady = cloudReady && isSystemAdminUser();

  if (runBtn) {
    runBtn.disabled = agentWorkflowState.loading || (cloudReady && !adminReady);
    runBtn.textContent = agentWorkflowState.loading ? '巡检中...' : '运行质控巡检';
  }

  metrics.innerHTML = [
    { label: '病例数', value: summary.caseCount },
    { label: '问题数', value: summary.findingCount },
    { label: '严重', value: summary.critical },
    { label: 'Token', value: summary.tokenEstimate },
  ].map(item => `
    <div class="agent-metric-card">
      <span>${escapeHtml(item.label)}</span>
      <strong>${escapeHtml(String(item.value))}</strong>
    </div>
  `).join('');

  const modeHint = !cloudReady
    ? '当前为本地模式：可扫描浏览器本地病例；部署到服务器后由同一架构接入定时任务。'
    : adminReady
      ? '当前为云端管理员模式：可手动运行巡检，服务器可用 cron/systemd 定时调用同一接口。'
      : '当前为云端普通用户：可查看架构说明，运行巡检需要系统管理员权限。';

  workflowList.innerHTML = `
    <div class="agent-mode-hint">${escapeHtml(modeHint)}</div>
    ${(agentWorkflowState.workflows.length ? agentWorkflowState.workflows : getDefaultAgentWorkflows()).map(w => `
      <div class="agent-workflow-item">
        <div>
          <strong>${escapeHtml(w.name)}</strong>
          <span>${escapeHtml(w.description || '')}</span>
        </div>
        <div class="agent-workflow-badges">
          <span>${escapeHtml(getAgentModeLabel(w.mode))}</span>
          <span>${w.ai_invoked ? '需确认 Token' : '0 Token'}</span>
          <span>${escapeHtml(w.cadence || '按需')}</span>
        </div>
      </div>
    `).join('')}
  `;

  const runs = agentWorkflowState.runs.length
    ? agentWorkflowState.runs
    : (agentWorkflowState.lastLocalRun ? [agentWorkflowState.lastLocalRun] : []);
  runList.innerHTML = runs.length
    ? runs.map(renderAgentRunItem).join('')
    : '<div class="settings-hint">尚未运行巡检。第一步建议先手动运行一次，确认规则命中是否符合你的工作习惯。</div>';
}

function renderAgentRunItem(run) {
  const summary = getAgentRunSummary(run);
  const findings = run?.result?.findings || [];
  const createdAt = run?.created_at || run?.createdAt || '';
  return `
    <div class="agent-run-item">
      <div class="agent-run-head">
        <div>
          <strong>${escapeHtml(run.workflow_name || '本地资料质控巡检')}</strong>
          <span>${escapeHtml(createdAt ? new Date(createdAt).toLocaleString() : '刚刚')}</span>
        </div>
        <div class="agent-workflow-badges">
          <span>${summary.aiInvoked ? 'AI 已调用' : '规则巡检'}</span>
          <span>${summary.tokenEstimate} Token</span>
          <span>${escapeHtml(run.status || 'completed')}</span>
        </div>
      </div>
      <div class="agent-run-summary">
        病例 ${summary.caseCount} 例，发现 ${summary.findingCount} 项：严重 ${summary.critical}，提醒 ${summary.warning}，提示 ${summary.info}。
      </div>
      <div class="agent-finding-list">
        ${findings.slice(0, 8).map(item => `
          <div class="agent-finding-item ${escapeHtml(item.severity || 'info')}">
            <strong>${escapeHtml(normalizeAgentSeverity(item.severity))} · ${escapeHtml(item.category || '质控')}</strong>
            <span>${escapeHtml(item.case_title || item.caseTitle || '全局')}：${escapeHtml(item.message || '')}</span>
            <em>${escapeHtml(item.next_action || item.nextAction || '')}</em>
          </div>
        `).join('')}
        ${findings.length > 8 ? `<div class="settings-hint">还有 ${findings.length - 8} 项未展开，可在后端运行记录中查看完整 JSON。</div>` : ''}
      </div>
    </div>
  `;
}

async function loadAgentWorkflowPanel() {
  if (!document.getElementById('agentWorkflowList')) return;
  agentWorkflowState.workflows = getDefaultAgentWorkflows();
  renderAgentWorkflowPanel();

  if (!isCloudMode() || !isLoggedIn()) return;
  try {
    const defs = await backendRequest('/api/agent-workflows/');
    agentWorkflowState.workflows = Array.isArray(defs.items) ? defs.items : getDefaultAgentWorkflows();
    if (isSystemAdminUser()) {
      const runs = await backendRequest('/api/agent-workflows/runs?limit=5');
      agentWorkflowState.runs = Array.isArray(runs.items) ? runs.items : [];
    }
  } catch (err) {
    showToast('⚠️', `Agent 工作流读取失败：${err.message}`);
  }
  renderAgentWorkflowPanel();
}

async function runAgentWorkflowScan() {
  if (agentWorkflowState.loading) return;
  if (isCloudMode() && isLoggedIn() && !isSystemAdminUser()) {
    showToast('🔐', '运行云端自动化巡检需要系统管理员权限');
    return;
  }

  agentWorkflowState.loading = true;
  renderAgentWorkflowPanel();
  try {
    if (isCloudMode() && isLoggedIn()) {
      const run = await backendRequest('/api/agent-workflows/run', { method: 'POST' });
      agentWorkflowState.runs = [run, ...agentWorkflowState.runs].slice(0, 5);
      showToast('✅', '云端规则巡检完成，未调用大模型');
    } else {
      agentWorkflowState.lastLocalRun = runLocalAgentWorkflowScan();
      showToast('✅', '本地规则巡检完成，未调用大模型');
    }
  } catch (err) {
    showToast('⚠️', `Agent 巡检失败：${err.message}`);
  } finally {
    agentWorkflowState.loading = false;
    renderAgentWorkflowPanel();
  }
}

function runLocalAgentWorkflowScan() {
  const cases = getVisibleCases();
  const findings = [];
  const severityCounts = { critical: 0, warning: 0, info: 0 };
  const workflowCounts = {};
  const moduleCounts = {};
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const soon = new Date(today);
  soon.setDate(soon.getDate() + 7);

  cases.forEach(c => {
    const mod = MODULES[c.module];
    if (!mod) return;
    const progress = calcOverallProgress(c);
    const media = calcMediaProgress(c, mod);
    const stage = c.workflow_state || c.data?.workflow_review_status || c.data?.review_status || 'draft';
    workflowCounts[stage] = (workflowCounts[stage] || 0) + 1;
    moduleCounts[c.module] = (moduleCounts[c.module] || 0) + 1;

    if (progress < 60) {
      addLocalAgentFinding(findings, 'warning', '资料完整度', c, `病例完整度 ${progress}%，低于 60%。`, '补齐基础信息、分型、评分与治疗计划后再提交审核。');
    }
    if (media.filled === 0) {
      addLocalAgentFinding(findings, 'warning', '影像资料', c, '病例尚未上传任何影像或视频资料。', '按病种影像槽位上传术前、术中/治疗中和随访资料。');
    }
    if (getCachedCenters().length > 0 && !(c.center_id || c.centerId)) {
      addLocalAgentFinding(findings, 'info', '多中心归属', c, '病例未归属研究中心。', '正式进入多中心研究前建议补齐中心归属。');
    }

    const deadlineRaw = c.data?.workflow_deadline || c.data?.deadline;
    const deadline = deadlineRaw ? new Date(deadlineRaw) : null;
    if (deadline && !Number.isNaN(deadline.getTime()) && stage !== 'approved') {
      deadline.setHours(0, 0, 0, 0);
      if (deadline < today) {
        addLocalAgentFinding(findings, 'critical', '随访/审核截止', c, `流程截止日期已逾期：${deadlineRaw}。`, '立即处理待补资料、审核或随访任务。');
      } else if (deadline <= soon) {
        addLocalAgentFinding(findings, 'warning', '随访/审核截止', c, `流程截止日期将在 7 天内到期：${deadlineRaw}。`, '安排资料补录、审核或随访提醒。');
      }
    }

    if (c.module === 'N' && (!c.data?.imaging_ultrasound_done || c.data?.imaging_ultrasound_done === '未做')) {
      addLocalAgentFinding(findings, 'warning', '病种规则', c, '莫顿神经瘤病例未标记已完成超声检查。', '补充超声检查状态，并上传静态/动态超声资料。');
    }
    if (c.module === 'M' && (!c.data?.imaging_ct_done || c.data?.imaging_ct_done === '未做')) {
      addLocalAgentFinding(findings, 'warning', '病种规则', c, '跗骨联合病例未标记已完成 CT 检查。', '补充 CT 检查状态，便于联合范围和性质评估。');
    }
  });

  findings.forEach(item => { severityCounts[item.severity] = (severityCounts[item.severity] || 0) + 1; });
  const summary = {
    case_count: cases.length,
    finding_count: findings.length,
    severity_counts: severityCounts,
    workflow_counts: workflowCounts,
    module_counts: moduleCounts,
    ai_invoked: false,
    token_estimate: 0,
  };
  return {
    id: `local-${Date.now()}`,
    workflow_id: 'daily_quality_scan',
    workflow_name: '本地每日资料质控巡检',
    status: 'completed',
    trigger: 'manual',
    ai_invoked: false,
    token_estimate: 0,
    duration_ms: 0,
    summary,
    result: { summary, findings, truncated: false, generated_at: new Date().toISOString() },
    created_at: new Date().toISOString(),
  };
}

function addLocalAgentFinding(findings, severity, category, c, message, nextAction) {
  const mod = MODULES[c.module];
  const name = c.data?.basic_name || c.name || c.patient_name || c.patientName || '未命名病例';
  findings.push({
    severity,
    category,
    case_id: c.backendId || c.id,
    case_title: `${name} · ${mod?.name || c.module}`,
    message,
    next_action: nextAction,
  });
}



/* assets/ai.js */
// ===================== SSE STREAM READER =====================
/**
 * 调用后端流式接口（/api/ai/generate-stream）。
 * 每收到一个 token 片段就调用 onChunk(partialFullText)，完成后返回完整文本。
 * 自动处理 401 token 刷新重试。
 */
// signal: AbortController.signal — pass to allow mid-stream stopping
async function _callStreamCloud(path, requestBody, onChunk, signal) {
  const _doFetch = async () => {
    return fetch(`${getBackendBase()}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify(requestBody),
      signal
    });
  };

  let resp = await _doFetch();

  // 401 → 尝试刷新 token 后重试一次
  if (resp.status === 401) {
    const ok = await _tryRefreshToken();
    if (ok) {
      resp = await _doFetch();
    } else {
      clearAuth();
      if (typeof renderAuthState === 'function') renderAuthState();
      throw new Error('登录已过期，请重新登录');
    }
  }

  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data?.detail || `请求失败 ${resp.status}`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let fullText = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // 按行切分，保留未结束的行
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const data = line.slice(6).trim();
        if (data === '[DONE]') return fullText;
        // 1. Parse JSON — skip silently on malformed lines
        let parsed;
        try { parsed = JSON.parse(data); } catch { continue; }
        // 2. Application-level error sent inside the stream (always propagate)
        if (parsed.error) throw new Error(parsed.error);
        // 3. Accumulate text
        if (parsed.text) {
          fullText += parsed.text;
          onChunk(fullText);
        }
      }
    }
  } catch (err) {
    // AbortError = user clicked stop; return whatever we have so far (not an error)
    if (err.name === 'AbortError') return fullText;
    throw err;
  } finally {
    reader.releaseLock();
  }
  return fullText;
}


// ===================== CROSS-BORDER (PIPL) CONSENT =====================
// 跨境文书类型：这些类型在云端自动分层时会落到「高级档（境外 jeniya 模型）」，
// 即把（已脱敏的）病例文本传输出境。须与后端 DOCUMENT_TIER 的 advanced 档保持一致
// （见 docs/api-contract.md：advanced = case_report / academic_presentation / wechat_article / case_ppt）。
const CROSS_BORDER_DOC_TYPES = ['case_report', 'academic_presentation', 'wechat_article', 'case_ppt', 'ppt_narrative'];

// 「本次会话不再提示」的轻量记忆，仅 session 级（sessionStorage），关闭标签页即失效。
const _CROSS_BORDER_ACK_KEY = 'foot_ankle_cross_border_ack_v1';

function _crossBorderAckedThisSession() {
  try { return sessionStorage.getItem(_CROSS_BORDER_ACK_KEY) === '1'; }
  catch { return false; }
}
function _setCrossBorderAck() {
  try { sessionStorage.setItem(_CROSS_BORDER_ACK_KEY, '1'); } catch { /* 忽略存储异常 */ }
}

// 判断某 provider 是否为境外模型（手动选具体模型覆盖分层时用）。
function _isCrossBorderProvider(provider) {
  return ['anthropic', 'openai', 'gemini'].includes(String(provider || '').toLowerCase());
}

/**
 * 判断本次生成是否会触发数据出境，需要跨境知情同意确认。
 * 命中条件（任一）：
 *  ① 云端自动分层（用户未手动选具体模型）+ type 属于高级档跨境类型；
 *  ② 用户手动选了具体的境外模型 provider（anthropic/openai/gemini）。
 * 仅在云端模式（isCloudMode）下才有出境含义；本地演示模式直连用户自配 key，不在此范围。
 */
function _needsCrossBorderConsent(caseId, type) {
  if (typeof isCloudMode === 'function' && !isCloudMode()) return false;
  const sel = (typeof getSelectedGenerationModel === 'function')
    ? getSelectedGenerationModel(caseId)
    : { provider: '', model: '' };
  // 用户手动选了具体模型（覆盖分层）
  if (sel && sel.model) return _isCrossBorderProvider(sel.provider);
  // 自动分层：仅跨境文书类型会落到境外模型
  return CROSS_BORDER_DOC_TYPES.includes(type);
}

/**
 * 展示跨境数据知情同意确认框（自建 modal，无内联 onclick，按 SEC-6 走 data-action）。
 * 确认 → 调用 onConfirm()；取消 → 直接关闭，不发任何请求。
 */
let _crossBorderPending = null;   // { onConfirm } —— 等待用户决定的回调
function _showCrossBorderConsent(onConfirm) {
  _crossBorderPending = { onConfirm };
  closeCrossBorderConsent();   // 防重复叠加
  const ov = document.createElement('div');
  ov.id = 'crossBorderConsentOverlay';
  ov.className = 'report-preview-overlay';
  ov.innerHTML = `
    <div class="report-preview-dialog" role="dialog" aria-modal="true" aria-label="跨境数据知情同意确认" style="max-width:520px;">
      <div class="report-preview-bar">
        <span class="report-preview-title">🌐 跨境数据处理确认</span>
        <button class="report-preview-close" type="button" aria-label="取消" data-action="cancel-cross-border">✕</button>
      </div>
      <div style="padding:20px 22px;line-height:1.7;font-size:14px;color:var(--text);">
        <p style="margin:0 0 12px;">本功能将把<strong>脱敏后</strong>的病例文本传输至<strong>境外 jeniya 模型</strong>生成。系统已自动隐去患者姓名、身份证、手机、住址、住院号等个人信息。</p>
        <p style="margin:0 0 12px;">在继续之前，请确认：</p>
        <ol style="margin:0 0 16px;padding-left:20px;">
          <li>已获得患者对<strong>跨境数据处理</strong>的知情同意；</li>
          <li>不要在自由文本里手动填写无关第三方的身份信息。</li>
        </ol>
        <label style="display:flex;align-items:center;gap:8px;margin:0 0 18px;font-size:13px;color:var(--text3);cursor:pointer;">
          <input type="checkbox" id="crossBorderRemember" />
          本次会话不再提示
        </label>
        <div style="display:flex;gap:10px;justify-content:flex-end;">
          <button class="btn" type="button" data-action="cancel-cross-border">取消</button>
          <button class="btn btn-primary" type="button" data-action="confirm-cross-border">确认并继续</button>
        </div>
      </div>
    </div>`;
  ov.addEventListener('click', (e) => { if (e.target === ov) closeCrossBorderConsent(); });
  document.body.appendChild(ov);
}

function closeCrossBorderConsent() {
  const ov = document.getElementById('crossBorderConsentOverlay');
  if (ov) ov.remove();
}

/** dispatcher: 确认并继续 —— 记忆勾选则写入 session，关闭弹窗后执行真正的生成。 */
function confirmCrossBorder() {
  const remember = document.getElementById('crossBorderRemember')?.checked;
  if (remember) _setCrossBorderAck();
  const pending = _crossBorderPending;
  _crossBorderPending = null;
  closeCrossBorderConsent();
  if (pending && typeof pending.onConfirm === 'function') pending.onConfirm();
}

/** dispatcher: 取消 —— 中止生成，不发任何请求。 */
function cancelCrossBorder() {
  _crossBorderPending = null;
  closeCrossBorderConsent();
}

// ===================== AI GENERATION =====================
// 跨境合规入口：若本次生成会导致（脱敏）数据出境，先弹知情同意确认；
// 用户确认后再调用真正的生成逻辑 _doGenerateAI。其余情况直接生成。
async function generateAI(caseId, type) {
  if (_needsCrossBorderConsent(caseId, type) && !_crossBorderAckedThisSession()) {
    _showCrossBorderConsent(() => { _doGenerateAI(caseId, type); });
    return;
  }
  return _doGenerateAI(caseId, type);
}

// ── 生成过程状态机（流式渲染 / 停止 / 计时 / 重新生成） ──────────────────
const _aiGen = {
  abort: null,        // AbortController（停止生成）
  timerId: null,      // 计时器 interval
  startTs: 0,
  lastPaint: 0,       // 渲染节流时间戳
  caseId: null,
  type: null,
  modelLabel: '',
  partial: '',        // 已收到的流式文本（停止时保留）
  stickBottom: true,  // 是否自动跟随到底部（用户上滚则暂停跟随）
};

function _aiGenEl(id) { return document.getElementById(id); }

/** 生成 modal 的骨架：状态栏 + 输出区。整个生成周期只建一次，chunk 只更新输出区。 */
function _aiGenShell(statusText) {
  const body = _aiGenEl('aiModalBody');
  body.innerHTML = `
    <div class="ai-gen-statusbar">
      <span class="ai-gen-status" id="aiGenStatus">${escapeHtml(statusText)}</span>
      <span class="ai-gen-meta">
        <span id="aiGenModel">${escapeHtml(_aiGen.modelLabel)}</span>
        · <span id="aiGenTimer">0.0s</span>
        · <span id="aiGenChars">0 字</span>
      </span>
    </div>
    <div class="output-text" id="aiGenOutput"><span class="stream-cursor"></span></div>`;
  // 用户上滚则停止自动跟随；滚回底部恢复
  body.onscroll = () => {
    _aiGen.stickBottom = body.scrollHeight - body.scrollTop - body.clientHeight < 48;
  };
}

function _aiGenStartTimer() {
  _aiGen.startTs = Date.now();
  clearInterval(_aiGen.timerId);
  _aiGen.timerId = setInterval(() => {
    const el = _aiGenEl('aiGenTimer');
    if (el) el.textContent = ((Date.now() - _aiGen.startTs) / 1000).toFixed(1) + 's';
  }, 100);
}

function _aiGenStopTimer() {
  clearInterval(_aiGen.timerId);
  _aiGen.timerId = null;
}

function _aiGenElapsed() {
  return ((Date.now() - _aiGen.startTs) / 1000).toFixed(1);
}

/** 流式渲染（节流 ~120ms）：Markdown 实时格式化 + 字数 + 自动跟随滚动。 */
function _aiGenPaint(partial, force = false) {
  _aiGen.partial = partial;
  const now = Date.now();
  if (!force && now - _aiGen.lastPaint < 120) return;
  _aiGen.lastPaint = now;
  const out = _aiGenEl('aiGenOutput');
  if (!out) return;
  const status = _aiGenEl('aiGenStatus');
  if (status && status.textContent !== '正在生成…') status.textContent = '正在生成…';
  out.innerHTML = _formatAIText(partial) + '<span class="stream-cursor"></span>';
  const chars = _aiGenEl('aiGenChars');
  if (chars) chars.textContent = `${partial.length} 字`;
  if (_aiGen.stickBottom) {
    const body = _aiGenEl('aiModalBody');
    if (body) body.scrollTop = body.scrollHeight;
  }
}

/** footer 按阶段/文书类型渲染按钮。 */
function _aiGenFooter(stage) {
  const footer = _aiGenEl('aiModalFooter');
  if (!footer) return;
  const esc = escapeHtml;
  const caseAttr = `data-case-id="${esc(_aiGen.caseId || '')}" data-ai-type="${esc(_aiGen.type || '')}"`;
  if (stage === 'streaming') {
    footer.innerHTML = `
      <button class="btn btn-ghost" data-action="close-ai-modal">关闭</button>
      <button class="btn btn-danger" data-action="stop-generate-ai">⏹ 停止生成</button>`;
    footer.style.display = 'flex';
    return;
  }
  if (stage === 'hidden') { footer.style.display = 'none'; return; }
  // done / stopped：按文书类型给动作
  const isHtmlDoc = _aiGen.type === 'html_case_brief' || _aiGen.type === 'case_ppt';
  const briefBtns = _aiGen.type === 'html_case_brief'
    ? `<button class="btn btn-ghost" data-action="export-case-brief-ai-html">📄 简报HTML</button>
       <button class="btn btn-ghost" data-action="export-case-brief-ai-pdf">🖨️ 简报PDF</button>` : '';
  footer.innerHTML = `
    <button class="btn btn-ghost" data-action="close-ai-modal">关闭</button>
    <button class="btn btn-ghost" data-action="regenerate-ai" ${caseAttr}>🔄 重新生成</button>
    <button class="btn btn-ghost" data-action="copy-ai-output">📋 复制</button>
    ${isHtmlDoc ? '' : '<button class="btn btn-ghost" data-action="print-ai-output">🖨️ 打印</button>'}
    ${briefBtns}
    <button class="btn btn-primary" data-action="download-ai-output">⬇️ 下载</button>`;
  footer.style.display = 'flex';
}

/** dispatcher: 停止生成 —— 中止请求，保留已生成部分（可复制/下载/重新生成）。 */
function stopAIGeneration() {
  if (!_aiGen.abort) return;
  _aiGen.abort.abort();
  _aiGenStopTimer();
  const partial = _aiGen.partial || '';
  currentAiOutput = partial;
  const meta = `⏹ 已停止 · ${partial.length} 字 · ${_aiGenElapsed()}s`;
  const body = _aiGenEl('aiModalBody');
  if (body) {
    body.innerHTML = `
      <div class="ai-gen-statusbar"><span class="ai-gen-status stopped">${escapeHtml(meta)}</span>
        <span class="ai-gen-meta">${escapeHtml(_aiGen.modelLabel)}</span></div>
      <div class="output-text">${partial ? _formatAIText(partial) : '<span style="color:var(--text3);">（尚未生成内容，可点「重新生成」再试）</span>'}</div>`;
  }
  _aiGenFooter('done');
}

/** dispatcher: 重新生成（走跨境确认入口）。 */
function regenerateAI(caseId, type) {
  generateAI(caseId || _aiGen.caseId, type || _aiGen.type);
}

/** dispatcher: 关闭生成 modal —— 若仍在生成先中止，避免后台白耗 token。 */
function closeAIModal() {
  if (_aiGen.abort) _aiGen.abort.abort();
  document.getElementById('aiOutputModal')?.classList.remove('open');
}

async function _doGenerateAI(caseId, type) {
  const cases = loadDB();
  const c = cases.find(x => x.id === caseId);
  if (!c) return;
  if (typeof _caseBriefAICaseId !== 'undefined') _caseBriefAICaseId = caseId;

  const mod = MODULES[c.module];
  const typeLabels = {
    case_report: '个案报道',
    html_case_brief: 'HTML格式病例简报',
    discharge_summary: '术前评估小结',
    academic_presentation: '学术交流发言稿',
    operative_note: '手术记录草稿',
    consent_form: '术前知情同意要点',
    wechat_article: '公众号科普文章',
    case_ppt: '病例简报PPT',
    ppt_narrative: 'AI汇报型PPT'
  };

  // ── 初始化生成状态机（停止旧的、起新的） ──
  if (_aiGen.abort) _aiGen.abort.abort();
  const { provider: _selProvider, model: _selModel } = getSelectedGenerationModel(caseId);
  _aiGen.abort = new AbortController();
  _aiGen.caseId = caseId;
  _aiGen.type = type;
  _aiGen.partial = '';
  _aiGen.lastPaint = 0;
  _aiGen.stickBottom = true;
  _aiGen.modelLabel = _selModel ? `${_selProvider || ''} ${_selModel}`.trim() : '自动选模（按文书分层）';
  const _thisAbort = _aiGen.abort;

  document.getElementById('aiModalTitle').textContent = `🤖 AI生成 · ${typeLabels[type]}`;
  _aiGenShell('正在连接 AI 服务…');
  _aiGenStartTimer();
  _aiGenFooter('streaming');
  document.getElementById('aiOutputModal').classList.add('open');

  // Build data summary
  const dataSummary = buildDataSummary(c, mod);

  const prompts = {
    case_report: `你是一位足踝外科医师，请根据以下术前资料，撰写一份符合中文骨科期刊投稿格式的个案报道。
要求：摘要（100字以内）、病例介绍（病史+查体+影像学）、讨论（手术方案选择理由、文献依据、技术要点）、结论。
格式专业、学术，可适当引用相关文献类型。

病例资料：
${dataSummary}`,
    html_case_brief: `你是一位足踝外科医师，请根据以下术前资料，生成一份完整 HTML 格式病例简报。
要求：
1. 只输出可直接保存为 .html 的完整 HTML 文档，包含 <!doctype html>、<html>、<head>、<style>、<body>。
2. 使用内联 CSS，排版适合科室病例汇报、打印和移动端阅读。
3. 内容包括：病例概览、核心病史、查体、影像证据、诊断与分型、治疗计划、风险提示、随访计划、资料缺口。
4. 如果病例资料末尾包含【影像资料URL】，必须用真实 <img src="..."> 嵌入图片；不要用占位图、emoji 或 Markdown。
5. 不要输出 Markdown，不要解释。

病例资料：
${dataSummary}`,
    discharge_summary: `你是一位足踝外科医师，请根据以下资料，生成符合中国病历书写规范的术前评估小结。
要求：主诉、现病史、既往史、专科查体、辅助检查结果、诊断、手术指征、手术方案、麻醉方式、注意事项。

病例资料：
${dataSummary}`,
    academic_presentation: `你是一位足踝外科医师，请根据以下病例资料，撰写一份适合学术会议发言的文字稿（约600-800字）。
语言流畅专业，重点突出该病例的特殊性、手术方案选择的循证依据、技术难点及临床价值。

病例资料：
${dataSummary}`,
    operative_note: `你是一位足踝外科医师，请根据以下术前资料，撰写一份标准格式的手术记录草稿。
要求：手术名称、手术体位、麻醉方式、切口入路描述、手术步骤（按${mod.name}标准流程）、内固定/假体型号（待填）、术中出血量（待填）、术毕处理。

病例资料：
${dataSummary}`,
    consent_form: `你是一位足踝外科医师，请根据以下病例资料，生成一份术前谈话的核心要点清单。
要求：手术目的、手术方式简述、主要风险（分：一般风险+专科特殊风险）、替代方案、预期预后、康复要点、注意事项。专业且易于患者理解。

病例资料：
${dataSummary}`,
    wechat_article: `你是一位足踝外科医师兼科普作者，请根据以下病例资料，撰写一篇面向大众的微信公众号足踝外科科普文章。
要求：
1. 起一个吸引人、有共鸣的标题。
2. 通俗化表达，避免生硬术语；必要的专业名词要用大白话解释。
3. 分小标题、分段落，适合手机屏幕阅读，节奏轻快。
4. 不暴露任何患者隐私（不写姓名、住院号、联系方式等），以"一位患者"的方式叙述。
5. 结尾加上温馨提示，并附一句科普免责声明（本文仅作健康科普，不能替代面诊，如有不适请及时就医）。
6. 输出正文（可用 Markdown 标题/小标题/列表），不要输出 HTML 文档，不要解释。

病例资料：
${dataSummary}`,
    case_ppt: `你是一位足踝外科医师，请根据以下术前资料，生成一份可直接演示的【病例简报PPT】——一份自包含的 HTML 幻灯片文档。
要求：
1. 只输出可直接保存为 .html 的完整 HTML 文档，包含 <!doctype html>、<html>、<head>、<style>、<body>；不要输出 Markdown，不要解释。
2. 纯 HTML + 内联 CSS，绝对不要使用任何 <script> 或 JavaScript（系统会在安全沙箱中预览，脚本会被剥离）。
3. 幻灯片为 16:9 分页：每一页是一个 <section class="slide">，用 CSS 固定宽高比（如 aspect-ratio:16/9 或固定尺寸），页与页之间分页（@media print 时每页 page-break-after:always），既能逐页演示，也能打印/导出 PDF。
4. 排版适合投影演示：标题大、要点精炼、字号偏大、留白充足、对比清晰；可用页眉小标题 + 页脚页码。
5. 建议页序：封面（病种 + 简短标题）、病例概览、核心病史与查体、影像证据、诊断与分型、治疗计划、风险与知情、随访计划、小结/要点。可按资料丰俭增删页。
6. 如果病例资料末尾包含【影像资料地址】，必须用真实 <img src="..."> 把影像嵌入对应幻灯片，地址逐字照抄、不要改写或拼接域名；系统会在预览/下载时自动把这些 /api/media 地址内联为图片。不要用占位图、emoji 或 Markdown 图片语法。
7. 严格隐私脱敏：不要写出患者姓名、住院号、身份证、手机、住址等个人信息，统一以"患者"指代（资料已脱敏）。

病例资料：
${dataSummary}`
  };

  try {
    const provider = _selProvider, model = _selModel;
    const prompt = prompts[type];
    const hasBackend = isCloudMode();
    let text = '';

    if (hasBackend) {
      if (!isLoggedIn()) throw new Error(featureGateMessage('AI 文书生成'));
      // ── 流式调用：节流渲染 Markdown + 自动跟随滚动 + 可停止 ──
      text = await _callStreamCloud(
        '/api/ai/generate-stream',
        { document_type: type, module_name: mod.name, data_summary: dataSummary, provider, model },
        (partial) => _aiGenPaint(partial),
        _thisAbort.signal
      );
    } else {
      const apiKey = getAPIKey();
      if (!apiKey) {
        document.getElementById('aiModalBody').innerHTML = `<div style="text-align:center;padding:32px;">
          <div style="font-size:36px;margin-bottom:12px;">🔑</div>
          <div style="font-size:15px;font-weight:600;color:var(--text);margin-bottom:8px;">尚未配置 AI 服务</div>
          <div style="font-size:13px;color:var(--text3);margin-bottom:20px;">${escapeHtml(featureGateMessage('AI 文书生成'))}</div>
          <button class="btn btn-primary" data-action="close-modal-then" data-modal="aiOutputModal" data-then="showSettingsModal">⚙️ 前往设置</button>
        </div>`;
        document.getElementById('aiModalFooter').style.display = 'none';
        return;
      }

      if (provider === 'anthropic') {
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
          'anthropic-version': '2023-06-01'
        },
        body: JSON.stringify({
          model,
          max_tokens: 4096,
          messages: [{ role: 'user', content: prompt }]
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error?.message || 'Anthropic 请求失败');
      text = data.content?.map(b => b.text || '').join('') || '';
      } else if (provider === 'deepseek') {
      const response = await fetch('https://api.deepseek.com/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model,
          temperature: 0.2,
          messages: [{ role: 'user', content: prompt }]
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error?.message || 'DeepSeek 请求失败');
      text = data?.choices?.[0]?.message?.content || '';
      } else if (provider === 'openai') {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model,
          temperature: 0.2,
          messages: [{ role: 'user', content: prompt }]
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error?.message || 'OpenAI 请求失败');
      text = data?.choices?.[0]?.message?.content || '';
      } else if (provider === 'glm') {
      const response = await fetch('https://open.bigmodel.cn/api/paas/v4/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model,
          temperature: 0.2,
          messages: [{ role: 'user', content: prompt }]
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error?.message || 'GLM 请求失败');
      text = data?.choices?.[0]?.message?.content || '';
      } else if (provider === 'gemini') {
      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(model)}:generateContent?key=${encodeURIComponent(apiKey)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text: prompt }] }]
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error?.message || 'Gemini 请求失败');
      const parts = data?.candidates?.[0]?.content?.parts || [];
      text = parts.map(p => p.text || '').join('');
      } else if (provider === 'banana') {
      const endpoint = getBananaEndpoint();
      if (!endpoint) throw new Error('请先在设置中填写 Banana Pro 兼容网关地址');
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model,
          temperature: 0.2,
          messages: [{ role: 'user', content: prompt }]
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error?.message || 'Banana Pro 请求失败');
      text = data?.choices?.[0]?.message?.content || data?.output || '';
      } else if (provider === 'compatible') {
      const endpoint = getCompatibleEndpoint();
      if (!endpoint) throw new Error('请先在设置中填写第三方兼容网关地址');
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model,
          temperature: 0.2,
          messages: [{ role: 'user', content: prompt }]
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error?.message || '第三方兼容网关请求失败');
      text = data?.choices?.[0]?.message?.content || data?.output || '';
      }
    }

    // 用户已点停止：UI 已切到"已停止"态，丢弃迟到结果
    if (_thisAbort.signal.aborted) return;

    if (!text) text = '生成失败，请重试。';
    currentAiOutput = text;
    _aiGenStopTimer();

    // 让后续 _inlineReportImages（读取 _aiChatCaseId）能拿到当前病例的影像 mediaId 映射。
    if (typeof _aiChatCaseId !== 'undefined') _aiChatCaseId = caseId;

    const metaLine = `✓ 已完成 · ${text.length} 字 · ${_aiGenElapsed()}s`;
    // 若 AI 输出是 HTML 文书（html_case_brief，或被 ```html``` 围栏/前导文字包裹的整段 HTML），
    // 不再把源码转义成"一堆代码"显示，而是给出"报告已生成"状态 + 渲染预览入口。
    const _extractedHtml = _extractHtmlCode(text);
    if (_extractedHtml) {
      document.getElementById('aiModalBody').innerHTML = `
        <div class="ai-gen-statusbar"><span class="ai-gen-status done">${escapeHtml(metaLine)}</span>
          <span class="ai-gen-meta">${escapeHtml(_aiGen.modelLabel)}</span></div>
        <div class="ai-report-ready" style="text-align:center;padding:24px 8px;">
          <div style="font-size:40px;margin-bottom:12px;">📄</div>
          <div style="font-size:16px;font-weight:600;color:var(--text);margin-bottom:8px;">病例报告已生成</div>
          <div style="font-size:13px;color:var(--text3);max-width:44ch;margin:0 auto 20px;">系统已生成一份带影像的 HTML 病例报告。点击下方按钮可在安全沙箱中预览渲染效果，或使用底部「下载」保存为自包含 .html（影像已内联，永久可用）。</div>
          <button class="btn btn-primary" data-action="preview-ai-output-html">👁️ 预览渲染报告</button>
          <details style="margin-top:18px;text-align:left;">
            <summary style="cursor:pointer;font-size:12px;color:var(--text3);">查看 HTML 源码</summary>
            <div class="output-text" style="margin-top:8px;max-height:240px;overflow:auto;font-family:monospace;font-size:12px;white-space:pre-wrap;">${escapeHtml(text)}</div>
          </details>
        </div>`;
    } else {
      // 文本文书：Markdown 渲染（标题/加粗/列表），与聊天一致的成熟阅读体验
      document.getElementById('aiModalBody').innerHTML = `
        <div class="ai-gen-statusbar"><span class="ai-gen-status done">${escapeHtml(metaLine)}</span>
          <span class="ai-gen-meta">${escapeHtml(_aiGen.modelLabel)}</span></div>
        <div class="output-text">${_formatAIText(text)}</div>`;
    }
    _aiGenFooter('done');

    // Update AI count
    c.aiGenCount = (c.aiGenCount || 0) + 1;
    saveDB(cases);
    incAICount();
    updateStats();
    renderSidebar();

  } catch (err) {
    _aiGenStopTimer();
    // 用户主动停止：stopAIGeneration 已渲染"已停止"态，这里不再覆盖
    if (err?.name === 'AbortError' || _thisAbort.signal.aborted) return;
    // #13 — composed inline error (surfaces Batch F's clean 503/502 detail) + retry.
    // 流式中途断开时保留已生成的部分，避免医生白等一场。
    const partialBlock = _aiGen.partial
      ? `<details open style="margin-top:14px;text-align:left;"><summary style="cursor:pointer;font-size:12px;color:var(--text3);">已生成的部分内容（${_aiGen.partial.length} 字）</summary>
         <div class="output-text" style="margin-top:8px;max-height:300px;overflow:auto;">${_formatAIText(_aiGen.partial)}</div></details>`
      : '';
    document.getElementById('aiModalBody').innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">⚠️</div>
        <div class="empty-state-title">生成失败</div>
        <div class="field-error" style="text-align:center;max-width:46ch;">${escapeHtml(err.message || 'AI 服务调用失败，请稍后重试')}</div>
        <div class="empty-state-action">
          <button class="btn btn-primary" data-action="retry-generate-ai" data-case-id="${escapeHtml(caseId)}" data-ai-type="${escapeHtml(type)}">重试</button>
        </div>
        ${partialBlock}
      </div>`;
    document.getElementById('aiModalFooter').style.display = 'none';
  }
}

function getSelectedGenerationModel(caseId) {
  // 读病例内联下拉的原始值（不做回退），用于判断用户是否显式选了具体模型。
  const rawProvider = (document.getElementById(`ai-provider-${caseId}`)?.value || '').trim();
  const rawModel = (document.getElementById(`ai-model-${caseId}`)?.value || '').trim();

  // 云端模式：模型留空 = 「自动（按文书类型智能选模型）」默认档。
  // 返回空 provider/model，后端据此触发按 document_type 的模型分层
  // （fast=flash / medium=pro / advanced=jeniya）。用户手动填了具体模型则照常返回，覆盖分层。
  if (isCloudMode()) {
    if (!rawModel) return { provider: '', model: '' };
    return { provider: rawProvider, model: rawModel };
  }

  // 本地演示模式：无后端可分层，必须回退到一个具体模型，否则没模型可用。
  const provider = rawProvider || getAIProvider();
  const model = rawModel || getAIModel();
  return { provider, model };
}

// ── 结构化患者信息脱敏（默认开启，发往 LLM 的摘要专用） ──────────────────
// 目的：在把病例资料喂给大模型之前，丢弃/替换身份字段，最小化暴露给 LLM 的 PII。
// 这一层负责"姓名→患者"和结构化丢弃；后端另有一道强制正则脱敏兜底
// （身份证/手机/邮箱），二者互补。注意：只作用于 LLM 摘要，不影响 UI 渲染。

// 直接按字段键丢弃的强标识字段（住院号、录入医生人名）。
const _PII_DROP_KEYS = new Set(['basic_caseid', 'workflow_entry_doctor']);

// 键命中即视为 PII（身份证/电话/手机/住址/联系人等强标识）。
const _PII_KEY_PATTERN = /(_|^)(id_?card|idcard|phone|mobile|tel|address|contact)/i;

// 标签命中即视为 PII（中文标签：身份证/电话/手机/住址/联系）。
const _PII_LABEL_PATTERN = /(身份证|电话|手机|住址|联系)/;

/**
 * 判断某字段（键 + 标签一起看）是否属于应丢弃的身份信息。
 * @param {string} key   字段键，约定为 `${section.id}_${field.id}`（如 basic_caseid）
 * @param {string} label 字段中文标签（如「住院号」「联系电话」）
 * @returns {boolean} true 表示该字段不应拼进发往 LLM 的摘要
 */
function isPiiFieldKey(key, label) {
  const k = String(key || '');
  if (_PII_DROP_KEYS.has(k)) return true;
  if (_PII_KEY_PATTERN.test(k)) return true;
  if (_PII_LABEL_PATTERN.test(String(label || ''))) return true;
  return false;
}

/** 转义正则元字符，使任意字符串可安全用于 RegExp 构造。 */
function _escapeRegExp(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * 把摘要文本里出现的患者真实姓名全局替换成「患者」，
 * 覆盖主诉/病程等自由文本中嵌入的姓名。
 * 仅当姓名长度 >= 2 时替换（避免单字误伤常用字）。
 * @param {string} text        待脱敏文本
 * @param {string} patientName 患者真实姓名
 * @returns {string} 脱敏后的文本
 */
function deidentifyPatientName(text, patientName) {
  const name = String(patientName || '').trim();
  if (!text || name.length < 2) return text;
  return String(text).replace(new RegExp(_escapeRegExp(name), 'g'), '患者');
}

function buildDataSummary(c, mod) {
  const lines = [`【模块】${mod.name}`, `【创建日期】${c.createdAt}`];
  mod.sections.forEach(sec => {
    if (sec.type === 'media') return;
    const sectionData = [];
    (sec.fields || []).forEach(f => {
      const key = `${sec.id}_${f.id}`;
      // 姓名字段：不拼真名，用占位「患者」保留语义结构。
      if (key === 'basic_name') {
        const nameVal = c.data?.[key];
        if (nameVal && nameVal !== '') sectionData.push(`${f.label}: 患者`);
        return;
      }
      // 结构化脱敏：丢弃身份字段（住院号/录入医生/身份证/电话/住址/联系…）。
      if (isPiiFieldKey(key, f.label)) return;
      const val = c.data?.[key];
      if (val && val !== '' && !(Array.isArray(val) && val.length === 0)) {
        sectionData.push(`${f.label}: ${Array.isArray(val) ? val.join('、') : val}`);
      }
    });
    if (sectionData.length > 0) {
      lines.push(`\n【${sec.title}】`);
      lines.push(...sectionData);
    }
  });

  // ── 追加实际影像 URL（供 AI 在 HTML 中直接用 <img src=""> 显示真实图片）──
  const mediaSummary = _buildMediaUrlSummary(c, mod);
  if (mediaSummary) lines.push(mediaSummary);

  // ── 姓名全局兜底：覆盖自由文本（主诉/病程等）里嵌入的真实姓名 ──
  // 在拼接影像地址之后做，但姓名替换不会命中 /api/media/{id} 这类地址，安全。
  const patientName = (c.data?.basic_name || c.data?.name || '').trim();
  return deidentifyPatientName(lines.join('\n'), patientName);
}

/**
 * 将 c.media 里的图片实际 URL 整理成可读文本，插入 data_summary 末尾。
 * AI 收到后可直接在生成的 HTML 中使用这些 URL。
 * 视频文件跳过（HTML 中 <img> 不支持）。
 */
function _buildMediaUrlSummary(c, mod) {
  const allMedia = c.media || {};
  if (!Object.keys(allMedia).length) return '';

  // 构建 slot_key → 人类可读标签 的映射（从模块定义读取）
  const labelMap = {};
  (mod?.sections || []).forEach(sec => {
    if (sec.type !== 'media') return;
    (sec.fields || []).forEach(f => {
      labelMap[`${sec.id}_${f.id}`] = `${sec.title} / ${f.label}`;
    });
  });

  const imageLines = [];
  Object.entries(allMedia).forEach(([slotKey, images]) => {
    const imgs = Array.isArray(images) ? images : [images];
    imgs.filter(img => img?.data).forEach((img, i) => {
      // 跳过视频（mime 或文件扩展名）
      const mime = img.mime || '';
      if (mime.startsWith('video/') || /\.(mp4|mov|avi|mkv)$/i.test(img.filename || '')) return;
      const label = labelMap[slotKey] || img.name || slotKey;
      const suffix = imgs.length > 1 ? `[${i + 1}/${imgs.length}]` : '';
      // 优先用稳定的系统内部地址 /api/media/{id}（云端模式），而不是 ~1h 过期的预签名 COS URL：
      // 这样导出/预览时 _inlineReportImages 能 100% 解析出 mediaId 并内联成 base64。
      // 本地演示模式无 backendMediaId，img.data 本身即 base64（自包含），直接给出。
      const ref = img.backendMediaId ? `/api/media/${img.backendMediaId}` : img.data;
      imageLines.push(`- ${label}${suffix}: ${ref}`);
    });
  });

  if (!imageLines.length) return '';
  return (
    '\n\n【影像资料地址（生成HTML时请将以下地址原样放入 <img src=""> 标签中——务必逐字照抄，' +
    '不要改写、截断、拼接或补全域名）。这些是系统内部稳定图片地址，系统会在导出/预览时自动把它们' +
    '替换为内嵌图片（base64），生成自包含、可永久离线查看的报告】\n' +
    imageLines.join('\n')
  );
}

function copyAiOutput() {
  navigator.clipboard.writeText(currentAiOutput).then(() => showToast('📋', '已复制到剪贴板'));
}

/**
 * 在站内沙箱 iframe 中预览生成 modal 当前的 AI 输出（currentAiOutput）。
 * 复用聊天路径的成熟逻辑：_extractHtmlCode → _inlineReportImages → _sanitizeReportHtml → 预览。
 * _inlineReportImages 读取全局 _aiChatCaseId 建 mediaId 映射；generateAI 完成时已将其指向当前病例。
 */
async function previewAIOutputHTML() {
  let html = _extractHtmlCode(currentAiOutput) || currentAiOutput;
  if (!html) return;
  const t = showToast('🖼️', '正在嵌入影像，生成自包含报告…', 0);
  try { html = await _inlineReportImages(html); } catch (_) {}
  finally { if (t) t.remove(); }
  html = _sanitizeReportHtml(html);
  _openReportPreviewModal(html);
}

async function downloadAiOutput() {
  // 先尝试提取 HTML（处理 ```html``` 围栏 / 前导寒暄文字 / 整段 <!DOCTYPE>）。
  const extracted = _extractHtmlCode(currentAiOutput);
  if (extracted) {
    const t = showToast('🖼️', '正在嵌入影像，生成自包含报告…', 0);
    let html = extracted;
    try { html = await _inlineReportImages(html); } catch (_) {}
    finally { if (t) t.remove(); }
    html = _sanitizeReportHtml(html);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const date = new Date().toLocaleDateString('zh-CN').replace(/\//g, '-');
    a.href = url; a.download = `病例报告_${date}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    // 内联完整性检查：还有 /api/media 外链 = 部分影像没拉下来（弱网超时等），
    // 明确告知医生重下，而不是让 ta 发出去才发现"图片显示不全"。
    const remaining = (html.match(/<img\b[^>]*?\bsrc\s*=\s*["'][^"']*\/api\/media\//gi) || []).length;
    if (remaining > 0) {
      showToast('⚠️', `报告已下载，但有 ${remaining} 张影像未能嵌入（网络较慢）。建议稍后重新点「下载」生成完整版`, 8000);
    } else {
      showToast('📄', 'HTML 报告已下载（影像已内联，永久可用）');
    }
    return;
  }
  // 非 HTML：退化为纯文本 .txt。
  const blob = new Blob([currentAiOutput || ''], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `AI生成_${new Date().toLocaleDateString('zh-CN').replace(/\//g, '-')}.txt`;
  a.click(); URL.revokeObjectURL(url);
}

// ===================== AI CHAT DIALOG =====================
let _aiChatHistory = [];
let _aiChatCaseId = null;
let _aiChatSending = false;
let _aiChatPresetsShown = true;
let _aiChatAbortCtrl = null;   // AbortController for the live stream; null when idle

/** 用户点击"停止生成"按钮时调用 */
function stopAIChat() {
  if (_aiChatAbortCtrl) _aiChatAbortCtrl.abort();
}

// ── 流式渲染节流（RAF throttle） ──────────────────────────
// 无论 token 来多快，DOM 更新最多每帧一次（≤60fps），消除代码生成时的闪屏效果
let _chatRafId = null;      // requestAnimationFrame 句柄（null 表示未排队）
let _chatRafDirty = false;  // 有待渲染的更新

/**
 * 在流式 onChunk 中调用，而不是直接调用 _renderAIChatMessages()。
 * 相同帧内多次调用只渲染一次。
 */
function _scheduleStreamRender() {
  _chatRafDirty = true;
  if (_chatRafId !== null) return;  // 已有一帧在排队，不重复注册
  _chatRafId = requestAnimationFrame(() => {
    _chatRafId = null;
    if (_chatRafDirty) {
      _chatRafDirty = false;
      _renderAIChatMessages(/* stream */ true);
    }
  });
}

/** 取消未执行的 RAF，在流结束/出错时调用 */
function _cancelStreamRender() {
  if (_chatRafId !== null) {
    cancelAnimationFrame(_chatRafId);
    _chatRafId = null;
  }
  _chatRafDirty = false;
}

// ── 对话历史持久化 ────────────────────────────────────────
const _AI_CHAT_HISTORY_KEY = 'foot_ankle_ai_chat_v1';

/** localStorage key：每个用户 × 每个病例独立一份 */
function _aiChatPersistKey(caseId) {
  const uid = getCurrentUserKey() || 'local';
  return `${_AI_CHAT_HISTORY_KEY}_${uid}_${caseId}`;
}

/** 从 localStorage 读取历史（去掉临时状态，只返回真实消息）*/
function _loadPersistedAIChat(caseId) {
  try {
    const raw = localStorage.getItem(_aiChatPersistKey(caseId));
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

/**
 * 将当前对话历史写入 localStorage。
 * 只保存真实的用户消息和成功的 AI 回复（跳过 loading / streaming / error / welcome）。
 * 最多保留 60 条（约 30 轮），防止 localStorage 超配额。
 */
function _persistAIChat(caseId) {
  if (!caseId) return;
  try {
    const toSave = _aiChatHistory
      .filter(m => !m.isWelcome && !m.loading && !m.streaming && !m.error && m.content)
      .slice(-60);
    localStorage.setItem(_aiChatPersistKey(caseId), JSON.stringify(toSave));
    // 同步清空按钮显隐
    _syncClearBtn(toSave.length > 0);
  } catch { /* 存储满时静默跳过 */ }
}

/** 清空当前病例的对话记录（含持久化），恢复初始欢迎页 */
async function clearAIChatHistory() {
  const caseId = _aiChatCaseId;
  if (!caseId) return;
  const ok = await showAppConfirm({ title: '清空对话记录', message: '确定清空这个病例的全部对话记录？此操作不可恢复。', confirmText: '清空', danger: true });
  if (!ok) return;
  localStorage.removeItem(_aiChatPersistKey(caseId));
  // 重建只有欢迎消息的 history
  const welcome = _aiChatHistory.find(m => m.isWelcome);
  _aiChatHistory = welcome ? [welcome] : [];
  _aiChatPresetsShown = true;
  const presetsEl = document.getElementById('aiChatPresets');
  if (presetsEl) presetsEl.style.display = 'flex';
  _syncClearBtn(false);
  _renderAIChatMessages();
  showToast('🗑️', '对话记录已清空');
}

/** 控制 header 清空按钮的显示 */
function _syncClearBtn(show) {
  const btn = document.getElementById('aiChatClearBtn');
  if (btn) btn.style.display = show ? 'inline-flex' : 'none';
}

function openAIChat(caseId) {
  const isNewCase = _aiChatCaseId !== caseId;
  _aiChatCaseId = caseId;
  _aiChatSending = false;

  const cases = loadDB();
  const c = cases.find(x => x.id === caseId);
  if (!c) return;

  const mod = MODULES[c.module];
  const patientName = (typeof displayPatientName === 'function') ? displayPatientName(c) : (c.data?.name || c.data?.basic_name || '未命名患者');
  const fieldCount = Object.keys(c.data || {}).filter(k => {
    const v = c.data[k];
    return v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);
  }).length;

  document.getElementById('aiChatModalTitle').textContent = `💬 AI 助手 · ${patientName}`;
  document.getElementById('aiChatContextText').textContent = `${mod.name} · 已填 ${fieldCount} 个字段`;

  // ── 恢复持久化历史 ───────────────────────────────────────
  // 每次切换病例（或首次打开）都从 localStorage 加载
  if (isNewCase || _aiChatHistory.length === 0) {
    const stored = _loadPersistedAIChat(caseId);
    const hasHistory = stored.length > 0;

    // 欢迎语根据是否有历史记录调整文案
    const welcomeContent = hasHistory
      ? `欢迎回来！继续为 **${patientName}** 服务（${mod.name}，已填 ${fieldCount} 个字段）。\n\n⬇️ 以下是你之前的对话记录，可直接继续提问。`
      : `你好！我已加载 **${patientName}** 的病例资料（${mod.name}，已填 ${fieldCount} 个字段）。\n\n你可以点击下方快捷按钮，或直接输入你想要生成的内容，例如：\n- "生成一份HTML格式病例简报"\n- "帮我写一份术前谈话要点"\n- "对这个病例做综合分析"`;

    _aiChatHistory = [{ role: 'assistant', content: welcomeContent, isWelcome: true }, ...stored];
    _aiChatPresetsShown = !hasHistory;  // 有历史时隐藏预设按钮

    _syncClearBtn(hasHistory);
  }

  const presetsEl = document.getElementById('aiChatPresets');
  if (presetsEl) presetsEl.style.display = _aiChatPresetsShown ? 'flex' : 'none';

  // 恢复对话档位选择（持久化）
  const tierSel = document.getElementById('aiChatTierSel');
  if (tierSel) tierSel.value = _aiChatTier();

  _renderAIChatMessages();
  document.getElementById('aiChatModal').classList.add('open');
  document.body.style.overflow = 'hidden';

  // 滚动到底部（恢复历史时看到最新消息）
  setTimeout(() => {
    const msgs = document.getElementById('aiChatMessages');
    if (msgs) msgs.scrollTop = msgs.scrollHeight;
    const inp = document.getElementById('aiChatInput');
    if (inp) inp.focus();
  }, 120);
}

function closeAIChat() {
  document.getElementById('aiChatModal').classList.remove('open');
  document.body.style.overflow = '';
}

function _renderAIChatMessages(streaming = false) {
  const container = document.getElementById('aiChatMessages');
  if (!container) return;

  // ── STREAMING PATH: surgical in-place update ─────────────────────────────
  // Instead of rebuilding the entire message list (which causes full repaint
  // of every bubble on every 16ms RAF tick), find the one streaming bubble
  // already in the DOM and update only its text node. Zero flicker.
  if (streaming) {
    const el = document.getElementById('ai-streaming-msg');
    if (el) {
      const streamMsg = _aiChatHistory.find(m => m.streaming);
      if (streamMsg) {
        const textEl = el.querySelector('.ai-bubble-text');
        if (textEl) {
          textEl.innerHTML = _formatAIText(streamMsg.content) + '<span class="stream-cursor"></span>';
          const threshold = 80;
          const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
          if (nearBottom) container.scrollTop = container.scrollHeight;
        }
        return;  // done — no full re-render needed
      }
    }
    // Element not yet in DOM (first streaming tick after loading→streaming
    // transition): fall through to full render so the bubble gets created with id.
  }

  // ── FULL RE-RENDER (message added / removed / finalised / history loaded) ─
  const threshold = 80;
  const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  const shouldScroll = !streaming || nearBottom;

  container.innerHTML = _aiChatHistory.map((msg, idx) => {
    if (msg.role === 'assistant') {
      if (msg.loading) {
        return `<div class="ai-chat-bubble ai-chat-bubble-assistant">
          <div class="ai-chat-bubble-icon">🤖</div>
          <div class="ai-chat-bubble-content">
            <span class="ai-typing-indicator"><span></span><span></span><span></span></span>
          </div>
        </div>`;
      }
      if (msg.error) {
        const prevUser = _aiChatHistory.slice(0, idx).reverse().find(m => m.role === 'user');
        const retryText = prevUser ? escapeJsArg(prevUser.content) : '';
        return `<div class="ai-chat-bubble ai-chat-bubble-assistant">
          <div class="ai-chat-bubble-icon">🤖</div>
          <div class="ai-chat-bubble-content ai-chat-bubble-error">
            <span>⚠️ 生成失败：${escapeHtml(msg.content)}</span>
            ${retryText ? `<div style="margin-top:8px;">
              <button class="ai-bubble-retry-btn" data-action="retry-ai-chat" data-retry-text="${escapeHtml(prevUser ? prevUser.content : '')}" data-idx="${idx}">↺ 重试</button>
            </div>` : ''}
          </div>
        </div>`;
      }
      // Streaming bubble: give it a stable id so subsequent ticks can patch it in-place
      const isStreaming = !!msg.streaming;
      const streamId = isStreaming ? ' id="ai-streaming-msg"' : '';
      const { lead, html: htmlCode } = (!isStreaming && !msg.isWelcome)
        ? _splitChatHtml(msg.content) : { lead: '', html: null };
      const hasHtml = !!htmlCode;
      // 命中 HTML 文档：不再把整段源码怼进气泡，改为「报告已生成」卡片 +
      // 折叠源码（与生成弹窗一致）。卡片前保留模型可能写的引导文字。
      let bodyHtml;
      if (hasHtml) {
        bodyHtml =
          (lead ? `<div class="ai-bubble-text">${_formatAIText(lead)}</div>` : '') +
          `<div class="ai-html-card">
             <span class="ai-html-card-icon">📄</span>
             <div class="ai-html-card-main">
               <div class="ai-html-card-title">HTML 报告已生成</div>
               <div class="ai-html-card-sub">点「预览 HTML」看渲染效果，或「下载 HTML」保存自包含网页（影像已内联）。</div>
             </div>
           </div>
           <details class="ai-html-src"><summary>查看 HTML 源码</summary><pre class="ai-code-pre">${escapeHtml(htmlCode)}</pre></details>`;
      } else {
        bodyHtml = `<div class="ai-bubble-text">${_formatAIText(msg.content)}${isStreaming ? '<span class="stream-cursor"></span>' : ''}</div>`;
      }
      return `<div class="ai-chat-bubble ai-chat-bubble-assistant"${streamId}>
        <div class="ai-chat-bubble-icon">🤖</div>
        <div class="ai-chat-bubble-content">
          ${bodyHtml}
          ${(!msg.isWelcome && !isStreaming) ? `<div class="ai-bubble-actions">
            ${hasHtml ? `<button class="ai-bubble-html-btn" data-action="preview-ai-chat-html" data-idx="${idx}">👁️ 预览 HTML</button>` : ''}
            ${hasHtml ? `<button class="ai-bubble-html-btn" data-action="download-ai-chat-html" data-idx="${idx}">📄 下载 HTML</button>` : ''}
            <button data-action="copy-ai-chat-msg" data-idx="${idx}">📋 复制</button>
            <button data-action="download-ai-chat-msg" data-idx="${idx}">⬇️ 下载文本</button>
          </div>` : ''}
        </div>
      </div>`;
    }
    // User bubble
    return `<div class="ai-chat-bubble ai-chat-bubble-user">
      <div class="ai-chat-bubble-content ai-chat-bubble-user-content">
        <div class="ai-bubble-text">${escapeHtml(msg.content)}</div>
      </div>
    </div>`;
  }).join('');

  if (shouldScroll) container.scrollTop = container.scrollHeight;
}

function _formatAIText(text) {
  if (!text) return '';

  // ① 先把代码块提取出来，用不可见占位符代替，避免后续转义破坏代码内容
  const codeBlocks = [];
  let processed = text.replace(/```(\w*)\n?([\s\S]*?)```/g, (_, lang, code) => {
    codeBlocks.push({ lang: lang.trim(), code });
    return `\x00CODEBLOCK${codeBlocks.length - 1}\x00`;
  });

  // ② 对剩余文本做 HTML 转义 + Markdown → HTML
  processed = escapeHtml(processed)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*\n]+?)\*/g, '<em>$1</em>')
    // 表格：表头行 + |---| 分隔行 + 数据行 → <table>（AI 文书常用表格组织风险/对照项）
    .replace(/(^\|.+\|[ \t]*$\n^\|[ \t:|-]+\|[ \t]*$\n(?:^\|.+\|[ \t]*$\n?)*)/gm, (block) => {
      const rows = block.trim().split('\n')
        .map(r => r.replace(/^\s*\||\|\s*$/g, '').split('|').map(cell => cell.trim()));
      const header = rows[0];
      const body = rows.slice(2);
      return '<table class="ai-md-table"><thead><tr>'
        + header.map(h => `<th>${h}</th>`).join('')
        + '</tr></thead><tbody>'
        + body.map(r => '<tr>' + r.map(cell => `<td>${cell}</td>`).join('') + '</tr>').join('')
        + '</tbody></table>';
    })
    .replace(/^-{3,}[ \t]*$/gm, '<hr class="ai-md-hr">')
    .replace(/^#{1,4} (.+)$/gm, '<div class="ai-md-heading">$1</div>')
    .replace(/^[-•] (.+)$/gm, '<div class="ai-md-li">• $1</div>')
    .replace(/^\d+\. (.+)$/gm, (m, item) => `<div class="ai-md-li">${m.match(/^\d+/)[0]}. ${item}</div>`)
    .replace(/\n\n/g, '<br><br>')
    .replace(/\n/g, '<br>');

  // ③ 把代码块还原为带样式的 <pre> 块（代码内容单独转义，保留换行和缩进）
  processed = processed.replace(/\x00CODEBLOCK(\d+)\x00/g, (_, idxStr) => {
    const { lang, code } = codeBlocks[parseInt(idxStr)];
    const escapedCode = escapeHtml(code);
    const langLabel = lang || 'code';
    return (
      `<div class="ai-code-block">` +
        `<div class="ai-code-lang">${langLabel}</div>` +
        `<pre class="ai-code-pre">${escapedCode}</pre>` +
      `</div>`
    );
  });

  return processed;
}

// ── HTML 报告：提取 / 预览 / 下载 ─────────────────────────

// 结构性区块标签（用于判断"是不是 HTML"以及切出片段起点）。
const _HTML_BLOCK_TAGS = 'div|section|header|footer|article|main|table|thead|tbody|tr|td|th|h[1-6]|ul|ol|li|p|span|style|img|figure|nav|aside';
const _HTML_START_RE = new RegExp('<(?:!doctype\\s+html|html|head|body|style|' + _HTML_BLOCK_TAGS + ')\\b', 'i');

/** 内容是否“像 HTML”：含 <style> 块，或出现 ≥4 个结构性区块标签。 */
function _looksLikeHtml(s) {
  if (!s) return false;
  if (/<style[\s>]/i.test(s)) return true;
  const re = new RegExp('<(?:' + _HTML_BLOCK_TAGS + ')\\b', 'gi');
  return (s.match(re) || []).length >= 4;
}

/** 从第一处结构性起始标签切到最后一个 '>'，去掉前后夹的解释性文字。 */
function _sliceHtmlFragment(s) {
  const m = s.match(_HTML_START_RE);
  if (!m) return null;
  const start = s.indexOf(m[0]);
  const lastClose = s.lastIndexOf('>');
  if (start < 0 || lastClose <= start) return null;
  return s.slice(start, lastClose + 1).trim();
}

/** 片段若无 <html>/<!DOCTYPE> 外壳，补一层最小文档，确保独立渲染/下载可用。 */
function _wrapHtmlFragment(frag) {
  if (!frag) return frag;
  if (/^\s*<!DOCTYPE/i.test(frag) || /<html[\s>]/i.test(frag)) return frag;
  return '<!DOCTYPE html><html lang="zh"><head><meta charset="utf-8">' +
    '<meta name="viewport" content="width=device-width, initial-scale=1">' +
    '</head><body>' + frag + '</body></html>';
}

/**
 * 从 AI 回复文本里提取可用的 HTML（返回可独立渲染/下载的完整文档，或 null）。
 * 覆盖：```html 围栏 / 裸 ``` 围栏 / 正文内嵌 <html>…</html> / 整段 HTML 开头 /
 *      无 <html> 外壳的 HTML 片段（模型常直接输出 <style>+<div> 简报）。
 */
function _extractHtmlCode(text) {
  if (!text) return null;
  const t = text.trim();

  // 1) ```html ... ``` / ```HTML ... ```（显式 html 语言标签的代码围栏）
  let m = text.match(/```html?\s*\n?([\s\S]*?)```/i);
  if (m && m[1].trim()) return _wrapHtmlFragment(m[1].trim());

  // 2) 无语言标签的 ``` ... ``` 围栏，内容像 HTML
  m = text.match(/```\s*\n?([\s\S]*?)```/);
  if (m && _looksLikeHtml(m[1])) return _wrapHtmlFragment(m[1].trim());

  // 3) 正文里直接出现的完整 HTML 文档（前后可夹解释文字）
  let doc = t.match(/<!DOCTYPE\s+html[\s\S]*?<\/html\s*>/i);
  if (doc) return doc[0].trim();
  doc = t.match(/<html[\s\S]*?<\/html\s*>/i);
  if (doc) return doc[0].trim();

  // 4) 整段以 HTML 文档开头（流式被截断、没等到闭合 </html> 也算）
  if (/^\s*<!DOCTYPE\s+html/i.test(t) || /^\s*<html[\s>]/i.test(t)) return t;

  // 5) 无 <html> 外壳的 HTML 片段：模型直接输出 <style>+<div class=...> 简报。
  //    截出结构部分并补成完整文档，使其可预览/下载。
  if (_looksLikeHtml(t)) {
    const frag = _sliceHtmlFragment(t);
    if (frag) return _wrapHtmlFragment(frag);
  }

  return null;
}

/** 拆出气泡里的「前导解释文字」与「HTML 文档」，供对话气泡分别渲染。 */
function _splitChatHtml(content) {
  const html = _extractHtmlCode(content);
  if (!html) return { lead: content, html: null };
  const fenceIdx = content.search(/```/);
  const tagIdx = content.search(_HTML_START_RE);
  let cut = -1;
  if (fenceIdx >= 0 && (tagIdx < 0 || fenceIdx <= tagIdx)) cut = fenceIdx;
  else if (tagIdx >= 0) cut = tagIdx;
  const lead = cut > 0 ? content.slice(0, cut).trim() : '';
  return { lead, html };
}

// ── 把报告里的影像 URL 内联成 base64（让报告自包含、永不过期） ──────────
// 背景：云端模式下 img.data 是 COS 预签名 URL（默认约1小时过期）。AI 把这些 URL
// 写进 <img src>，报告隔天再打开就裂图。预览/下载前，把每张图按 backendMediaId
// 从后端重新读取原始字节并转 base64 内联，报告即自包含、永久有效。
function _buildCaseMediaIdMap(c) {
  const map = new Map();   // 归一化 URL/路径 → backendMediaId
  const add = (img) => {
    if (!img || !img.backendMediaId || !img.data) return;
    const url = String(img.data);
    map.set(url, img.backendMediaId);
    map.set(url.split('?')[0], img.backendMediaId);   // 去掉预签名参数后的纯路径
  };
  Object.values(c.media || {}).forEach(slot => {
    (Array.isArray(slot) ? slot : [slot]).forEach(add);
  });
  return map;
}

function _resolveMediaIdFromUrl(url, map) {
  if (map.has(url)) return map.get(url);
  const path = url.split('?')[0];
  if (map.has(path)) return map.get(path);
  const m = url.match(/\/api\/media\/(\d+)/);   // 兜底：直接从 /api/media/{id} 提取
  if (m) return parseInt(m[1], 10);
  return null;
}

async function _fetchMediaBase64(mediaId, timeoutMs = 45000) {
  // 报告内联取图：服务端按 max_dim=1600 缩图重编码（原图几 MB → ~百 KB），
  // 弱网也能在超时内拉完；仍超时/失败则重试一次，再失败由上层保留原链接。
  // （此前 20s 超时 + 原图直传，是"下载的报告图片显示不全"的根因。）
  const _once = async () => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const res = await backendRequest(
        `/api/media/${mediaId}/base64?max_dim=1600`,
        { method: 'GET', signal: ctrl.signal }
      );
      return res?.data_uri || null;
    } finally {
      clearTimeout(timer);
    }
  };
  try {
    return await _once();
  } catch (_) {
    await new Promise(r => setTimeout(r, 800));
    return await _once();
  }
}

/**
 * 把 html 中所有外链 <img src> 替换成 base64 data URI。
 * 仅云端模式有意义（本地模式 img.data 本身就是 base64，无需处理）。
 * 单张失败时保留原 URL，不阻断其余图片。返回内联后的 html。
 */
async function _inlineReportImages(html) {
  if (!html || !isCloudMode() || !isLoggedIn()) return html;
  const c = loadDB().find(x => x.id === _aiChatCaseId);
  if (!c) return html;

  const idMap = _buildCaseMediaIdMap(c);
  if (!idMap.size) return html;

  const srcs = [...html.matchAll(/<img\b[^>]*?\bsrc\s*=\s*["']([^"']+)["']/gi)].map(m => m[1]);
  const external = [...new Set(srcs)].filter(u => /^https?:\/\//i.test(u) || /\/api\/media\//.test(u));
  if (!external.length) return html;

  const replacements = {};
  // 仅保留能解析到 media id 的外链，按"每批 N 张"限流拉取 base64。
  // 旧实现一次性并发拉取所有大图（无超时、无上限），单张卡住即令整份报告
  // 永远停在"正在嵌入影像…"。这里加并发上限，配合 _fetchMediaBase64 的单张超时，
  // 保证整体一定在有限时间内收敛；个别失败的图保留原链接，不阻断其余。
  const tasks = external
    .map(u => ({ u, id: _resolveMediaIdFromUrl(u, idMap) }))
    .filter(x => x.id);
  const CONCURRENCY = 4;
  for (let i = 0; i < tasks.length; i += CONCURRENCY) {
    const batch = tasks.slice(i, i + CONCURRENCY);
    await Promise.all(batch.map(async ({ u, id }) => {
      try {
        const dataUri = await _fetchMediaBase64(id);
        if (dataUri) replacements[u] = dataUri;
      } catch (_) { /* 单张失败/超时：保留原链接 */ }
    }));
  }

  let out = html;
  for (const [u, d] of Object.entries(replacements)) out = out.split(u).join(d);
  return out;
}

/**
 * 净化 AI 生成的 HTML 报告，消除存储型 XSS / 提示注入风险。
 * AI 的输入里可能混入 OCR 来的患者文档内容，恶意/被篡改文档可注入脚本。
 * 报告是静态医疗文档，本就不需要脚本，故剥离所有可执行向量：
 *   - <script>/<iframe>/<object>/<embed>/<link import>/<meta http-equiv> 等节点
 *   - on* 事件处理属性、javascript: 伪协议、style 里的 expression()、srcdoc
 * 保留 data:image（内联影像）。解析失败时退化为强力剥离，绝不原样输出。
 */
function _sanitizeReportHtml(html) {
  if (!html) return html;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll(
      'script, iframe, object, embed, frame, frameset, base, ' +
      'link[rel="import"], meta[http-equiv], [srcdoc]'
    ).forEach(n => n.remove());

    const all = doc.querySelectorAll('*');
    all.forEach(el => {
      [...el.attributes].forEach(attr => {
        const name = (attr.name || '').toLowerCase();
        const val = (attr.value || '').replace(/\s+/g, '').toLowerCase();
        if (name.startsWith('on')) {
          el.removeAttribute(attr.name);
        } else if (
          ['href', 'src', 'xlink:href', 'formaction', 'action', 'background'].includes(name) &&
          (val.startsWith('javascript:') || val.startsWith('vbscript:') || val.startsWith('data:text/html'))
        ) {
          el.removeAttribute(attr.name);
        } else if (name === 'style' && val.includes('expression(')) {
          el.removeAttribute(attr.name);
        }
      });
    });
    return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
  } catch (_) {
    return String(html)
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/<iframe[\s\S]*?<\/iframe>/gi, '')
      .replace(/ on\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
      .replace(/javascript:/gi, '');
  }
}

/** 在站内沙箱 iframe 中预览 AI 生成的 HTML 报告（图片已内联为 base64） */
let _reportPreviewKeyHandler = null;
async function _previewAIChatHTML(idx) {
  const msg = _aiChatHistory[idx];
  if (!msg) return;
  let html = _extractHtmlCode(msg.content);
  if (!html) return;
  const t = showToast('🖼️', '正在嵌入影像，生成自包含报告…', 0);
  try { html = await _inlineReportImages(html); } catch (_) {}
  finally { if (t) t.remove(); }
  html = _sanitizeReportHtml(html);
  _openReportPreviewModal(html);
}

// 安全隔离预览：站内 modal + sandbox iframe（srcdoc）。
// 双重防护：① _sanitizeReportHtml 已剥离脚本/事件；② iframe sandbox="" 禁脚本执行、
// 视为独立来源、禁表单/弹窗，即使净化有疏漏也无法触达本站 DOM / localStorage / token。
function _openReportPreviewModal(html) {
  closeReportPreviewModal();
  const ov = document.createElement('div');
  ov.id = 'reportPreviewOverlay';
  ov.className = 'report-preview-overlay';
  ov.innerHTML = `
    <div class="report-preview-dialog" role="dialog" aria-modal="true" aria-label="病例报告预览">
      <div class="report-preview-bar">
        <span class="report-preview-title">📄 病例报告预览 · 安全沙箱</span>
        <button class="report-preview-close" type="button" aria-label="关闭预览" data-action="close-report-preview">✕</button>
      </div>
      <iframe class="report-preview-frame" referrerpolicy="no-referrer" title="病例报告预览"></iframe>
    </div>`;
  ov.addEventListener('click', (e) => { if (e.target === ov) closeReportPreviewModal(); });
  document.body.appendChild(ov);
  const frame = ov.querySelector('.report-preview-frame');
  frame.setAttribute('sandbox', '');   // 空串 = 应用全部限制（禁脚本、禁同源、禁表单/弹窗）
  frame.srcdoc = html;
  _reportPreviewKeyHandler = (e) => { if (e.key === 'Escape') closeReportPreviewModal(); };
  document.addEventListener('keydown', _reportPreviewKeyHandler, true);
}

function closeReportPreviewModal() {
  const ov = document.getElementById('reportPreviewOverlay');
  if (ov) ov.remove();
  if (_reportPreviewKeyHandler) {
    document.removeEventListener('keydown', _reportPreviewKeyHandler, true);
    _reportPreviewKeyHandler = null;
  }
}

/** 下载 AI 生成的 HTML 报告为 .html 文件（图片已内联为 base64，永久有效） */
async function _downloadAIChatHTML(idx) {
  const msg = _aiChatHistory[idx];
  if (!msg) return;
  let html = _extractHtmlCode(msg.content);
  if (!html) return;
  const t = showToast('🖼️', '正在嵌入影像，生成自包含报告…', 0);
  try { html = await _inlineReportImages(html); } catch (_) {}
  finally { if (t) t.remove(); }
  html = _sanitizeReportHtml(html);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const date = new Date().toLocaleDateString('zh-CN').replace(/\//g, '-');
  a.href = url;
  a.download = `病例报告_${date}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
  showToast('📄', 'HTML 报告已下载（影像已内联，永久可用）');
}

function sendAIChatPreset(text) {
  const inp = document.getElementById('aiChatInput');
  if (inp) inp.value = text;
  sendAIChat();
}

// Enter = send, Shift+Enter = newline
function _aiChatKeydown(e) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    sendAIChat();
  }
}

// ── 对话模型档位开关（独立于病例页模型下拉，持久化到 localStorage） ──
const _AI_CHAT_TIER_KEY = 'foot_ankle_ai_chat_tier_v1';

function _aiChatTier() {
  try { return localStorage.getItem(_AI_CHAT_TIER_KEY) || ''; } catch { return ''; }
}

/** dispatcher: 切换对话档位。切到高级（境外）先过跨境知情确认，不同意则弹回标准。 */
function aiChatTierChange() {
  const sel = document.getElementById('aiChatTierSel');
  const v = sel?.value || '';
  if (v === 'advanced' && !_crossBorderAckedThisSession()) {
    // 未确认前先回弹标准档（取消即维持标准）；确认回调里再落 advanced 并把 select 拨回去
    if (sel) sel.value = '';
    _showCrossBorderConsent(() => {
      try { localStorage.setItem(_AI_CHAT_TIER_KEY, 'advanced'); } catch {}
      const s2 = document.getElementById('aiChatTierSel');
      if (s2) s2.value = 'advanced';
      showToast('🌐', '对话已切到高级档（Claude·境外），内容仍强制脱敏后才出境');
    });
    return;
  }
  try { localStorage.setItem(_AI_CHAT_TIER_KEY, v); } catch {}
  if (v === 'advanced') showToast('🌐', '对话已切到高级档（Claude·境外），内容仍强制脱敏后才出境');
  else showToast('🏠', '对话已切回标准档（DeepSeek·境内）');
}

async function sendAIChat() {
  if (_aiChatSending) return;

  const inp = document.getElementById('aiChatInput');
  const text = (inp?.value || '').trim();
  if (!text) return;
  if (inp) inp.value = '';

  _aiChatSending = true;
  const sendBtn = document.getElementById('aiChatSendBtn');
  const stopBtn = document.getElementById('aiChatStopBtn');
  // swap send → stop
  if (sendBtn) { sendBtn.disabled = true; sendBtn.style.display = 'none'; }
  if (stopBtn) stopBtn.style.display = 'flex';

  // Create fresh AbortController for this stream
  _aiChatAbortCtrl = new AbortController();

  // Hide presets after first message
  if (_aiChatPresetsShown) {
    _aiChatPresetsShown = false;
    const presetsEl = document.getElementById('aiChatPresets');
    if (presetsEl) presetsEl.style.display = 'none';
  }

  // Add user message + loading placeholder
  // Capture streamIdx BEFORE push so the closure always targets the right entry,
  // even after we replace {loading:true} with {streaming:true} during stream.
  _aiChatHistory.push({ role: 'user', content: text });
  _aiChatHistory.push({ role: 'assistant', content: '', loading: true });
  const streamIdx = _aiChatHistory.length - 1;  // ← fixed index, never changes
  _renderAIChatMessages();

  try {
    const cases = loadDB();
    const c = cases.find(x => x.id === _aiChatCaseId);
    if (!c) throw new Error('病例数据未找到，请关闭后重新打开');

    const mod = MODULES[c.module];
    const dataSummary = buildDataSummary(c, mod);
    const { provider, model } = getSelectedGenerationModel(_aiChatCaseId);

    // Build history context — up to last 12 real messages.
    // Strip image data-URLs and <img> tags to:
    //   a) save tokens (base64 strings are huge)
    //   b) prevent cross-case image contamination (AI reusing URLs from prior chats)
    const historyContext = _aiChatHistory
      .filter(m => !m.loading && !m.isWelcome && !m.error && m.content)
      .slice(-13, -1)  // up to 12 messages; exclude current user msg (sent as custom_prompt)
      .map(m => ({
        role: m.role,
        content: m.content
          .replace(/data:image\/[^;]+;base64,[A-Za-z0-9+/=]{20,}/g, '[图片]')
          .replace(/<img\b[^>]*>/gi, '[图片]')
          .slice(0, 4000)   // cap per-message length
      }));

    let responseText = '';

    if (isCloudMode()) {
      if (!isLoggedIn()) throw new Error('请先登录后使用 AI 对话功能');
      // ── 流式调用：每个 token 直接写入 streamIdx，不搜索 ──────
      responseText = await _callStreamCloud(
        '/api/ai/generate-stream',
        {
          document_type: 'freeform',
          module_name: mod.name,
          data_summary: dataSummary,
          custom_prompt: text,
          conversation_history: historyContext,
          provider,
          model,
          // 对话档位开关：'' = 自动（freeform→medium·境内），'advanced' = Claude·境外
          tier: _aiChatTier() || null
        },
        (partial) => {
          // Always use the captured index — never call _aiChatLastLoadingIdx() here
          _aiChatHistory[streamIdx] = { role: 'assistant', content: partial, streaming: true };
          _scheduleStreamRender();  // RAF throttle: many tokens → at most one DOM write per frame
        },
        _aiChatAbortCtrl.signal  // ← allows user to abort mid-stream
      );
    } else {
      const apiKey = getAPIKey();
      if (!apiKey) throw new Error(featureGateMessage('AI 助手'));
      responseText = await _callAIChatDirect(provider, model, apiKey, mod.name, dataSummary, historyContext, text);
    }

    if (!responseText) throw new Error('AI 返回内容为空，请重试');

    // 流结束：取消任何还在排队的 RAF，再做最终渲染
    _cancelStreamRender();
    // Finalize: remove streaming cursor, show action buttons
    _aiChatHistory[streamIdx] = { role: 'assistant', content: responseText };

    // 持久化：每次成功交流后保存
    _persistAIChat(_aiChatCaseId);

    currentAiOutput = responseText;
    incAICount();

  } catch (err) {
    _cancelStreamRender();

    // User clicked "stop" — preserve partial text rather than showing an error
    const partialContent = _aiChatHistory[streamIdx]?.content || '';
    if (err.name === 'AbortError' || _aiChatHistory[streamIdx]?.streaming) {
      if (partialContent.trim()) {
        _aiChatHistory[streamIdx] = {
          role: 'assistant',
          content: partialContent + '\n\n*（已停止生成）*'
        };
        _persistAIChat(_aiChatCaseId);
        currentAiOutput = partialContent;
      } else {
        // Nothing was generated: remove both the empty AI bubble and the user msg
        _aiChatHistory.splice(streamIdx, 1);
        if (_aiChatHistory[streamIdx - 1]?.role === 'user') {
          _aiChatHistory.splice(streamIdx - 1, 1);
          const inp2 = document.getElementById('aiChatInput');
          if (inp2) inp2.value = text;  // restore user text so they can re-send
        }
      }
    } else {
      // Regular error: show error bubble with retry option
      _aiChatHistory[streamIdx] = { role: 'assistant', content: err.message || '未知错误', error: true };
      _persistAIChat(_aiChatCaseId);
    }
  } finally {
    _aiChatSending = false;
    _aiChatAbortCtrl = null;
    // swap stop → send
    if (sendBtn) { sendBtn.disabled = false; sendBtn.style.display = 'flex'; }
    if (stopBtn) stopBtn.style.display = 'none';
    _renderAIChatMessages();  // final full render
    if (inp) inp.focus();
  }
}

function _aiChatLastLoadingIdx() {
  for (let i = _aiChatHistory.length - 1; i >= 0; i--) {
    if (_aiChatHistory[i].loading) return i;
  }
  return -1;
}

// Retry: remove the error bubble, restore the user message in the input, re-send
function _retryAIChat(userText, errorBubbleIdx) {
  // Remove the error entry from history
  _aiChatHistory.splice(errorBubbleIdx, 1);
  // Also remove the preceding user message from history (it will be re-pushed by sendAIChat)
  const prevUserIdx = errorBubbleIdx - 1;
  if (prevUserIdx >= 0 && _aiChatHistory[prevUserIdx]?.role === 'user') {
    _aiChatHistory.splice(prevUserIdx, 1);
  }
  // Save after removing the error (so storage reflects clean state)
  _persistAIChat(_aiChatCaseId);
  // Put text back in input and send
  const inp = document.getElementById('aiChatInput');
  if (inp) inp.value = userText;
  _renderAIChatMessages();
  sendAIChat();
}

async function _callAIChatDirect(provider, model, apiKey, moduleName, dataSummary, history, currentUserMsg) {
  // Build multi-turn messages array with a system context as first turn
  const systemContent = `你是一位足踝外科AI医学文书助手，请根据以下病例资料和用户要求，以专业、准确的方式给出回答。\n病种模块：${moduleName}\n\n病例资料：\n${dataSummary}`;

  const messages = [
    { role: 'user', content: systemContent },
    { role: 'assistant', content: '好的，我已了解该病例资料。请告诉我你需要生成或分析什么。' },
    ...history.filter(m => m.role === 'user' || m.role === 'assistant').map(m => ({ role: m.role, content: m.content })),
    { role: 'user', content: currentUserMsg }
  ];

  if (provider === 'anthropic') {
    const resp = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey, 'anthropic-version': '2023-06-01' },
      body: JSON.stringify({ model, max_tokens: 4096, messages })
    });
    const data = await resp.json();
    if (!resp.ok) throw new Error(data?.error?.message || 'Anthropic 请求失败');
    return data.content?.map(b => b.text || '').join('') || '';
  }

  const endpoints = {
    deepseek: 'https://api.deepseek.com/chat/completions',
    openai: 'https://api.openai.com/v1/chat/completions',
    glm: 'https://open.bigmodel.cn/api/paas/v4/chat/completions',
    gemini: null,  // Gemini chat handled below
    banana: getBananaEndpoint(),
    compatible: getCompatibleEndpoint()
  };

  if (provider === 'gemini') {
    // Gemini uses different multi-turn format
    const contents = messages.map(m => ({
      role: m.role === 'assistant' ? 'model' : 'user',
      parts: [{ text: m.content }]
    }));
    const resp = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(model)}:generateContent?key=${encodeURIComponent(apiKey)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contents })
    });
    const data = await resp.json();
    if (!resp.ok) throw new Error(data?.error?.message || 'Gemini 请求失败');
    return (data?.candidates?.[0]?.content?.parts || []).map(p => p.text || '').join('');
  }

  const endpoint = endpoints[provider];
  if (!endpoint) throw new Error(`请配置 ${provider} 的网关地址`);
  const resp = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
    body: JSON.stringify({ model, temperature: 0.3, messages })
  });
  const data = await resp.json();
  if (!resp.ok) throw new Error(data?.error?.message || `${provider} 请求失败`);
  return data?.choices?.[0]?.message?.content || data?.output || '';
}

function _copyAIChatMsg(idx) {
  const msg = _aiChatHistory[idx];
  if (!msg?.content) return;
  navigator.clipboard.writeText(msg.content).then(() => showToast('📋', '已复制到剪贴板'));
}

function _downloadAIChatMsg(idx) {
  const msg = _aiChatHistory[idx];
  if (!msg?.content) return;
  const blob = new Blob([msg.content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `AI回复_${new Date().toLocaleDateString()}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}



/* assets/scan.js */
// ===================== PHOTO SCAN FIELD EXTRACTION =====================
let currentScanCaseId = null;
let currentScanImageData = '';
let currentScanMatches = [];
let currentAdmissionImageData = '';
let currentAdmissionDraft = null;

function openScanModal(caseId) {
  currentScanCaseId = caseId;
  currentScanImageData = '';
  currentScanMatches = [];
  const input = document.getElementById('scanImageInput');
  if (input) input.value = '';
  document.getElementById('scanPreview').innerHTML = '<div class="scan-placeholder">请选择或拍摄一张门诊病历、检查单、检验单或术前记录照片</div>';
  document.getElementById('scanResultBody').innerHTML = '<div class="scan-empty">识别结果会显示在这里，确认后再写入病例字段。</div>';
  document.getElementById('scanApplyBtn').disabled = true;
  document.getElementById('scanModal').classList.add('open');
}

function handleScanImage(input) {
  const file = input.files?.[0];
  if (!file) return;
  if (!file.type.startsWith('image/')) {
    showToast('⚠️', '请上传图片文件');
    return;
  }
  const reader = new FileReader();
  reader.onload = (event) => {
    currentScanImageData = event.target.result;
    document.getElementById('scanPreview').innerHTML = `<img src="${currentScanImageData}" alt="待识别资料照片">`;
    document.getElementById('scanResultBody').innerHTML = '<div class="scan-empty">照片已载入，请点击“开始识别字段”。</div>';
    document.getElementById('scanApplyBtn').disabled = true;
  };
  reader.readAsDataURL(file);
}

function getScanFieldCatalog(c) {
  const mod = MODULES[c.module];
  const sections = [...(mod?.sections || []), ...getConditionalSections(c)];
  return sections
    .filter(section => section.type !== 'media')
    .flatMap(section => getVisibleFields(section, c).map(field => ({
      key: `${section.id}_${field.id}`,
      label: field.label,
      section: section.title,
      type: field.type || 'text',
      options: field.opts || [],
      requirement: getFieldRequirementLevel(field).label
    })));
}

async function runScanExtraction() {
  if (!currentScanCaseId) return;
  if (!currentScanImageData) {
    showToast('⚠️', '请先选择或拍摄照片');
    return;
  }
  const cases = loadDB();
  const c = cases.find(item => item.id === currentScanCaseId);
  if (!c) return;
  const mod = MODULES[c.module];
  const fieldCatalog = getScanFieldCatalog(c);

  document.getElementById('scanResultBody').innerHTML = '<div class="scan-loading">正在识别图片内容并匹配病例字段...</div>';
  document.getElementById('scanApplyBtn').disabled = true;

  try {
    let fields = [];
    if (isCloudMode()) {
      if (!isLoggedIn()) throw new Error('请先登录后端账号，再使用服务端扫描识别');
      const result = await backendRequest('/api/ai/extract-fields', {
        method: 'POST',
        body: JSON.stringify({
          module_name: mod.name,
          field_catalog: fieldCatalog,
          image_data: currentScanImageData
        })
      });
      fields = result.fields || [];
    } else {
      fields = await extractFieldsWithLocalAI(mod.name, fieldCatalog, currentScanImageData);
    }
    currentScanMatches = normalizeScanMatches(fields, fieldCatalog, c);
    renderScanResults(currentScanMatches);
  } catch (err) {
    document.getElementById('scanResultBody').innerHTML = `<div class="scan-error">识别失败：${escapeHtml(err.message)}</div>`;
  }
}

async function extractFieldsWithLocalAI(moduleName, fieldCatalog, imageData) {
  const apiKey = getAPIKey();
  const provider = getAIProvider();
  const model = getAIModel();
  if (!apiKey) throw new Error(featureGateMessage('扫描识别'));
  if (!['anthropic', 'gemini', 'banana'].includes(provider)) {
    throw new Error('当前模型平台暂不支持图片扫描，请使用系统内置的图片识别（境内 OCR）补全字段');
  }
  const prompt = buildScanPrompt(moduleName, fieldCatalog);
  let text = '';
  if (provider === 'anthropic') text = await callAnthropicVision(apiKey, model, prompt, imageData);
  if (provider === 'gemini') text = await callGeminiVision(apiKey, model, prompt, imageData);
  if (provider === 'banana') text = await callOpenAICompatibleVision(apiKey, model, prompt, imageData);
  return parseScanJson(text);
}

function buildScanPrompt(moduleName, fieldCatalog) {
  return `你是一位足踝外科资料录入助手。请读取图片中的门诊病历、住院记录、检查单、检验单或术前资料，只把能明确识别并能匹配到字段目录的内容提取出来。
必须严格返回 JSON，不要输出 Markdown，不要解释。JSON 格式：{"fields":[{"key":"字段key","label":"字段中文名","value":"识别值","confidence":0.0,"evidence":"图片中的依据短句"}]}
规则：
1. key 必须来自字段目录，不能自造字段。
2. 不确定的字段不要返回，confidence 建议低于 0.6 的不要返回。
3. 复选字段 value 用数组；普通字段 value 用字符串或数字。
4. 不要提取姓名、电话、身份证号、地址等直接身份信息，除非字段目录明确包含该字段。

病种模块：${moduleName}
字段目录：${JSON.stringify(fieldCatalog)}`;
}

function splitDataUrl(dataUrl) {
  const match = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
  if (!match) throw new Error('图片数据格式不正确');
  return { mime: match[1], data: match[2] };
}

async function callAnthropicVision(apiKey, model, prompt, imageData) {
  const image = splitDataUrl(imageData);
  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01'
    },
    body: JSON.stringify({
      model,
      max_tokens: 2048,
      messages: [{
        role: 'user',
        content: [
          { type: 'text', text: prompt },
          { type: 'image', source: { type: 'base64', media_type: image.mime, data: image.data } }
        ]
      }]
    })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.error?.message || '图片识别请求失败');
  return data.content?.map(block => block.text || '').join('') || '';
}

async function callGeminiVision(apiKey, model, prompt, imageData) {
  const image = splitDataUrl(imageData);
  const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(model)}:generateContent?key=${encodeURIComponent(apiKey)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      contents: [{ parts: [{ text: prompt }, { inline_data: { mime_type: image.mime, data: image.data } }] }]
    })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.error?.message || 'Gemini 图片识别请求失败');
  return data?.candidates?.[0]?.content?.parts?.map(part => part.text || '').join('') || '';
}

async function callOpenAICompatibleVision(apiKey, model, prompt, imageData) {
  const endpoint = getBananaEndpoint();
  if (!endpoint) throw new Error('请先在设置中填写 Banana Pro 兼容网关地址');
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
    body: JSON.stringify({
      model,
      temperature: 0.1,
      messages: [{
        role: 'user',
        content: [
          { type: 'text', text: prompt },
          { type: 'image_url', image_url: { url: imageData } }
        ]
      }]
    })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.error?.message || '兼容视觉模型请求失败');
  return data?.choices?.[0]?.message?.content || data?.output || '';
}

function parseScanJson(text) {
  const cleaned = (text || '').trim().replace(/^```(?:json)?\s*/, '').replace(/\s*```$/, '');
  const parsed = JSON.parse(cleaned);
  return Array.isArray(parsed?.fields) ? parsed.fields : [];
}

function normalizeScanMatches(fields, fieldCatalog, c) {
  const catalog = new Map(fieldCatalog.map(field => [field.key, field]));
  return (fields || [])
    .filter(item => catalog.has(item.key))
    .map(item => {
      const field = catalog.get(item.key);
      return {
        key: item.key,
        label: field.label,
        section: field.section,
        value: item.value,
        confidence: Number(item.confidence || 0),
        evidence: item.evidence || '',
        existing: c.data?.[item.key] || ''
      };
    });
}

function renderScanResults(matches) {
  const body = document.getElementById('scanResultBody');
  const applyBtn = document.getElementById('scanApplyBtn');
  if (!matches.length) {
    body.innerHTML = '<div class="scan-empty">没有识别到可自动填入的字段。可以换一张更清晰的照片，或手动录入。</div>';
    applyBtn.disabled = true;
    return;
  }
  body.innerHTML = matches.map((item, index) => `
    <label class="scan-result-row">
      <input type="checkbox" id="scan_match_${index}" checked>
      <span>
        <strong>${escapeHtml(item.section)} · ${escapeHtml(item.label)}</strong>
        <em>${escapeHtml(formatScanValue(item.value))}</em>
        ${item.existing ? `<small>当前已有：${escapeHtml(formatScanValue(item.existing))}</small>` : ''}
        ${item.evidence ? `<small>依据：${escapeHtml(item.evidence)}</small>` : ''}
      </span>
      <b>${Math.round(item.confidence * 100) || 0}%</b>
    </label>
  `).join('');
  applyBtn.disabled = false;
}

function formatScanValue(value) {
  return Array.isArray(value) ? value.join('、') : String(value ?? '');
}

function applyScanMatches() {
  if (!currentScanCaseId) return;
  const cases = loadDB();
  const c = cases.find(item => item.id === currentScanCaseId);
  if (!c) return;
  c.data = c.data || {};
  let applied = 0;
  currentScanMatches.forEach((match, index) => {
    const checkbox = document.getElementById(`scan_match_${index}`);
    if (!checkbox?.checked) return;
    c.data[match.key] = match.value;
    applied++;
  });
  saveDB(cases);
  closeModal('scanModal');
  renderCaseForm(currentScanCaseId);
  renderSidebar();
  updateStats();
  showToast('✅', `已写入 ${applied} 个字段，请核对后保存`);
}

function openAdmissionScanModal() {
  currentAdmissionImageData = '';
  currentAdmissionDraft = null;
  const input = document.getElementById('admissionImageInput');
  if (input) input.value = '';
  document.getElementById('admissionPreview').innerHTML = '<div class="scan-placeholder">请拍摄或上传住院条、入院通知单、门诊收治单</div>';
  document.getElementById('admissionResultBody').innerHTML = '<div class="scan-empty">识别后会生成一条“待录入任务”，住院医/管床可在首页待录入目录中接收。</div>';
  document.getElementById('admissionDispatchBtn').disabled = true;
  document.getElementById('admissionScanModal').classList.add('open');
}

function handleAdmissionImage(input) {
  const file = input.files?.[0];
  if (!file) return;
  if (!file.type.startsWith('image/')) {
    showToast('⚠️', '请上传图片文件');
    return;
  }
  const reader = new FileReader();
  reader.onload = (event) => {
    currentAdmissionImageData = event.target.result;
    document.getElementById('admissionPreview').innerHTML = `<img src="${currentAdmissionImageData}" alt="待识别住院条">`;
    document.getElementById('admissionResultBody').innerHTML = '<div class="scan-empty">住院条已载入，请点击“扫描生成待录入任务”。</div>';
    document.getElementById('admissionDispatchBtn').disabled = true;
  };
  reader.readAsDataURL(file);
}

async function runAdmissionScan() {
  if (!currentAdmissionImageData) {
    showToast('⚠️', '请先拍摄或上传住院条');
    return;
  }
  document.getElementById('admissionResultBody').innerHTML = '<div class="scan-loading">正在识别住院条，并整理为待录入任务...</div>';
  document.getElementById('admissionDispatchBtn').disabled = true;
  try {
    currentAdmissionDraft = await extractAdmissionSlip(currentAdmissionImageData);
    renderAdmissionDraft(currentAdmissionDraft);
    document.getElementById('admissionDispatchBtn').disabled = false;
  } catch (err) {
    document.getElementById('admissionResultBody').innerHTML = `<div class="scan-error">识别失败：${escapeHtml(err.message)}</div>`;
  }
}

async function extractAdmissionSlip(imageData) {
  const provider = getAIProvider();
  const model = getAIModel();
  const prompt = buildAdmissionPrompt();
  if (isCloudMode()) {
    if (!isLoggedIn()) throw new Error('请先登录后端账号，再使用服务端扫描分发');
    const result = await backendRequest('/api/ai/extract-fields', {
      method: 'POST',
      body: JSON.stringify({
        module_name: '住院条任务分发',
        field_catalog: getAdmissionFieldCatalog(),
        image_data: imageData
      })
    });
    return admissionFieldsToDraft(result.fields || []);
  }
  const apiKey = getAPIKey();
  if (!apiKey) throw new Error(featureGateMessage('扫描识别'));
  if (!['anthropic', 'gemini', 'banana'].includes(provider)) {
    throw new Error('当前模型平台暂不支持图片扫描，请使用系统内置的图片识别（境内 OCR）补全字段');
  }
  let text = '';
  if (provider === 'anthropic') text = await callAnthropicVision(apiKey, model, prompt, imageData);
  if (provider === 'gemini') text = await callGeminiVision(apiKey, model, prompt, imageData);
  if (provider === 'banana') text = await callOpenAICompatibleVision(apiKey, model, prompt, imageData);
  return parseAdmissionJson(text);
}

function getAdmissionFieldCatalog() {
  return [
    { key: 'patient_name', label: '患者姓名', section: '住院条', type: 'text' },
    { key: 'gender', label: '性别', section: '住院条', type: 'select', options: ['男', '女'] },
    { key: 'age', label: '年龄', section: '住院条', type: 'number' },
    { key: 'caseid', label: '住院号/门诊号', section: '住院条', type: 'text' },
    { key: 'diagnosis', label: '初步诊断', section: '住院条', type: 'text' },
    { key: 'department', label: '收治科室', section: '住院条', type: 'text' },
    { key: 'admission_date', label: '拟入院日期', section: '住院条', type: 'date' },
    { key: 'supervisor', label: '收治上级医师', section: '住院条', type: 'text' },
    { key: 'assignee', label: '分配给住院医/管床医师', section: '住院条', type: 'text' },
    { key: 'module', label: '建议病种模块', section: '住院条', type: 'select', options: ['A', 'B', 'C', 'D', 'E', 'F'] },
    { key: 'priority', label: '任务优先级', section: '住院条', type: 'select', options: ['普通', '加急', '术前优先'] },
    { key: 'notes', label: '备注', section: '住院条', type: 'textarea' }
  ];
}

function buildAdmissionPrompt() {
  return `你是一位足踝外科住院条分诊助手。请读取图片中的住院条、入院通知单、门诊收治单，整理为一条待录入任务。
必须严格返回 JSON，不要输出 Markdown，不要解释。JSON 格式：
{"patient_name":"","gender":"","age":"","caseid":"","diagnosis":"","department":"","admission_date":"","supervisor":"","assignee":"","module":"A-F之一或空","priority":"普通/加急/术前优先","notes":"","confidence":0.0}
规则：
1. 只提取图片中明确存在的信息，不确定留空。
2. module 根据诊断推断：A拇外翻，B踝关节置换，C踝关节融合，D踝关节骨折，E跟腱病，F平足/高弓足；不能判断则留空。
3. 不要编造医师姓名、住院号或日期。
4. notes 可放置需要管床补充的简短提示。`;
}

function admissionFieldsToDraft(fields) {
  const data = {};
  (fields || []).forEach(field => {
    data[field.key] = field.value;
  });
  data.confidence = Math.max(...(fields || []).map(field => Number(field.confidence || 0)), 0);
  return normalizeAdmissionDraft(data);
}

function parseAdmissionJson(text) {
  const cleaned = (text || '').trim().replace(/^```(?:json)?\s*/, '').replace(/\s*```$/, '');
  return normalizeAdmissionDraft(JSON.parse(cleaned));
}

function normalizeAdmissionDraft(data) {
  const diagnosis = String(data.diagnosis || '');
  const inferredModule = data.module || inferModuleFromDiagnosis(diagnosis);
  return {
    patient_name: data.patient_name || '',
    gender: data.gender || '',
    age: data.age || '',
    caseid: data.caseid || '',
    diagnosis,
    department: data.department || '足踝外科',
    admission_date: data.admission_date || new Date().toISOString().split('T')[0],
    supervisor: data.supervisor || '',
    assignee: data.assignee || '',
    module: MODULES[inferredModule] ? inferredModule : '',
    priority: data.priority || '普通',
    notes: data.notes || '',
    confidence: Number(data.confidence || 0)
  };
}

function inferModuleFromDiagnosis(text) {
  if (/拇外翻|hallux/i.test(text)) return 'A';
  if (/置换|假体|TAR|踝关节置换/i.test(text)) return 'B';
  if (/融合|关节融合|arthrodesis/i.test(text)) return 'C';
  if (/后踝|三踝|踝关节骨折/i.test(text)) return 'D';
  if (/跟腱|Achilles/i.test(text)) return 'E';
  if (/平足|高弓|扁平足|cavus|flatfoot/i.test(text)) return 'F';
  return '';
}

function renderAdmissionDraft(draft) {
  const moduleName = draft.module ? `${draft.module} ${MODULES[draft.module]?.name || ''}` : '待管床判断';
  document.getElementById('admissionResultBody').innerHTML = `
    <div class="admission-draft-grid">
      ${renderAdmissionDraftItem('患者姓名', draft.patient_name || '未识别')}
      ${renderAdmissionDraftItem('性别 / 年龄', `${draft.gender || '未识别'} / ${draft.age || '未识别'}`)}
      ${renderAdmissionDraftItem('住院号 / 门诊号', draft.caseid || '未识别')}
      ${renderAdmissionDraftItem('初步诊断', draft.diagnosis || '未识别')}
      ${renderAdmissionDraftItem('建议模块', moduleName)}
      ${renderAdmissionDraftItem('拟入院日期', draft.admission_date || '未识别')}
      ${renderAdmissionDraftItem('收治上级医师', draft.supervisor || '未识别')}
      ${renderAdmissionDraftItem('分配对象', draft.assignee || '待分配')}
      ${renderAdmissionDraftItem('优先级', draft.priority)}
      ${renderAdmissionDraftItem('备注', draft.notes || '无')}
    </div>
  `;
}

function renderAdmissionDraftItem(label, value) {
  return `<div class="admission-draft-item"><span>${escapeHtml(label)}</span><strong>${escapeHtml(String(value))}</strong></div>`;
}

// 云端模式下任务存后端；本地模式继续走 localStorage 缓存
async function dispatchAdmissionTask() {
  if (!currentAdmissionDraft) return;
  const payload = {
    patient_name: currentAdmissionDraft.patient_name || '未命名患者',
    gender: currentAdmissionDraft.gender || null,
    age: currentAdmissionDraft.age ? Number(currentAdmissionDraft.age) : null,
    diagnosis: currentAdmissionDraft.diagnosis || null,
    module: currentAdmissionDraft.module || null,
    bed_no: currentAdmissionDraft.bed_no || currentAdmissionDraft.caseid || null,
    ward: currentAdmissionDraft.ward || null,
    admission_date: currentAdmissionDraft.admission_date || null,
    priority: currentAdmissionDraft.priority || '普通',
    notes: currentAdmissionDraft.notes || null,
  };

  if (isCloudMode() && isLoggedIn()) {
    try {
      const created = await backendRequest('/api/admission-tasks', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      // 同步缓存一份到本地，便于离线展示
      const tasks = loadAdmissionTasks();
      tasks.unshift({ ...created, _backend: true });
      saveAdmissionTasks(tasks);
      closeModal('admissionScanModal');
      await refreshAdmissionTasks();
      showToast('☁️', '已分发到云端，住院医/管床可在工作台接收');
      return;
    } catch (err) {
      showToast('⚠️', `云端分发失败：${err.message}`);
      return;
    }
  }

  // 本地降级：仅当前浏览器可见
  const tasks = loadAdmissionTasks();
  tasks.unshift({
    id: 'admission_' + Date.now(),
    ...payload,
    status: 'pending',
    supervisor_id: getAuthUser()?.id || null,
    supervisor_name: getAuthUser()?.full_name || getAuthUser()?.username || '未登录',
    assignee_id: null,
    assignee_name: null,
    created_at: new Date().toISOString(),
    _local: true,
  });
  saveAdmissionTasks(tasks);
  closeModal('admissionScanModal');
  renderAdmissionTaskBoard();
  showToast('📨', '本地模式：仅在当前浏览器可见。请连接后端启用跨账号分发。');
}

async function refreshAdmissionTasks() {
  if (isCloudMode() && isLoggedIn()) {
    try {
      const list = await backendRequest('/api/admission-tasks?status=pending');
      const assigned = await backendRequest('/api/admission-tasks?status=assigned');
      const inProgress = await backendRequest('/api/admission-tasks?status=in_progress');
      const merged = [
        ...(Array.isArray(list) ? list : []),
        ...(Array.isArray(assigned) ? assigned : []),
        ...(Array.isArray(inProgress) ? inProgress : []),
      ].map(t => ({ ...t, _backend: true }));
      saveAdmissionTasks(merged);
    } catch (err) {
      // 静默：保留本地缓存
    }
  }
  renderAdmissionTaskBoard();
}

function renderAdmissionTaskBoard() {
  const board = document.getElementById('admissionTaskBoard');
  if (!board) return;
  const tasks = loadAdmissionTasks().filter(t => !['converted', 'archived', '已建档'].includes(t.status));
  if (!tasks.length) {
    board.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">📋</div>
        <div class="empty-state-title">暂无待录入任务</div>
        <div class="empty-state-hint">上级医师拍摄住院条后，待录入任务会自动出现在这里。</div>
      </div>`;
    return;
  }
  const user = getAuthUser() || {};
  board.innerHTML = tasks.map(task => {
    const isAssignee = task.assignee_id && String(task.assignee_id) === String(user.id);
    const isSupervisor = task.supervisor_id && String(task.supervisor_id) === String(user.id);
    const canConvert = isAssignee || isSupervisor || isSystemAdminUser(user);
    const canAssign = (isSupervisor || isSystemAdminUser(user)) && task.status !== 'assigned';
    return `
    <div class="admission-task-card">
      <div>
        <div class="admission-task-title">${escapeHtml(task.patient_name || '未命名患者')} <span>${escapeHtml(task.priority || '普通')}</span></div>
        <div class="admission-task-meta">诊断：${escapeHtml(task.diagnosis || '待补充')} · 建议模块：${task.module ? escapeHtml(`${task.module} ${MODULES[task.module]?.name || ''}`) : '待判断'}</div>
        <div class="admission-task-meta">收治：${escapeHtml(task.supervisor_name || '未识别')} · 分配：${escapeHtml(task.assignee_name || '待分配')} · 日期：${escapeHtml(task.admission_date || '')}</div>
      </div>
      <div class="admission-task-actions">
        ${canAssign ? `<button class="case-action-btn" data-action="prompt-assign-admission-task" data-task-id="${escapeHtml(String(task.id))}">指派</button>` : ''}
        ${canConvert ? `<button class="case-action-btn" data-action="create-case-from-admission-task" data-task-id="${escapeHtml(String(task.id))}">转为病例</button>` : ''}
        ${(isSupervisor || isSystemAdminUser(user)) ? `<button class="case-action-btn" data-action="archive-admission-task" data-task-id="${escapeHtml(String(task.id))}">归档</button>` : ''}
      </div>
    </div>`;
  }).join('');
}

async function promptAssignAdmissionTask(taskId) {
  if (!isCloudMode() || !isLoggedIn()) {
    showToast('⚠️', '指派功能需要连接后端账号');
    return;
  }
  const username = await showAppPrompt({ title: '指派住院条任务', message: '请输入接收住院医的用户名（系统会自动匹配用户名或姓名）', placeholder: '用户名或姓名', confirmText: '指派' });
  if (!username) return;
  try {
    const users = await backendRequest(`/api/users/?limit=100`);
    const target = (Array.isArray(users) ? users : []).find(u =>
      u.username === username.trim() || u.full_name === username.trim()
    );
    if (!target) {
      showToast('⚠️', '未找到该用户');
      return;
    }
    await backendRequest(`/api/admission-tasks/${taskId}/assign`, {
      method: 'POST',
      body: JSON.stringify({ assignee_id: target.id }),
    });
    await refreshAdmissionTasks();
    showToast('✅', `已指派给 ${target.full_name || target.username}`);
  } catch (err) {
    showToast('⚠️', `指派失败：${err.message}`);
  }
}

async function createCaseFromAdmissionTask(taskId) {
  // 后端任务 → 调 convert 端点
  if (isCloudMode() && isLoggedIn() && !String(taskId).startsWith('admission_')) {
    try {
      const result = await backendRequest(`/api/admission-tasks/${taskId}/convert`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      await syncCloudCasesToLocal();
      await refreshAdmissionTasks();
      // 跳转到对应的本地缓存病例
      const local = loadDB().find(c => c.backendId === result.case_id);
      if (local) openCase(local.id);
      showToast('✅', '已从住院条任务创建正式病例');
      return;
    } catch (err) {
      showToast('⚠️', `转为病例失败：${err.message}`);
      return;
    }
  }

  // 本地降级
  const tasks = loadAdmissionTasks();
  const task = tasks.find(item => String(item.id) === String(taskId));
  if (!task) return;
  const module = task.module || await showAppPrompt({ title: '选择病例模块', message: 'A 拇外翻 / B 踝置换 / C 踝融合 / D 踝骨折\nE 跟腱病 / F 扁平足 / G 高弓足 / H 踝韧带损伤\nI 距骨软骨损伤 / J Lisfranc / K 跖骨基底撕脱 / L 跟骨骨折', defaultValue: 'A', maxLength: 1, confirmText: '创建' });
  if (!MODULES[module]) {
    showToast('⚠️', '病例模块无效，请重新选择');
    return;
  }
  const newCase = {
    id: 'case_' + Date.now(),
    module,
    createdAt: task.admission_date || new Date().toISOString().split('T')[0],
    createdByUserId: getAuthUser()?.id || '',
    createdByUsername: getAuthUser()?.username || '',
    ownerRole: getAuthUser()?.role || '',
    data: {
      basic_name: task.patient_name || '',
      name: task.patient_name || '',
      basic_gender: task.gender || '',
      basic_age: task.age || '',
      basic_caseid: task.bed_no || task.caseid || '',
      basic_date: task.admission_date || '',
      history_diagnosis: task.diagnosis || '',
      workflow_case_stage: '术前',
      workflow_review_status: '待审核',
    },
    media: {},
    tags: ['住院条分发', '待录入'],
    admissionTaskId: task.id,
    aiGenCount: 0
  };
  const cases = loadDB();
  cases.unshift(newCase);
  saveDB(cases);
  task.status = 'converted';
  task.caseId = newCase.id;
  saveAdmissionTasks(tasks);
  openCase(newCase.id);
  showToast('✅', '已从住院条任务创建病例（本地）');
}

async function archiveAdmissionTask(taskId) {
  if (isCloudMode() && isLoggedIn() && !String(taskId).startsWith('admission_')) {
    try {
      await backendRequest(`/api/admission-tasks/${taskId}/archive`, { method: 'POST' });
      await refreshAdmissionTasks();
      showToast('✅', '任务已归档');
      return;
    } catch (err) {
      showToast('⚠️', `归档失败：${err.message}`);
      return;
    }
  }
  const tasks = loadAdmissionTasks();
  const task = tasks.find(item => String(item.id) === String(taskId));
  if (!task) return;
  task.status = 'archived';
  saveAdmissionTasks(tasks);
  renderAdmissionTaskBoard();
  showToast('✅', '任务已归档（本地）');
}



/* assets/user-settings.js */
function showSettingsModal() {
  const s = loadSettings();
  const lockedBackend = getConfiguredBackendBase();
  refreshGenerationModelDatalist();
  document.getElementById('setting-provider').value = s.provider || 'deepseek';
  document.getElementById('setting-apikey').value = s.apiKey || '';
  document.getElementById('setting-model').value = s.model || 'deepseek-v4-pro';
  document.getElementById('setting-banana-endpoint').value = s.bananaEndpoint || '';
  document.getElementById('setting-compatible-endpoint').value = s.compatibleEndpoint || '';
  document.getElementById('setting-backend').value = lockedBackend || s.backendUrl || '';
  document.getElementById('setting-backend').disabled = !!lockedBackend;
  const hint = document.getElementById('backendSettingHint');
  if (hint) {
    hint.textContent = lockedBackend
      ? '后端地址已由管理员在 app-config.js 中预置；这里不会修改服务器后端 .env。AI、OCR、对象存储由云端统一管理。'
      : '这里仅保存浏览器本地连接地址，不会编辑后端 .env。正式部署建议由管理员在 app-config.js 和服务器环境变量中预置。';
  }
  document.getElementById('setting-reminder-mode').value = s.reminderMode || 'none';
  document.getElementById('settingsModal').classList.add('open');
  loadCentersIntoSettings();
  if (typeof loadAgentWorkflowPanel === 'function') loadAgentWorkflowPanel();
}

function openCenterSettings() {
  showSettingsModal();
  switchSettingsTab('centers');
  loadCentersIntoSettings();
}

let _registrationCenters = [];

async function loadRegistrationCenters() {
  const selects = [
    document.getElementById('auth-reg-center'),
    document.getElementById('welcome-reg-center'),
  ].filter(Boolean);
  if (!selects.length) return;
  const empty = '<option value="">暂无中心 / 稍后由管理员分配</option>';
  if (!isCloudMode()) {
    selects.forEach(select => { select.innerHTML = empty; select.disabled = true; });
    return;
  }
  selects.forEach(select => {
    select.disabled = true;
    select.innerHTML = '<option value="">正在读取研究中心...</option>';
  });
  try {
    const centers = await backendRequest('/api/centers/public', {}, false);
    _registrationCenters = Array.isArray(centers) ? centers : [];
    const html = empty + _registrationCenters.map(c =>
      `<option value="${c.id}">${escapeHtml(c.name)} (${escapeHtml(c.code || '')})</option>`
    ).join('');
    selects.forEach(select => {
      select.innerHTML = html;
      select.disabled = false;
    });
  } catch (err) {
    selects.forEach(select => {
      select.innerHTML = '<option value="">中心列表读取失败，可稍后由管理员分配</option>';
      select.disabled = false;
    });
  }
}

async function saveSettings() {
  const oldSettings = loadSettings();
  const provider = document.getElementById('setting-provider').value;
  const apiKey = document.getElementById('setting-apikey').value.trim();
  const model = document.getElementById('setting-model').value.trim() || 'deepseek-v4-pro';
  const bananaEndpoint = document.getElementById('setting-banana-endpoint').value.trim();
  const compatibleEndpoint = document.getElementById('setting-compatible-endpoint').value.trim();
  const backendUrl = document.getElementById('setting-backend').value.trim();
  const reminderMode = document.getElementById('setting-reminder-mode').value;
  const defaultCenterId = document.getElementById('setting-default-center')?.value || '';
  saveSettingsData({
    ...oldSettings,
    provider,
    apiKey,
    model,
    bananaEndpoint,
    compatibleEndpoint,
    backendUrl,
    reminderMode,
    defaultCenterId: defaultCenterId ? Number(defaultCenterId) : null
  });
  if (isCloudMode() && isLoggedIn()) {
    try {
      await setDefaultCenter(defaultCenterId || null);
    } catch (err) {
      showToast('⚠️', `默认中心保存失败：${err.message}`);
    }
  }
  closeModal('settingsModal');
  renderAuthState();
  renderCenterFilterChips();
  renderDashboard(currentModuleFilter || 'all');
  showToast('✅', backendUrl ? '系统设置已保存，已使用本地后端连接地址' : '系统设置已保存，当前仍为本地单机模式');
}

async function loadCentersIntoSettings() {
  const select = document.getElementById('setting-default-center');
  const list = document.getElementById('settingsCenterList');
  if (!select || !list) return;
  if (!isCloudMode() || !isLoggedIn()) {
    select.innerHTML = '<option value="">云端登录后可选择中心</option>';
    list.innerHTML = '<div class="settings-hint">研究中心需要云端后端和登录账号。</div>';
    return;
  }
  list.innerHTML = '<div class="settings-hint">正在读取研究中心...</div>';
  const centers = await refreshCenters();
  const defaultId = getDefaultCenterId();
  select.innerHTML = '<option value="">未设置默认中心</option>' + centers.map(c =>
    `<option value="${c.id}" ${String(defaultId || '') === String(c.id) ? 'selected' : ''}>${escapeHtml(c.name)} (${escapeHtml(c.code)})</option>`
  ).join('');
  list.innerHTML = centers.length
    ? centers.map(c => `
        <div class="center-list-item">
          <strong>${escapeHtml(c.name)}</strong>
          <span>${escapeHtml(c.code)}${c.irb_code ? ` · IRB ${escapeHtml(c.irb_code)}` : ''}</span>
        </div>`).join('')
    : '<div class="settings-hint">暂无研究中心。可在下方创建一个中心。</div>';
  renderCenterFilterChips();
}

async function createCenterFromSettings() {
  if (!isCloudMode() || !isLoggedIn()) {
    showToast('⚠️', '请先登录云端账号后再创建研究中心');
    return;
  }
  const nameEl = document.getElementById('setting-center-name');
  const codeEl = document.getElementById('setting-center-code');
  const name = nameEl.value.trim();
  const code = codeEl.value.trim();
  if (!name || !code) {
    showToast('⚠️', '请填写中心名称和中心编码');
    (name ? codeEl : nameEl).focus();
    return;
  }
  try {
    const center = await backendRequest('/api/centers/', {
      method: 'POST',
      body: JSON.stringify({ name, code, institution_type: 'hospital' }),
    });
    nameEl.value = '';
    codeEl.value = '';
    await refreshCenters();
    const select = document.getElementById('setting-default-center');
    if (select) select.value = center.id;
    await setDefaultCenter(center.id);
    await loadCentersIntoSettings();
    renderAuthState();
    renderDashboard(currentModuleFilter || 'all');
    showToast('✅', '研究中心已创建并设为默认中心');
  } catch (err) {
    showToast('⚠️', `研究中心创建失败：${err.message}`);
  }
}

function refreshGenerationModelDatalist() {
  const list = document.getElementById('generationModelList');
  if (!list) return;
  list.innerHTML = getGenerationModelOptions().map(item =>
    `<option value="${escapeHtml(item.model)}">${escapeHtml(getProviderLabel(item.provider))} · ${escapeHtml(item.label || item.model)}</option>`
  ).join('');
}

function saveCurrentModelAsPreset() {
  const provider = document.getElementById('setting-provider').value;
  const model = document.getElementById('setting-model').value.trim();
  if (!model) {
    showToast('⚠️', '请先输入模型名称');
    return;
  }
  const settings = loadSettings();
  const customModels = Array.isArray(settings.customModels) ? settings.customModels : [];
  if (!customModels.some(item => item.provider === provider && item.model === model)
    && !GENERATION_MODEL_PRESETS.some(item => item.provider === provider && item.model === model)) {
    customModels.push({ provider, model, label: `${getProviderLabel(provider)} · ${model}` });
  }
  saveSettingsData({ ...settings, provider, model, customModels });
  refreshGenerationModelDatalist();
  showToast('✅', '已加入常用模型列表');
}

async function showAuthModal() {
  if (isLoggedIn()) {
    const ok = await showAppConfirm({ title: '退出登录', message: '当前已登录，是否退出当前账号？', confirmText: '退出' });
    if (!ok) return;
    clearAuth();
    renderAuthState();
    updateStats();
    renderSidebar();
    renderDashboard(currentModuleFilter || 'all');
    showToast('👋', '已退出登录');
    return;
  }
  renderAuthModePanel();
  document.getElementById('authModal').classList.add('open');
}

function renderAuthModePanel() {
  const panel = document.getElementById('authModePanel');
  const title = document.getElementById('authModeTitle');
  const desc = document.getElementById('authModeDesc');
  const settingsBtn = document.getElementById('authModeSettingsBtn');
  if (!panel || !title || !desc || !settingsBtn) return;
  // 空 backendUrl（nginx 同源代理）和显式填写地址都算"已配置"
  const hasBackend = isCloudMode();
  const cloudMode = hasBackend;
  panel.classList.toggle('backend-ready', hasBackend);
  title.textContent = hasBackend ? '云端协作模式已启用' : '当前为本地单机模式';
  const backendDesc = getBackendBase() || '（nginx 同源代理）';
  desc.textContent = hasBackend
    ? `后端：${backendDesc}。AI、OCR、对象存储和团队权限由云端统一配置，普通用户无需填写 API Key。`
    : '本地病例、住院条待录入任务、导入导出可继续使用；注册登录、团队权限和服务端共享需要配置 FastAPI 后端。';
  settingsBtn.textContent = hasBackend ? '修改后端地址' : '前往后端设置';
}

function renderDeploymentStatus() {
  const card = document.getElementById('deploymentStatusCard');
  const title = document.getElementById('deploymentStatusTitle');
  const desc = document.getElementById('deploymentStatusDesc');
  if (!card || !title || !desc) return;
  const backend = getBackendBase();
  // 空 backendUrl（nginx 同源代理）时 backend="" 是 falsy，但仍是云端模式
  const cloud = isCloudMode();
  card.classList.toggle('cloud-ready', !!cloud);
  if (cloud) {
    title.textContent = '☁️ 云端协作';
    desc.textContent = '';
    card.title = `已连接：${backend}`;
  } else {
    title.textContent = '🖥️ 本地模式';
    desc.textContent = '';
    card.title = '病例保存在浏览器本地';
  }
}

function goBackendSettingsFromAuth() {
  closeModal('authModal');
  showSettingsModal();
  setTimeout(() => document.getElementById('setting-backend')?.focus(), 0);
}

function loginDemoUser() {
  if (isCloudMode()) {
    showToast('🔐', '云端模式请使用正式账号登录');
    return;
  }
  saveAuth({
    accessToken: 'demo-local-admin-token',
    refreshToken: '',
    user: {
      id: 'demo-admin',
      username: 'demo_admin',
      full_name: '系统管理员测试账号',
      role: 'admin',
      department: '足踝外科',
      demo: true
    }
  });
  closeModal('authModal');
  renderAuthState();
  updateStats();
  renderSidebar();
  renderDashboard(currentModuleFilter || 'all');
  showToast('✅', '已进入系统管理员测试账号，可测试本地功能');
}

function switchWelcomeAuth(tab) {
  const isLogin = tab === 'login';
  document.getElementById('welcome-tab-login')?.classList.toggle('active', isLogin);
  document.getElementById('welcome-tab-register')?.classList.toggle('active', !isLogin);
  document.getElementById('welcome-login-panel')?.classList.toggle('active', isLogin);
  document.getElementById('welcome-register-panel')?.classList.toggle('active', !isLogin);
  const hint = document.getElementById('welcomeModeHint');
  if (hint) hint.textContent = isLogin ? '登录后进入病例工作台' : '提交后等待系统管理员启用';
  if (!isLogin) loadRegistrationCenters();
}

function focusWelcomeLogin() {
  switchWelcomeAuth('login');
  document.getElementById('welcome-login-username')?.focus();
}

async function loginWelcomeUser() {
  document.getElementById('auth-login-username').value = document.getElementById('welcome-login-username').value.trim();
  document.getElementById('auth-login-password').value = document.getElementById('welcome-login-password').value;
  await loginUser();
}

async function registerWelcomeUser() {
  document.getElementById('auth-reg-username').value = document.getElementById('welcome-reg-username').value.trim();
  document.getElementById('auth-reg-email').value = document.getElementById('welcome-reg-email').value.trim();
  document.getElementById('auth-reg-fullname').value = document.getElementById('welcome-reg-fullname').value.trim();
  document.getElementById('auth-reg-dept').value = document.getElementById('welcome-reg-dept').value.trim();
  document.getElementById('auth-reg-center').value = document.getElementById('welcome-reg-center')?.value || '';
  document.getElementById('auth-reg-password').value = document.getElementById('welcome-reg-password').value;
  await registerUser();
  document.getElementById('welcome-login-username').value = document.getElementById('welcome-reg-username').value.trim();
  document.getElementById('welcome-login-password').value = document.getElementById('welcome-reg-password').value;
  switchWelcomeAuth('login');
}

function switchAuthTab(tab) {
  const loginTab = document.getElementById('auth-tab-login');
  const registerTab = document.getElementById('auth-tab-register');
  const loginPanel = document.getElementById('auth-login-panel');
  const registerPanel = document.getElementById('auth-register-panel');
  const isLogin = tab === 'login';
  loginTab.classList.toggle('active', isLogin);
  registerTab.classList.toggle('active', !isLogin);
  loginPanel.classList.toggle('active', isLogin);
  registerPanel.classList.toggle('active', !isLogin);
  if (!isLogin) loadRegistrationCenters();
}

async function loginUser() {
  try {
    if (!isCloudMode()) {
      renderAuthModePanel();
      showToast('💡', '当前是本地单机模式；登录前请先配置 FastAPI 后端地址');
      return;
    }
    const username = document.getElementById('auth-login-username').value.trim();
    const password = document.getElementById('auth-login-password').value;
    if (!username || !password) throw new Error('请填写用户名和密码');
    const data = await backendRequest('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password })
    }, false);
    saveAuth({
      accessToken: data.access_token || '',
      refreshToken: data.refresh_token || '',
      user: data.user || { username }
    });
    closeModal('authModal');
    renderAuthState();
    await refreshCenters();
    await syncCloudCasesToLocal();
    updateStats();
    renderSidebar();
    renderDashboard(currentModuleFilter || 'all');
    openDashboardView();
    showToast('✅', `欢迎，${username}`);
  } catch (err) {
    showToast('❌', `登录失败：${err.message}`);
  }
}

async function registerUser() {
  try {
    if (!isCloudMode()) {
      renderAuthModePanel();
      showToast('💡', '注册账号需要后端数据库；请先配置 FastAPI 后端地址');
      return;
    }
    const clinicalTitle = document.getElementById('auth-reg-clinical-title')?.value || '';
    const centerValue = document.getElementById('auth-reg-center')?.value || '';
    const payload = {
      username: document.getElementById('auth-reg-username').value.trim(),
      email: document.getElementById('auth-reg-email').value.trim(),
      full_name: document.getElementById('auth-reg-fullname').value.trim(),
      role: 'member',
      clinical_title: clinicalTitle || null,
      department: document.getElementById('auth-reg-dept').value.trim(),
      default_center_id: centerValue ? Number(centerValue) : null,
      password: document.getElementById('auth-reg-password').value
    };
    if (!payload.username || !payload.email || !payload.password) throw new Error('用户名、邮箱、密码为必填');
    const result = await backendRequest('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(payload)
    }, false);
    document.getElementById('auth-login-username').value = payload.username;
    document.getElementById('auth-login-password').value = payload.password;
    switchAuthTab('login');
    if (result?.is_active === false) {
      showToast('✅', '注册成功，账号已提交系统管理员启用，启用后即可登录');
    } else {
      showToast('✅', '注册成功，请点击登录');
    }
  } catch (err) {
    const networkHint = err.message === 'Failed to fetch' ? '无法连接后端，请检查前端地址、后端端口 18000 和 CORS_ORIGINS' : err.message;
    showToast('❌', `注册失败：${networkHint}`);
  }
}

function renderAuthState() {
  const badge = document.getElementById('authBadge');
  const btn = document.getElementById('authBtn');
  if (isCloudMode() && isDemoAuth()) clearAuth();
  const user = getAuthUser();
  const logged = isLoggedIn();
  if (!badge || !btn) return;
  const adminBtn = document.getElementById('userAdminBtn');
  const adminConfigLink = document.getElementById('adminConfigLink');
  const settingsBtn = document.getElementById('settingsBtn');
  const adminOnlyEls = document.querySelectorAll('.admin-only');
  const showAdminControls = isSystemAdminUser(user);
  adminOnlyEls.forEach(el => {
    if (el.id === 'userAdminBtn' || el.id === 'adminConfigLink' || el.id === 'settingsBtn') return;
    el.style.display = showAdminControls ? '' : 'none';
  });
  if (logged) {
    document.body.classList.add('authenticated');
    document.body.classList.remove('guest');
    const roleLabel = typeof getUserRoleLabel === 'function' ? getUserRoleLabel(user?.role) : '已登录';
    const titleLabel = typeof getClinicalTitleLabel === 'function' ? getClinicalTitleLabel(user?.clinical_title) : '';
    const parts = [user?.full_name || user?.username || '已登录', titleLabel || roleLabel].filter(Boolean);
    badge.textContent = parts.join(' · ');
    btn.textContent = '🚪 退出';
    if (adminBtn) adminBtn.style.display = showAdminControls ? '' : 'none';
    if (adminConfigLink) adminConfigLink.style.display = showAdminControls ? '' : 'none';
    if (settingsBtn) settingsBtn.style.display = showAdminControls ? '' : 'none';
  } else {
    document.body.classList.add('guest');
    document.body.classList.remove('authenticated');
    badge.textContent = isCloudMode() ? '未登录 · 云端协作模式' : '本地单机模式';
    btn.textContent = '🔐 登录';
    if (adminBtn) adminBtn.style.display = 'none';
    if (adminConfigLink) adminConfigLink.style.display = 'none';
    if (settingsBtn) settingsBtn.style.display = 'none';
  }
  renderDeploymentStatus();
  if (typeof renderOnboardingBanner === 'function') renderOnboardingBanner();
  if (typeof renderPiiToggle === 'function') renderPiiToggle();
}

async function showUserAdminModal() {
  const user = getAuthUser();
  if (!isSystemAdminUser(user)) {
    showToast('⚠️', '只有系统管理员可以管理账号');
    return;
  }
  document.getElementById('userAdminModal').classList.add('open');
  await loadUserAdminList();
}

async function loadUserAdminList() {
  const body = document.getElementById('userAdminBody');
  if (!body) return;
  body.innerHTML = '<div class="scan-loading">正在读取账号列表...</div>';
  try {
    await refreshCenters();
    const users = await backendRequest('/api/users/?limit=100');
    const groups = groupUsersByCenter(users);
    body.innerHTML = `
      <div class="user-admin-list">
        ${groups.map(group => `
          <section class="user-admin-center-group">
            <div class="user-admin-center-head">
              <strong>${escapeHtml(group.name)}</strong>
              <span>${group.users.length} 人</span>
            </div>
            ${group.users.map(renderUserAdminRow).join('')}
          </section>
        `).join('')}
      </div>
    `;
  } catch (err) {
    if (err.status === 401) {
      body.innerHTML = '<div class="scan-error">登录状态已失效，请关闭本窗口后重新登录系统管理员账号。</div>';
      showToast('⚠️', '登录状态已失效，请重新登录');
      return;
    }
    body.innerHTML = `<div class="scan-error">读取账号失败：${escapeHtml(err.message)}</div>`;
  }
}

function groupUsersByCenter(users) {
  const centers = getCachedCenters();
  const centerMap = new Map(centers.map(c => [String(c.id), c]));
  const groups = new Map();
  users.forEach(user => {
    const key = user.default_center_id ? String(user.default_center_id) : 'none';
    const center = centerMap.get(key);
    const name = key === 'none' ? '未分配研究中心' : (center?.name || `中心 ${key}`);
    if (!groups.has(key)) groups.set(key, { key, name, users: [] });
    groups.get(key).users.push(user);
  });
  return Array.from(groups.values()).sort((a, b) => {
    if (a.key === 'none') return 1;
    if (b.key === 'none') return -1;
    return a.name.localeCompare(b.name, 'zh-CN');
  });
}

function renderUserCenterOptions(defaultCenterId) {
  const centers = getCachedCenters();
  return '<option value="">未分配中心</option>' + centers.map(c =>
    `<option value="${c.id}" ${String(defaultCenterId || '') === String(c.id) ? 'selected' : ''}>${escapeHtml(c.name)} (${escapeHtml(c.code || '')})</option>`
  ).join('');
}

function renderUserAdminRow(user) {
  // 账号权限（系统层） — 只有 admin / member（deleted 是软删占位，不可手选）
  const roleOptions = [
    ['member', '普通成员'],
    ['admin', '系统管理员']
  ].map(([value, label]) => `<option value="${value}" ${user.role === value ? 'selected' : ''}>${label}</option>`).join('');
  // 临床职称 — 审核流转中起作用
  const titleOptions = [
    ['', '— 未设置 —'],
    ['resident', '住院医'],
    ['fellow', '进修 / Fellow'],
    ['attending', '主治'],
    ['secondline', '二线 / 副主任'],
    ['chief', '主任 / 教授'],
    ['nurse', '管床护士'],
  ].map(([value, label]) => `<option value="${value}" ${(user.clinical_title || '') === value ? 'selected' : ''}>${label}</option>`).join('');
  const centerOptions = renderUserCenterOptions(user.default_center_id);
  return `
    <div class="user-admin-row">
      <div>
        <strong>${escapeHtml(user.full_name || user.username)}</strong>
        <span>${escapeHtml(user.username)} · ${escapeHtml(user.email || '')}</span>
        <small>${user.is_active ? '已启用' : '待审核/已停用'} · ${escapeHtml(user.department || '未填写科室')} · ${escapeHtml(getCenterName(user.default_center_id))}</small>
      </div>
      <div style="display:flex;flex-direction:column;gap:6px;">
        <select class="field-input" id="user-center-${user.id}" title="所属研究中心">${centerOptions}</select>
        <select class="field-input" id="user-role-${user.id}" title="系统权限">${roleOptions}</select>
        <select class="field-input" id="user-title-${user.id}" title="临床职称">${titleOptions}</select>
      </div>
      <button class="btn ${user.is_active ? 'btn-ghost' : 'btn-primary'}" data-action="update-user-admin" data-user-id="${user.id}" data-activate="${user.is_active ? '0' : '1'}">
        ${user.is_active ? '停用' : '启用'}
      </button>
      <button class="btn btn-ghost" data-action="save-user-role" data-user-id="${user.id}">保存权限</button>
      <button class="btn btn-danger" data-action="delete-user-account" data-user-id="${user.id}" data-username="${escapeHtml(user.username || '')}">删除账户</button>
    </div>
  `;
}

async function updateUserAdmin(userId, active) {
  try {
    await backendRequest(`/api/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ is_active: active })
    });
    showToast('✅', active ? '账号已启用' : '账号已停用');
    await loadUserAdminList();
  } catch (err) {
    showToast('❌', `操作失败：${err.message}`);
  }
}

async function saveUserRole(userId) {
  const role = document.getElementById(`user-role-${userId}`)?.value;
  const centerEl = document.getElementById(`user-center-${userId}`);
  const titleEl = document.getElementById(`user-title-${userId}`);
  const clinical_title = titleEl ? (titleEl.value || null) : undefined;
  const body = { role };
  if (clinical_title !== undefined) body.clinical_title = clinical_title;
  if (centerEl) body.default_center_id = centerEl.value ? Number(centerEl.value) : null;
  try {
    await backendRequest(`/api/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(body)
    });
    showToast('✅', '账号权限已保存');
    await loadUserAdminList();
  } catch (err) {
    showToast('❌', `保存失败：${err.message}`);
  }
}

async function deleteUserAccount(userId, username = '') {
  const ok = await showAppConfirm({ title: '删除账户', message: `确认删除账户 ${username || userId}？删除后该账号将无法登录。`, confirmText: '删除', danger: true });
  if (!ok) return;
  try {
    await backendRequest(`/api/users/${userId}`, { method: 'DELETE' });
    showToast('🗑️', '账户已删除');
    await loadUserAdminList();
  } catch (err) {
    showToast('❌', `删除失败：${err.message}`);
  }
}

async function clearAllData() {
  const ok = await showAppConfirm({ title: '清空本地数据', message: '确认清空所有本地病例数据？此操作不可恢复，建议先导出备份！', confirmText: '清空', danger: true });
  if (!ok) return;
  localStorage.removeItem(DB_KEY);
  localStorage.removeItem(AI_COUNT_KEY);
  currentCaseId = null;
  showPage('dashboard');
  showToast('🗑️', '已清空所有本地数据');
}



/* assets/import-export.js */
function exportAllData() {
  const cases = loadDB();
  const json = JSON.stringify(cases, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `足踝病例数据_${new Date().toLocaleDateString('zh-CN')}.json`;
  a.click(); URL.revokeObjectURL(url);
  showToast('📤', `已导出 ${cases.length} 个病例`);
}

// 匿名化策略：改“黑名单”为“分段+白名单”。
// 旧黑名单只剔除固定 8 个 key，极易遗漏（如 caseid 住院号、occupation 职业），
// 导致“匿名化包”实际仍泄露身份信息。现做法：
//   1) 基本信息段(basic)内，仅保留去标识化的人口学字段(age/gender/side/bmi)，其余全部剔除；
//   2) 任何 key（不限段）只要命中已知身份字段名或其 basic_ 前缀变体，一律剔除；
//   3) 其余临床字段（影像测量、评分、术式等）保留，供研究/教学使用。
const ANON_BASIC_KEEP = ['age', 'gender', 'side', 'bmi'];
const ANON_SENSITIVE_NAMES = [
  'name', 'phone', 'id_card', 'address', 'occupation',
  'caseid', 'case_id', 'contact', 'emergency_contact', 'guardian', 'workplace'
];

function isAnonymizedSensitiveKey(key, basicFieldIds) {
  const bare = key.replace(/^basic_/, '');
  // 命中已知身份字段（或其 basic_ 变体）→ 敏感
  if (ANON_SENSITIVE_NAMES.includes(bare)) return true;
  // 属于基本信息段、且不在去标识化白名单内 → 敏感（含主诉/病程等自由文本）
  if (basicFieldIds.has(bare) && !ANON_BASIC_KEEP.includes(bare)) return true;
  return false;
}

function anonymizeCaseData(data = {}, mod = null) {
  const basicSection = (mod?.sections || []).find(s => s.id === 'basic');
  const basicFieldIds = new Set((basicSection?.fields || []).map(f => f.id));
  return Object.entries(data).reduce((acc, [key, value]) => {
    if (isAnonymizedSensitiveKey(key, basicFieldIds)) return acc;
    acc[key] = value;
    return acc;
  }, {});
}

function makeResearchSubjectCode(c) {
  const raw = `${c.module || 'X'}:${c.createdAt || ''}:${c.id || ''}`;
  let hash = 2166136261;
  for (let i = 0; i < raw.length; i++) {
    hash ^= raw.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return `FAM-${c.module || 'X'}-${(hash >>> 0).toString(36).toUpperCase().padStart(7, '0').slice(0, 7)}`;
}

function getAnonymizedFieldDictionary(data = {}, mod = null) {
  const keptKeys = new Set(Object.keys(data || {}));
  const rows = [];
  [...(mod?.sections || []), ...getConditionalSections({ module: mod?.id, data })].forEach(section => {
    if (!section || section.type === 'media') return;
    (section.fields || []).forEach(field => {
      const key = `${section.id}_${field.id}`;
      if (!keptKeys.has(key)) return;
      const level = typeof getFieldRequirementLevel === 'function' ? getFieldRequirementLevel(field) : { key: 'optional', label: '可选' };
      rows.push({
        key,
        label: field.label,
        section: section.title,
        type: field.type || 'text',
        unit: field.unit || '',
        requirement: level.label,
      });
    });
  });
  return rows;
}

function getMediaSlotLookup(mod = null) {
  const mediaSection = (mod?.sections || []).find(s => s.type === 'media');
  const lookup = {};
  (mediaSection?.slots || []).forEach((slot, i) => {
    const meta = getSlotMeta(slot, i);
    lookup[meta.key] = meta;
    if (meta.legacyKey) lookup[meta.legacyKey] = meta;
  });
  return lookup;
}

function summarizeMedia(media = {}, mod = null) {
  // 不导出原始 URL/文件名/标题：文件名常含患者姓名、拼音或住院号，属隐私泄露面。
  // 仅保留槽位、阶段、类型、大小、上传时间和匿名 mediaRef，便于资料完整性复核。
  const slotLookup = getMediaSlotLookup(mod);
  const rows = [];
  Object.entries(media || {}).forEach(([slot, value]) => {
    const files = Array.isArray(value) ? value : (value ? [value] : []);
    const meta = slotLookup[slot] || { key: slot, label: slot, phase: '', group: '' };
    files.forEach((file, index) => {
      rows.push({
        mediaRef: `M${String(rows.length + 1).padStart(3, '0')}`,
        slot: meta.key || slot,
        label: meta.label || slot,
        phase: meta.phase || '',
        group: meta.group || '',
        indexInSlot: index + 1,
        mime: file?.mime || '',
        fileSize: file?.fileSize || null,
        cloud: !!file?.cloud,
        uploadedAt: file?.uploadedAt || '',
      });
    });
    if (!files.length) {
      rows.push({
        mediaRef: '',
        slot: meta.key || slot,
        label: meta.label || slot,
        phase: meta.phase || '',
        group: meta.group || '',
        indexInSlot: 0,
        mime: '',
        fileSize: null,
        cloud: false,
        uploadedAt: '',
      });
    }
  });
  return rows;
}

function buildResearchReadiness(c, mod) {
  const required = getRequiredFieldStatus(c, mod);
  const mediaProgress = calcMediaProgress(c, mod);
  const qualityChecks = getQualityChecks(c, mod, required, mediaProgress);
  return {
    overallProgress: calcOverallProgress(c),
    fieldProgress: calcProgress(c),
    mediaProgress,
    missingCoreFields: required.missing.map(item => ({
      key: item.key,
      label: item.label,
      section: item.section,
    })),
    qualityChecks: qualityChecks.map(item => ({
      level: item.level,
      text: item.text,
    })),
  };
}

function exportAnonymizedCase(caseId) {
  const cases = loadDB();
  const c = cases.find(item => item.id === caseId);
  if (!c) return;
  const mod = MODULES[c.module];
  const anonymizedData = anonymizeCaseData(c.data || {}, mod);
  const readiness = buildResearchReadiness(c, mod);
  const payload = {
    packageType: '足踝病例匿名化导出包',
    schemaVersion: 'research-anonymized-v2',
    exportedAt: new Date().toISOString(),
    exporter: {
      app: 'foot-ankle-v2',
      piiIncluded: false,
      note: '不包含患者姓名、住院号、联系方式、原始影像 URL 或原始文件名。',
    },
    case: {
      subjectCode: makeResearchSubjectCode(c),
      module: c.module,
      moduleName: mod?.name || c.module,
      createdAt: c.createdAt || '',
      center: {
        id: c.center_id || c.centerId || null,
        name: (typeof getCenterName === 'function') ? getCenterName(c.center_id || c.centerId || null) : '',
      },
      favorite: !!c.favorite,
      tags: getCaseTags(c),
      aiGenCount: c.aiGenCount || 0,
      stage: getCaseStageSummary(c),
      readiness,
      data: anonymizedData,
      dataDictionary: getAnonymizedFieldDictionary(anonymizedData, mod),
      mediaInventory: summarizeMedia(c.media || {}, mod),
    }
  };
  const json = JSON.stringify(payload, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const date = new Date().toLocaleDateString('zh-CN').replace(/\//g, '-');
  a.href = url;
  a.download = `匿名化病例包_${c.module}_${date}.json`;
  a.click();
  URL.revokeObjectURL(url);
  showToast('📦', '已导出匿名化病例包');
}



/* assets/import-workflow.js */
// ===================== IMPORT =====================
let importBuffer = null;

function showImportModal() {
  importBuffer = null;
  document.getElementById('importPreview').style.display = 'none';
  document.getElementById('importConfirmBtn').style.display = 'none';
  document.getElementById('importFileInput').value = '';
  document.getElementById('importModal').classList.add('open');
}

function handleImportDrop(e) {
  e.preventDefault();
  document.getElementById('importDropZone').classList.remove('dragover');
  const file = e.dataTransfer.files[0];
  if (file) processImportFile(file);
}

function handleImportFile(input) {
  const file = input.files[0];
  if (file) processImportFile(file);
}

function processImportFile(file) {
  if (!/\.(json|csv)$/i.test(file.name)) {
    showToast('❌', '请选择 .json 或 .csv 格式文件');
    return;
  }
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      importBuffer = parseImportPayload(e.target.result, file.name);
      const preview = document.getElementById('importPreview');
      preview.style.display = 'block';
      preview.innerHTML = renderImportPreview(importBuffer);
      document.getElementById('importConfirmBtn').style.display = 'block';
    } catch(err) {
      showToast('❌', '文件格式错误：' + err.message);
    }
  };
  reader.readAsText(file);
}

function confirmImport() {
  if (!importBuffer) return;
  const existing = loadDB();
  const existingIds = new Set(existing.map(c => c.id));
  let added = 0, replaced = 0;
  importBuffer.cases.forEach(c => {
    if (existingIds.has(c.id)) {
      const idx = existing.findIndex(x => x.id === c.id);
      existing[idx] = c;
      replaced++;
    } else {
      existing.unshift(c);
      added++;
    }
  });
  saveDB(existing);
  if (importBuffer.tasks.length) {
    const tasks = loadAdmissionTasks();
    importBuffer.tasks.forEach(task => tasks.unshift(task));
    saveAdmissionTasks(tasks);
  }
  closeModal('importModal');
  renderSidebar();
  updateStats();
  renderDashboard();
  showToast('📥', `导入完成：新增 ${added} 个，更新 ${replaced} 个，待录入 ${importBuffer.tasks.length} 个`);
}

function parseImportPayload(text, filename) {
  const isCsv = /\.csv$/i.test(filename);
  const sourceRows = isCsv ? parseCsvRows(text) : normalizeJsonRows(JSON.parse(text));
  if (!sourceRows.length) throw new Error('未找到可导入的数据记录');

  const looksNative = !isCsv && Array.isArray(JSON.parse(text)) && sourceRows.every(row => row.id && row.module && row.data);
  if (looksNative) {
    return {
      format: '本系统病例备份',
      cases: sourceRows.map(row => normalizeNativeCase(row)),
      tasks: [],
      skipped: 0
    };
  }

  const cases = [];
  const tasks = [];
  let skipped = 0;
  sourceRows.forEach((row, index) => {
    const normalized = normalizeMedicalRecord(row, index);
    if (!normalized.name && !normalized.diagnosis && !normalized.caseid) {
      skipped++;
      return;
    }
    if (normalized.module) cases.push(recordToCase(normalized, index));
    else tasks.push(recordToAdmissionTask(normalized, index));
  });
  return {
    format: isCsv ? '病历夹 CSV / 表格导出' : '病历夹 JSON / 通用病例导出',
    cases,
    tasks,
    skipped
  };
}

function normalizeJsonRows(payload) {
  if (Array.isArray(payload)) return payload;
  const candidates = [
    payload?.cases,
    payload?.records,
    payload?.patients,
    payload?.data,
    payload?.list,
    payload?.items,
    payload?.result
  ];
  const rows = candidates.find(Array.isArray);
  if (rows) return rows;
  if (payload?.case) return [payload.case];
  if (payload && typeof payload === 'object') return [payload];
  return [];
}

function parseCsvRows(text) {
  const rows = parseCsv(text);
  if (rows.length < 2) return [];
  const headers = rows[0].map(cell => cell.trim());
  return rows.slice(1)
    .filter(row => row.some(cell => String(cell).trim()))
    .map(row => headers.reduce((acc, header, index) => {
      acc[header || `字段${index + 1}`] = row[index] || '';
      return acc;
    }, {}));
}

function parseCsv(text) {
  const rows = [];
  let row = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const next = text[i + 1];
    if (ch === '"' && inQuotes && next === '"') {
      cell += '"';
      i++;
    } else if (ch === '"') {
      inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      row.push(cell);
      cell = '';
    } else if ((ch === '\n' || ch === '\r') && !inQuotes) {
      if (ch === '\r' && next === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += ch;
    }
  }
  if (cell || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
}

function normalizeNativeCase(row) {
  return {
    ...row,
    id: row.id || `case_import_${Date.now()}_${Math.random().toString(16).slice(2)}`,
    media: row.media || {},
    data: row.data || {},
    tags: Array.isArray(row.tags) ? row.tags : ['导入病例']
  };
}

function normalizeMedicalRecord(row, index) {
  const flat = flattenRecord(row);
  const pick = (...keys) => pickValue(flat, keys);
  const diagnosis = pick('诊断', '初步诊断', '入院诊断', '出院诊断', 'diagnosis', 'diag', '病种', '疾病');
  const module = normalizeModule(pick('模块', '病种模块', 'module', '分类', '疾病分类')) || inferModuleFromDiagnosis(diagnosis);
  return {
    sourceIndex: index + 1,
    name: pick('姓名', '患者姓名', '病人姓名', 'patient_name', 'patientName', 'name'),
    gender: pick('性别', 'gender', 'sex'),
    age: pick('年龄', 'age'),
    caseid: pick('住院号', '门诊号', '病历号', '病案号', 'caseid', 'patientNo', '住院ID'),
    date: normalizeDate(pick('就诊日期', '入院日期', '创建时间', '记录时间', 'date', 'createdAt', 'admission_date')),
    side: pick('患侧', '侧别', 'side'),
    diagnosis,
    chiefComplaint: pick('主诉', 'chief_complaint', 'chiefComplaint'),
    history: pick('现病史', '病史', 'history', 'presentIllness'),
    pastHistory: pick('既往史', 'past_history', 'pastHistory'),
    physical: pick('查体', '体格检查', '专科查体', 'physical', 'exam'),
    imaging: pick('影像', '影像学', '辅助检查', 'imaging', 'xray'),
    plan: pick('治疗方案', '手术方案', '计划', 'plan', 'treatment'),
    doctor: pick('管床医生', '住院医', '录入医生', 'doctor', 'assignee'),
    supervisor: pick('上级医师', '主治', '主任', 'supervisor'),
    department: pick('科室', 'department'),
    module,
    raw: flat
  };
}

function flattenRecord(input, prefix = '', out = {}) {
  Object.entries(input || {}).forEach(([key, value]) => {
    const label = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      flattenRecord(value, label, out);
    } else {
      out[key] = value;
      out[label] = value;
    }
  });
  return out;
}

function pickValue(source, keys) {
  const normalized = Object.entries(source).reduce((acc, [key, value]) => {
    acc[normalizeKey(key)] = value;
    return acc;
  }, {});
  for (const key of keys) {
    const value = normalized[normalizeKey(key)];
    if (value !== undefined && value !== null && String(value).trim() !== '') return value;
  }
  return '';
}

function normalizeKey(key) {
  return String(key).toLowerCase().replace(/[\s_：:()（）-]/g, '');
}

function normalizeModule(value) {
  const text = String(value || '').trim();
  if (/^[A-F]$/i.test(text)) return text.toUpperCase();
  return inferModuleFromDiagnosis(text);
}

function normalizeDate(value) {
  if (!value) return new Date().toISOString().split('T')[0];
  const text = String(value).trim();
  const match = text.match(/(\d{4})[-/.年](\d{1,2})[-/.月](\d{1,2})/);
  if (match) return `${match[1]}-${match[2].padStart(2, '0')}-${match[3].padStart(2, '0')}`;
  return text.slice(0, 10);
}

function recordToCase(record, index) {
  const idSeed = record.caseid || record.name || `${Date.now()}_${index}`;
  return {
    id: `binglijia_${String(idSeed).replace(/[^\w\u4e00-\u9fa5-]/g, '')}_${index}`,
    module: record.module,
    createdAt: record.date,
    data: {
      basic_name: record.name,
      name: record.name,
      basic_gender: record.gender,
      gender: record.gender,
      basic_age: record.age,
      age: record.age,
      basic_caseid: record.caseid,
      caseid: record.caseid,
      basic_date: record.date,
      date: record.date,
      basic_side: record.side,
      side: record.side,
      basic_chief_complaint: record.chiefComplaint,
      history_chief_complaint: record.chiefComplaint,
      history_previous_treatment: record.history,
      history_past_history: record.pastHistory,
      physical_physical_notes: record.physical,
      imaging_imaging_notes: record.imaging,
      plan_plan_notes: record.plan,
      workflow_case_stage: '术前',
      workflow_review_status: '待审核',
      workflow_entry_doctor: record.doctor,
      imported_diagnosis: record.diagnosis,
      imported_department: record.department,
      imported_supervisor: record.supervisor,
      imported_source: '病历夹既往数据',
      imported_raw_summary: JSON.stringify(record.raw).slice(0, 6000)
    },
    media: {},
    tags: ['病历夹导入', record.module ? `${record.module}模块` : '待分型'],
    aiGenCount: 0
  };
}

function recordToAdmissionTask(record, index) {
  return {
    id: `binglijia_task_${Date.now()}_${index}`,
    status: '待录入',
    createdAt: new Date().toISOString(),
    source: '病历夹既往数据导入',
    patient_name: record.name,
    gender: record.gender,
    age: record.age,
    caseid: record.caseid,
    diagnosis: record.diagnosis,
    department: record.department || '足踝外科',
    admission_date: record.date,
    supervisor: record.supervisor,
    assignee: record.doctor,
    module: '',
    priority: '普通',
    notes: '由病历夹既往数据导入，病种模块待管床确认。'
  };
}

function renderImportPreview(buffer) {
  const modules = {};
  buffer.cases.forEach(c => { modules[c.module] = (modules[c.module] || 0) + 1; });
  const modSummary = Object.entries(modules).map(([k, v]) => `${k}模块×${v}`).join('、') || '无直接建档病例';
  const taskText = buffer.tasks.length ? `另有 ${buffer.tasks.length} 条进入“住院医/管床待录入目录”` : '没有待录入任务';
  return `<div style="color:var(--green);font-weight:600;margin-bottom:6px;">✅ 文件解析成功：${buffer.format}</div>
    <div>可直接导入 <strong>${buffer.cases.length}</strong> 个病例 — ${modSummary}</div>
    <div style="margin-top:4px;">${taskText}</div>
    ${buffer.skipped ? `<div style="margin-top:4px;color:var(--amber);">跳过 ${buffer.skipped} 条空记录或无效记录</div>` : ''}
    <div style="margin-top:8px;color:var(--text3);font-size:12px;">支持病历夹常见 JSON/CSV 字段：姓名、住院号、诊断、主诉、查体、影像、治疗方案等；相同ID病例会被覆盖。</div>`;
}



/* assets/print.js */
// ===================== PRINT =====================
function printAiOutput() {
  const content = currentAiOutput;
  if (!content) return;
  const w = window.open('', '_blank');
  w.document.write(`<!DOCTYPE html><html><head>
    <meta charset="UTF-8">
    <title>AI生成内容</title>
    <style>
      body { font-family: 'Noto Sans SC', 'Microsoft YaHei', sans-serif; font-size: 14px; line-height: 1.9; padding: 40px; color: #1c1917; max-width: 800px; margin: 0 auto; }
      h1 { font-size: 20px; margin-bottom: 24px; color: #1c1917; border-bottom: 2px solid #e8dcc8; padding-bottom: 12px; }
      pre { white-space: pre-wrap; word-wrap: break-word; }
      @media print { body { padding: 20px; } }
    </style>
  </head><body>
    <h1>足踝外科术前资料 · AI生成内容</h1>
    <pre>${escapeHtml(content)}</pre>
    <div style="margin-top:40px;font-size:11px;color:#78716c;">生成时间：${new Date().toLocaleString('zh-CN')} · 足踝外科术前资料收集系统</div>
  </body></html>`);
  w.document.close();
  setTimeout(() => w.print(), 400);
}



/* assets/case-brief.js */
// ===================== CASE BRIEF EXPORT =====================
// 病例简报：完全离线、确定性的 HTML 简报，从结构化 c.data 按
// 模块 sections/fields（字段键约定 `${section.id}_${field.id}`）生成。
// 复用 print.js 的样式取向与 escapeHtml/_sanitizeReportHtml。
// 所有用户/患者数据一律 escapeHtml 转义后注入，杜绝 XSS。

/**
 * 把一个病例整理成确定性的简报数据结构。
 * 不调用任何网络/AI，纯本地。
 */
function buildCaseBriefData(c, mod) {
  const name = (typeof displayPatientName === 'function')
    ? displayPatientName(c)
    : (c.data?.basic_name || c.data?.name || '未命名患者');
  const required = (typeof getRequiredFieldStatus === 'function')
    ? getRequiredFieldStatus(c, mod) : { total: 0, filled: 0, missing: [] };
  const pct = (typeof calcOverallProgress === 'function') ? calcOverallProgress(c)
    : ((typeof calcProgress === 'function') ? calcProgress(c) : 0);
  const mediaPct = (typeof calcMediaProgress === 'function')
    ? calcMediaProgress(c, mod) : { filled: 0, total: 0, pct: 0 };
  const stage = (typeof getCaseStageSummary === 'function')
    ? getCaseStageSummary(c) : { stage: '', status: '', review: '', reviewer: '', deadline: '' };

  // 按 section 分组，仅收录“当前可见且已填”的字段（与进度条口径一致）。
  const sections = [];
  const allSections = [
    ...((mod?.sections) || []),
    ...((typeof getConditionalSections === 'function') ? getConditionalSections(c) : []),
  ];
  allSections.forEach(sec => {
    if (sec.type === 'media') return;
    const fields = (typeof getVisibleFields === 'function')
      ? getVisibleFields(sec, c) : (sec.fields || []);
    const rows = [];
    fields.forEach(f => {
      const key = `${sec.id}_${f.id}`;
      const val = c.data?.[key];
      const filled = Array.isArray(val) ? val.length > 0
        : (val !== undefined && val !== null && val !== '');
      if (!filled) return;
      let value = Array.isArray(val) ? val.join('、') : String(val);
      // 导出默认脱敏：未授权显示时，对姓名/电话/身份证等 PII 字段做掩码。
      if (typeof maskPiiFieldValue === 'function' && typeof isPiiRevealed === 'function' && !isPiiRevealed()) {
        value = String(maskPiiFieldValue(key, value));
      }
      rows.push({
        label: f.label,
        value,
        unit: f.unit || '',
      });
    });
    if (rows.length) sections.push({ title: sec.title, icon: sec.icon || '', rows });
  });

  // 影像槽位计数（不导出原始文件名，避免 PII）。
  const mediaSection = (mod?.sections || []).find(s => s.type === 'media');
  const mediaGroups = [];
  if (mediaSection && typeof getSlotMeta === 'function') {
    (mediaSection.slots || []).forEach((slot, i) => {
      const meta = getSlotMeta(slot, i);
      const imgs = (typeof getSlotImages === 'function')
        ? getSlotImages(c, meta.key, meta.legacyKey) : [];
      if (imgs.length) mediaGroups.push({ label: meta.label, count: imgs.length, phase: meta.phase || '' });
    });
  }

  return {
    name,
    moduleName: mod?.name || c.module || '',
    moduleIcon: mod?.icon || '',
    caseId: c.id || '',
    createdAt: c.createdAt || '',
    pct,
    required,
    mediaPct,
    stage,
    sections,
    mediaGroups,
    aiGenCount: c.aiGenCount || 0,
    generatedAt: new Date().toLocaleString('zh-CN'),
  };
}

function _countCaseImageMedia(c) {
  let count = 0;
  Object.values(c?.media || {}).forEach(slot => {
    const items = Array.isArray(slot) ? slot : [slot];
    items.forEach(img => {
      const mime = img?.mime || '';
      const name = img?.filename || img?.name || '';
      if (mime.startsWith('video/') || /\.(mp4|mov|avi|mkv)$/i.test(name)) return;
      if (img?.data || img?.backendMediaId) count += 1;
    });
  });
  return count;
}

function _pptProgressEsc(s) {
  if (typeof escapeHtml === 'function') return escapeHtml(s);
  return String(s ?? '').replace(/[&<>"']/g, ch => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[ch]));
}

function _openPptProgressModal({ title, expanded, mediaCount }) {
  const existing = document.getElementById('pptProgressOverlay');
  if (existing) existing.remove();
  const stages = expanded ? [
    '同步病例资料',
    '高级模型生成汇报结构',
    '压缩并展开全部影像',
    '渲染 PowerPoint 页面',
    '准备下载文件',
  ] : [
    '同步病例资料',
    '高级模型生成汇报结构',
    '整理摘要影像',
    '渲染 PowerPoint 页面',
    '准备下载文件',
  ];
  const overlay = document.createElement('div');
  overlay.id = 'pptProgressOverlay';
  overlay.className = 'modal-overlay open ppt-progress-overlay';
  overlay.innerHTML = `
    <div class="modal ppt-progress-modal" role="dialog" aria-modal="true" aria-label="${_pptProgressEsc(title)}">
      <div class="modal-header">
        <div>
          <div class="modal-title">${_pptProgressEsc(title)}</div>
          <div class="ppt-progress-sub">${expanded ? '全图展开模式' : '摘要模式'} · 影像 ${mediaCount || 0} 张 · 已脱敏后生成</div>
        </div>
      </div>
      <div class="modal-body">
        <div class="ppt-progress-head">
          <span class="ppt-progress-spinner">↻</span>
          <div>
            <strong id="pptProgressStage">${_pptProgressEsc(stages[0])}</strong>
            <p id="pptProgressHint">正在准备导出请求，请保持页面打开。</p>
          </div>
          <b id="pptProgressElapsed">0s</b>
        </div>
        <div class="ppt-progress-track"><i id="pptProgressFill" style="width: 8%"></i></div>
        <div class="ppt-progress-steps">
          ${stages.map((s, i) => `<span class="${i === 0 ? 'active' : ''}" data-ppt-step="${i}">${_pptProgressEsc(s)}</span>`).join('')}
        </div>
        <div class="ppt-progress-note">提示：复杂病例会经历模型聚合、影像压缩和 PPT 排版。当前为阶段显化，服务端完成后会自动下载。</div>
      </div>
    </div>`;
  document.body.appendChild(overlay);

  const started = Date.now();
  let current = 0;
  let closed = false;
  const fill = overlay.querySelector('#pptProgressFill');
  const stageEl = overlay.querySelector('#pptProgressStage');
  const hintEl = overlay.querySelector('#pptProgressHint');
  const elapsedEl = overlay.querySelector('#pptProgressElapsed');
  const stepEls = [...overlay.querySelectorAll('[data-ppt-step]')];
  const hints = [
    '正在确认病例与权限，并整理本地简报数据。',
    '正在调用模型生成结构化 PPT 汇报内容。',
    expanded ? '正在压缩影像并按顺序生成全图展开页。' : '正在压缩摘要影像并生成影像页。',
    '正在套用智慧足踝 PPT 模板并写入 .pptx。',
    '正在接收文件并准备浏览器下载。',
  ];
  const setStage = (idx, hint) => {
    current = Math.max(0, Math.min(idx, stages.length - 1));
    stageEl.textContent = stages[current];
    hintEl.textContent = hint || hints[current];
    stepEls.forEach((el, i) => {
      el.classList.toggle('done', i < current);
      el.classList.toggle('active', i === current);
    });
    const pct = Math.min(96, 8 + current * 21);
    fill.style.width = `${pct}%`;
  };
  const timer = setInterval(() => {
    if (closed) return;
    const elapsed = Math.round((Date.now() - started) / 1000);
    elapsedEl.textContent = `${elapsed}s`;
    if (elapsed > 8 && current < 1) setStage(1);
    if (elapsed > 25 && current < 2) setStage(2);
    if (elapsed > 55 && current < 3) setStage(3);
  }, 1000);
  return {
    setStage,
    done(message = 'PPT 已生成，正在下载。') {
      if (closed) return;
      closed = true;
      clearInterval(timer);
      setStage(4, message);
      fill.style.width = '100%';
      overlay.classList.add('is-done');
      setTimeout(() => overlay.remove(), 650);
    },
    fail(message = '生成失败，请稍后重试。') {
      if (closed) return;
      closed = true;
      clearInterval(timer);
      hintEl.textContent = message;
      overlay.classList.add('is-error');
      fill.style.width = '100%';
      setTimeout(() => overlay.remove(), 4500);
    },
  };
}

/** 渲染简报为自包含 HTML 字符串（所有动态内容已转义）。 */
function buildCaseBriefHtml(c, mod, opts = {}) {
  const b = buildCaseBriefData(c, mod);
  const esc = (typeof escapeHtml === 'function') ? escapeHtml : (s) => String(s);

  const sectionsHtml = b.sections.length
    ? b.sections.map(sec => `
        <section class="brief-section">
          <h2>${esc(sec.icon)} ${esc(sec.title)}</h2>
          <table class="brief-table">
            ${sec.rows.map(r => `
              <tr>
                <th>${esc(r.label)}</th>
                <td>${esc(r.value)}${r.unit ? ` <span class="unit">${esc(r.unit)}</span>` : ''}</td>
              </tr>`).join('')}
          </table>
        </section>`).join('')
    : '<section class="brief-section"><p class="brief-empty">尚无已填写的结构化字段。</p></section>';

  const mediaHtml = b.mediaGroups.length
    ? `<section class="brief-section">
        <h2>🖼️ 影像证据链</h2>
        <table class="brief-table">
          ${b.mediaGroups.map(g => `
            <tr><th>${esc(g.label)}${g.phase ? `（${esc(g.phase)}）` : ''}</th><td>${esc(g.count)} 张</td></tr>`).join('')}
        </table>
        <p class="brief-note">共 ${esc(b.mediaPct.filled)}/${esc(b.mediaPct.total)} 个标准槽位，上传进度 ${esc(b.mediaPct.pct)}%。</p>
      </section>`
    : '';

  const aiNarrative = opts.aiNarrative
    ? `<section class="brief-section brief-ai">
        <h2>🤖 AI 智能简报</h2>
        <div class="brief-ai-body">${esc(opts.aiNarrative).replace(/\n/g, '<br>')}</div>
        <p class="brief-note">以上内容由 AI 生成，仅供参考，需经临床医师核对。</p>
      </section>`
    : '';

  const raw = `<!DOCTYPE html><html lang="zh-CN"><head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>病例简报 · ${esc(b.name)}</title>
    <style>
      body { font-family: 'Noto Sans SC', 'Microsoft YaHei', sans-serif; font-size: 13px; line-height: 1.7; color: #1c1917; max-width: 820px; margin: 0 auto; padding: 36px 40px; background: #fff; }
      .brief-head { border-bottom: 2px solid #e8dcc8; padding-bottom: 16px; margin-bottom: 20px; }
      .brief-head h1 { font-size: 22px; margin: 0 0 6px; }
      .brief-head .meta { font-size: 12px; color: #78716c; }
      .brief-stats { display: flex; flex-wrap: wrap; gap: 10px; margin: 16px 0 8px; }
      .brief-stat { border: 1px solid #e7e5e4; border-radius: 8px; padding: 8px 14px; min-width: 110px; }
      .brief-stat span { display: block; font-size: 11px; color: #78716c; }
      .brief-stat strong { font-size: 15px; }
      .brief-section { margin: 22px 0; page-break-inside: avoid; }
      .brief-section h2 { font-size: 15px; border-left: 3px solid #c2a878; padding-left: 8px; margin: 0 0 10px; }
      .brief-table { width: 100%; border-collapse: collapse; }
      .brief-table th { text-align: left; width: 38%; vertical-align: top; padding: 5px 10px 5px 0; color: #57534e; font-weight: 500; }
      .brief-table td { padding: 5px 0; vertical-align: top; }
      .brief-table .unit { color: #a8a29e; font-size: 11px; }
      .brief-empty, .brief-note { color: #78716c; font-size: 12px; }
      .brief-ai-body { white-space: normal; }
      .brief-foot { margin-top: 36px; font-size: 11px; color: #a8a29e; border-top: 1px solid #eee; padding-top: 12px; }
      @media print { body { padding: 16px 20px; } }
    </style>
  </head><body>
    <div class="brief-head">
      <h1>${esc(b.moduleIcon)} 病例简报 · ${esc(b.name)}</h1>
      <div class="meta">病种：${esc(b.moduleName)} · 病例ID：${esc(b.caseId)} · 建档：${esc(b.createdAt)}</div>
    </div>
    <div class="brief-stats">
      <div class="brief-stat"><span>资料完整度</span><strong>${esc(b.pct)}%</strong></div>
      <div class="brief-stat"><span>核心必填</span><strong>${esc(b.required.filled)}/${esc(b.required.total)}</strong></div>
      <div class="brief-stat"><span>影像上传</span><strong>${b.mediaPct.filled ? '已上传' : '未上传'}</strong></div>
      <div class="brief-stat"><span>病例阶段</span><strong>${esc(b.stage.stage)}</strong></div>
      <div class="brief-stat"><span>审核状态</span><strong>${esc(b.stage.review)}</strong></div>
      <div class="brief-stat"><span>AI 生成</span><strong>${esc(b.aiGenCount)} 次</strong></div>
    </div>
    ${aiNarrative}
    ${sectionsHtml}
    ${mediaHtml}
    <div class="brief-foot">生成时间：${esc(b.generatedAt)} · 足踝外科病例信息系统 · 本简报仅供临床/教学使用，请遵循患者隐私保护规范。</div>
  </body></html>`;

  // 复用 AI 报告的 HTML 净化器（移除 script/on* 等），再次兜底防注入。
  return (typeof _sanitizeReportHtml === 'function') ? _sanitizeReportHtml(raw) : raw;
}

/** 下载病例简报为独立 .html 文件。 */
function exportCaseBriefHtml(caseId, opts = {}) {
  const c = loadDB().find(x => x.id === caseId);
  if (!c) { showToast('⚠️', '未找到病例'); return; }
  const mod = MODULES[c.module];
  const html = buildCaseBriefHtml(c, mod, opts);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const date = new Date().toLocaleDateString('zh-CN').replace(/\//g, '-');
  a.href = url;
  a.download = `病例简报_${c.module || ''}_${date}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
  showToast('📄', '病例简报 HTML 已下载');
}

/**
 * 导出真 .pptx 病例简报（POST /api/cases/{id}/ppt，契约见 docs/api-contract.md）。
 * brief 由前端确定性生成（已按导出口径脱敏）；影像不经前端传输，
 * 由服务端按病例取字节内嵌——离线可看、可在 WPS/PowerPoint 编辑，全程零 AI。
 */
async function exportCaseBriefPptx(caseId) {
  const c = loadDB().find(x => x.id === caseId);
  if (!c) { showToast('⚠️', '未找到病例'); return; }
  const mod = MODULES[c.module];
  showToast('📊', '正在生成简报 PPT…');
  try {
    const backendCaseId = await ensureBackendCase(c);
    const brief = buildCaseBriefData(c, mod);
    const base = getBackendBase();
    const doFetch = () => fetch(`${base}/api/cases/${backendCaseId}/ppt`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}),
      },
      body: JSON.stringify({ brief }),
    });
    let resp = await doFetch();
    if (resp.status === 401 && typeof _tryRefreshToken === 'function' && await _tryRefreshToken()) {
      resp = await doFetch();
    }
    if (!resp.ok) {
      let msg = `简报 PPT 导出失败（${resp.status}）`;
      try {
        const d = await resp.json();
        if (d && typeof d.detail === 'string') msg = d.detail;
      } catch (_) { /* 非 JSON 错误体，保留默认文案 */ }
      showToast('⚠️', msg);
      return;
    }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const date = new Date().toLocaleDateString('zh-CN').replace(/\//g, '-');
    a.href = url;
    a.download = `病例简报_${c.module || ''}_${date}.pptx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    showToast('📊', '简报 PPT 已下载，可用 WPS / PowerPoint 打开编辑');
  } catch (e) {
    showToast('⚠️', (e && (e.friendly || e.message)) || '简报 PPT 导出失败，请稍后重试');
  }
}

/** AI 生成汇报型 PPT：模型只生成结构化 narrative，后端仍确定性渲染真 .pptx。 */
async function exportCaseNarrativePptx(caseId, opts = {}) {
  if (typeof _needsCrossBorderConsent === 'function'
      && _needsCrossBorderConsent(caseId, 'ppt_narrative')
      && typeof _crossBorderAckedThisSession === 'function'
      && !_crossBorderAckedThisSession()) {
    if (typeof _showCrossBorderConsent === 'function') {
      _showCrossBorderConsent(() => { exportCaseNarrativePptx(caseId, opts); });
      return;
    }
  }

  const c = loadDB().find(x => x.id === caseId);
  if (!c) { showToast('⚠️', '未找到病例'); return; }
  const mod = MODULES[c.module];
  const expanded = opts.imageMode === 'expanded';
  const progress = _openPptProgressModal({
    title: expanded ? 'AI 汇报型 PPT（全图展开）' : 'AI 汇报型 PPT',
    expanded,
    mediaCount: _countCaseImageMedia(c),
  });
  showToast('🤖', expanded ? '正在生成 AI 汇报型 PPT（全图展开）…' : '正在生成 AI 汇报型 PPT…');
  try {
    progress.setStage(0);
    const backendCaseId = await ensureBackendCase(c);
    const brief = buildCaseBriefData(c, mod);
    const base = getBackendBase();
    progress.setStage(1);
    const doFetch = () => fetch(`${base}/api/cases/${backendCaseId}/ppt`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}),
      },
      body: JSON.stringify({ brief, mode: 'narrative', image_mode: expanded ? 'expanded' : 'summary' }),
    });
    let resp = await doFetch();
    if (resp.status === 401 && typeof _tryRefreshToken === 'function' && await _tryRefreshToken()) {
      progress.setStage(1, '登录态已刷新，正在重新提交生成请求。');
      resp = await doFetch();
    }
    if (!resp.ok) {
      let msg = `${expanded ? 'AI 汇报型 PPT（全图展开）' : 'AI 汇报型 PPT'}生成失败（${resp.status}）`;
      try {
        const d = await resp.json();
        if (d && typeof d.detail === 'string') msg = d.detail;
      } catch (_) { /* 非 JSON 错误体，保留默认文案 */ }
      progress.fail(msg);
      showToast('⚠️', msg);
      return;
    }
    progress.setStage(4);
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const date = new Date().toLocaleDateString('zh-CN').replace(/\//g, '-');
    a.href = url;
    a.download = `${expanded ? 'AI汇报型PPT_全图展开' : 'AI汇报型PPT'}_${c.module || ''}_${date}.pptx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    progress.done();
    showToast('🤖', `${expanded ? 'AI 汇报型 PPT（全图展开）' : 'AI 汇报型 PPT'}已下载，请由医生核定后使用`);
  } catch (e) {
    const msg = (e && (e.friendly || e.message)) || `${expanded ? 'AI 汇报型 PPT（全图展开）' : 'AI 汇报型 PPT'}生成失败，请稍后重试`;
    progress.fail(msg);
    showToast('⚠️', msg);
  }
}

/** 打开打印样式窗口并触发 window.print()（浏览器“另存为 PDF”）。 */
function exportCaseBriefPdf(caseId, opts = {}) {
  const c = loadDB().find(x => x.id === caseId);
  if (!c) { showToast('⚠️', '未找到病例'); return; }
  const mod = MODULES[c.module];
  const html = buildCaseBriefHtml(c, mod, opts);
  const w = window.open('', '_blank');
  if (!w) { showToast('⚠️', '浏览器拦截了新窗口，请允许弹出后重试'); return; }
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  w.location.href = url;
  // 等待渲染完成后调起打印。
  w.addEventListener('load', () => { try { w.focus(); w.print(); } catch (_) {} });
  setTimeout(() => { try { w.focus(); w.print(); } catch (_) {} }, 600);
  setTimeout(() => URL.revokeObjectURL(url), 120000);
}

/**
 * AI 智能简报：复用既有 generateAI 流程生成叙述性内容，
 * 完成后在 AI 输出弹窗里追加“导出简报 HTML/PDF”的入口。
 * 这里直接调用 generateAI（discharge_summary 最贴近“简报”用途）。
 */
function generateCaseBriefAI(caseId) {
  if (typeof generateAI !== 'function') {
    showToast('⚠️', 'AI 模块未加载');
    return;
  }
  // 标记当前 AI 输出来源于哪个病例，供导出按钮使用。
  _caseBriefAICaseId = caseId;
  generateAI(caseId, 'discharge_summary');
}

let _caseBriefAICaseId = null;

/** 把当前 AI 输出（currentAiOutput）连同结构化数据一起导出为简报。 */
function exportCaseBriefAIHtml() {
  if (!_caseBriefAICaseId) { showToast('⚠️', '请先生成 AI 简报'); return; }
  exportCaseBriefHtml(_caseBriefAICaseId, { aiNarrative: currentAiOutput || '' });
}
function exportCaseBriefAIPdf() {
  if (!_caseBriefAICaseId) { showToast('⚠️', '请先生成 AI 简报'); return; }
  exportCaseBriefPdf(_caseBriefAICaseId, { aiNarrative: currentAiOutput || '' });
}

/** 复制患者基本信息的简短文本摘要到剪贴板。 */
function copyCaseBasicInfo(caseId) {
  const c = loadDB().find(x => x.id === caseId);
  if (!c) { showToast('⚠️', '未找到病例'); return; }
  const mod = MODULES[c.module];
  const d = c.data || {};
  const stage = (typeof getCaseStageSummary === 'function') ? getCaseStageSummary(c) : {};
  const pct = (typeof calcOverallProgress === 'function') ? calcOverallProgress(c)
    : ((typeof calcProgress === 'function') ? calcProgress(c) : '');
  const lines = [
    `姓名：${d.basic_name || d.name || '未命名患者'}`,
    `病种：${mod?.name || c.module || ''}`,
    (d.basic_gender || d.gender) ? `性别：${d.basic_gender || d.gender}` : '',
    (d.basic_age || d.age) ? `年龄：${d.basic_age || d.age}` : '',
    (d.basic_side || d.side) ? `患侧：${d.basic_side || d.side}` : '',
    `建档：${c.createdAt || ''}`,
    `完整度：${pct}% · 审核状态：${stage.review || ''}`,
  ].filter(Boolean);
  const text = lines.join('\n');
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text)
      .then(() => showToast('📋', '患者基本信息已复制到剪贴板'))
      .catch(() => showToast('⚠️', '复制失败，请手动复制'));
  } else {
    showToast('⚠️', '当前浏览器不支持剪贴板写入');
  }
}



/* assets/auth-reset.js */
// ===================== FORGOT / RESET PASSWORD =====================
// 前端对接后端公开端点（无需 Authorization 头）：
//   POST /api/auth/forgot-password  body {identifier}        → 永远 200，通用提示
//   POST /api/auth/reset-password   body {token, new_password} → 200 成功 / 400 {detail}
// 这两个端点是公开的：backendRequest(..., requireAuth=false)。

// ── 忘记密码弹窗 ──────────────────────────────────────────────
function showForgotPasswordModal() {
  const modal = document.getElementById('forgotPasswordModal');
  if (!modal) return;
  const input = document.getElementById('fp-identifier');
  if (input) input.value = '';
  const msg = document.getElementById('fp-message');
  if (msg) { msg.textContent = ''; msg.className = 'field-error'; msg.style.display = 'none'; }
  modal.classList.add('open');
  setTimeout(() => input?.focus(), 0);
}

async function submitForgotPassword() {
  const input = document.getElementById('fp-identifier');
  const msg = document.getElementById('fp-message');
  const identifier = (input?.value || '').trim();
  if (!identifier) {
    if (msg) { msg.textContent = '请输入用户名或邮箱'; msg.style.display = 'block'; }
    return;
  }
  if (!isCloudMode()) {
    if (msg) { msg.textContent = '找回密码需要云端后端；当前为本地单机模式。'; msg.style.display = 'block'; }
    return;
  }
  const btn = document.getElementById('fp-submit-btn');
  if (btn) { btn.disabled = true; btn.textContent = '提交中…'; }
  try {
    // 后端永远返回 200 + 通用消息，不暴露账号是否存在。
    const result = await backendRequest('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ identifier }),
    }, false);
    const generic = (result && result.message)
      ? result.message
      : '如果该账号存在，我们已发送密码重置邮件，请查收。';
    if (msg) { msg.className = 'field-hint'; msg.style.color = 'var(--green, #16a34a)'; msg.textContent = generic; msg.style.display = 'block'; }
  } catch (err) {
    // 网络/服务异常也给中性提示，不区分账号存在与否。
    if (msg) { msg.className = 'field-error'; msg.style.color = ''; msg.textContent = err.message || '请求失败，请稍后重试'; msg.style.display = 'block'; }
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = '发送重置邮件'; }
  }
}

// ── 重置密码视图（携带 ?reset_token= 时显示）──────────────────
function getResetTokenFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('reset_token') || '';
  } catch (_) { return ''; }
}

function maybeShowResetPasswordView() {
  const token = getResetTokenFromUrl();
  const view = document.getElementById('resetPasswordView');
  if (!view) return;
  if (!token) { view.hidden = true; return; }
  view.hidden = false;
  // 盖住登录欢迎页，避免两者叠加。
  const welcome = document.getElementById('loginWelcome');
  if (welcome) welcome.style.display = 'none';
  const err = document.getElementById('rp-error');
  if (err) { err.textContent = ''; err.style.display = 'none'; }
  const p1 = document.getElementById('rp-password');
  if (p1) setTimeout(() => p1.focus(), 0);
}

// 与注册口径一致的最简强度提示（至少 8 位）。
function updateResetPasswordHint() {
  const p1 = document.getElementById('rp-password');
  const hint = document.getElementById('rp-hint');
  if (!p1 || !hint) return;
  const v = p1.value || '';
  if (!v) { hint.textContent = '至少 8 位，建议包含字母与数字'; hint.style.color = ''; return; }
  if (v.length < 8) { hint.textContent = `还需 ${8 - v.length} 位（至少 8 位）`; hint.style.color = 'var(--amber, #d97706)'; }
  else { hint.textContent = '✓ 长度符合要求'; hint.style.color = 'var(--green, #16a34a)'; }
}

async function submitResetPassword() {
  const token = getResetTokenFromUrl();
  const p1 = document.getElementById('rp-password');
  const p2 = document.getElementById('rp-password2');
  const err = document.getElementById('rp-error');
  const showErr = (m) => { if (err) { err.textContent = m; err.style.display = 'block'; } };

  const pwd = p1?.value || '';
  const pwd2 = p2?.value || '';
  if (!token) { showErr('重置链接无效或已过期，请重新申请。'); return; }
  if (pwd.length < 8) { showErr('密码至少 8 位'); return; }
  if (pwd !== pwd2) { showErr('两次输入的密码不一致'); return; }

  const btn = document.getElementById('rp-submit-btn');
  if (btn) { btn.disabled = true; btn.textContent = '提交中…'; }
  try {
    await backendRequest('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: pwd }),
    }, false);
    showToast('✅', '密码已重置，请使用新密码登录');
    // 从 URL 剥离 token，避免刷新后再次触发重置视图 / 泄露 token。
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('reset_token');
      window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
    } catch (_) {}
    const view = document.getElementById('resetPasswordView');
    if (view) view.hidden = true;
    const welcome = document.getElementById('loginWelcome');
    if (welcome) welcome.style.display = '';
    if (typeof focusWelcomeLogin === 'function') focusWelcomeLogin();
  } catch (e) {
    // 400 时后端返回 {detail}，inline 展示。
    showErr(e.detail || e.message || '重置失败，链接可能已失效，请重新申请。');
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = '重置密码'; }
  }
}

// 页面加载时检测 reset_token。
window.addEventListener('DOMContentLoaded', maybeShowResetPasswordView);



/* assets/app.js */
// ===================== INIT =====================
window.addEventListener('DOMContentLoaded', async () => {
  const today = new Date().toISOString().split('T')[0];
  const dateEl = document.getElementById('nc-date');
  if (dateEl) dateEl.value = today;

  renderSidebar();
  updateStats();
  renderDashboard();
  renderAuthState();
  const syncToast = (isCloudMode() && isLoggedIn())
    ? showToast('同步', '正在同步云端病例...', 0)
    : null;
  try {
    await refreshCenters();
    await syncCloudCasesToLocal();
    if (typeof refreshAdmissionTasks === 'function') await refreshAdmissionTasks();
  } finally {
    if (syncToast) syncToast.remove();
  }
  updateStats();
  renderSidebar();
  renderDashboard();
  switchAuthTab('login');

  // Click outside modal to close
  document.querySelectorAll('.modal-overlay').forEach(overlay => {
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) overlay.classList.remove('open');
    });
  });

  // 模式/登录引导改用顶部常驻横幅（替代易被忽略且会重复打扰的一次性 toast）
  if (typeof renderOnboardingBanner === 'function') renderOnboardingBanner();
  if (typeof renderPiiToggle === 'function') renderPiiToggle();

  // SEC-6: 集中事件委托。所有 [data-action] 元素都在这里集中分发，
  // 避免在 HTML/动态模板里写 inline `on*` 属性，CSP 即可去掉 'unsafe-inline'。
  installCspEventDispatchers();

  // 网络字体非阻塞加载收尾：index.html 里以 media=print 引入（不阻塞首屏渲染），
  // 应用就绪后切回 all，display=swap 让标题衬线/等宽字体渐进生效。
  const webfonts = document.getElementById('webfontsCss');
  if (webfonts) webfonts.media = 'all';
});

// ===================== SEC-6: CSP 事件委托 =====================
// 设计：每种事件类型一张 ACTIONS 表，避免不同事件混用造成歧义。
// 元素带 `data-action="xxx"`，dispatcher 在对应事件流里查表并调用既有全局函数。
// dispatcher 把 (el, e) 两参数都传给 handler，保留原 `event.stopPropagation()`/`preventDefault()` 语义。
function installCspEventDispatchers() {
  /** 安全调用全局函数（不存在时静默忽略，避免动态渲染时机问题） */
  const call = (name, ...args) => {
    const fn = window[name];
    if (typeof fn === 'function') return fn(...args);
  };
  const intArg = (v, fallback = NaN) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : fallback;
  };
  // 旧 inline 处理函数读 `e.currentTarget`，而事件委托下 currentTarget=document。
  // 用 Proxy 透传原 event 字段、把 `currentTarget` 改写为匹配元素，零侵入兼容。
  const withCurrentTarget = (e, el) => {
    if (!e) return e;
    return new Proxy(e, {
      get(target, key) {
        if (key === 'currentTarget') return el;
        const v = target[key];
        return typeof v === 'function' ? v.bind(target) : v;
      },
    });
  };

  /* ---------- click ---------- */
  const CLICK_ACTIONS = {
    // —— index.html 静态按钮 ——
    'switch-welcome-auth-login':  () => call('switchWelcomeAuth', 'login'),
    'switch-welcome-auth-register': () => call('switchWelcomeAuth', 'register'),
    'login-welcome':              () => call('loginWelcomeUser'),
    'login-demo':                 () => call('loginDemoUser'),
    'show-forgot-password':       () => call('showForgotPasswordModal'),
    'register-welcome':           () => call('registerWelcomeUser'),
    'submit-reset-password':      () => call('submitResetPassword'),
    'toggle-mobile-sidebar':      () => call('toggleMobileSidebar'),
    'close-mobile-sidebar':       () => call('closeMobileSidebar'),
    'toggle-pii-reveal':          () => call('togglePiiReveal'),
    'open-dashboard':             () => call('openDashboardView'),
    'open-todo-workbench':        () => call('openTodoWorkbenchView'),
    'open-admission-scan':        () => call('openAdmissionScanModal'),
    'show-auth':                  () => call('showAuthModal'),
    'show-new-case':              () => call('showNewCaseModal'),
    'toggle-more-menu':           (_el, e) => call('toggleMoreMenu', e),
    'show-feature-overview':      () => { call('showFeatureOverviewModal'); call('closeMoreMenu'); },
    'open-center-settings':       () => { call('openCenterSettings'); call('closeMoreMenu'); },
    'show-import':                () => { call('showImportModal'); call('closeMoreMenu'); },
    'export-all':                 () => { call('exportAllData'); call('closeMoreMenu'); },
    'show-user-admin':            () => { call('showUserAdminModal'); call('closeMoreMenu'); },
    'show-settings':              () => { call('showSettingsModal'); call('closeMoreMenu'); },
    'show-settings-direct':       () => call('showSettingsModal'),
    'set-folder-filter':          (el) => call('setFolderFilter', el.dataset.folder),
    'filter-cases':               (el) => call('filterCases', el.dataset.filter),
    'close-modal':                (el) => call('closeModal', el.dataset.modal),
    // 「关闭 + 跳转」组合（功能总览卡片）
    'close-modal-then':           (el) => {
      const mod = el.dataset.modal;
      const then = el.dataset.then;
      if (mod) call('closeModal', mod);
      if (then) call(then);
    },
    'show-toast':                 (el) => call('showToast', el.dataset.toastTitle || '提示', el.dataset.toastBody || ''),
    'create-case':                () => call('createCase'),
    'run-admission-scan':         () => call('runAdmissionScan'),
    'dispatch-admission-task':    () => call('dispatchAdmissionTask'),
    'run-scan-extraction':        () => call('runScanExtraction'),
    'apply-scan-matches':         () => call('applyScanMatches'),
    'copy-ai-output':             () => call('copyAiOutput'),
    'print-ai-output':            () => call('printAiOutput'),
    'export-case-brief-ai-html':  () => call('exportCaseBriefAIHtml'),
    'export-case-brief-ai-pdf':   () => call('exportCaseBriefAIPdf'),
    'export-case-pptx':           (el) => call('exportCaseBriefPptx', el.dataset.caseId),
    'export-case-narrative-pptx': (el) => call('exportCaseNarrativePptx', el.dataset.caseId),
    'export-case-narrative-pptx-expanded': (el) => call('exportCaseNarrativePptx', el.dataset.caseId, { imageMode: 'expanded' }),
    'download-ai-output':         () => call('downloadAiOutput'),
    'preview-ai-output-html':     () => call('previewAIOutputHTML'),
    'confirm-cross-border':       () => call('confirmCrossBorder'),
    'cancel-cross-border':        () => call('cancelCrossBorder'),
    'clear-ai-chat-history':      () => call('clearAIChatHistory'),
    'close-ai-chat':              () => call('closeAIChat'),
    'send-ai-chat-preset':        (el) => call('sendAIChatPreset', el.dataset.preset || ''),
    'send-ai-chat':               () => call('sendAIChat'),
    'stop-ai-chat':               () => call('stopAIChat'),
    'switch-settings-tab':        (el) => call('switchSettingsTab', el.dataset.tab),
    'save-current-model-preset':  () => call('saveCurrentModelAsPreset'),
    'run-agent-workflow-scan':    () => call('runAgentWorkflowScan'),
    'load-centers-into-settings': () => call('loadCentersIntoSettings'),
    'create-center-from-settings': () => call('createCenterFromSettings'),
    'clear-all-data':             () => call('clearAllData'),
    'save-settings':              () => call('saveSettings'),
    'load-user-admin-list':       () => call('loadUserAdminList'),
    'go-backend-settings-from-auth': () => call('goBackendSettingsFromAuth'),
    'switch-auth-tab':            (el) => call('switchAuthTab', el.dataset.tab),
    'login-user':                 () => call('loginUser'),
    'register-user':              () => call('registerUser'),
    'submit-forgot-password':     () => call('submitForgotPassword'),
    'confirm-import':             () => call('confirmImport'),
    'open-import-file-picker':    () => document.getElementById('importFileInput')?.click(),

    // —— render.js 动态模板按钮 ——
    'show-auth-modal':            () => call('showAuthModal'),
    'dismiss-onboarding':         () => call('dismissOnboarding'),
    'set-case-facet-filter':      (el) => call('setCaseFacetFilter', el.dataset.facetKey, el.dataset.facetId),
    'set-dashboard-queue':        (el) => call('setDashboardQueue', el.dataset.queueId),
    'set-work-scope':             (el) => call('setWorkScope', el.dataset.scopeId),
    'set-todo-scope':             (el) => call('setTodoScope', el.dataset.scopeId),
    'open-case-at-phase':         (el) => call('openCaseAtPhase', el.dataset.caseId, el.dataset.phaseId),
    'open-case':                  (el) => call('openCase', el.dataset.caseId),
    'toggle-favorite':            (el) => call('toggleFavorite', el.dataset.caseId),
    'remove-case-tag':            (el) => call('removeCaseTag', el.dataset.caseId, el.dataset.tag),
    'prompt-add-tag':             (el) => call('promptAddTag', el.dataset.caseId),
    'prompt-change-case-module':  (el) => call('promptChangeCaseModule', el.dataset.caseId),
    'export-anonymized-case':     (el) => call('exportAnonymizedCase', el.dataset.caseId),
    'set-case-phase':             (el) => call('setCasePhase', el.dataset.phaseId),
    'toggle-section':             (el) => call('toggleSection', el.dataset.sectionId),
    'expand-all-sections':        () => call('toggleAllSections', true),
    'collapse-all-sections':      () => call('toggleAllSections', false),
    'go-back':                    () => call('navBack'),
    'open-scan-modal':            (el) => call('openScanModal', el.dataset.caseId),
    'generate-ai':                (el) => call('generateAI', el.dataset.caseId, el.dataset.aiType),
    'save-case':                  (el) => call('saveCase', el.dataset.caseId),
    'window-print':               () => window.print(),
    'delete-case':                (el) => call('deleteCase', el.dataset.caseId),
    'focus-form-section':         (el) => call('focusFormSection', el.dataset.targetSection),
    'focus-missing-field':        (el) => call('focusMissingField', el.dataset.caseId, el.dataset.mode || 'first'),
    'focus-phase-missing':        (el) => call('focusPhaseMissing', el.dataset.phaseType, el.dataset.sectionId, el.dataset.fieldKey),
    'set-review-status':          (el) => call('setReviewStatus', el.dataset.caseId, el.dataset.reviewStatus),
    'set-center-filter':          (el) => call('setCenterFilter', el.dataset.centerKey),
    'trigger-upload':             (el) => call('triggerUpload', el.dataset.mediaKey),
    'show-move-slot-picker':      (el, e) => {
      if (e) e.stopPropagation();
      call('showMoveSlotPicker', el.dataset.caseId, el.dataset.mediaKey, intArg(el.dataset.mediaIdx));
    },
    'remove-media':               (el) => call('removeMedia', el.dataset.caseId, el.dataset.mediaKey, intArg(el.dataset.mediaIdx)),
    'open-window':                (el) => window.open(el.dataset.url || '', '_blank'),
    'open-lightbox':              (el) => {
      const node = el.tagName === 'IMG' || el.tagName === 'VIDEO' ? el : el.querySelector('img,video');
      const src = (node && (node.currentSrc || node.src)) || el.dataset.lightboxSrc || '';
      const label = el.dataset.lightboxLabel || '';
      const isVideo = el.dataset.lightboxVideo === '1';
      call('openLightbox', src, label, isVideo);
    },
    'open-ai-chat':               (el) => call('openAIChat', el.dataset.caseId),
    'show-settings-modal':        () => call('showSettingsModal'),

    // —— render.js dash-card 内嵌按钮 ——
    // 旧函数依赖 e.currentTarget = 触发元素；事件委托后 currentTarget=document，
    // 用 Proxy 把 currentTarget 重写为匹配到的元素，避免改全局函数签名。
    'case-card-menu':             (el, e) => {
      if (e) { e.stopPropagation(); e.preventDefault(); }
      call('handleCaseCardMenuBtn', withCurrentTarget(e, el), el.dataset.caseId);
    },
    'focus-missing-stop':         (el, e) => {
      if (e) e.stopPropagation();
      call('focusMissingField', el.dataset.caseId);
    },

    // —— case-actions.js Lightbox / Move picker / context menu ——
    'close-lightbox':             () => call('closeLightbox'),
    'retry-upload':               (el) => call('retryUpload', el.dataset.tempId),
    'remove-error-thumb':         (el) => {
      const c = el.closest('.image-thumb-error');
      if (c) c.remove();
    },
    'close-move-slot-picker':     () => call('closeMoveSlotPicker'),
    'move-media-to-slot':         (el) => {
      call('closeMoveSlotPicker');
      call('moveMediaToSlot', el.dataset.caseId, el.dataset.fromKey, intArg(el.dataset.mediaIdx), el.dataset.toKey);
    },
    'case-context-menu-item':     (el) => {
      // 来自 openCaseContextMenu 的菜单项：data-fn=函数名, data-case-id=参数
      call('closeCaseContextMenu');
      const fn = el.dataset.fn;
      const caseId = el.dataset.caseId;
      if (fn) call(fn, caseId);
    },

    // —— ai.js ——
    'retry-generate-ai':          (el) => call('generateAI', el.dataset.caseId, el.dataset.aiType),
    'stop-generate-ai':           () => call('stopAIGeneration'),
    'regenerate-ai':              (el) => call('regenerateAI', el.dataset.caseId, el.dataset.aiType),
    'close-ai-modal':             () => call('closeAIModal'),
    'retry-ai-chat':              (el) => call('_retryAIChat', el.dataset.retryText || '', intArg(el.dataset.idx)),
    'copy-ai-chat-msg':           (el) => call('_copyAIChatMsg', intArg(el.dataset.idx)),
    'download-ai-chat-msg':       (el) => call('_downloadAIChatMsg', intArg(el.dataset.idx)),
    'preview-ai-chat-html':       (el) => call('_previewAIChatHTML', intArg(el.dataset.idx)),
    'download-ai-chat-html':      (el) => call('_downloadAIChatHTML', intArg(el.dataset.idx)),
    'close-report-preview':       () => call('closeReportPreviewModal'),

    // —— user-settings.js ——
    'update-user-admin':          (el) => call('updateUserAdmin', intArg(el.dataset.userId), el.dataset.activate === '1'),
    'save-user-role':             (el) => call('saveUserRole', intArg(el.dataset.userId)),
    'delete-user-account':        (el) => call('deleteUserAccount', intArg(el.dataset.userId), el.dataset.username || ''),

    // —— render.js 患者通知（出院后注意事项）Phase 1 ——
    'pm-save-consent':            (el) => call('pmSaveConsent', el.dataset.caseId),
    'pm-generate':                (el) => call('pmGenerate', el.dataset.caseId),
    'pm-save-draft':              (el) => call('pmSaveDraft', el.dataset.caseId),
    'pm-submit':                  (el) => call('pmSubmit', el.dataset.caseId),
    'pm-approve':                 (el) => call('pmApprove', el.dataset.caseId),
    'pm-reject':                  (el) => call('pmReject', el.dataset.caseId),
    'pm-preview':                 (el) => call('pmPreview', el.dataset.caseId),
    'close-patient-msg-preview':  () => call('closePatientMsgPreviewModal'),
    'copy-patient-msg-sms':       () => call('copyPatientMsgSms'),

    // —— render.js FAM 互联（A：X 线手动同步；B/C：测量结果查看与采纳）——
    'fam-sync':                   (el) => call('famSyncCase', el.dataset.caseId),
    'fam-load-measurements':      (el) => call('famLoadMeasurements', el.dataset.caseId),
    'fam-adopt-measurement':      (el) => call('famAdoptMeasurement', el.dataset.caseId, el.dataset.measurementId),

    // —— scan.js ——
    'prompt-assign-admission-task': (el) => call('promptAssignAdmissionTask', el.dataset.taskId),
    'create-case-from-admission-task': (el) => call('createCaseFromAdmissionTask', el.dataset.taskId),
    'archive-admission-task':     (el) => call('archiveAdmissionTask', el.dataset.taskId),
  };

  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-action]');
    if (!t) return;
    const fn = CLICK_ACTIONS[t.dataset.action];
    if (fn) fn(t, e);
  });

  /* ---------- change ---------- */
  const CHANGE_ACTIONS = {
    'case-sort-mode':             (el) => call('setCaseSortMode', el.value),
    'handle-admission-image':     (el) => call('handleAdmissionImage', el),
    'handle-scan-image':          (el) => call('handleScanImage', el),
    'handle-import-file':         (el) => call('handleImportFile', el),
    'handle-checkbox':            (el) => {
      call('handleCheckbox', el.dataset.fieldKey, el.dataset.opt, el.checked, el);
    },
    'autosave-field-change':      (el) => call('autoSaveField', el.dataset.fieldKey, el.value),
    'handle-upload':              (el) => call('handleUpload', el.dataset.caseId, el.dataset.mediaKey, el.dataset.mediaLabel, el),
    'ai-chat-tier-change':        () => call('aiChatTierChange'),
  };
  document.addEventListener('change', (e) => {
    const t = e.target.closest('[data-action-change]');
    if (!t) return;
    const fn = CHANGE_ACTIONS[t.dataset.actionChange];
    if (fn) fn(t, e);
  });

  /* ---------- input ---------- */
  const INPUT_ACTIONS = {
    'update-reset-password-hint': () => call('updateResetPasswordHint'),
    'autosave-field':             (el) => call('autoSaveField', el.dataset.fieldKey, el.value),
    'autosave-field-grow':        (el) => { call('autoGrowTextarea', el); call('autoSaveField', el.dataset.fieldKey, el.value); },
    'dashboard-search':           (el) => call('setDashboardSearch', el.value),
  };
  document.addEventListener('input', (e) => {
    const t = e.target.closest('[data-action-input]');
    if (!t) return;
    const fn = INPUT_ACTIONS[t.dataset.actionInput];
    if (fn) fn(t, e);
  });

  /* ---------- keydown ---------- */
  const KEYDOWN_ACTIONS = {
    'ai-chat-keydown':            (_el, e) => call('_aiChatKeydown', e),
  };
  document.addEventListener('keydown', (e) => {
    const t = e.target.closest('[data-action-keydown]');
    if (!t) return;
    const fn = KEYDOWN_ACTIONS[t.dataset.actionKeydown];
    if (fn) fn(t, e);
  });

  /* ---------- dragstart / dragover / dragleave / drop ---------- */
  document.addEventListener('dragstart', (e) => {
    const t = e.target.closest('[data-action-dragstart]');
    if (!t) return;
    if (t.dataset.actionDragstart === 'thumb-drag') {
      call('_thumbDragStart', e, t.dataset.caseId, t.dataset.mediaKey, intArg(t.dataset.mediaIdx));
    }
  });
  document.addEventListener('dragover', (e) => {
    const t = e.target.closest('[data-action-dragover]');
    if (!t) return;
    const v = t.dataset.actionDragover;
    if (v === 'slot-drag-over') call('_slotDragOver', e);
    else if (v === 'import-drop-zone') {
      e.preventDefault();
      t.classList.add('dragover');
    }
  });
  document.addEventListener('dragleave', (e) => {
    const t = e.target.closest('[data-action-dragleave]');
    if (!t) return;
    const v = t.dataset.actionDragleave;
    if (v === 'slot-drag-leave') call('_slotDragLeave', e);
    else if (v === 'import-drop-zone') t.classList.remove('dragover');
  });
  document.addEventListener('drop', (e) => {
    const t = e.target.closest('[data-action-drop]');
    if (!t) return;
    const v = t.dataset.actionDrop;
    if (v === 'slot-drop') call('_slotDrop', e, t.dataset.caseId, t.dataset.mediaKey);
    else if (v === 'import-drop') call('handleImportDrop', e);
  });

  /* ---------- contextmenu ---------- */
  document.addEventListener('contextmenu', (e) => {
    const t = e.target.closest('[data-action-contextmenu]');
    if (!t) return;
    if (t.dataset.actionContextmenu === 'case-card') {
      call('handleCaseCardContextMenu', withCurrentTarget(e, t), t.dataset.caseId);
    }
  });

  /* ---------- touchstart / touchend / touchmove ---------- */
  document.addEventListener('touchstart', (e) => {
    const t = e.target.closest('[data-action-touchstart]');
    if (!t) return;
    if (t.dataset.actionTouchstart === 'case-card') {
      call('handleCaseCardTouchStart', withCurrentTarget(e, t), t.dataset.caseId);
    }
  }, { passive: true });
  document.addEventListener('touchend', (e) => {
    const t = e.target.closest('[data-action-touchend]');
    if (!t) return;
    if (t.dataset.actionTouchend === 'case-card') call('handleCaseCardTouchEnd');
  });
  document.addEventListener('touchmove', (e) => {
    const t = e.target.closest('[data-action-touchmove]');
    if (!t) return;
    if (t.dataset.actionTouchmove === 'case-card') call('handleCaseCardTouchEnd');
  }, { passive: true });

  /* ---------- error (媒体预览失败) ---------- */
  // <img onerror> 不冒泡，必须用 capture 才能委托。
  document.addEventListener('error', (e) => {
    const t = e.target;
    if (!t || !t.dataset) return;
    const v = t.dataset.actionError;
    if (!v) return;
    if (v === 'mark-media-error') call('markMediaPreviewError', t);
    else if (v === 'media-fallback') {
      const fallback = t.dataset.fallbackSrc;
      if (fallback && t.src !== fallback) {
        t.dataset.actionError = 'mark-media-error'; // 回退失败再走标准错误处理
        t.src = fallback;
      }
    }
  }, true);
}

